import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { gsap } from "gsap";
import { generatePersonalityProfile, getStage1Encouragement, ZODIAC_TRAITS } from "./personalityEngine";
import { API_URL, api, auth } from "./config";
import { initAutoSync, pullFromServer, fetchReceivedReactions, setUserTier as setUserTierSync, saveInterviewProgress, clearInterviewProgress, saveTheme, saveColorScheme, addVoicemailReaction, saveGivenReaction, saveUserReport, saveDatingPreferences, savePollResponse, syncAppConnections, syncSecretContext, syncUserContext, syncExactContext } from "./autoSync";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { SpeechRecognition as CapSpeechRecognition } from "@capacitor-community/speech-recognition";
import { TextToSpeech as CapTTS } from "@capacitor-community/text-to-speech";

// ── Platform detection ──
const IS_NATIVE = Capacitor.isNativePlatform();

// ── API base URL for fetch calls (handles both web/Nginx and Android/Capacitor) ──
const API_BASE = API_URL || "";

// ── Session persistence helpers ────────────────────────────────
const SESSION_KEY = "soul_session_token";
const ANSWERS_KEY  = "soul_stage1_answers";
function saveStage1Locally(answers, sessionToken) {
  try {
    localStorage.setItem(ANSWERS_KEY, JSON.stringify(answers));
    if (sessionToken) localStorage.setItem(SESSION_KEY, sessionToken);
  } catch {}
}
function loadStage1Locally() {
  try { return JSON.parse(localStorage.getItem(ANSWERS_KEY) || "null"); } catch { return null; }
}
function getLocalSessionToken() {
  try { return localStorage.getItem(SESSION_KEY); } catch { return null; }
}

// ─── Premium Tier & Feature Flags ─────────────────────────────
const PREMIUM_KEY = "soul_premium_tier";
const FEATURE_FLAGS_KEY = "admin_feature_flags";

const TIERS = {
  free: { id: "free", label: "Free", price: 0, period: "" },
  monthly: { id: "monthly", label: "Premium Monthly", price: 19.99, period: "/month" },
  annual: { id: "annual", label: "Premium Annual", price: 100, period: "/year" }
};

const DEFAULT_FEATURE_FLAGS = {
  emoji_reactions: { enabled: true, label: "Emoji Reaction Gifts", description: "Send emoji reactions to your match", tier: "free" },
  polls: { enabled: true, label: "Send Polls", description: "Create and send polls to your match", tier: "free" },
  send_phone: { enabled: true, label: "Share Phone Number", description: "Send your phone number to a match", tier: "premium" },
  send_message: { enabled: true, label: "Chat & Send Images", description: "Send text messages and images to your match", tier: "premium" },
  in_app_call: { enabled: true, label: "In-App Calling", description: "Call your match directly through the app", tier: "premium" }
};

function getUserTier() {
  try { return localStorage.getItem(PREMIUM_KEY) || "free"; } catch { return "free"; }
}
function setUserTier(tier) {
  setUserTierSync(tier); // Auto-syncs to Supabase
}
function isPremiumUser() {
  const t = getUserTier();
  return t === "monthly" || t === "annual";
}
function getFeatureFlags() {
  try {
    const saved = localStorage.getItem(FEATURE_FLAGS_KEY);
    if (saved) return JSON.parse(saved);
    return DEFAULT_FEATURE_FLAGS;
  } catch { return DEFAULT_FEATURE_FLAGS; }
}
function isFeatureEnabled(featureKey) {
  const flags = getFeatureFlags();
  return flags[featureKey]?.enabled !== false;
}
function canAccessFeature(featureKey) {
  if (!isFeatureEnabled(featureKey)) return false;
  const flags = getFeatureFlags();
  if (flags[featureKey]?.tier === "free") return true;
  return isPremiumUser();
}

// ─── Design Tokens ────────────────────────────────────────────
const COLOR_SCHEMES = {
  // 1. Classic Rose (Default)
  rose: {
    name: "Classic Rose",
    light: {
      rose:     "#f43f5e",
      roseDeep: "#e11d48",
      roseGlow: "rgba(244,63,94,0.15)",
      roseSoft: "rgba(244,63,94,0.08)",
      amber:    "#f59e0b",
      teal:     "#14b8a6",
      violet:   "#8b5cf6",
      violetDeep: "#7c3aed",
      bg:       "#fafafa",
      card:     "#ffffff",
      cardAlt:  "#f5f5f5",
      border:   "rgba(0,0,0,0.08)",
      borderHover: "rgba(244,63,94,0.25)",
      text:     "#1a1a1a",
      muted:    "#737373",
      dim:      "#e5e5e5",
    },
    dark: {
      rose:     "#f43f5e",
      roseDeep: "#e11d48",
      roseGlow: "rgba(244,63,94,0.3)",
      roseSoft: "rgba(244,63,94,0.12)",
      amber:    "#f59e0b",
      teal:     "#14b8a6",
      violet:   "#8b5cf6",
      violetDeep: "#7c3aed",
      bg:       "#080610",
      card:     "#110f1a",
      cardAlt:  "#1a1726",
      border:   "rgba(255,255,255,0.07)",
      borderHover: "rgba(244,63,94,0.35)",
      text:     "#f0edf5",
      muted:    "#8b7fa0",
      dim:      "#2d2840",
    }
  },
  // 2. Soft Beach - Minimal Colors
  beach: {
    name: "Soft Beach",
    light: {
      rose:     "#ffa8b6",
      roseDeep: "#ff8fa0",
      roseGlow: "rgba(255,168,182,0.15)",
      roseSoft: "rgba(255,168,182,0.08)",
      amber:    "#edf756",
      teal:     "#9df9ef",
      violet:   "#51e2f5",
      violetDeep: "#3dcce0",
      bg:       "#fefefe",
      card:     "#ffffff",
      cardAlt:  "#f9f9f9",
      border:   "rgba(162,128,137,0.12)",
      borderHover: "rgba(255,168,182,0.25)",
      text:     "#2d2d2d",
      muted:    "#a28089",
      dim:      "#f0f0f0",
    },
    dark: {
      rose:     "#ffa8b6",
      roseDeep: "#ff8fa0",
      roseGlow: "rgba(255,168,182,0.25)",
      roseSoft: "rgba(255,168,182,0.12)",
      amber:    "#edf756",
      teal:     "#9df9ef",
      violet:   "#51e2f5",
      violetDeep: "#3dcce0",
      bg:       "#1a1520",
      card:     "#241f2d",
      cardAlt:  "#2d2838",
      border:   "rgba(255,255,255,0.08)",
      borderHover: "rgba(255,168,182,0.3)",
      text:     "#f5f0f8",
      muted:    "#a28089",
      dim:      "#3a3545",
    }
  },
  // 3. Purple 90's
  purple90s: {
    name: "Purple 90's",
    light: {
      rose:     "#d0bdf4",
      roseDeep: "#b8a0e8",
      roseGlow: "rgba(208,189,244,0.15)",
      roseSoft: "rgba(208,189,244,0.08)",
      amber:    "#e5eaf5",
      teal:     "#a0d2eb",
      violet:   "#8458b3",
      violetDeep: "#6d4a95",
      bg:       "#f8f7fc",
      card:     "#ffffff",
      cardAlt:  "#f0eef8",
      border:   "rgba(132,88,179,0.12)",
      borderHover: "rgba(208,189,244,0.25)",
      text:     "#2d2440",
      muted:    "#8b7fa0",
      dim:      "#e8e5f0",
    },
    dark: {
      rose:     "#d0bdf4",
      roseDeep: "#b8a0e8",
      roseGlow: "rgba(208,189,244,0.25)",
      roseSoft: "rgba(208,189,244,0.12)",
      amber:    "#e5eaf5",
      teal:     "#a0d2eb",
      violet:   "#8458b3",
      violetDeep: "#6d4a95",
      bg:       "#1a1528",
      card:     "#251f38",
      cardAlt:  "#2f2842",
      border:   "rgba(255,255,255,0.08)",
      borderHover: "rgba(208,189,244,0.3)",
      text:     "#f0edf5",
      muted:    "#a28fb8",
      dim:      "#3d3550",
    }
  },
  // 4. Bright Power
  brightPower: {
    name: "Bright Power",
    light: {
      rose:     "#ff1d58",
      roseDeep: "#e01048",
      roseGlow: "rgba(255,29,88,0.15)",
      roseSoft: "rgba(255,29,88,0.08)",
      amber:    "#fff685",
      teal:     "#00ddff",
      violet:   "#0049b7",
      violetDeep: "#003a95",
      bg:       "#fefefe",
      card:     "#ffffff",
      cardAlt:  "#f9f9f9",
      border:   "rgba(0,0,0,0.08)",
      borderHover: "rgba(255,29,88,0.25)",
      text:     "#1a1a1a",
      muted:    "#666666",
      dim:      "#f0f0f0",
    },
    dark: {
      rose:     "#ff1d58",
      roseDeep: "#e01048",
      roseGlow: "rgba(255,29,88,0.3)",
      roseSoft: "rgba(255,29,88,0.12)",
      amber:    "#fff685",
      teal:     "#00ddff",
      violet:   "#0049b7",
      violetDeep: "#003a95",
      bg:       "#0a0a0f",
      card:     "#15151f",
      cardAlt:  "#1f1f2a",
      border:   "rgba(255,255,255,0.08)",
      borderHover: "rgba(255,29,88,0.35)",
      text:     "#f5f5f5",
      muted:    "#999999",
      dim:      "#2a2a35",
    }
  },
  // 5. Deep Blue Tan
  deepBlueTan: {
    name: "Deep Blue Tan",
    light: {
      rose:     "#c89666",
      roseDeep: "#b8864f",
      roseGlow: "rgba(200,150,102,0.15)",
      roseSoft: "rgba(200,150,102,0.08)",
      amber:    "#e1b382",
      teal:     "#2d545e",
      violet:   "#12343b",
      violetDeep: "#0a2228",
      bg:       "#faf8f5",
      card:     "#ffffff",
      cardAlt:  "#f5f3f0",
      border:   "rgba(45,84,94,0.12)",
      borderHover: "rgba(200,150,102,0.25)",
      text:     "#1a1a1a",
      muted:    "#6b7a82",
      dim:      "#e8e6e3",
    },
    dark: {
      rose:     "#c89666",
      roseDeep: "#b8864f",
      roseGlow: "rgba(200,150,102,0.25)",
      roseSoft: "rgba(200,150,102,0.12)",
      amber:    "#e1b382",
      teal:     "#2d545e",
      violet:   "#12343b",
      violetDeep: "#0a2228",
      bg:       "#0f1618",
      card:     "#1a2225",
      cardAlt:  "#242d30",
      border:   "rgba(255,255,255,0.08)",
      borderHover: "rgba(200,150,102,0.3)",
      text:     "#f0ede8",
      muted:    "#8b9aa2",
      dim:      "#2f3a3d",
    }
  },
  // 6. Earthy Modern
  earthy: {
    name: "Earthy Modern",
    light: {
      rose:     "#de493c",
      roseDeep: "#c83d30",
      roseGlow: "rgba(222,73,60,0.15)",
      roseSoft: "rgba(222,73,60,0.08)",
      amber:    "#ce9e62",
      teal:     "#608074",
      violet:   "#4b6777",
      violetDeep: "#3d5461",
      bg:       "#fafaf8",
      card:     "#ffffff",
      cardAlt:  "#f5f5f3",
      border:   "rgba(96,128,116,0.12)",
      borderHover: "rgba(222,73,60,0.25)",
      text:     "#2c2c2c",
      muted:    "#7a8a84",
      dim:      "#daefd9",
    },
    dark: {
      rose:     "#de493c",
      roseDeep: "#c83d30",
      roseGlow: "rgba(222,73,60,0.25)",
      roseSoft: "rgba(222,73,60,0.12)",
      amber:    "#ce9e62",
      teal:     "#608074",
      violet:   "#4b6777",
      violetDeep: "#3d5461",
      bg:       "#121614",
      card:     "#1c221f",
      cardAlt:  "#262d2a",
      border:   "rgba(255,255,255,0.08)",
      borderHover: "rgba(222,73,60,0.3)",
      text:     "#f0f0ed",
      muted:    "#9aaa9f",
      dim:      "#303835",
    }
  },
  // 7. Fresh Modern
  fresh: {
    name: "Fresh Modern",
    light: {
      rose:     "#e76d89",
      roseDeep: "#d85a75",
      roseGlow: "rgba(231,109,137,0.15)",
      roseSoft: "rgba(231,109,137,0.08)",
      amber:    "#f2a1a1",
      teal:     "#0db4b9",
      violet:   "#1d1145",
      violetDeep: "#150d35",
      bg:       "#fafafa",
      card:     "#ffffff",
      cardAlt:  "#f5f5f5",
      border:   "rgba(29,17,69,0.12)",
      borderHover: "rgba(231,109,137,0.25)",
      text:     "#1d1145",
      muted:    "#6b5f8a",
      dim:      "#e8e5f0",
    },
    dark: {
      rose:     "#e76d89",
      roseDeep: "#d85a75",
      roseGlow: "rgba(231,109,137,0.25)",
      roseSoft: "rgba(231,109,137,0.12)",
      amber:    "#f2a1a1",
      teal:     "#0db4b9",
      violet:   "#1d1145",
      violetDeep: "#150d35",
      bg:       "#0d0a1a",
      card:     "#181428",
      cardAlt:  "#221d35",
      border:   "rgba(255,255,255,0.08)",
      borderHover: "rgba(231,109,137,0.3)",
      text:     "#f0edf8",
      muted:    "#8b7fa5",
      dim:      "#2d2840",
    }
  },
  // 8. Bright Colorful
  colorful: {
    name: "Bright Colorful",
    light: {
      rose:     "#f95d9b",
      roseDeep: "#e84a88",
      roseGlow: "rgba(249,93,155,0.15)",
      roseSoft: "rgba(249,93,155,0.08)",
      amber:    "#ff8928",
      teal:     "#39a0ca",
      violet:   "#161748",
      violetDeep: "#0f0f35",
      bg:       "#fafafa",
      card:     "#ffffff",
      cardAlt:  "#f5f5f5",
      border:   "rgba(22,23,72,0.12)",
      borderHover: "rgba(249,93,155,0.25)",
      text:     "#161748",
      muted:    "#5f6088",
      dim:      "#e8e8f0",
    },
    dark: {
      rose:     "#f95d9b",
      roseDeep: "#e84a88",
      roseGlow: "rgba(249,93,155,0.25)",
      roseSoft: "rgba(249,93,155,0.12)",
      amber:    "#ff8928",
      teal:     "#39a0ca",
      violet:   "#161748",
      violetDeep: "#0f0f35",
      bg:       "#0a0a18",
      card:     "#141428",
      cardAlt:  "#1e1e35",
      border:   "rgba(255,255,255,0.08)",
      borderHover: "rgba(249,93,155,0.3)",
      text:     "#f0f0f8",
      muted:    "#7f80a8",
      dim:      "#282840",
    }
  }
};

const THEMES = {
  light: COLOR_SCHEMES.rose.light,
  dark: COLOR_SCHEMES.rose.dark
};

// Initialize theme from localStorage or default to light
let T = (() => {
  try {
    const savedMode = localStorage.getItem("soul_theme");
    const savedScheme = localStorage.getItem("soul_color_scheme") || "rose";
    const scheme = COLOR_SCHEMES[savedScheme] || COLOR_SCHEMES.rose;
    return savedMode === "dark" ? scheme.dark : scheme.light;
  } catch {
    return COLOR_SCHEMES.rose.light;
  }
})();

// ─── Responsive Breakpoints ───────────────────────────────────
const BpContext = React.createContext({ isMobile: true, isTablet: false, isDesktop: false, width: 375 });
function useBreakpoint() { return React.useContext(BpContext); }
function BreakpointProvider({ children }) {
  const [width, setWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  const bp = {
    width,
    isMobile:  width < 768,
    isTablet:  width >= 768 && width < 1024,
    isDesktop: width >= 1024,
  };
  return <BpContext.Provider value={bp}>{children}</BpContext.Provider>;
}

// ─── Unified Page Header ──────────────────────────────────────
// Consistent spacing: label → 4px → title → 4px → description
// Wrapper marginBottom: desktop 28, mobile 24
function PageHeader({ label, title, description, rightContent, children, alignTop, titleStyle, gradientAnimation }) {
  const bp = useBreakpoint();
  const baseTitleStyle = {
    fontSize: bp.isDesktop ? 36 : 26,
    fontWeight: 900,
    fontFamily: "'Segoe UI', sans-serif",
    lineHeight: 1.15,
    margin: 0,
    padding: "4px 0",
    overflow: "visible",
    ...titleStyle,
  };
  const gradientStyle = gradientAnimation ? {
    background: `linear-gradient(90deg, ${T.rose}, ${T.violet}, ${T.rose})`,
    backgroundSize: "300% 100%",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    animation: "aurora 6s ease-in-out infinite",
  } : {
    color: T.text,
  };
  return (
    <div style={{ marginBottom: bp.isDesktop ? 28 : 24 }}>
      <p style={{
        fontSize: bp.isDesktop ? 12 : 13,
        color: T.rose,
        fontWeight: 700,
        letterSpacing: "0.12em",
        marginBottom: 4,
        marginTop: 0,
      }}>{label}</p>
      {rightContent ? (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: alignTop ? "flex-start" : "flex-end" }}>
          <h2 style={{ ...baseTitleStyle, ...gradientStyle }}>{title}</h2>
          {rightContent}
        </div>
      ) : (
        <h2 style={{ ...baseTitleStyle, ...gradientStyle }}>{title}</h2>
      )}
      {description && (
        <p style={{
          fontSize: bp.isDesktop ? 14 : 15,
          color: T.muted,
          marginTop: 4,
          marginBottom: 0,
        }}>{description}</p>
      )}
      {children}
    </div>
  );
}

// ─── Voice Engine ─────────────────────────────────────────────
// Supports integrated TTS services (ElevenLabs, Hume, etc.) with Web Speech fallback
// Integrated TTS: ultra-realistic AI voices via API (default)
// Web Speech: browser native, works offline (fallback)

// Module-level voice cache - shared across ALL component instances for consistency
let _cachedVoice = null;

// Text preprocessing for more natural speech pacing
function preprocessTextForSpeech(text) {
  // Add natural pauses at punctuation
  let processed = text
    .replace(/([.!?])\s+/g, '$1 ... ') // Longer pause after sentences
    .replace(/([,;:])\s+/g, '$1 .. ')   // Medium pause after commas
    .replace(/--/g, ' ... ')            // Pause at dashes
    .replace(/\s+/g, ' ')               // Normalize whitespace
    .trim();
  return processed;
}

// Audio cache for integrated TTS responses
const _audioCache = new Map();
// Track current playing Audio element so stop() can kill it
let _currentTTSAudio = null;

// ── ElevenLabs Voice Configuration ────────────────────────────────
// Get voice IDs from: https://elevenlabs.io/app/voice-library (My Voices tab)
// Click on any voice → Copy the Voice ID
const ELEVENLABS_VOICES = {
  rachel: 'EXAVITQu4vr4xnSDxMaL',  // Default: Rachel - warm, natural female
  female: 'jqcCZkN6Knx8BJ5TBdYR',  // Custom female voice
  male: 'VCgLBmBjldJmfphyB8sZ',    // Custom male voice
};

// Select which voice to use for interview questions
const INTERVIEW_VOICE = ELEVENLABS_VOICES.female; // Using custom female voice

// Integrated TTS service (ElevenLabs, Hume, or other providers)
// Automatically tries available services and falls back to Web Speech
async function speakWithIntegratedTTS(text, setSpeaking, onDone) {
  try {
    console.log('🎤 Attempting integrated TTS for:', text.substring(0, 50) + '...');
    
    // Check cache first
    const cacheKey = `tts_${text.substring(0, 100)}`;
    let audioUrl = _audioCache.get(cacheKey);
    
    if (audioUrl) {
      console.log('✅ Using cached audio');
    } else {
      console.log('📡 Calling backend TTS API at /api/tts');
      
      // Call backend API endpoint - backend will try available providers
      const response = await fetch(`${API_BASE}/api/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          text,
          voice_id: INTERVIEW_VOICE // Uses voice configured above
        })
      });
      
      console.log('📡 TTS API response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('⚠️ Integrated TTS API error:', response.status, errorText);
        console.log('⚠️ Falling back to Web Speech');
        return false;
      }
      
      const blob = await response.blob();
      audioUrl = URL.createObjectURL(blob);
      console.log('✅ Received audio blob, size:', blob.size, 'bytes');
      
      // Cache the audio URL (limit cache size to 50 items)
      if (_audioCache.size >= 50) {
        const firstKey = _audioCache.keys().next().value;
        const oldUrl = _audioCache.get(firstKey);
        URL.revokeObjectURL(oldUrl);
        _audioCache.delete(firstKey);
      }
      _audioCache.set(cacheKey, audioUrl);
      console.log('💾 Cached audio for future use');
    }
    
    // Play the audio
    const audio = new Audio(audioUrl);
    _currentTTSAudio = audio;
    setSpeaking(true);
    
    audio.onended = () => {
      console.log('✅ ElevenLabs audio playback completed');
      _currentTTSAudio = null;
      setSpeaking(false);
      onDone?.();
    };
    
    audio.onerror = (e) => {
      console.error('❌ Audio playback error:', e);
      _currentTTSAudio = null;
      setSpeaking(false);
      onDone?.();
      return false;
    };
    
    console.log('▶️ Playing ElevenLabs audio...');
    await audio.play();
    return true;
    
  } catch (err) {
    console.log('⚠️ Integrated TTS error, will use Web Speech fallback:', err.message);
    return false;
  }
}

function useSpeechSynthesis() {
  const synthRef = useRef(window.speechSynthesis || null);
  const [speaking, setSpeaking] = useState(false);
  const [voices, setVoices] = useState([]);

  useEffect(() => {
    if (IS_NATIVE) return; // Skip web voices on native
    if (!synthRef.current) return;
    const load = () => setVoices(synthRef.current.getVoices());
    load();
    synthRef.current.onvoiceschanged = load;
  }, []);
  
  const getBestVoice = useCallback(() => {
    if (IS_NATIVE) return null; // Native uses CapTTS
    // Return module-level cached voice for consistency across all screens
    if (_cachedVoice) return _cachedVoice;
    
    // Prioritize neural/natural voices first
    const preferred = [
      // Tier 1: Neural voices (most natural)
      "Microsoft Jenny Online", "Microsoft Aria Online",  // Azure Neural
      "Google US English (Neural2)", "Google UK English Female (Neural2)",
      "Microsoft Jenny", "Microsoft Aria",                // Windows Neural
      
      // Tier 2: High-quality standard voices
      "Google UK English Female", "Google US English Female",
      "Samantha", "Karen", "Moira", "Tessa", "Victoria",  // Apple
      
      // Tier 3: Fallback
      "Zira", "Hazel"
    ];
    
    // Try to find neural voice first
    for (const name of preferred) {
      const v = voices.find(v => v.name.includes(name));
      if (v) {
        _cachedVoice = v;
        const isNeural = v.name.toLowerCase().includes('neural') || 
                        v.name.toLowerCase().includes('online') ||
                        v.name.includes('Jenny') || v.name.includes('Aria');
        console.log(`🎤 Voice selected (${isNeural ? 'Neural' : 'Standard'}):`, v.name);
        return v;
      }
    }
    
    // Fallback to any English female voice
    const fallback = voices.find(v => v.lang.startsWith("en") && v.name.toLowerCase().includes("female"))
      || voices.find(v => v.lang.startsWith("en"))
      || voices[0];
    if (fallback) {
      _cachedVoice = fallback;
      console.log("🎤 Voice fallback:", fallback.name);
    }
    return fallback || null;
  }, [voices]);

  const speak = useCallback(async (text, onDone) => {
    // ── ALL platforms: try ElevenLabs / integrated TTS first ──
    try {
      const success = await speakWithIntegratedTTS(text, setSpeaking, onDone);
      if (success) return; // ElevenLabs TTS succeeded
    } catch (err) {
      console.log('⚠️ Integrated TTS unavailable, falling back...');
    }

    // ── Fallback: Native Android device TTS ──
    if (IS_NATIVE) {
      try {
        console.log("🔊 Fallback: Native device TTS for:", text.substring(0, 50) + "...");
        setSpeaking(true);
        await CapTTS.speak({
          text: text,
          lang: "en-US",
          rate: 1.0,
          pitch: 1.0,
          volume: 1.0,
          category: "playback",
        });
        console.log("✅ Native TTS done");
        setSpeaking(false);
        onDone?.();
      } catch (err) {
        console.error("❌ Native TTS error:", err);
        setSpeaking(false);
        onDone?.();
      }
      return;
    }

    // ── Fallback: Web Speech API (desktop browsers) ──
    if (!synthRef.current) { onDone?.(); return; }
    synthRef.current.cancel();
    
    // Preprocess text for better pacing
    const processedText = preprocessTextForSpeech(text);
    
    const utt = new SpeechSynthesisUtterance(processedText);
    const voice = getBestVoice();
    if (voice) utt.voice = voice;
    
    // Optimized parameters for more natural sound
    const isNeural = voice?.name.toLowerCase().includes('neural') || 
                     voice?.name.toLowerCase().includes('online') ||
                     voice?.name.includes('Jenny') || voice?.name.includes('Aria');
    
    utt.rate = isNeural ? 0.92 : 0.88;  // Slower = more natural (neural voices can go faster)
    utt.pitch = isNeural ? 1.0 : 0.98;  // Closer to natural pitch
    utt.volume = 1;
    
    utt.onstart = () => setSpeaking(true);
    utt.onend = () => { setSpeaking(false); onDone?.(); };
    utt.onerror = () => { setSpeaking(false); onDone?.(); };
    
    synthRef.current.speak(utt);
  }, [getBestVoice]);

  const stop = useCallback(async () => {
    // Stop ElevenLabs / integrated TTS audio element
    if (_currentTTSAudio) {
      try { _currentTTSAudio.pause(); _currentTTSAudio.currentTime = 0; } catch (e) { /* ok */ }
      _currentTTSAudio = null;
    }
    if (IS_NATIVE) {
      try { await CapTTS.stop(); } catch (e) { /* ok */ }
    } else if (synthRef.current) {
      synthRef.current.cancel();
    }
    setSpeaking(false);
  }, []);

  return { speak, stop, speaking, ready: IS_NATIVE || voices.length > 0 };
}

function useSpeechRecognition() {
  const recRef = useRef(null);
  const [listening, setListening] = useState(false);
  const listeningRef = useRef(false);
  const [transcript, setTranscript] = useState("");
  const onResultRef = useRef(null);
  const onEndRef = useRef(null);
  const debugRef = useRef(null); // injectable debug logger
  const startIdRef = useRef(0); // tracks current session to discard stale callbacks
  const nativeActiveRef = useRef(false); // true while native recognition is active
  const nativePermGrantedRef = useRef(false); // cache permission result
  const [supported] = useState(() => {
    if (IS_NATIVE) return true; // Native Capacitor plugin always available
    return "webkitSpeechRecognition" in window || "SpeechRecognition" in window || "mozSpeechRecognition" in window;
  });

  const _dbg = useCallback((m) => { console.log(m); debugRef.current?.(m); }, []);

  // Request native mic permission upfront (call early, before first listen)
  const requestNativePermission = useCallback(async () => {
    if (!IS_NATIVE) return true;
    if (nativePermGrantedRef.current) return true;
    try {
      const permResult = await CapSpeechRecognition.requestPermissions();
      _dbg("🎤 Permission: " + JSON.stringify(permResult));
      const granted = permResult?.speechRecognition === "granted" || permResult?.receive === "granted";
      if (granted) nativePermGrantedRef.current = true;
      else _dbg("⚠️ Mic permission not granted");
      return granted;
    } catch (err) {
      _dbg("❌ Permission error: " + (err?.message || err));
      return false;
    }
  }, [_dbg]);

  const start = useCallback(async (onResult, onEnd, onListeningStarted) => {
    const sid = ++startIdRef.current; // unique session id
    _dbg("start() sid=" + sid + " native=" + IS_NATIVE);
    onResultRef.current = onResult;
    onEndRef.current = onEnd;

    if (IS_NATIVE) {
      // ── Native Android via Capacitor plugin ──
      try {
        // Ensure mic permission is granted (uses cached result if already granted)
        const granted = await requestNativePermission();
        if (!granted) {
          _dbg("❌ Mic denied, opening app settings...");
          try {
            const { registerPlugin } = await import("@capacitor/core");
            const AppSettings = registerPlugin("AppSettings");
            await AppSettings.openSettings();
          } catch (e) { _dbg("⚠️ Could not open settings: " + (e?.message || e)); }
          onEnd?.();
          return;
        }

        // If a previous session is still active, stop it first
        if (nativeActiveRef.current) {
          _dbg("⚠️ Previous session still active, stopping first...");
          try { await CapSpeechRecognition.stop(); } catch (e) { /* ok */ }
          await new Promise(r => setTimeout(r, 300));
        }
        if (startIdRef.current !== sid) { _dbg("start() sid=" + sid + " superseded"); return; }

        // ── Helper: popup fallback (used if popup:false gives code 9) ──
        const doPopupFallback = async () => {
          _dbg("🔄 Fallback → popup:true...");
          await CapSpeechRecognition.removeAllListeners();
          nativeActiveRef.current = true;
          setListening(true);
          listeningRef.current = true;
          onListeningStarted?.();
          try {
            const result = await CapSpeechRecognition.start({
              language: "en-US", partialResults: false, popup: true,
            });
            if (startIdRef.current !== sid) return;
            const text = (result?.matches || [])[0] || "";
            _dbg("✅ Popup result: '" + text + "'");
            nativeActiveRef.current = false;
            setListening(false);
            listeningRef.current = false;
            if (text) { setTranscript(text); onResultRef.current?.(text); }
            setTimeout(() => { onEndRef.current?.(); }, 100);
          } catch (err) {
            if (startIdRef.current !== sid) return;
            _dbg("❌ Popup error: " + (err?.message || err));
            nativeActiveRef.current = false;
            setListening(false);
            listeningRef.current = false;
            onEndRef.current?.();
          }
        };

        // ── Try popup:false first (seamless, no Google UI) ──
        await CapSpeechRecognition.removeAllListeners();

        let listeningStartedFired = false;
        let endFired = false;
        let fallbackTriggered = false;
        const fireEnd = () => {
          if (endFired || fallbackTriggered) return;
          if (startIdRef.current !== sid) return;
          endFired = true;
          nativeActiveRef.current = false;
          setListening(false);
          listeningRef.current = false;
          onEndRef.current?.();
        };

        // Listen for partial results
        CapSpeechRecognition.addListener("partialResults", (data) => {
          if (startIdRef.current !== sid) return;
          const partial = (data.matches || [])[0] || "";
          _dbg("📝 partial: " + partial);
          if (partial) {
            setTranscript(partial);
            onResultRef.current?.(partial);
          }
        });

        // Listen for native errors — auto-fallback to popup on code 9
        CapSpeechRecognition.addListener("error", (data) => {
          if (startIdRef.current !== sid) return;
          _dbg("🔴 Native error: " + (data.error || "unknown") + " code=" + data.errorCode);
          if (data.errorCode === 9 && !fallbackTriggered) {
            fallbackTriggered = true;
            doPopupFallback();
          }
        });

        // Listen for state changes
        CapSpeechRecognition.addListener("listeningState", (data) => {
          if (startIdRef.current !== sid || fallbackTriggered) return;
          _dbg("👂 state: " + data.status + " sid=" + sid);
          if (data.status === "started") {
            if (!listeningStartedFired) {
              listeningStartedFired = true;
              onListeningStarted?.();
            }
          } else if (data.status === "stopped") {
            fireEnd();
          }
        });

        nativeActiveRef.current = true;
        setListening(true);
        listeningRef.current = true;
        _dbg("🚀 Calling CapSpeechRecognition.start() popup=false...");

        CapSpeechRecognition.start({
          language: "en-US",
          partialResults: true,
          popup: false,
        }).then(() => {
          if (startIdRef.current !== sid) return;
          _dbg("✅ start() resolved sid=" + sid);
          setTimeout(() => {
            if (startIdRef.current !== sid || endFired || fallbackTriggered) return;
            if (!listeningStartedFired) {
              _dbg("⚠️ listeningState:started never fired, forcing");
              listeningStartedFired = true;
              onListeningStarted?.();
            }
          }, 1000);
        }).catch((err) => {
          if (startIdRef.current !== sid) return;
          _dbg("❌ start() error: " + (err?.message || err));
          if (!fallbackTriggered) fireEnd();
        });

      } catch (err) {
        _dbg("❌ Native setup error: " + (err?.message || err));
        nativeActiveRef.current = false;
        setListening(false);
        listeningRef.current = false;
        onEndRef.current?.();
      }
      return;
    }

    // ── Web Speech API (desktop browsers) ──
    if (!supported) {
      console.error("❌ Speech recognition not supported");
      return;
    }
    
    try {
      console.log("🎤 Requesting microphone permission...");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log("✅ Microphone permission granted");
      stream.getTracks().forEach(track => track.stop());
      
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition || window.mozSpeechRecognition;
      const rec = new SpeechRecognition();
      rec.continuous = false;
      rec.interimResults = true;
      rec.lang = "en-US";
      rec.maxAlternatives = 1;

      rec.onstart = () => {
        console.log("✅ Speech recognition started");
        setListening(true);
      };
      
      rec.onresult = (e) => {
        const t = Array.from(e.results).map(r => r[0].transcript).join("");
        console.log("📝 Transcript:", t);
        setTranscript(t);
        if (e.results[e.results.length - 1].isFinal) onResult?.(t);
      };

      rec.onerror = (e) => {
        console.error("❌ Speech recognition error:", e.error, e);
        setListening(false);
        if (e.error !== 'no-speech') onEnd?.();
      };

      rec.onend = () => {
        console.log("🛑 Speech recognition ended");
        setListening(false);
        onEnd?.();
      };

      recRef.current = rec;
      console.log("🚀 Starting speech recognition...");
      rec.start();
    } catch (error) {
      console.error("❌ Error requesting microphone permission:", error);
      setListening(false);
      onEnd?.();
    }
  }, [supported, _dbg]);

  const stop = useCallback(async () => {
    _dbg("stop() called, nativeActive=" + nativeActiveRef.current);
    if (IS_NATIVE) {
      // Bump startId so any in-flight callbacks from the old session are ignored
      startIdRef.current++;
      try {
        await CapSpeechRecognition.stop();
        _dbg("✅ Native stop() done");
      } catch (e) { _dbg("⚠️ Native stop error (ok): " + (e?.message || e)); }
      // Force cleanup
      nativeActiveRef.current = false;
      setListening(false);
      listeningRef.current = false;
      // Do NOT fire onEnd here — askQuestion calls stopRec() to clean up,
      // and firing onEnd would trigger the "empty speech" retry path
      return;
    }
    if (recRef.current) {
      try {
        recRef.current.stop();
        console.log("✅ Speech recognition stopped");
      } catch (error) {
        console.error("❌ Error stopping speech recognition:", error);
      }
    }
  }, [_dbg]);

  return { start, stop, listening, transcript, supported, requestNativePermission, debugRef };
}

// ─── Play Triangle Icon (rounded 3px points) ────────────────
function PlayTriangle({ size = 18, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" style={{ display: "block" }}>
      <polygon points="6,3 21,12 6,21" />
    </svg>
  );
}

// ─── Audio Visualizer ─────────────────────────────────────────
function VoiceOrb({ state, size = 180 }) {
  // state: idle | listening | speaking | thinking
  const colors = {
    idle:      ["#2d2840", "#1a1726"],
    listening: [T.rose, T.roseDeep],
    speaking:  [T.teal, T.violet],
    thinking:  [T.amber, T.rose],
  };
  const [c1, c2] = colors[state] || colors.idle;
  const isActive = state !== "idle";
  const rings = [1, 2, 3];

  return (
    <div style={{ position: "relative", width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {/* Pulsing rings */}
      {isActive && rings.map(i => (
        <div key={i} style={{
          position: "absolute",
          width: size + i * 28,
          height: size + i * 28,
          borderRadius: "50%",
          border: `1px solid ${c1}`,
          opacity: 0,
          animation: `orbRing 2s ${i * 0.4}s ease-out infinite`,
        }} />
      ))}

      {/* Main orb */}
      <div style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: `radial-gradient(circle at 35% 35%, ${c1}, ${c2})`,
        boxShadow: isActive
          ? `0 0 40px ${c1}60, 0 0 80px ${c1}30, inset 0 0 40px rgba(255,255,255,0.05)`
          : `0 0 20px rgba(0,0,0,0.5), inset 0 0 20px rgba(255,255,255,0.03)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.5s ease",
        animation: state === "speaking" ? "orbPulse 0.8s ease-in-out infinite alternate"
          : state === "listening" ? "orbBreath 1.5s ease-in-out infinite alternate"
          : state === "thinking" ? "orbSpin 3s linear infinite"
          : "none",
        position: "relative",
        overflow: "hidden",
        cursor: "pointer",
      }}>
        {/* Inner shimmer */}
        <div style={{
          position: "absolute", inset: 0, borderRadius: "50%",
          background: "linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 60%)",
        }} />

        {/* Icon */}
        <div style={{ fontSize: size * 0.28, filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.5))", zIndex: 1, userSelect: "none" }}>
          {state === "idle" && "✦"}
          {state === "listening" && "◉"}
          {state === "speaking" && "◎"}
          {state === "thinking" && "⟳"}
        </div>
      </div>

      {/* State label */}
      <div style={{
        position: "absolute",
        bottom: -28,
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: "0.12em",
        color: isActive ? c1 : T.dim,
        textTransform: "uppercase",
        transition: "color 0.4s",
      }}>
        {state === "idle" && "Tap to speak"}
        {state === "listening" && "Listening..."}
        {state === "speaking" && "Speaking..."}
        {state === "thinking" && "Thinking..."}
      </div>
    </div>
  );
}

// ─── Waveform bars ────────────────────────────────────────────
function Waveform({ active, color = T.rose, bars = 28 }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 2, height: 32 }}>
      {Array.from({ length: bars }).map((_, i) => (
        <div key={i} style={{
          width: 3,
          borderRadius: 2,
          background: color,
          opacity: active ? 0.8 : 0.2,
          height: active ? undefined : 4,
          minHeight: 4,
          maxHeight: 28,
          animation: active ? `wave ${0.8 + (i % 5) * 0.15}s ${(i * 0.04)}s ease-in-out infinite alternate` : "none",
        }} />
      ))}
    </div>
  );
}

// ─── SVG Icon Components ────────────────────────────────────
const LocationIcon = ({ size = 14, color = "#737373" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block", verticalAlign: "middle" }}>
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill={color} />
  </svg>
);

const MusicIcon = ({ size = 20, color = "#1DB954" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" fill={color}/>
  </svg>
);

const CameraIcon = ({ size = 20, color = "#E4405F" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" fill={color}/>
  </svg>
);

const MovieIcon = ({ size = 20, color = "#E50914" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" fill={color}/>
  </svg>
);

const BookIcon = ({ size = 20, color = "#553B08" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 4h5v8l-2.5-1.5L6 12V4z" fill={color}/>
  </svg>
);

const BriefcaseIcon = ({ size = 20, color = "#0A66C2" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z" fill={color}/>
  </svg>
);

const RunIcon = ({ size = 20, color = "#FC4C02" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M13.49 5.48c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm-3.6 13.9l1-4.4 2.1 2v6h2v-7.5l-2.1-2 .6-3c1.3 1.5 3.3 2.5 5.5 2.5v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1l-5.2 2.2v4.7h2v-3.4l1.8-.7-1.6 8.1-4.9-1-.4 2 7 1.4z" fill={color}/>
  </svg>
);

const PinIcon = ({ size = 20, color = "#E60023" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M16 9V4h1c.55 0 1-.45 1-1s-.45-1-1-1H7c-.55 0-1 .45-1 1s.45 1 1 1h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3z" fill={color}/>
  </svg>
);

const FacebookIcon = ({ size = 20, color = "#1877F2" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill={color}/>
  </svg>
);

const MicIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" fill={color}/>
    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" fill={color}/>
  </svg>
);

const SparklesIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M12 2l2.4 7.2H22l-6 4.8 2.4 7.2L12 16.8 5.6 21.2 8 14l-6-4.8h7.6L12 2z" fill={color}/>
  </svg>
);

const HeartIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill={color}/>
  </svg>
);

const CalendarIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM9 14H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2zm-8 4H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2z" fill={color}/>
  </svg>
);

const UsersIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill={color}/>
  </svg>
);

const ChartIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" fill={color}/>
  </svg>
);

const LightbulbIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7zm2.85 11.1l-.85.6V16h-4v-2.3l-.85-.6C7.8 12.16 7 10.63 7 9c0-2.76 2.24-5 5-5s5 2.24 5 5c0 1.63-.8 3.16-2.15 4.1z" fill={color}/>
  </svg>
);

const TikTokIcon = ({ size = 20, color = "#000000" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" fill={color}/>
  </svg>
);

const AppleMusicIcon = ({ size = 20, color = "#FA243C" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M18 3v12.5c0 2.49-2.01 4.5-4.5 4.5S9 17.99 9 15.5s2.01-4.5 4.5-4.5c.88 0 1.69.25 2.38.68V7h4V3h-2zm-2.5 14c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill={color}/>
  </svg>
);

const SearchIcon = ({ size = 20, color = "#4285F4" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill={color}/>
  </svg>
);

const TwitterIcon = ({ size = 20, color = "#1DA1F2" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M22.46 6c-.85.38-1.78.64-2.75.76a4.8 4.8 0 0 0 2.1-2.66c-.92.55-1.95.95-3.04 1.17a4.77 4.77 0 0 0-8.13 4.35A13.55 13.55 0 0 1 3.8 4.7a4.77 4.77 0 0 0 1.48 6.37 4.73 4.73 0 0 1-2.16-.6v.06c0 2.31 1.64 4.24 3.82 4.68a4.78 4.78 0 0 1-2.16.08 4.78 4.78 0 0 0 4.46 3.31A9.57 9.57 0 0 1 2 19.54 13.5 13.5 0 0 0 9.29 22c8.75 0 13.54-7.25 13.54-13.54 0-.21 0-.41-.01-.61A9.68 9.68 0 0 0 24 5.34a9.47 9.47 0 0 0-2.54.7z" fill={color}/>
  </svg>
);

const SettingsIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94L14.4 2.81c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" fill={color}/>
  </svg>
);

const DashboardIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm10 0h8v8h-8v-8z" fill={color}/>
  </svg>
);

const UserIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill={color}/>
  </svg>
);

const GlobeIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" fill={color}/>
  </svg>
);

const LockIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" fill={color}/>
  </svg>
);

const BrainIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M13 3C9.23 3 6.19 5.95 6 9.66l-1.92.58C3.41 10.42 3 11.06 3 11.79v.42c0 1.66 1.34 3 3 3l1-.01c0 3.86 3.13 7 7 7s7-3.14 7-7v-1c1.66 0 3-1.34 3-3 0-1.66-1.34-3-3-3 0-3.31-2.69-6-6-6h-2z" fill={color}/>
  </svg>
);

const ScaleIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M12 2l-5.5 9h11L12 2zm0 3.84L13.93 9h-3.87L12 5.84zM17.5 13c-2.49 0-4.5 2.01-4.5 4.5s2.01 4.5 4.5 4.5 4.5-2.01 4.5-4.5-2.01-4.5-4.5-4.5zm0 7c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5zM3 21.5h8v-8H3v8zm2-6h4v4H5v-4z" fill={color}/>
  </svg>
);

const WineIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M6 3v6c0 2.97 2.16 5.43 5 5.91V19H8v2h8v-2h-3v-4.09c2.84-.48 5-2.94 5-5.91V3H6zm10 5H8V5h8v3z" fill={color}/>
  </svg>
);

const RestaurantIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z" fill={color}/>
  </svg>
);

const MoonIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M9 2c-1.05 0-2.05.16-3 .46 4.06 1.27 7 5.06 7 9.54 0 4.48-2.94 8.27-7 9.54.95.3 1.95.46 3 .46 5.52 0 10-4.48 10-10S14.52 2 9 2z" fill={color}/>
  </svg>
);

const CrystalBallIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <circle cx="12" cy="10" r="8" fill={color} opacity="0.3"/>
    <path d="M12 2C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7zm0 11.5c-2.49 0-4.5-2.01-4.5-4.5S9.51 4.5 12 4.5s4.5 2.01 4.5 4.5-2.01 4.5-4.5 4.5zM7 19h10v2H7z" fill={color}/>
  </svg>
);

const BlockIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9C4.63 15.55 4 13.85 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1C19.37 8.45 20 10.15 20 12c0 4.42-3.58 8-8 8z" fill={color}/>
  </svg>
);

const LeafIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89.67C7.91 17.17 10.91 12.89 17 11.28V8zm-5.55 8.89c-2.34.29-3.84.93-4.45 1.29.61-.36 2.11-1 4.45-1.29zM13 2v7c0 1.1.9 2 2 2h7v2c0 5.5-4.5 10-10 10S2 18.5 2 13.5 6.5 4 12 4V2h1z" fill={color}/>
  </svg>
);

const SmileIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z" fill={color}/>
  </svg>
);

const DNAIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M2 3h2v2H2V3zm0 4h2v2H2V7zm0 4h2v2H2v-2zm0 4h2v2H2v-2zm0 4h2v2H2v-2zm18-16h2v2h-2V3zm0 4h2v2h-2V7zm0 4h2v2h-2v-2zm0 4h2v2h-2v-2zm0 4h2v2h-2v-2zM6 3h12v2H6V3zm0 16h12v2H6v-2zm0-8h12v2H6v-2z" fill={color}/>
  </svg>
);

const MessageIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 9h12v2H6V9zm8 5H6v-2h8v2zm4-6H6V6h12v2z" fill={color}/>
  </svg>
);

const GiftBoxIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" style={{ display: "inline-block" }}>
    <rect x="8" y="28" width="48" height="28" rx="3" fill={color}/>
    <rect x="12" y="16" width="40" height="14" rx="3" fill={color} opacity="0.85"/>
    <rect x="28" y="16" width="8" height="40" fill={color} opacity="0.15"/>
    <path d="M32 16c0 0-6-12-14-8s2 12 14 8z" fill={color} opacity="0.7"/>
    <path d="M32 16c0 0 6-12 14-8s-2 12-14 8z" fill={color} opacity="0.7"/>
    <rect x="29" y="12" width="6" height="44" rx="1" fill="#fff" opacity="0.25"/>
    <rect x="8" y="26" width="48" height="6" rx="1" fill="#fff" opacity="0.15"/>
  </svg>
);

const PollClipboardIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" style={{ display: "inline-block" }}>
    <rect x="12" y="8" width="40" height="50" rx="4" fill={color}/>
    <rect x="22" y="4" width="20" height="10" rx="3" fill={color} opacity="0.8"/>
    <rect x="20" y="24" width="10" height="6" rx="1.5" fill="#fff" opacity="0.9"/>
    <rect x="20" y="34" width="18" height="6" rx="1.5" fill="#fff" opacity="0.7"/>
    <rect x="20" y="44" width="14" height="6" rx="1.5" fill="#fff" opacity="0.5"/>
    <circle cx="44" cy="27" r="2" fill="#fff" opacity="0.5"/>
    <circle cx="44" cy="37" r="2" fill="#fff" opacity="0.5"/>
    <circle cx="44" cy="47" r="2" fill="#fff" opacity="0.5"/>
  </svg>
);

const PhoneShareIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" style={{ display: "inline-block" }}>
    <rect x="16" y="4" width="32" height="56" rx="6" fill={color}/>
    <rect x="20" y="10" width="24" height="36" rx="2" fill="#fff" opacity="0.2"/>
    <circle cx="32" cy="52" r="3" fill="#fff" opacity="0.4"/>
    <path d="M32 20l8-6v4c6 1 10 5 10 11-2-4-6-6-10-5v4l-8-6z" fill="#fff" opacity="0.85" transform="translate(0,4)"/>
  </svg>
);

const ChatBubbleIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" style={{ display: "inline-block" }}>
    <path d="M8 14c0-3.3 2.7-6 6-6h36c3.3 0 6 2.7 6 6v24c0 3.3-2.7 6-6 6H22l-10 10V44h2c-3.3 0-6-2.7-6-6V14z" fill={color}/>
    <circle cx="22" cy="26" r="3" fill="#fff" opacity="0.7"/>
    <circle cx="32" cy="26" r="3" fill="#fff" opacity="0.7"/>
    <circle cx="42" cy="26" r="3" fill="#fff" opacity="0.7"/>
  </svg>
);

const PhoneCallIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" style={{ display: "inline-block" }}>
    <path d="M18.5 6.5c-2.2-1.1-5-.2-6.2 2L9 15c-1 1.8-.7 4 .6 5.5C17 29 29 41 37.5 48.5c1.5 1.3 3.7 1.6 5.5.6l6.5-3.3c2.2-1.2 3.1-4 2-6.2l-3.5-7c-1-2-3.3-3-5.5-2.2l-4 1.5c-.7.3-1.5.1-2-.4L25.3 20.3c-.5-.5-.7-1.3-.4-2l1.5-4c.8-2.2-.2-4.5-2.2-5.5l-5.7-2.3z" fill={color}/>
    <path d="M40 6c0 0 4 1 7 4s4 7 4 7" stroke={color} strokeWidth="3" strokeLinecap="round" fill="none" opacity="0.5"/>
    <path d="M40 13c0 0 2 .5 3.5 2s2 3.5 2 3.5" stroke={color} strokeWidth="3" strokeLinecap="round" fill="none" opacity="0.5"/>
  </svg>
);

const WarningIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" fill={color}/>
  </svg>
);

const CoupleIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M16.5 13c-1.2 0-3.07.34-4.5 1-1.43-.67-3.3-1-4.5-1C5.33 13 1 14.08 1 16.25V19h22v-2.75c0-2.17-4.33-3.25-6.5-3.25zm-4 4.5h-10v-1.25c0-.54 2.56-1.75 5-1.75s5 1.21 5 1.75v1.25zm9 0H14v-1.25c0-.46-.2-.86-.52-1.22.88-.3 1.96-.53 3.02-.53 2.44 0 5 1.21 5 1.75v1.25zM7.5 12c1.93 0 3.5-1.57 3.5-3.5S9.43 5 7.5 5 4 6.57 4 8.5 5.57 12 7.5 12zm0-5.5c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm9 5.5c1.93 0 3.5-1.57 3.5-3.5S18.43 5 16.5 5 13 6.57 13 8.5s1.57 3.5 3.5 3.5zm0-5.5c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2z" fill={color}/>
  </svg>
);

const AlertIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "inline-block" }}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="12" y1="9" x2="12" y2="13" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="12" y1="17" x2="12.01" y2="17" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ─── Fake Matches Data ────────────────────────────────────────
const MATCHES = [
  {
    id: 1, name: "Sophia", age: 28, city: "Los Angeles", gender: "Woman", height: "5'6\"",
    photo: "https://i.pravatar.cc/400?img=47",
    compatibility: 94, personalityScore: 91, valuesScore: 96, lifestyleScore: 89,
    traits: ["Intellectually curious", "Adventurous", "Family-oriented"],
    intro: "Introversion 0.7 · Risk 0.6 · Family Focus 0.9",
    simulation: {
      firstDate: 87, conversationFlow: 92, conflictRisk: 18, longTerm: 84,
      summary: "You two connect instantly over travel stories and deep ideas. She challenges your thinking in ways you secretly enjoy. Expect long evenings talking about everything and nothing.",
      conflict: "Planning vs. spontaneity — workable with communication.",
      vibe: "Coffee shop → gallery walk → lost in conversation until closing time."
    }
  },
  {
    id: 2, name: "Aria", age: 26, city: "Austin", gender: "Woman", height: "5'4\"",
    photo: "https://i.pravatar.cc/400?img=45",
    compatibility: 88, personalityScore: 85, valuesScore: 90, lifestyleScore: 82,
    traits: ["Creative soul", "Direct communicator", "Career-driven"],
    intro: "Introversion 0.4 · Risk 0.8 · Career Priority 0.9",
    simulation: {
      firstDate: 79, conversationFlow: 88, conflictRisk: 31, longTerm: 76,
      summary: "High energy from the start. She's witty and sharp — you'll be laughing within minutes. The chemistry is electric but needs grounding.",
      conflict: "Career ambitions may clash — respect and clear boundaries make this work.",
      vibe: "Rooftop drinks → spontaneous live music → staying way too late."
    }
  },
  {
    id: 3, name: "Zoe", age: 30, city: "San Diego", gender: "Woman", height: "5'7\"",
    photo: "https://i.pravatar.cc/400?img=44",
    compatibility: 82, personalityScore: 88, valuesScore: 79, lifestyleScore: 85,
    traits: ["Calm & grounded", "Nature lover", "Emotionally intelligent"],
    intro: "Introversion 0.8 · Family Focus 0.7 · Risk 0.3",
    simulation: {
      firstDate: 83, conversationFlow: 80, conflictRisk: 12, longTerm: 91,
      summary: "Slow burn — the kind you don't realize is happening until it already has. She listens fully and speaks carefully. You leave the first date feeling genuinely seen.",
      conflict: "Almost no conflict risk. Watch for stagnation if neither pushes for growth.",
      vibe: "Farmer's market → beach walk → home-cooked dinner."
    }
  }
];

// ─── Stage definitions for progressive profile completion ─────
const STAGES = {
  1:  { name: "Quick Match",            desc: "Core identity & intent" },
  2:  { name: "Physical Traits",        desc: "Attraction compatibility" },
  3:  { name: "Culture & Background",   desc: "Cultural compatibility" },
  4:  { name: "Family & History",       desc: "Relationship history" },
  5:  { name: "Career & Stability",     desc: "Life goals & stability" },
  6:  { name: "Lifestyle",              desc: "Day-to-day compatibility" },
  7:  { name: "Dating Style",           desc: "How you date" },
  8:  { name: "Logistics & Money",      desc: "Practical compatibility" },
  9:  { name: "Food & Social Habits",   desc: "Social lifestyle" },
  10: { name: "Travel",                 desc: "Adventure compatibility" },
  11: { name: "Values & Beliefs",       desc: "Core values alignment" },
  12: { name: "Relationship Psychology",desc: "Emotional compatibility" },
  13: { name: "Self Awareness",         desc: "Personality depth" },
  14: { name: "Must-Haves",             desc: "Dealbreakers & non-negotiables" },
};

const AI_QUESTIONS = [
  // ── Stage 1: Quick Match ─────────────────────────────────────
  { id: 1,  stage: 1, text: "What's your first name? I want to make this feel personal!", key: "first_name" },
  { id: 2,  stage: 1, text: "How old are you?", key: "age" },
  { id: 3,  stage: 1, text: "What gender do you identify as?", key: "gender" },
  { id: 4,  stage: 1, text: "What gender or genders are you interested in dating?", key: "dating_gender_preference" },
  { id: 5,  stage: 1, text: "What city and country are you currently living in?", key: "location" },
  { id: 6,  stage: 1, text: "How far are you willing to travel to meet someone? Just your city, a few hours away, or are you open to long distance?", key: "travel_distance" },
  { id: 7,  stage: 1, text: "What type of relationship are you looking for right now — something casual, long-term, or just seeing where things go?", key: "relationship_intent" },
  { id: 8,  stage: 1, text: "What age range are you open to dating?", key: "age_preference" },
  { id: 9,  stage: 1, text: "What's your current relationship status? Single, divorced, separated, widowed?", key: "relationship_status" },
  { id: 10, stage: 1, text: "Got it. And how long have you been single?", key: "single_duration" },
  { id: 10.5, stage: 1, text: "One more — what's your date of birth? This helps us with compatibility signals. Just say something like 'March 15, 1990'.", key: "birthday" },

  // ── Stage 2: Physical Traits & Attraction ────────────────────
  { id: 11, stage: 2, text: "Let's talk about you physically. How tall are you?", key: "height" },
  { id: 12, stage: 2, text: "How would you describe your body type?", key: "body_type" },
  { id: 13, stage: 2, text: "What's your ethnicity or cultural background?", key: "ethnicity" },
  { id: 14, stage: 2, text: "Eye color and hair color — what are yours?", key: "eye_hair_color" },
  { id: 15, stage: 2, text: "Now let's talk attraction. What height range do you prefer in a partner?", key: "height_preference" },
  { id: 16, stage: 2, text: "What body types are you most attracted to?", key: "body_type_preference" },
  { id: 17, stage: 2, text: "Are there any physical traits that are especially attractive to you, or any you'd prefer to avoid?", key: "physical_preferences" },

  // ── Stage 3: Culture, Language & Background ──────────────────
  { id: 18, stage: 3, text: "What languages do you speak fluently?", key: "languages" },
  { id: 19, stage: 3, text: "What country were you born in, and where were your parents from?", key: "birth_country" },
  { id: 20, stage: 3, text: "How important is cultural compatibility to you in a relationship?", key: "cultural_importance" },
  { id: 21, stage: 3, text: "Are you open to dating someone from a different cultural background?", key: "cultural_openness" },
  { id: 22, stage: 3, text: "Are there any cultural traditions that are important to you and you'd want a partner to respect?", key: "cultural_traditions" },

  // ── Stage 4: Family & Relationship History ───────────────────
  { id: 23, stage: 4, text: "How close are you with your family? Do you talk to your parents regularly?", key: "family_closeness" },
  { id: 24, stage: 4, text: "Do you have children, and do you want children in the future?", key: "children" },
  { id: 25, stage: 4, text: "What was your last relationship like, and who initiated the breakup?", key: "last_relationship" },
  { id: 26, stage: 4, text: "What was the main reason that relationship ended?", key: "breakup_reason" },
  { id: 27, stage: 4, text: "What did that relationship teach you about yourself?", key: "relationship_lesson" },

  // ── Stage 5: Career & Stability ──────────────────────────────
  { id: 28, stage: 5, text: "What's your employment situation and what line of work are you in?", key: "employment" },
  { id: 29, stage: 5, text: "What does your typical work week look like?", key: "work_schedule" },
  { id: 30, stage: 5, text: "What are your plans for the near future — any big career moves or life changes coming up?", key: "near_future_plans" },
  { id: 31, stage: 5, text: "Looking further ahead, where do you see yourself in 10 years — career, family, lifestyle?", key: "long_term_goals" },
  { id: 31.1, stage: 5, text: "What days of the week are you typically free, and what times of day work best for you to go on a date — mornings, afternoons, evenings, or weekends?", key: "schedule_availability" },

  // ── Stage 6: Lifestyle & Daily Life ──────────────────────────
  { id: 32, stage: 6, text: "What does your typical weekend look like? Are you more of a homebody or someone who enjoys going out?", key: "weekend_lifestyle" },
  { id: 33, stage: 6, text: "Are you a morning person or a night owl?", key: "daily_rhythm" },
  { id: 34, stage: 6, text: "How often do you exercise, and what kind of exercise do you enjoy?", key: "fitness" },
  { id: 35, stage: 6, text: "Are you part of any communities, clubs, or regular social groups? What do you do for fun?", key: "social_activities" },
  { id: 36, stage: 6, text: "What hobbies or activities mean the most to you?", key: "hobbies" },

  // ── Stage 7: Dating Style ─────────────────────────────────────
  { id: 37, stage: 7, text: "On a first date, do you prefer something quick and low-key like coffee or tea, a full dinner, or a longer day activity like a hike, concert, or outing?", key: "date_preferences" },
  { id: 37.1, stage: 7, text: "What kind of atmosphere do you enjoy most for a date — casual and cozy, upscale and romantic, outdoors, or somewhere with live music or events?", key: "date_atmosphere" },
  { id: 38, stage: 7, text: "How often would you ideally like to see a partner each week?", key: "date_frequency" },
  { id: 38.1, stage: 7, text: "When you imagine a perfect evening with someone you really like, what does that look like?", key: "ideal_date_vision" },

  // ── Stage 8: Dating Logistics & Money ────────────────────────
  { id: 40, stage: 8, text: "What's your typical budget for a date?", key: "date_budget" },
  { id: 41, stage: 8, text: "How do you feel about splitting the bill, covering the date, or being treated?", key: "financial_preferences" },
  { id: 42, stage: 8, text: "How far are you realistically willing to travel for a relationship?", key: "relationship_distance" },

  // ── Stage 9: Food, Drinks & Social Habits ────────────────────
  { id: 43, stage: 9, text: "What are your top three favorite foods or cuisines? Any dietary restrictions?", key: "food_preferences" },
  { id: 43.1, stage: 9, text: "Are there any foods you strongly dislike or can't stand? Even if it seems silly, it helps us find the right match for dinner dates.", key: "disliked_foods" },
  { id: 44, stage: 9, text: "How do you take your coffee, and what's your relationship with alcohol?", key: "drink_preferences" },
  { id: 45, stage: 9, text: "Do you smoke or use recreational substances? And are you comfortable dating someone who does?", key: "substance_preferences" },

  // ── Stage 10: Travel & Experiences ───────────────────────────
  { id: 46, stage: 10, text: "What countries have you traveled to, and where are you dreaming of going next?", key: "travel_history" },
  { id: 47, stage: 10, text: "How important is travel in your life? Is it something you need a partner to share with you?", key: "travel_importance" },

  // ── Stage 11: Values & Beliefs ───────────────────────────────
  { id: 48, stage: 11, text: "How would you describe your political views, and does it matter to you that a partner shares them?", key: "political_views" },
  { id: 49, stage: 11, text: "How important is religion or spirituality in your life, and does your partner need to share those beliefs?", key: "religion" },
  { id: 50, stage: 11, text: "Are there cultural traditions or values that are important to you in a relationship?", key: "core_values" },

  // ── Stage 12: Relationship Psychology ────────────────────────
  { id: 51, stage: 12, text: "What qualities do you value most in a relationship? What makes you feel truly connected?", key: "relationship_values" },
  { id: 52, stage: 12, text: "What behaviors are your biggest relationship dealbreakers?", key: "dealbreakers" },
  { id: 53, stage: 12, text: "How do you usually handle conflict? Do you address it directly, step back, or something else?", key: "conflict_resolution" },
  { id: 54, stage: 12, text: "When you're stressed or going through a hard time, what do you need most from a partner?", key: "stress_needs" },
  { id: 55, stage: 12, text: "What is your primary love language — words of affirmation, quality time, physical touch, acts of service, or gifts?", key: "love_language" },
  { id: 56, stage: 12, text: "What makes you feel most loved and appreciated in a relationship?", key: "appreciation_style" },

  // ── Stage 13: Self Awareness ──────────────────────────────────
  { id: 57, stage: 13, text: "What are your biggest pet peeves in relationships — the small things that really get to you?", key: "pet_peeves" },
  { id: 58, stage: 13, text: "What do people usually misunderstand about you when they first meet you?", key: "first_impression" },
  { id: 59, stage: 13, text: "What are three words your closest friends would use to describe you?", key: "self_description" },
  { id: 60, stage: 13, text: "What kind of partner truly brings out the best version of you?", key: "ideal_partner_effect" },
  { id: 61, stage: 13, text: "In your own words, what does a healthy relationship look like to you?", key: "healthy_relationship_vision" },

  // ── Stage 14: Must-Haves & Dealbreakers ──────────────────────
  { id: 62, stage: 14, text: "What qualities are absolute must-haves in a partner — things you simply won't compromise on?", key: "must_haves" },
  { id: 63, stage: 14, text: "What qualities or behaviors would make a relationship completely impossible for you?", key: "hard_dealbreakers" },
  { id: 64, stage: 14, text: "Are there specific lifestyle choices that would rule someone out for you? For example: smoking, no desire for kids, different religion, long distance?", key: "lifestyle_dealbreakers" },
  { id: 64.1, stage: 14, text: "Last step — record a 60-second voicemail for your profile. Just speak naturally: tell potential matches a little about yourself, what you're looking for, and what makes you laugh. I'll let you know when to start.", key: "voicemail_prompt", type: "voicemail" },
];

// ─── Voicemail Reactions Store ────────────────────────────────
const REACTION_EMOJIS = ["😂", "😭", "❤️", "🤣", "🔥", "🥺"];
const _getVoicemailReactions = () => {
  try {
    const rawData = localStorage.getItem("soul_voicemail_reactions");
    console.log('📖 Loading reactions from localStorage:', rawData);
    const saved = JSON.parse(rawData || "null");
    if (saved) {
      console.log('✅ Found saved reactions:', saved);
      return saved;
    }
    // First time - initialize with base counts
    const base = { "😂": 3, "😭": 1, "❤️": 8, "🤣": 5, "🔥": 12, "🥺": 2 };
    localStorage.setItem("soul_voicemail_reactions", JSON.stringify(base));
    console.log('🆕 Initialized with base reactions:', base);
    return base;
  } catch (err) { 
    console.error('❌ Error loading reactions:', err);
    const base = { "😂": 3, "😭": 1, "❤️": 8, "🤣": 5, "🔥": 12, "🥺": 2 };
    return base;
  }
};
const _addVoicemailReaction = (emoji) => {
  addVoicemailReaction(emoji); // Auto-syncs to Supabase
};

// Helper functions to persist given reactions per match
const _getGivenReactions = () => {
  try {
    const saved = localStorage.getItem("soul_given_reactions");
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
};

const _saveGivenReaction = (matchId, emoji) => {
  saveGivenReaction(matchId, emoji); // Auto-syncs to Supabase
};

const triggerRipple = (e) => {
  const btn = e.currentTarget;
  const span = document.createElement('span');
  span.style.cssText = 'position:absolute;border-radius:50%;background:rgba(255,255,255,0.32);width:40px;height:40px;transform:scale(0);animation:ripple 0.5s ease;pointer-events:none;top:50%;left:50%;margin:-20px 0 0 -20px;z-index:0;';
  btn.style.position = 'relative';
  btn.style.overflow = 'hidden';
  btn.appendChild(span);
  setTimeout(() => { if (span.parentNode) span.remove(); }, 520);
};

// ─── Reusable UI ──────────────────────────────────────────────
function Pill({ children, color = T.rose }) {
  return (
    <span style={{ padding: "3px 11px", borderRadius: 8, fontSize: 13, fontWeight: 600, background: `${color}18`, color, border: `1px solid ${color}35`, letterSpacing: "0.02em" }}>
      {children}
    </span>
  );
}

function CountUpValue({ value, duration = 1600, style, suffix = "%" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const count = useCountUp(value, duration, visible);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return <div ref={ref} style={style}>{count}{suffix}</div>;
}

function ScoreRing({ value, size = 64, color = T.rose, label, delay = 0, strokeW = 7 }) {
  const r = (size - strokeW - 3) / 2, circ = 2 * Math.PI * r;
  const [visible, setVisible] = useState(false);
  const [count, setCount] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    const dur = 1200, start = performance.now();
    const delayMs = delay * 1000;
    const timer = setTimeout(() => {
      const animate = (now) => {
        const elapsed = now - start - delayMs;
        if (elapsed < 0) { requestAnimationFrame(animate); return; }
        const progress = Math.min(elapsed / dur, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setCount(Math.round(eased * value));
        if (progress < 1) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    }, delayMs);
    return () => clearTimeout(timer);
  }, [visible, value, delay]);

  const dash = visible ? (count / 100) * circ : 0;

  return (
    <div ref={ref} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
      <div style={{ position: "relative", width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={T.dim} strokeWidth={strokeW} opacity={0.4} />
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={strokeW}
            strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
            style={{ transition: "stroke-dasharray 0.05s linear", filter: `drop-shadow(0 0 6px ${color}50)` }} />
        </svg>
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size >= 88 ? Math.round(size * 0.24) : size > 50 ? 16 : 11, fontWeight: 900, color: T.text, fontFamily: "'Segoe UI', sans-serif" }}>
          {count}%
        </div>
      </div>
      {label && <div style={{ fontSize: 12, color: T.muted, textAlign: "center", lineHeight: 1.3 }}>{label}</div>}
    </div>
  );
}

function useCountUp(target, duration = 1200, active = true) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) { setCount(0); return; }
    const start = performance.now();
    const animate = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [active, target, duration]);
  return count;
}

function ProgressBar({ value, color = T.rose, label }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  const count = useCountUp(value, 1400, visible);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.2 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
        <span style={{ fontSize: 13, color: T.muted }}>{label}</span>
        <span style={{ fontSize: 13, color, fontWeight: 700 }}>{count}%</span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: T.dim, overflow: "hidden" }}>
        <div style={{ height: "100%", width: visible ? `${value}%` : "0%", background: `linear-gradient(90deg, ${color}, ${color}90)`, borderRadius: 3, transition: "width 1.4s cubic-bezier(0.22, 1, 0.36, 1)" }} />
      </div>
    </div>
  );
}

// ─── SCREEN 1: Landing ────────────────────────────────────────
function LoginModal({ showLogin, setShowLogin, loginEmail, setLoginEmail, loginPassword, setLoginPassword, loginError, handleLogin, onStart, onLoginSuccess }) {
  const [googleError, setGoogleError] = useState("");
  const [googleLoading, setGoogleLoading] = useState(false);
  const googleBtnRef = useRef(null);

  // Load Google Identity Services and render button
  useEffect(() => {
    if (!showLogin) return;
    const GOOGLE_CLIENT_ID = "755202867188-032uclc167sgvcfgv4odup817rkrfqmv.apps.googleusercontent.com";

    const handleCredentialResponse = async (response) => {
      setGoogleError("");
      setGoogleLoading(true);
      try {
        const r = await fetch(`${API_BASE}/api/auth/google`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ credential: response.credential }),
        });
        const d = await r.json();
        if (!r.ok) { setGoogleError(d.error || "Google sign-in failed"); setGoogleLoading(false); return; }
        localStorage.setItem("soul_session_token", d.token);
        localStorage.setItem("soul_user", JSON.stringify(d.user));
        console.log("🟢 Google login success:", d.user.email);
        setShowLogin(false);
        if (onLoginSuccess) onLoginSuccess(d.user, d.token);
        else onStart();
      } catch (err) {
        console.error("Google auth error:", err);
        setGoogleError("Connection error");
      }
      setGoogleLoading(false);
    };

    // Load the GIS script if not already loaded
    const initGoogle = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID, callback: handleCredentialResponse });
        if (googleBtnRef.current) {
          googleBtnRef.current.innerHTML = "";
          window.google.accounts.id.renderButton(googleBtnRef.current, {
            type: "standard", theme: "outline", size: "large", width: 316, text: "continue_with", shape: "rectangular",
          });
        }
        return;
      }
      if (!document.getElementById("google-gis-script")) {
        const script = document.createElement("script");
        script.id = "google-gis-script";
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.onload = () => {
          window.google.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID, callback: handleCredentialResponse });
          if (googleBtnRef.current) {
            googleBtnRef.current.innerHTML = "";
            window.google.accounts.id.renderButton(googleBtnRef.current, {
              type: "standard", theme: "outline", size: "large", width: 316, text: "continue_with", shape: "rectangular",
            });
          }
        };
        document.head.appendChild(script);
      }
    };
    // Small delay to ensure ref is mounted
    const t = setTimeout(initGoogle, 100);
    return () => clearTimeout(t);
  }, [showLogin, onLoginSuccess, onStart, setShowLogin]);

  if (!showLogin) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 2000, backdropFilter: "blur(8px)" }}>
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: 32, maxWidth: 380, width: "100%", boxShadow: `0 40px 120px rgba(0,0,0,0.4)` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div>
            <h3 style={{ fontSize: 22, fontWeight: 800, color: T.text, fontFamily: "'Segoe UI', sans-serif" }}>Welcome back</h3>
            <p style={{ fontSize: 14, color: T.muted, marginTop: 4 }}>Sign in to your settup account</p>
          </div>
          <button onClick={() => setShowLogin(false)} style={{ background: T.dim, border: "none", color: T.muted, fontSize: 18, cursor: "pointer", fontFamily: "inherit", width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Google Sign-In Button */}
          <div ref={googleBtnRef} style={{ display: "flex", justifyContent: "center", minHeight: 44 }} />
          {googleLoading && <div style={{ fontSize: 13, color: T.muted, textAlign: "center" }}>Signing in with Google...</div>}
          {googleError && <div style={{ fontSize: 13, color: T.rose, padding: "8px 12px", background: `${T.rose}12`, borderRadius: 8 }}>{googleError}</div>}

          <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "4px 0" }}>
            <div style={{ flex: 1, height: 1, background: T.border }} />
            <span style={{ fontSize: 12, color: T.muted, fontWeight: 600, letterSpacing: "0.05em" }}>OR</span>
            <div style={{ flex: 1, height: 1, background: T.border }} />
          </div>

          <input type="email" placeholder="Email address" value={loginEmail} onChange={e => setLoginEmail(e.target.value)}
            style={{ padding: "13px 16px", borderRadius: 6, background: T.bg, border: `1px solid ${T.border}`, color: T.text, fontSize: 15, fontFamily: "inherit", outline: "none" }} />
          <input type="password" placeholder="Password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleLogin()}
            style={{ padding: "13px 16px", borderRadius: 6, background: T.bg, border: `1px solid ${T.border}`, color: T.text, fontSize: 15, fontFamily: "inherit", outline: "none" }} />
          {loginError && <div style={{ fontSize: 13, color: T.rose, padding: "8px 12px", background: `${T.rose}12`, borderRadius: 8 }}>{loginError}</div>}
          <button onClick={handleLogin} disabled={!loginEmail.trim() || !loginPassword.trim()}
            style={{ padding: "14px", borderRadius: 6, background: `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, color: "#fff", fontSize: 16, fontWeight: 700, border: "none", cursor: "pointer", fontFamily: "inherit", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", marginTop: 4 }}>
            Sign In →
          </button>
          <div style={{ fontSize: 13, color: T.muted, textAlign: "center", paddingTop: 8 }}>
            New here? <button onClick={() => { setShowLogin(false); onStart(); }} style={{ background: "none", border: "none", color: T.rose, cursor: "pointer", fontFamily: "inherit", fontWeight: 700 }}>Start your interview →</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PhoneMockup({ activeScreen = 0 }) {
  const screens = [
    {
      label: "Voice Interview",
      content: (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", padding: "16px 12px", gap: 12 }}>
          <div style={{ fontSize: 11, color: T.rose, fontWeight: 700, letterSpacing: "0.1em" }}>STAGE 1 · CORE VALUES</div>
          <div style={{ width: 80, height: 80, borderRadius: "50%", background: `radial-gradient(circle at 35% 35%, ${T.rose}, ${T.roseDeep} 60%, #6d1b3a)`, boxShadow: `0 0 30px ${T.roseGlow}, 0 0 60px ${T.roseGlow}40`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>✦</div>
          <div style={{ fontSize: 11, color: T.text, textAlign: "center", lineHeight: 1.5, padding: "0 8px" }}>"What does a fulfilling relationship look like to you?"</div>
          <div style={{ display: "flex", gap: 3 }}>{[...Array(8)].map((_, i) => <div key={i} style={{ width: 3, borderRadius: 2, background: T.rose, height: 8 + Math.sin(i * 0.8) * 6, opacity: 0.7 + i * 0.03 }} />)}</div>
          <div style={{ fontSize: 10, color: T.muted }}>Listening…</div>
        </div>
      )
    },
    {
      label: "Your Matches",
      content: (
        <div style={{ padding: "12px 10px", overflow: "hidden" }}>
          <div style={{ fontSize: 11, color: T.rose, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 8 }}>YOUR MATCHES</div>
          {[{ name: "Aria", age: 27, pct: 97, city: "San Francisco" }, { name: "Maya", age: 29, pct: 94, city: "New York" }, { name: "Zoe", age: 26, pct: 91, city: "Austin" }].map(m => (
            <div key={m.name} style={{ display: "flex", alignItems: "center", gap: 7, padding: "7px 8px", background: T.card, borderRadius: 6, marginBottom: 5, border: `1px solid ${T.border}` }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: `linear-gradient(135deg, ${T.rose}, ${T.violet})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, flexShrink: 0 }}>♥</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.text }}>{m.name}, {m.age} · {m.city}</div>
                <div style={{ fontSize: 10, color: T.muted }}>Compatible</div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 800, color: T.rose }}>{m.pct}%</div>
            </div>
          ))}
        </div>
      )
    },
    {
      label: "Profile",
      content: (
        <div style={{ padding: "14px 10px", display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 11, color: T.rose, fontWeight: 700, letterSpacing: "0.1em" }}>YOUR PROFILE</div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 20 }}>🌹</div>
            <div style={{ fontSize: 13, fontWeight: 800, color: T.text, fontFamily: "'Segoe UI', sans-serif" }}>The Romantic Visionary</div>
            <div style={{ fontSize: 10, color: T.muted }}>INFJ · Secure Attachment</div>
          </div>
          {[["Openness", 88, T.violet], ["Empathy", 92, T.rose], ["Ambition", 74, T.amber]].map(([l, v, c]) => (
            <div key={l}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                <span style={{ fontSize: 10, color: T.muted }}>{l}</span>
                <span style={{ fontSize: 10, color: c, fontWeight: 700 }}>{v}%</span>
              </div>
              <div style={{ height: 3, borderRadius: 2, background: T.dim }}>
                <div style={{ height: "100%", width: `${v}%`, background: c, borderRadius: 2 }} />
              </div>
            </div>
          ))}
        </div>
      )
    }
  ];
  const [active, setActive] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActive(a => (a + 1) % screens.length), 3000);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={{ position: "relative", width: 220, height: 440, margin: "0 auto", flexShrink: 0 }}>
      <div style={{ position: "absolute", inset: 0, borderRadius: 36, background: `linear-gradient(145deg, ${T.card}, ${T.dark})`, border: `1.5px solid ${T.border}`, boxShadow: `0 50px 100px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.04), 0 0 60px ${T.roseGlow}` }}>
        <div style={{ position: "absolute", top: 10, left: "50%", transform: "translateX(-50%)", width: 56, height: 5, borderRadius: 3, background: T.dim }} />
        <div style={{ position: "absolute", inset: "22px 5px 5px", borderRadius: 30, overflow: "hidden", background: T.bg }}>
          <div style={{ position: "absolute", inset: 0, transition: "opacity 0.5s", opacity: 1 }}>
            {screens[active].content}
          </div>
        </div>
        <div style={{ position: "absolute", bottom: 14, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 5 }}>
          {screens.map((_, i) => (
            <div key={i} onClick={() => setActive(i)} style={{ width: i === active ? 16 : 5, height: 5, borderRadius: 3, background: i === active ? T.rose : T.dim, transition: "all 0.3s", cursor: "pointer" }} />
          ))}
        </div>
      </div>
      <div style={{ position: "absolute", top: "15%", right: -20, width: 80, height: 80, borderRadius: "50%", background: `radial-gradient(circle, ${T.roseGlow}, transparent 70%)`, filter: "blur(20px)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: "20%", left: -20, width: 60, height: 60, borderRadius: "50%", background: `radial-gradient(circle, rgba(20,184,166,0.2), transparent 70%)`, filter: "blur(16px)", pointerEvents: "none" }} />
    </div>
  );
}

function Landing({ onStart, onLoginSuccess }) {
  const [vis, setVis] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  useEffect(() => { setTimeout(() => setVis(true), 80); }, []);

  const bp = useBreakpoint();

  const handleLogin = async () => {
    setLoginError("");
    try {
      // Try regular user login first
      let r = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
      });
      let d = await r.json();

      if (!r.ok) {
        // Fallback: try admin login
        const ar = await fetch(`${API_BASE}/api/admin/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: loginEmail, password: loginPassword }),
        });
        const ad = await ar.json();
        if (!ar.ok) { setLoginError(ad.error || "Invalid credentials"); return; }
        localStorage.setItem("soul_session_token", ad.token);
        localStorage.setItem("soul_user", JSON.stringify({ email: ad.admin?.email || loginEmail, name: ad.admin?.display_name || "Admin", is_admin: true }));
        setShowLogin(false);
        setLoginEmail("");
        setLoginPassword("");
        if (onLoginSuccess) onLoginSuccess(ad.admin, ad.token);
        else onStart();
        return;
      }

      localStorage.setItem("soul_session_token", d.token);
      localStorage.setItem("soul_user", JSON.stringify(d.user));
      console.log("🟢 Login success, server user:", JSON.stringify(d.user));
      console.log("🟢 profile_stage from server:", d.user?.profile_stage);
      setShowLogin(false);
      setLoginEmail("");
      setLoginPassword("");
      if (onLoginSuccess) onLoginSuccess(d.user, d.token);
      else onStart();
    } catch (err) {
      console.error("Login fetch error:", err);
      setLoginError("Connection error: " + (err.message || "Unknown"));
    }
  };

  const modalProps = { showLogin, setShowLogin, loginEmail, setLoginEmail, loginPassword, setLoginPassword, loginError, handleLogin, onStart, onLoginSuccess };

  // ── DESKTOP: Full multi-section landing page ──────────────────
  if (bp.isDesktop) {
    const maxW = 1160;
    const sectionPad = "100px 60px";
    const features = [
      { Icon: MicIcon, title: "Voice-First Interview", desc: "Answer 14 stages of deep, conversational questions about your values, attachment style, lifestyle, and goals — all by voice. No forms, no swipes." },
      { Icon: DNAIcon, title: "Personality Modeling", desc: "Our AI builds your Big Five OCEAN profile, MBTI type, and attachment style, then simulates how you'd interact with potential matches before you ever meet." },
      { Icon: SparklesIcon, title: "Deep Compatibility", desc: "Weighted scoring across personality, values, lifestyle, goals, and even astrology (10%) produces compatibility scores that actually predict relationship success." },
      { Icon: CalendarIcon, title: "Smart Date Planning", desc: "When you match, we suggest real local venues, handle scheduling, and send calendar invites — making that first date happen effortlessly." },
      { Icon: LockIcon, title: "Blind Match Mode", desc: "Optionally hide photos and bios — matching purely on voice and personality profile. Hear their voicemail, check compatibility, then decide to reveal." },
      { Icon: GlobeIcon, title: "Premium Travel Radius", desc: "Upgrade to expand your matching radius beyond your city. Meet compatible people wherever you travel — your perfect match might be anywhere." },
    ];
    const steps = [
      { n: "01", title: "Take the Interview", desc: "Spend ~15 minutes talking with our AI. It asks 64 questions across 14 stages — everything from values and attachment patterns to your relationship dealbreakers." },
      { n: "02", title: "Get Your Profile", desc: "Instantly see your personality archetype, Big Five scores, MBTI type, attachment style, and zodiac compatibility breakdown — a full psychological portrait." },
      { n: "03", title: "Meet Your Matches", desc: "Receive 3–5 curated weekly matches with 90%+ compatibility scores. Hear voicemails, check profiles, schedule dates — all from one place." },
    ];
    return (
      <div style={{ background: T.bg, color: T.text, fontFamily: "'DM Sans', sans-serif", overflowX: "hidden" }}>
        <LoginModal {...modalProps} />

        {/* ── TOP NAV ── */}
        <header style={{ position: "sticky", top: 0, zIndex: 200, background: T.bg === "#fafafa" ? "rgba(250,250,250,0.92)" : "rgba(8,6,16,0.92)", backdropFilter: "blur(20px)", borderBottom: `1px solid ${T.border}` }}>
          <div style={{ maxWidth: maxW, margin: "0 auto", padding: "0 60px", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 42, fontWeight: 900, fontFamily: "'MBF Canno', 'Segoe UI', sans-serif", background: `linear-gradient(90deg, ${T.rose}, ${T.violet}, ${T.rose})`, backgroundSize: "300% 100%", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "aurora 6s ease-in-out infinite", textTransform: "lowercase", padding: "18px 0", margin: "-18px 0 -18px 0", overflow: "visible", lineHeight: 1, display: "inline-flex", alignItems: "center", position: "relative", top: -9 }}>settup</span>
            </div>
            <nav style={{ display: "flex", gap: 32, alignItems: "center" }}>
              {["Features", "How It Works", "About"].map(l => (
                <a key={l} href={`#${l.toLowerCase().replace(/ /g, "-")}`} style={{ fontSize: 15, color: T.muted, textDecoration: "none", fontWeight: 500, transition: "color 0.2s" }}
                  onMouseEnter={e => e.target.style.color = T.text} onMouseLeave={e => e.target.style.color = T.muted}>{l}</a>
              ))}
            </nav>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setShowLogin(true)} style={{ padding: "9px 22px", borderRadius: 50, background: "transparent", border: `1px solid ${T.border}`, color: T.text, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T.rose; e.currentTarget.style.color = T.rose; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.text; }}>Sign In</button>
              <button onClick={onStart} style={{ padding: "9px 22px", borderRadius: 50, background: `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, border: "none", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", transition: "all 0.2s" }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.18)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)"; }}>Get Started →</button>
            </div>
          </div>
        </header>

        {/* ── SECTION 1: HERO ── */}
        <section style={{ minHeight: "92vh", display: "flex", alignItems: "center", position: "relative", overflow: "hidden", padding: "80px 60px" }}>
          <div style={{ position: "absolute", top: "0%", left: "0%", width: 700, height: 700, borderRadius: "50%", background: `radial-gradient(circle, ${T.roseGlow} 0%, transparent 60%)`, filter: "blur(80px)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", bottom: "0%", right: "10%", width: 500, height: 500, borderRadius: "50%", background: `radial-gradient(circle, rgba(20,184,166,0.1) 0%, transparent 60%)`, filter: "blur(60px)", pointerEvents: "none" }} />
          <div style={{ maxWidth: maxW, margin: "0 auto", width: "100%", display: "grid", gridTemplateColumns: "1fr auto", gap: 80, alignItems: "center" }}>
            <div style={{ opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(32px)", transition: "all 1s cubic-bezier(0.16,1,0.3,1)" }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "7px 18px", borderRadius: 30, border: `1px solid ${T.rose}40`, background: T.roseSoft, marginBottom: 32 }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: T.rose, boxShadow: `0 0 10px ${T.rose}`, display: "inline-block" }} />
                <span style={{ fontSize: 13, color: T.rose, letterSpacing: "0.14em", fontWeight: 800 }}>AI-POWERED · COMPATIBILITY MATCHMAKING</span>
              </div>
              <h1 style={{ fontSize: "clamp(52px, 5vw, 80px)", fontWeight: 900, lineHeight: 1.02, marginBottom: 24, fontFamily: "'Segoe UI', sans-serif", background: `linear-gradient(135deg, ${T.text} 0%, ${T.rose} 60%, ${T.amber} 100%)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", letterSpacing: "-2px" }}>
                Your AI found<br />your person.
              </h1>
              <p style={{ fontSize: 19, color: T.muted, lineHeight: 1.8, marginBottom: 32, maxWidth: 520 }}>
                Just talk. Your AI agent listens, learns who you truly are, simulates your relationships, and delivers curated matches — before you say hello to anyone.
              </p>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 12, padding: "10px 22px", borderRadius: 40, background: T.card, border: `1px solid ${T.border}`, marginBottom: 40 }}>
                <Waveform active bars={18} color={T.teal} />
                <span style={{ fontSize: 14, color: T.muted, fontWeight: 500 }}>Speaks naturally back to you</span>
                <Waveform active bars={18} color={T.rose} />
              </div>
              <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                <button onClick={onStart} style={{ padding: "16px 40px", borderRadius: 50, background: `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, color: "#fff", fontSize: 17, fontWeight: 700, border: "none", cursor: "pointer", boxShadow: "0 4px 14px rgba(0,0,0,0.15)", fontFamily: "inherit", transition: "all 0.25s" }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.18)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.15)"; }}>
                  🎙 Start Voice Interview
                </button>
                <button onClick={() => setShowLogin(true)} style={{ padding: "16px 32px", borderRadius: 50, background: "transparent", border: `1px solid ${T.border}`, color: T.text, fontSize: 16, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = T.rose; e.currentTarget.style.color = T.rose; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.text; }}>
                  Sign In
                </button>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 20 }}>
                <div style={{ display: "flex" }}>
                  {["🟣","🔵","🟠","🟢","🔴"].map((c,i) => (
                    <div key={i} style={{ width: 28, height: 28, borderRadius: "50%", background: `linear-gradient(135deg, ${T.rose}, ${T.violet})`, border: `2px solid ${T.bg}`, marginLeft: i > 0 ? -8 : 0, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>♥</div>
                  ))}
                </div>
                <span style={{ fontSize: 14, color: T.muted }}>Joined by <strong style={{ color: T.text }}>10,000+</strong> people finding real connections</span>
              </div>
            </div>
            <div style={{ opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(32px)", transition: "all 1s 0.2s cubic-bezier(0.16,1,0.3,1)" }}>
              <PhoneMockup />
            </div>
          </div>
        </section>

        {/* ── SECTION 2: STATS BAND ── */}
        <section style={{ background: T.card, borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`, padding: "40px 60px" }}>
          <div style={{ maxWidth: maxW, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0 }}>
            {[["94%", "Match Accuracy", T.rose], ["< 15 min", "AI Onboarding", T.teal], ["3–5", "Weekly Matches", T.violet], ["14 Stages", "Deep Interview", T.amber]].map(([v, l, c], i) => (
              <div key={l} style={{ textAlign: "center", padding: "0 24px", borderRight: i < 3 ? `1px solid ${T.border}` : "none" }}>
                <div style={{ fontSize: 36, fontWeight: 900, fontFamily: "'Segoe UI', sans-serif", color: c, marginBottom: 4 }}>{v}</div>
                <div style={{ fontSize: 13, color: T.muted, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>{l}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── SECTION 3: FEATURES ── */}
        <section id="features" style={{ padding: sectionPad }}>
          <div style={{ maxWidth: maxW, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 64 }}>
              <div style={{ fontSize: 13, color: T.rose, fontWeight: 800, letterSpacing: "0.16em", marginBottom: 14 }}>WHY YOU'LL LOVE SETTUP</div>
              <h2 style={{ fontSize: "clamp(32px, 3vw, 48px)", fontWeight: 900, fontFamily: "'Segoe UI', sans-serif", color: T.text, lineHeight: 1.1, marginBottom: 16 }}>Everything you need to find<br />a real connection</h2>
              <p style={{ fontSize: 17, color: T.muted, maxWidth: 520, margin: "0 auto", lineHeight: 1.7 }}>No algorithms based on looks. No swiping fatigue. Just a deep, honest conversation that leads to your most compatible match.</p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }}>
              {features.map((f, i) => (
                <div key={f.title} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "28px 26px", transition: "all 0.25s", animation: `fadeIn 0.5s ${i * 0.08}s both`, cursor: "default" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = T.borderHover; e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.12)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}>
                  <div style={{ width: 48, height: 48, borderRadius: 6, background: T.roseSoft, border: `1px solid ${T.rose}30`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18 }}><f.Icon size={24} color={T.rose} /></div>
                  <h3 style={{ fontSize: 17, fontWeight: 800, color: T.text, marginBottom: 10, fontFamily: "'Segoe UI', sans-serif" }}>{f.title}</h3>
                  <p style={{ fontSize: 15, color: T.muted, lineHeight: 1.7 }}>{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 4: HOW IT WORKS ── */}
        <section id="how-it-works" style={{ padding: sectionPad, background: T.card, borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}` }}>
          <div style={{ maxWidth: maxW, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 64 }}>
              <div style={{ fontSize: 13, color: T.teal, fontWeight: 800, letterSpacing: "0.16em", marginBottom: 14 }}>HOW IT WORKS</div>
              <h2 style={{ fontSize: "clamp(32px, 3vw, 48px)", fontWeight: 900, fontFamily: "'Segoe UI', sans-serif", color: T.text, lineHeight: 1.1 }}>Three steps to your<br />perfect match</h2>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 48, position: "relative" }}>
              <div style={{ position: "absolute", top: 28, left: "17%", right: "17%", height: 1, background: `linear-gradient(90deg, ${T.rose}40, ${T.violet}40)`, pointerEvents: "none" }} />
              {steps.map((s, i) => (
                <div key={s.n} style={{ textAlign: "center", position: "relative" }}>
                  <div style={{ width: 56, height: 56, borderRadius: "50%", background: `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", boxShadow: `0 0 0 8px ${T.roseSoft}, 0 4px 12px rgba(0,0,0,0.15)` }}>
                    <span style={{ fontSize: 16, fontWeight: 800, color: "#fff" }}>{s.n}</span>
                  </div>
                  <h3 style={{ fontSize: 20, fontWeight: 800, color: T.text, marginBottom: 12, fontFamily: "'Segoe UI', sans-serif" }}>{s.title}</h3>
                  <p style={{ fontSize: 15, color: T.muted, lineHeight: 1.75 }}>{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 5: SCREENSHOT SHOWCASE ── */}
        <section style={{ padding: sectionPad }}>
          <div style={{ maxWidth: maxW, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 64 }}>
              <div style={{ fontSize: 13, color: T.violet, fontWeight: 800, letterSpacing: "0.16em", marginBottom: 14 }}>INSIDE SETTUP</div>
              <h2 style={{ fontSize: "clamp(32px, 3vw, 48px)", fontWeight: 900, fontFamily: "'Segoe UI', sans-serif", color: T.text, lineHeight: 1.1 }}>A beautiful way to find<br />the one you've been waiting for</h2>
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: 40, alignItems: "flex-end" }}>
              {[{ label: "Voice Interview", offset: 40 }, { label: "Your Matches", offset: 0 }, { label: "Your Profile", offset: 40 }].map((item, i) => (
                <div key={item.label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, transform: `translateY(${item.offset}px)` }}>
                  <div style={{ width: 180, height: 360, borderRadius: 30, background: `linear-gradient(145deg, ${T.card}, ${T.dark})`, border: `1.5px solid ${T.border}`, boxShadow: i === 1 ? "0 40px 80px rgba(0,0,0,0.3), 0 4px 0 0 " + T.rose + "60" : "0 24px 48px rgba(0,0,0,0.2)", overflow: "hidden", display: "flex", flexDirection: "column", padding: "20px 10px 10px", transition: "all 0.25s" }}
                    onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-8px)"; e.currentTarget.style.boxShadow = "0 56px 100px rgba(0,0,0,0.35)"; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; }}>
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 10 }}>
                      {i === 0 && <>
                        <div style={{ width: 64, height: 64, borderRadius: "50%", background: `radial-gradient(circle at 35% 35%, ${T.rose}, ${T.roseDeep} 60%, #6d1b3a)`, boxShadow: `0 0 24px ${T.roseGlow}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>✦</div>
                        <div style={{ fontSize: 12, color: T.muted, textAlign: "center" }}>14-stage voice interview</div>
                        <div style={{ display: "flex", gap: 2 }}>{[...Array(10)].map((_, k) => <div key={k} style={{ width: 3, height: 6 + Math.sin(k) * 4, borderRadius: 2, background: T.rose, opacity: 0.7 }} />)}</div>
                      </>}
                      {i === 1 && <>
                        {[97, 94, 91].map((pct, k) => (
                          <div key={k} style={{ width: "100%", background: T.bg, borderRadius: 6, padding: "8px 10px", display: "flex", alignItems: "center", gap: 8, border: `1px solid ${T.border}` }}>
                            <div style={{ width: 24, height: 24, borderRadius: "50%", background: `linear-gradient(135deg, ${T.rose}, ${T.violet})`, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>♥</div>
                            <div style={{ flex: 1 }}><div style={{ fontSize: 11, fontWeight: 700, color: T.text }}>Match #{k+1}</div></div>
                            <div style={{ fontSize: 13, fontWeight: 800, color: T.rose }}>{pct}%</div>
                          </div>
                        ))}
                      </>}
                      {i === 2 && <>
                        <div style={{ fontSize: 18 }}>🌹</div>
                        <div style={{ fontSize: 13, fontWeight: 800, color: T.text, textAlign: "center", fontFamily: "'Segoe UI', sans-serif" }}>Romantic Visionary</div>
                        {[["Openness", 88], ["Empathy", 92]].map(([l, v]) => (
                          <div key={l} style={{ width: "100%" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                              <span style={{ fontSize: 10, color: T.muted }}>{l}</span>
                              <span style={{ fontSize: 10, color: T.rose, fontWeight: 700 }}>{v}%</span>
                            </div>
                            <div style={{ height: 3, borderRadius: 2, background: T.dim }}>
                              <div style={{ height: "100%", width: `${v}%`, background: T.rose, borderRadius: 2 }} />
                            </div>
                          </div>
                        ))}
                      </>}
                    </div>
                  </div>
                  <div style={{ fontSize: 13, color: T.muted, fontWeight: 600, letterSpacing: "0.06em" }}>{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 6: SIGN IN CTA ── */}
        <section id="about" style={{ padding: sectionPad, background: `linear-gradient(135deg, ${T.roseSoft}, ${T.bg})`, borderTop: `1px solid ${T.border}` }}>
          <div style={{ maxWidth: 640, margin: "0 auto", textAlign: "center" }}>
            <div style={{ fontSize: 13, color: T.rose, fontWeight: 800, letterSpacing: "0.16em", marginBottom: 16 }}>GET STARTED TODAY</div>
            <h2 style={{ fontSize: "clamp(32px, 3vw, 52px)", fontWeight: 900, fontFamily: "'Segoe UI', sans-serif", color: T.text, lineHeight: 1.1, marginBottom: 20 }}>Ready to meet the one?</h2>
            <p style={{ fontSize: 17, color: T.muted, lineHeight: 1.7, marginBottom: 48 }}>Start with our 15-minute voice interview. Your AI agent is waiting — and so is your person.</p>
            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 24, padding: "40px 48px", boxShadow: `0 32px 80px rgba(0,0,0,0.12)` }}>
              <div style={{ fontSize: 16, color: T.muted, marginBottom: 20 }}>Sign in to save your profile and matches</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
                <button onClick={() => alert('Google sign-in coming soon!')} style={{ padding: "14px 20px", borderRadius: 50, background: "#fff", color: "#1a1a1a", fontSize: 16, fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.12)", transition: "all 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
                  <span style={{ fontSize: 20, fontWeight: 900 }}>G</span> Continue with Google
                </button>
                <button onClick={() => alert('Facebook sign-in coming soon!')} style={{ padding: "14px 20px", borderRadius: 50, background: "#1877F2", color: "#fff", fontSize: 16, fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 12, transition: "all 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
                  <span style={{ fontSize: 17, fontWeight: 900 }}>f</span> Continue with Facebook
                </button>
                <button onClick={() => setShowLogin(true)} style={{ padding: "14px 20px", borderRadius: 50, background: T.bg, color: T.text, fontSize: 16, fontWeight: 600, border: `1px solid ${T.border}`, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 12, transition: "all 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
                  📧 Continue with Email
                </button>
              </div>
              <div style={{ position: "relative", margin: "24px 0", display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ flex: 1, height: 1, background: T.border }} />
                <span style={{ fontSize: 13, color: T.muted }}>or</span>
                <div style={{ flex: 1, height: 1, background: T.border }} />
              </div>
              <button onClick={onStart} style={{ width: "100%", padding: "16px", borderRadius: 50, background: `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, color: "#fff", fontSize: 17, fontWeight: 700, border: "none", cursor: "pointer", fontFamily: "inherit", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", transition: "all 0.2s" }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.18)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)"; }}>
                🎙 Start as Guest — No Account Needed
              </button>
            </div>
          </div>
        </section>

        {/* ── FOOTER ── */}
        <footer style={{ background: T.dark, borderTop: `1px solid ${T.border}`, padding: "40px 60px" }}>
          <div style={{ maxWidth: maxW, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 40, marginBottom: 40 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <span style={{ fontSize: 28, fontWeight: 900, fontFamily: "'MBF Canno', 'Segoe UI', sans-serif", background: `linear-gradient(90deg, ${T.rose}, ${T.violet}, ${T.rose})`, backgroundSize: "300% 100%", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "aurora 6s ease-in-out infinite", textTransform: "lowercase", padding: "6px 0", overflow: "visible", lineHeight: 1.3, display: "inline-block" }}>settup</span>
              </div>
              <p style={{ fontSize: 14, color: T.muted, lineHeight: 1.7, maxWidth: 260 }}>Compatibility AI matchmaking that understands who you are — not just your face.</p>
            </div>
            <div>
              <div style={{ fontSize: 13, color: T.muted, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 16 }}>PRODUCT</div>
              {["Features", "How It Works", "Pricing", "Blog"].map(l => (
                <div key={l} style={{ fontSize: 14, color: T.muted, marginBottom: 10, cursor: "pointer", transition: "color 0.2s" }}
                  onMouseEnter={e => e.target.style.color = T.text} onMouseLeave={e => e.target.style.color = T.muted}>{l}</div>
              ))}
            </div>
            <div>
              <div style={{ fontSize: 13, color: T.muted, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 16 }}>COMPANY</div>
              {["About", "Privacy Policy", "Terms of Service", "Contact"].map(l => (
                <div key={l} style={{ fontSize: 14, color: T.muted, marginBottom: 10, cursor: "pointer", transition: "color 0.2s" }}
                  onMouseEnter={e => e.target.style.color = T.text} onMouseLeave={e => e.target.style.color = T.muted}>{l}</div>
              ))}
            </div>
          </div>
          <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 24, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 13, color: T.muted }}>© 2026 settup.ai · All rights reserved.</span>
            <span style={{ fontSize: 13, color: T.muted }}>Made with ♥ for real connections</span>
          </div>
        </footer>
      </div>
    );
  }

  // ── MOBILE / TABLET: Full landing page (responsive) ───────────
  const mobPad = bp.isTablet ? "0 36px" : "0 20px";
  const mobSectionPad = bp.isTablet ? "64px 36px" : "56px 20px";
  const features = [
    { Icon: MicIcon, title: "Voice-First Interview", desc: "Answer 14 stages of deep, conversational questions about your values, attachment style, lifestyle, and goals — all by voice." },
    { Icon: DNAIcon, title: "Personality Modeling", desc: "Our AI builds your Big Five OCEAN profile, MBTI type, and attachment style, then simulates how you'd interact with potential matches." },
    { Icon: SparklesIcon, title: "Deep Compatibility", desc: "Weighted scoring across personality, values, lifestyle, goals, and astrology produces compatibility scores that predict relationship success." },
    { Icon: CalendarIcon, title: "Smart Date Planning", desc: "When you match, we suggest real local venues, handle scheduling, and send calendar invites — making that first date effortless." },
    { Icon: LockIcon, title: "Blind Match Mode", desc: "Optionally hide photos and bios — matching purely on voice and personality. Hear their voicemail, check compatibility, then decide." },
    { Icon: GlobeIcon, title: "Premium Travel Radius", desc: "Upgrade to expand your matching radius beyond your city. Your perfect match might be anywhere." },
  ];
  const steps = [
    { n: "01", title: "Take the Interview", desc: "Spend ~15 minutes talking with our AI. It asks 64 questions across 14 stages — values, attachment patterns, and dealbreakers." },
    { n: "02", title: "Get Your Profile", desc: "Instantly see your personality archetype, Big Five scores, MBTI type, attachment style, and zodiac breakdown." },
    { n: "03", title: "Meet Your Matches", desc: "Receive 3–5 curated weekly matches with 90%+ compatibility scores. Hear voicemails, check profiles, schedule dates." },
  ];
  return (
    <div style={{ background: T.bg, color: T.text, fontFamily: "'DM Sans', sans-serif", overflowX: "hidden" }}>
      <LoginModal {...modalProps} />

      {/* ── MOBILE NAV ── */}
      <header style={{ position: "sticky", top: 0, zIndex: 200, background: T.bg === "#fafafa" ? "rgba(250,250,250,0.92)" : "rgba(8,6,16,0.92)", backdropFilter: "blur(20px)", borderBottom: `1px solid ${T.border}` }}>
        <div style={{ padding: mobPad, height: 56, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 28, fontWeight: 900, fontFamily: "'MBF Canno', 'Segoe UI', sans-serif", background: `linear-gradient(90deg, ${T.rose}, ${T.violet}, ${T.rose})`, backgroundSize: "300% 100%", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "aurora 6s ease-in-out infinite", textTransform: "lowercase", padding: "12px 0", margin: "-12px 0", overflow: "visible", lineHeight: 1, display: "inline-flex", alignItems: "center", position: "relative", top: -5 }}>settup</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setShowLogin(true)} style={{ padding: "7px 16px", borderRadius: 50, background: "transparent", border: `1px solid ${T.border}`, color: T.text, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Sign In</button>
            <button onClick={onStart} style={{ padding: "7px 16px", borderRadius: 50, background: `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, border: "none", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Get Started</button>
          </div>
        </div>
      </header>

      {/* ── HERO ── */}
      <section style={{ padding: bp.isTablet ? "60px 36px" : "48px 20px", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "-10%", left: "-20%", width: 400, height: 400, borderRadius: "50%", background: `radial-gradient(circle, ${T.roseGlow} 0%, transparent 60%)`, filter: "blur(60px)", pointerEvents: "none" }} />
        <div style={{ opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(24px)", transition: "all 0.9s cubic-bezier(0.16,1,0.3,1)", position: "relative" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "6px 14px", borderRadius: 30, border: `1px solid ${T.rose}40`, background: T.roseSoft, marginBottom: 24 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.rose, boxShadow: `0 0 8px ${T.rose}`, display: "inline-block" }} />
            <span style={{ fontSize: 11, color: T.rose, letterSpacing: "0.12em", fontWeight: 800 }}>AI-POWERED · COMPATIBILITY MATCHMAKING</span>
          </div>
          <h1 style={{ fontSize: bp.isTablet ? 52 : "clamp(36px, 9vw, 52px)", fontWeight: 900, lineHeight: 1.05, marginBottom: 18, fontFamily: "'Segoe UI', sans-serif", background: `linear-gradient(135deg, ${T.text} 0%, ${T.rose} 60%, ${T.amber} 100%)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", letterSpacing: "-1px" }}>
            Your AI found<br />your person.
          </h1>
          <p style={{ fontSize: 16, color: T.muted, lineHeight: 1.7, marginBottom: 24, maxWidth: 420, margin: "0 auto 24px" }}>
            Just talk. Your AI agent listens, learns who you truly are, simulates your relationships, and delivers curated matches — before you say hello to anyone.
          </p>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 16px", borderRadius: 40, background: T.card, border: `1px solid ${T.border}`, marginBottom: 24 }}>
            <Waveform active bars={12} color={T.teal} />
            <span style={{ fontSize: 13, color: T.muted }}>Speaks naturally back to you</span>
            <Waveform active bars={12} color={T.rose} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 320, margin: "0 auto 16px" }}>
            <button onClick={onStart} style={{ padding: "14px 32px", borderRadius: 50, background: `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, color: "#fff", fontSize: 16, fontWeight: 700, border: "none", cursor: "pointer", boxShadow: "0 4px 14px rgba(0,0,0,0.15)", fontFamily: "inherit" }}>
              🎙 Start Voice Interview
            </button>
            <button onClick={() => setShowLogin(true)} style={{ padding: "14px 32px", borderRadius: 50, background: "transparent", border: `1px solid ${T.border}`, color: T.text, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              Sign In
            </button>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center", marginTop: 16 }}>
            <div style={{ display: "flex" }}>
              {["🟣","🔵","🟠","🟢","🔴"].map((c,i) => (
                <div key={i} style={{ width: 24, height: 24, borderRadius: "50%", background: `linear-gradient(135deg, ${T.rose}, ${T.violet})`, border: `2px solid ${T.bg}`, marginLeft: i > 0 ? -6 : 0, fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>♥</div>
              ))}
            </div>
            <span style={{ fontSize: 13, color: T.muted }}>Joined by <strong style={{ color: T.text }}>10,000+</strong> people</span>
          </div>
        </div>
      </section>

      {/* ── STATS BAND ── */}
      <section style={{ background: T.card, borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`, padding: bp.isTablet ? "32px 36px" : "28px 20px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: bp.isTablet ? 24 : 16 }}>
          {[["94%", "Match Accuracy", T.rose], ["< 15 min", "AI Onboarding", T.teal], ["3–5", "Weekly Matches", T.violet], ["14 Stages", "Deep Interview", T.amber]].map(([v, l, c]) => (
            <div key={l} style={{ textAlign: "center", padding: "8px 0" }}>
              <div style={{ fontSize: bp.isTablet ? 28 : 24, fontWeight: 900, fontFamily: "'Segoe UI', sans-serif", color: c, marginBottom: 2 }}>{v}</div>
              <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" style={{ padding: mobSectionPad }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ fontSize: 12, color: T.rose, fontWeight: 800, letterSpacing: "0.14em", marginBottom: 12 }}>WHY YOU'LL LOVE SETTUP</div>
          <h2 style={{ fontSize: bp.isTablet ? 32 : 26, fontWeight: 900, fontFamily: "'Segoe UI', sans-serif", color: T.text, lineHeight: 1.15, marginBottom: 12 }}>Everything you need to find a real connection</h2>
          <p style={{ fontSize: 15, color: T.muted, maxWidth: 400, margin: "0 auto", lineHeight: 1.65 }}>No algorithms based on looks. No swiping fatigue. Just a deep, honest conversation that leads to your most compatible match.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: bp.isTablet ? "repeat(2, 1fr)" : "1fr", gap: 16 }}>
          {features.map((f, i) => (
            <div key={f.title} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "22px 20px", animation: `fadeIn 0.5s ${i * 0.06}s both` }}>
              <div style={{ width: 40, height: 40, borderRadius: 6, background: T.roseSoft, border: `1px solid ${T.rose}30`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}><f.Icon size={20} color={T.rose} /></div>
              <h3 style={{ fontSize: 15, fontWeight: 800, color: T.text, marginBottom: 8, fontFamily: "'Segoe UI', sans-serif" }}>{f.title}</h3>
              <p style={{ fontSize: 14, color: T.muted, lineHeight: 1.65 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" style={{ padding: mobSectionPad, background: T.card, borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}` }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ fontSize: 12, color: T.teal, fontWeight: 800, letterSpacing: "0.14em", marginBottom: 12 }}>HOW IT WORKS</div>
          <h2 style={{ fontSize: bp.isTablet ? 32 : 26, fontWeight: 900, fontFamily: "'Segoe UI', sans-serif", color: T.text, lineHeight: 1.15 }}>Three steps to your perfect match</h2>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
          {steps.map((s, i) => (
            <div key={s.n} style={{ textAlign: "center", position: "relative" }}>
              {i < steps.length - 1 && <div style={{ position: "absolute", left: "50%", top: 56, bottom: -32, width: 1, background: `linear-gradient(180deg, ${T.rose}40, transparent)`, pointerEvents: "none" }} />}
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", boxShadow: `0 0 0 6px ${T.roseSoft}, 0 4px 12px rgba(0,0,0,0.15)`, position: "relative", zIndex: 1 }}>
                <span style={{ fontSize: 14, fontWeight: 800, color: "#fff" }}>{s.n}</span>
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 800, color: T.text, marginBottom: 8, fontFamily: "'Segoe UI', sans-serif" }}>{s.title}</h3>
              <p style={{ fontSize: 14, color: T.muted, lineHeight: 1.7, maxWidth: 360, margin: "0 auto" }}>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── SCREENSHOT SHOWCASE ── */}
      <section style={{ padding: mobSectionPad }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ fontSize: 12, color: T.violet, fontWeight: 800, letterSpacing: "0.14em", marginBottom: 12 }}>INSIDE SETTUP</div>
          <h2 style={{ fontSize: bp.isTablet ? 32 : 26, fontWeight: 900, fontFamily: "'Segoe UI', sans-serif", color: T.text, lineHeight: 1.15 }}>A beautiful way to find the one</h2>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: bp.isTablet ? 28 : 16, alignItems: "flex-end" }}>
          {[{ label: "Voice Interview", offset: 24 }, { label: "Your Matches", offset: 0 }, { label: "Your Profile", offset: 24 }].map((item, i) => (
            <div key={item.label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, transform: `translateY(${item.offset}px)` }}>
              <div style={{ width: bp.isTablet ? 140 : 100, height: bp.isTablet ? 280 : 200, borderRadius: 20, background: `linear-gradient(145deg, ${T.card}, ${T.dark})`, border: `1.5px solid ${T.border}`, boxShadow: i === 1 ? `0 24px 48px rgba(0,0,0,0.25), 0 3px 0 0 ${T.rose}60` : "0 16px 32px rgba(0,0,0,0.15)", overflow: "hidden", display: "flex", flexDirection: "column", padding: bp.isTablet ? "14px 8px 8px" : "10px 6px 6px" }}>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 6 }}>
                  {i === 0 && <>
                    <div style={{ width: 40, height: 40, borderRadius: "50%", background: `radial-gradient(circle at 35% 35%, ${T.rose}, ${T.roseDeep} 60%, #6d1b3a)`, boxShadow: `0 0 16px ${T.roseGlow}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>✦</div>
                    <div style={{ fontSize: 9, color: T.muted, textAlign: "center" }}>14-stage voice interview</div>
                    <div style={{ display: "flex", gap: 1 }}>{[...Array(8)].map((_, k) => <div key={k} style={{ width: 2, height: 4 + Math.sin(k) * 3, borderRadius: 1, background: T.rose, opacity: 0.7 }} />)}</div>
                  </>}
                  {i === 1 && <>
                    {[97, 94, 91].map((pct, k) => (
                      <div key={k} style={{ width: "100%", background: T.bg, borderRadius: 4, padding: "5px 6px", display: "flex", alignItems: "center", gap: 5, border: `1px solid ${T.border}` }}>
                        <div style={{ width: 16, height: 16, borderRadius: "50%", background: `linear-gradient(135deg, ${T.rose}, ${T.violet})`, fontSize: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>♥</div>
                        <div style={{ flex: 1 }}><div style={{ fontSize: 8, fontWeight: 700, color: T.text }}>Match #{k+1}</div></div>
                        <div style={{ fontSize: 9, fontWeight: 800, color: T.rose }}>{pct}%</div>
                      </div>
                    ))}
                  </>}
                  {i === 2 && <>
                    <div style={{ fontSize: 14 }}>🌹</div>
                    <div style={{ fontSize: 9, fontWeight: 800, color: T.text, textAlign: "center", fontFamily: "'Segoe UI', sans-serif" }}>Romantic Visionary</div>
                    {[["Open", 88], ["Empathy", 92]].map(([l, v]) => (
                      <div key={l} style={{ width: "100%" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 1 }}>
                          <span style={{ fontSize: 8, color: T.muted }}>{l}</span>
                          <span style={{ fontSize: 8, color: T.rose, fontWeight: 700 }}>{v}%</span>
                        </div>
                        <div style={{ height: 2, borderRadius: 1, background: T.dim }}>
                          <div style={{ height: "100%", width: `${v}%`, background: T.rose, borderRadius: 1 }} />
                        </div>
                      </div>
                    ))}
                  </>}
                </div>
              </div>
              <div style={{ fontSize: 10, color: T.muted, fontWeight: 600, letterSpacing: "0.04em" }}>{item.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section id="about" style={{ padding: mobSectionPad, background: `linear-gradient(135deg, ${T.roseSoft}, ${T.bg})`, borderTop: `1px solid ${T.border}` }}>
        <div style={{ maxWidth: 480, margin: "0 auto", textAlign: "center" }}>
          <div style={{ fontSize: 12, color: T.rose, fontWeight: 800, letterSpacing: "0.14em", marginBottom: 12 }}>GET STARTED TODAY</div>
          <h2 style={{ fontSize: bp.isTablet ? 32 : 26, fontWeight: 900, fontFamily: "'Segoe UI', sans-serif", color: T.text, lineHeight: 1.15, marginBottom: 14 }}>Ready to meet the one?</h2>
          <p style={{ fontSize: 15, color: T.muted, lineHeight: 1.65, marginBottom: 32 }}>Start with our 15-minute voice interview. Your AI agent is waiting — and so is your person.</p>
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 20, padding: bp.isTablet ? "32px 36px" : "28px 20px", boxShadow: "0 24px 60px rgba(0,0,0,0.1)" }}>
            <div style={{ fontSize: 14, color: T.muted, marginBottom: 16 }}>Sign in to save your profile and matches</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
              <button onClick={() => alert('Google sign-in coming soon!')} style={{ padding: "12px 16px", borderRadius: 50, background: "#fff", color: "#1a1a1a", fontSize: 14, fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, boxShadow: "0 2px 10px rgba(0,0,0,0.1)" }}>
                <span style={{ fontSize: 18, fontWeight: 900 }}>G</span> Continue with Google
              </button>
              <button onClick={() => alert('Facebook sign-in coming soon!')} style={{ padding: "12px 16px", borderRadius: 50, background: "#1877F2", color: "#fff", fontSize: 14, fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                <span style={{ fontSize: 15, fontWeight: 900 }}>f</span> Continue with Facebook
              </button>
              <button onClick={() => setShowLogin(true)} style={{ padding: "12px 16px", borderRadius: 50, background: T.bg, color: T.text, fontSize: 14, fontWeight: 600, border: `1px solid ${T.border}`, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                📧 Continue with Email
              </button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "16px 0" }}>
              <div style={{ flex: 1, height: 1, background: T.border }} />
              <span style={{ fontSize: 12, color: T.muted }}>or</span>
              <div style={{ flex: 1, height: 1, background: T.border }} />
            </div>
            <button onClick={onStart} style={{ width: "100%", padding: "14px", borderRadius: 50, background: `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, color: "#fff", fontSize: 15, fontWeight: 700, border: "none", cursor: "pointer", fontFamily: "inherit", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>
              🎙 Start as Guest — No Account Needed
            </button>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ background: T.dark, borderTop: `1px solid ${T.border}`, padding: bp.isTablet ? "32px 36px" : "28px 20px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 24, marginBottom: 24 }}>
          <div>
            <span style={{ fontSize: 15, fontWeight: 900, fontFamily: "'MBF Canno', 'Segoe UI', sans-serif", background: `linear-gradient(90deg, ${T.rose}, ${T.violet}, ${T.rose})`, backgroundSize: "300% 100%", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "aurora 6s ease-in-out infinite", textTransform: "lowercase", padding: "4px 0", overflow: "visible", lineHeight: 1.1, display: "inline-block" }}>settup</span>
            <p style={{ fontSize: 13, color: T.muted, lineHeight: 1.6, marginTop: 8, maxWidth: 280 }}>Compatibility AI matchmaking that understands who you are — not just your face.</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <div>
              <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 10 }}>PRODUCT</div>
              {["Features", "How It Works", "Pricing", "Blog"].map(l => (
                <div key={l} style={{ fontSize: 13, color: T.muted, marginBottom: 8 }}>{l}</div>
              ))}
            </div>
            <div>
              <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 10 }}>COMPANY</div>
              {["About", "Privacy Policy", "Terms of Service", "Contact"].map(l => (
                <div key={l} style={{ fontSize: 13, color: T.muted, marginBottom: 8 }}>{l}</div>
              ))}
            </div>
          </div>
        </div>
        <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 16, display: "flex", flexDirection: "column", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: T.muted }}>© 2026 settup.ai · All rights reserved.</span>
          <span style={{ fontSize: 12, color: T.muted }}>Made with ♥ for real connections</span>
        </div>
      </footer>
    </div>
  );
}

// Stage 1 questions run on initial signup; stages 2-14 deepen match quality later
const STAGE1_QUESTIONS = AI_QUESTIONS.filter(q => q.stage === 1);

// ─── Answer Normalization ─────────────────────────────────────
// Deciphers slang, transcription errors, and natural speech patterns
function normalizeAnswer(text, questionKey) {
  if (!text) return text;
  const t = text.trim().toLowerCase().replace(/[.,!?]+$/, '').trim();

  // Extract first name from sentences like "Is George", "I'm George", "My name is George"
  if (questionKey === 'first_name' || questionKey === 'name') {
    const nameMatch = t.match(/(?:i'?m|is|it'?s|name is|call me|they call me)\s+([a-z]+)/i);
    if (nameMatch) return nameMatch[1].charAt(0).toUpperCase() + nameMatch[1].slice(1);
    // If single word, just capitalize it
    const words = t.split(/\s+/);
    if (words.length <= 2) return words.map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    return text.trim(); // Return as-is for longer sentences
  }

  // Normalize gender
  if (questionKey === 'gender') {
    if (/^(m|male|man|guy|dude|boy|mail|meal|mill|make|mate)$/i.test(t)) return 'Male';
    if (/^(f|female|woman|girl|lady|gal|female|feel)$/i.test(t)) return 'Female';
    if (/non.?binary|enby|nb|they|them/i.test(t)) return 'Non-binary';
    if (/other|prefer not|rather not/i.test(t)) return 'Prefer not to say';
  }

  // Normalize yes/no
  if (/^(yeah|yep|yup|sure|absolutely|definitely|of course|yes|correct|right|true|affirmative)$/i.test(t)) return 'Yes';
  if (/^(nope|nah|no|not really|negative|never|naw)$/i.test(t)) return 'No';

  // Extract age from sentences like "I am 44", "I'm 44 years old"
  if (questionKey === 'age') {
    const ageMatch = t.match(/(\d{1,3})/);
    if (ageMatch) return ageMatch[1];
  }

  // Parse birthday from spoken format: "March 15 1990", "the 15th of March 1990", "3/15/1990"
  if (questionKey === 'birthday') {
    const MONTHS = { january:1, february:2, march:3, april:4, may:5, june:6,
      july:7, august:8, september:9, october:10, november:11, december:12,
      jan:1, feb:2, mar:3, apr:4, jun:6, jul:7, aug:8, sep:9, oct:10, nov:11, dec:12 };
    // "March 15 1990" or "March 15th 1990"
    const spoken = t.match(/([a-z]+)\s+(\d{1,2})(?:st|nd|rd|th)?\s+(\d{4})/i);
    if (spoken) {
      const mo = MONTHS[spoken[1].toLowerCase()];
      if (mo) return `${spoken[3]}-${String(mo).padStart(2,'0')}-${String(spoken[2]).padStart(2,'0')}`;
    }
    // "15 March 1990"
    const spoken2 = t.match(/(\d{1,2})(?:st|nd|rd|th)?\s+(?:of\s+)?([a-z]+)\s+(\d{4})/i);
    if (spoken2) {
      const mo = MONTHS[spoken2[2].toLowerCase()];
      if (mo) return `${spoken2[3]}-${String(mo).padStart(2,'0')}-${String(spoken2[1]).padStart(2,'0')}`;
    }
    // "3/15/1990" or "03-15-1990"
    const numeric = t.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
    if (numeric) return `${numeric[3]}-${String(numeric[1]).padStart(2,'0')}-${String(numeric[2]).padStart(2,'0')}`;
    return text.trim();
  }

  // Return original with proper capitalization
  return text.trim();
}

// ─── SCREEN 2: Voice Interview ────────────────────────────────
const PROGRESS_KEY = "soul_interview_progress";

// saveInterviewProgress and clearInterviewProgress now imported from autoSync
function loadInterviewProgress(stage) {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return p.stage === stage ? p : null;
  } catch { return null; }
}

function VoiceInterview({ onComplete, onPause, startAtStage = 1 }) {
  const stageQuestions = AI_QUESTIONS.filter(q => q.stage === startAtStage);
  const savedProgress = loadInterviewProgress(startAtStage);
  const [step, setStep] = useState(savedProgress ? savedProgress.step : -1);
  const stepRef = useRef(savedProgress ? savedProgress.step : -1);
  const isProcessingRef = useRef(false); // Guard against re-entrant processAnswer calls
  const transcriptRef = useRef("");      // Always-current transcript, no stale closures
  const listeningActiveRef = useRef(false); // Track if we started a listen session
  const retryCountRef = useRef(0);         // Limit "didn't catch that" retries
  const [orbState, setOrbState] = useState("idle");
  const [liveTranscript, setLiveTranscript] = useState("");
  const [answers, setAnswers] = useState(savedProgress ? savedProgress.answers : {});
  const answersRef = useRef(savedProgress ? savedProgress.answers : {});
  const [subtitles, setSubtitles] = useState("");
  const [canTap, setCanTap] = useState(false);
  const [done, setDone] = useState(false);
  const [inputMode, setInputMode] = useState("voice");
  const inputModeRef = useRef("voice");
  const [textInput, setTextInput] = useState("");
  const textInputRef = useRef(null);
  const [userName, setUserName] = useState("");
  const [debugLog, setDebugLog] = useState([]);
  const dbg = (msg) => { console.log(msg); setDebugLog(prev => [...prev.slice(-15), `${new Date().toLocaleTimeString()}: ${msg}`]); };
  const resuming = !!(savedProgress && savedProgress.step >= 0);

  const { speak, stop: stopSpeech, speaking } = useSpeechSynthesis();
  const { start: startRec, stop: stopRec, listening, transcript: liveT, supported: voiceSupported, requestNativePermission, debugRef: speechDebugRef } = useSpeechRecognition();
  // Wire debug logger into the speech recognition hook
  useEffect(() => { speechDebugRef.current = dbg; }, []);

  // Request mic permission upfront on mount (avoids double-prompt feel on Android)
  useEffect(() => {
    requestNativePermission();
  }, []);

  // Auto-fallback to text if voice not supported
  useEffect(() => {
    console.log("🔧 Voice supported:", voiceSupported);
    if (!voiceSupported) {
      console.log("⚠️ Voice not supported, falling back to text mode");
      setInputMode("text");
      inputModeRef.current = "text";
    } else {
      console.log("✅ Voice mode enabled by default");
    }
  }, [voiceSupported]);

  // Track speaking state
  useEffect(() => {
    if (speaking) setOrbState("speaking");
  }, [speaking]);

  // Sync live transcript and ref
  useEffect(() => {
    setLiveTranscript(liveT);
    if (liveT) transcriptRef.current = liveT; // Keep ref current
  }, [liveT]);

  // Auto-start intro when component mounts
  useEffect(() => {
    let mounted = true;
    
    const startInterview = async () => {
      if (!mounted) return;
      if (resuming) {
        // Resume from saved step
        const resumeStep = stepRef.current;
        console.log("Resuming interview at step:", resumeStep);
        askQuestion(resumeStep);
      } else {
        console.log("Starting interview with step:", step);
        askQuestion(-1);
      }
    };

    // Wait for component to fully mount
    const timer = setTimeout(startInterview, 1500);
    
    return () => {
      mounted = false;
      clearTimeout(timer);
      // Cleanup on unmount: stop all audio/recognition
      stopRec();
      stopSpeech();
      console.log("🧹 VoiceInterview unmounted, cleaned up audio");
    };
  }, []); // Only run once on mount

  const startListeningForAnswer = () => {
    dbg("startListen: active=" + listeningActiveRef.current + " processing=" + isProcessingRef.current + " mode=" + inputModeRef.current);
    if (listeningActiveRef.current || isProcessingRef.current || inputModeRef.current === "text") { dbg("startListen: SKIPPED"); return; }
    listeningActiveRef.current = true;
    transcriptRef.current = "";
    setLiveTranscript("");
    setOrbState("listening");
    dbg("🎤 Starting listening session");

    // Silence timer — only starts AFTER recognition actually begins (not during permission dialog)
    const SILENCE_MS = IS_NATIVE ? 10000 : 3000;
    let silenceTimer = null;
    const resetSilenceTimer = () => {
      clearTimeout(silenceTimer);
      silenceTimer = setTimeout(() => {
        dbg("⏰ Silence timer expired - stopping");
        stopRec();
      }, SILENCE_MS);
    };
    // For web, start timer immediately. For native, wait for onListeningStarted callback.
    if (!IS_NATIVE) resetSilenceTimer();

    startRec(
      (result) => {
        dbg("📝 Got result: " + result);
        transcriptRef.current = result;
        resetSilenceTimer(); // Reset timer on each speech result
      },
      () => {
        dbg("🛑 onEnd fired, transcript='" + transcriptRef.current + "'");
        clearTimeout(silenceTimer);
        listeningActiveRef.current = false;
        // Delay 300ms — native SpeechRecognizer sometimes fires "stopped" before
        // the final partialResults event arrives. Give in-flight results time to settle.
        setTimeout(() => {
          const capturedText = transcriptRef.current;
          dbg("📋 Final transcript after settle: '" + capturedText + "'");
          if (capturedText.trim() && !isProcessingRef.current) {
            processAnswer(capturedText);
          } else if (!capturedText.trim()) {
            retryCountRef.current += 1;
            dbg("🔁 Empty speech, retry #" + retryCountRef.current);
            if (retryCountRef.current <= 1) {
              // First retry: show brief message then auto-restart listening
              setOrbState("idle");
              setSubtitles("I didn't catch that — listening again...");
              setTimeout(() => {
                setSubtitles("");
                if (inputModeRef.current === "voice" && !isProcessingRef.current) {
                  dbg("🔁 Auto-restarting listen after empty speech");
                  startListeningForAnswer();
                } else {
                  setCanTap(true);
                }
              }, 1500);
            } else {
              // After 1 auto-retry, wait for orb tap
              setOrbState("idle");
              setSubtitles("Tap the orb when you're ready to answer.");
              setCanTap(true);
              retryCountRef.current = 0;
            }
          }
        }, 300);
      },
      // onListeningStarted — native only: start silence timer after recognition actually begins
      () => {
        dbg("✅ onListeningStarted fired, starting silence timer");
        resetSilenceTimer();
      }
    );
  };

  const askQuestion = (qStep) => {
    dbg("askQ step=" + qStep);
    isProcessingRef.current = false; // Reset guard for new question
    listeningActiveRef.current = false;
    retryCountRef.current = 0; // Reset retry counter for new question
    stopRec(); // Stop any active recognition first

    const text = qStep === -1
      ? (startAtStage === 1 ? "Hey! I'm your settup AI. I'll ask you a few quick questions to understand who you are and what you're looking for. Ready?" : `Let's go through the ${STAGES[startAtStage]?.name || 'Stage ' + startAtStage} questions. Ready?`)
      : stageQuestions[qStep]?.text;

    if (!text) {
      dbg("❌ No question text for step: " + qStep);
      return;
    }

    setOrbState("speaking");
    setSubtitles(text);
    setCanTap(false);

    speak(text, () => {
      dbg("TTS done, mode=" + inputModeRef.current);
      setOrbState("idle");
      // In text mode, keep subtitles visible so user can read the question
      if (inputModeRef.current !== "text") setSubtitles("");
      setCanTap(true);

      if (qStep === -1) {
        setTimeout(() => {
          stepRef.current = 0;
          setStep(0);
          askQuestion(0);
        }, inputModeRef.current === "text" ? 2000 : 500);
      } else if (inputModeRef.current === "voice") {
        dbg("Will start listening in 600ms");
        setTimeout(startListeningForAnswer, 600);
      } else {
        // Text mode: auto-focus the text input after question is asked
        setTimeout(() => textInputRef.current?.focus(), 300);
      }
    });
  };

  const handleOrbClick = async (e) => {
    e.preventDefault();
    dbg("🔘 Orb tap canTap=" + canTap + " done=" + done + " orb=" + orbState + " mode=" + inputModeRef.current);
    
    if (!canTap || done) {
      dbg("❌ Click ignored");
      return;
    }
    
    // If in text mode, orb click submits text or focuses input
    if (inputModeRef.current === "text") {
      if (textInput.trim()) {
        dbg("🔘 Orb submitting text");
        submitText();
      } else {
        textInputRef.current?.focus();
      }
      return;
    }
    
    if (orbState === "listening") {
      // User manually stopped - submit what we have
      dbg("🔘 Manual stop, transcript='" + transcriptRef.current + "'");
      listeningActiveRef.current = false;
      stopRec();
      const finalText = transcriptRef.current;
      if (finalText.trim() && !isProcessingRef.current) {
        processAnswer(finalText);
      } else {
        setOrbState("idle");
      }
    } else if (orbState === "idle") {
      dbg("🔘 Starting listen from orb tap");
      startListeningForAnswer();
    }
  };

  const processAnswer = (text) => {
    // Guard: prevent simultaneous/duplicate calls
    if (isProcessingRef.current) {
      console.log("⛔ processAnswer blocked - already processing");
      return;
    }
    isProcessingRef.current = true;

    const currentQ = stageQuestions[stepRef.current];
    // Normalize: fix slang, transcription errors, extract names etc.
    const normalized = normalizeAnswer(text, currentQ?.key);
    console.log("🔄 processAnswer:", normalized, "(raw:", text.substring(0, 30), ") stepRef:", stepRef.current);

    if (!normalized.trim()) {
      isProcessingRef.current = false;
      return;
    }
    const finalText = normalized;

    setOrbState("thinking");
    setCanTap(false);
    transcriptRef.current = "";
    setLiveTranscript("");

    if (!currentQ) {
      console.error("❌ No current question! StepRef:", stepRef.current);
      isProcessingRef.current = false;
      setOrbState("idle");
      setCanTap(true);
      return;
    }

    const newAnswers = { ...answers, [currentQ.key]: finalText };
    console.log("💾 Saved:", currentQ.key, "=", finalText);
    setAnswers(newAnswers);
    answersRef.current = newAnswers;
    saveInterviewProgress(startAtStage, stepRef.current, newAnswers);
    
    // Track user's name for personalization
    if ((currentQ.key === "first_name" || currentQ.key === "name") && !userName) {
      setUserName(finalText.split(' ')[0]);
    }

    setTimeout(() => {
      const nextStep = stepRef.current + 1;
      console.log("➡️ Next step:", nextStep, "of", stageQuestions.length);

      if (nextStep >= stageQuestions.length) {
        console.log("🎉 Stage", startAtStage, "complete!");
        setDone(true);
        setOrbState("speaking");
        const doneText = userName
          ? `Great work, ${userName}! Stage ${startAtStage} is complete.`
          : `Stage ${startAtStage} complete!`;
        setSubtitles(doneText);
        speak(doneText, () => {
          setOrbState("thinking");
          setSubtitles("Saving your answers...");
          clearInterviewProgress(startAtStage);
          setTimeout(() => onComplete(newAnswers, startAtStage), 1200);
        });
      } else {
        console.log("🚀 Moving to next question");
        stepRef.current = nextStep;
        setStep(nextStep);
        
        // Use name sparingly - only on questions 3, 6, 9 for personal touch
        askQuestion(nextStep);
      }
    }, 800);
  };

  const submitText = () => {
    if (!textInput.trim() || !canTap) return;
    const t = textInput;
    setTextInput("");
    processAnswer(t);
  };

  const repeatQuestion = () => {
    if (!canTap || done || stepRef.current < 0) return;
    const q = stageQuestions[stepRef.current];
    if (!q) return;
    askQuestion(stepRef.current);
  };

  const handlePause = () => {
    console.log("⏸ Pausing interview at step:", stepRef.current);
    // Stop all audio and recognition
    stopRec();
    stopSpeech();
    // Reset UI state
    setOrbState("idle");
    setSubtitles("");
    setCanTap(false);
    listeningActiveRef.current = false;
    isProcessingRef.current = false;
    // Save progress (only if we've actually started - step >= 0)
    if (stepRef.current >= 0) {
      saveInterviewProgress(startAtStage, stepRef.current, answersRef.current);
    }
    // Navigate away
    if (onPause) onPause();
  };

  const progress = step < 0 ? 0 : ((step) / stageQuestions.length) * 100;
  const currentStage = step >= 0 && stageQuestions[step] ? STAGES[stageQuestions[step].stage] : null;

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", padding: "32px 24px 24px", position: "relative", overflow: "hidden" }}>
      {/* BG glow tied to orb state */}
      <div style={{
        position: "absolute", top: "30%", left: "50%", transform: "translate(-50%, -50%)",
        width: 500, height: 500, borderRadius: "50%",
        background: orbState === "listening" ? `radial-gradient(circle, ${T.roseGlow} 0%, transparent 65%)`
          : orbState === "speaking" ? `radial-gradient(circle, rgba(20,184,166,0.2) 0%, transparent 65%)`
          : orbState === "thinking" ? `radial-gradient(circle, rgba(245,158,11,0.15) 0%, transparent 65%)`
          : "transparent",
        filter: "blur(60px)", transition: "background 0.8s ease", pointerEvents: "none"
      }} />

      {/* Progress */}
      <div style={{ width: "100%", maxWidth: 480, marginBottom: 32 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={handlePause} title="Pause and return to profile"
              style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 8, color: T.muted, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", padding: "4px 12px", display: "flex", alignItems: "center", gap: 5 }}>
              ⏸ Pause
            </button>
            <div>
              <span style={{ fontSize: 13, color: T.rose, fontWeight: 700, letterSpacing: "0.1em" }}>SETTUP</span>
              <span style={{ fontSize: 12, color: T.muted, marginLeft: 8 }}>Stage {startAtStage}{currentStage ? ` · ${currentStage.name}` : ""}</span>
            </div>
          </div>
          <span style={{ fontSize: 13, color: T.muted }}>{step < 0 ? (resuming ? "Resuming..." : "Welcome") : `${step + 1} of ${stageQuestions.length}`}</span>
        </div>
        <div style={{ height: 2, borderRadius: 2, background: T.dim }}>
          <div style={{ height: "100%", width: `${done ? 100 : progress}%`, background: `linear-gradient(90deg, ${T.rose}, ${T.amber})`, borderRadius: 2, transition: "width 0.6s ease" }} />
        </div>
      </div>

      {/* Orb */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 48, width: "100%", maxWidth: 480 }}>
        <div 
          onClick={handleOrbClick}
          style={{ 
            cursor: canTap && inputMode === "voice" ? "pointer" : "default", 
            userSelect: "none",
            WebkitUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none",
            WebkitTouchCallout: "none",
            WebkitTapHighlightColor: "transparent"
          }}
        >
          <VoiceOrb state={orbState} size={200} />
        </div>

        {/* Subtitles / AI speech */}
        <div style={{ minHeight: 80, textAlign: "center", maxWidth: 420, padding: "0 8px" }}>
          {subtitles && (
            <div style={{ fontSize: 17, color: T.text, lineHeight: 1.7, animation: "fadeIn 0.4s ease" }}>
              {subtitles}
            </div>
          )}
          {listening && liveTranscript && (
            <div style={{ fontSize: 16, color: T.muted, lineHeight: 1.6, fontStyle: "italic", animation: "fadeIn 0.2s ease" }}>
              "{liveTranscript}"
            </div>
          )}
          {canTap && !listening && !subtitles && inputMode === "voice" && (
            <div style={{ fontSize: 15, color: T.dim, animation: "fadeIn 0.4s ease" }}>
              Tap the orb to start speaking
            </div>
          )}
          {canTap && listening && inputMode === "voice" && (
            <div style={{ fontSize: 15, color: T.rose, animation: "fadeIn 0.4s ease" }}>
              Tap again to submit your answer
            </div>
          )}
          {canTap && !listening && !subtitles && inputMode === "text" && (
            <div style={{ fontSize: 15, color: T.dim, animation: "fadeIn 0.4s ease" }}>
              Type your answer below
            </div>
          )}
        </div>

        {/* Repeat question button */}
        {canTap && !done && step >= 0 && (
          <button
            onClick={repeatQuestion}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 16px", borderRadius: 8, background: "transparent", border: `1px solid ${T.border}`, color: T.muted, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s", marginTop: -16 }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = T.rose; e.currentTarget.style.color = T.rose; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.muted; }}
          >
            <span style={{ fontSize: 15 }}>🔁</span> Repeat question
          </button>
        )}

        {/* Waveform while listening */}
        {listening && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            <Waveform active color={T.rose} bars={32} />
          </div>
        )}

        {/* Mode toggle + text fallback */}
        {canTap && !listening && (
          <div style={{ width: "100%", maxWidth: 420, animation: "fadeIn 0.4s ease" }}>
            {inputMode === "voice" && !voiceSupported && (
              <p style={{ fontSize: 13, color: T.amber, textAlign: "center", marginBottom: 12 }}>🎤 Voice recognition not available in this browser. Text input is ready below.</p>
            )}
            {voiceSupported && (
              <div style={{ display: "flex", justifyContent: "center", gap: 12, marginBottom: 16 }}>
                {["voice", "text"].map(m => (
                  <button key={m} onClick={() => { setInputMode(m); inputModeRef.current = m; }} style={{ padding: "12px 28px", borderRadius: 24, border: `2px solid ${inputMode === m ? T.rose : T.border}`, background: inputMode === m ? T.roseSoft : "transparent", color: inputMode === m ? T.rose : T.muted, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.05em", transition: "all 0.3s", boxShadow: inputMode === m ? `0 0 0 2px ${T.rose}40` : "none" }}>
                    {m === "voice" ? "🎙 Voice" : "⌨ Type"}
                  </button>
                ))}
              </div>
            )}

            {inputMode === "text" && (
              <div style={{ display: "flex", gap: 8 }}>
                <textarea ref={textInputRef} value={textInput} onChange={e => setTextInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitText(); }}}
                  placeholder="Type your answer..." rows={2}
                  style={{ flex: 1, background: T.card, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, fontSize: 15, padding: "10px 14px", fontFamily: "inherit", resize: "none", outline: "none" }} />
                <button onClick={submitText} style={{ width: 44, borderRadius: 6, background: textInput.trim() ? `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})` : T.dim, border: "none", color: "#fff", fontSize: 18, cursor: textInput.trim() ? "pointer" : "not-allowed", display: "flex", alignItems: "center", justifyContent: "center" }}>↑</button>
              </div>
            )}
          </div>
        )}
      </div>
      {/* DEBUG OVERLAY — remove after fixing */}
      {IS_NATIVE && debugLog.length > 0 && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, maxHeight: "35vh", overflow: "auto", background: "rgba(0,0,0,0.92)", color: "#0f0", fontSize: 10, fontFamily: "monospace", padding: "6px 8px", zIndex: 99999, lineHeight: 1.4 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <b>DEBUG LOG</b>
            <span onClick={() => setDebugLog([])} style={{ color: "#f66", cursor: "pointer" }}>CLEAR</span>
          </div>
          {debugLog.map((l, i) => <div key={i}>{l}</div>)}
        </div>
      )}
    </div>
  );
}

// ─── SCREEN 3: Matches Feed ───────────────────────────────────
function MatchesFeed({ matches = MATCHES, loading = false, onSelectMatch, blindMode = false, upcomingDates = [] }) {
  const [filter, setFilter] = useState("All");
  const [playingMatchId, setPlayingMatchId] = useState(null);
  const [givenReactions, setGivenReactions] = useState(_getGivenReactions);
  const bp = useBreakpoint();

  const handleOrbClick = (e, matchId) => {
    e.stopPropagation();
    setPlayingMatchId(prev => prev === matchId ? null : matchId);
  };
  const handleReaction = (e, matchId, emoji) => {
    e.stopPropagation();
    _addVoicemailReaction(emoji);
    _saveGivenReaction(matchId, emoji);
    setGivenReactions(prev => ({ ...prev, [matchId]: emoji }));
    setPlayingMatchId(null);
    window.dispatchEvent(new Event('givenReactionsChanged'));
  };

  useEffect(() => {
    const sync = () => setGivenReactions(_getGivenReactions());
    window.addEventListener('givenReactionsChanged', sync);
    return () => window.removeEventListener('givenReactionsChanged', sync);
  }, []);

  if (loading) {
    const shimmerStyle = { background: `linear-gradient(90deg, ${T.dim} 25%, ${T.cardAlt} 50%, ${T.dim} 75%)`, backgroundSize: "800px 100%", animation: "shimmer 1.4s infinite linear", borderRadius: 4 };
    return (
      <div style={{ maxWidth: bp.isDesktop ? 1400 : 480, margin: "0 auto", padding: bp.isDesktop ? "24px 32px 48px" : "24px 20px 100px" }}>
        <div style={{ marginBottom: 32 }}>
          <div style={{ ...shimmerStyle, width: 80, height: 12, marginBottom: 12 }} />
          <div style={{ ...shimmerStyle, width: 240, height: 28, marginBottom: 8 }} />
          <div style={{ ...shimmerStyle, width: 180, height: 14 }} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "repeat(3, 1fr)" : "1fr", gap: bp.isDesktop ? 24 : 14 }}>
          {[...Array(bp.isDesktop ? 6 : 3)].map((_, i) => (
            <div key={i} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden", animation: `fadeIn 0.3s ${i * 0.06}s ease both` }}>
              <div style={{ ...shimmerStyle, height: bp.isDesktop ? 180 : 140 }} />
              <div style={{ padding: "16px" }}>
                <div style={{ ...shimmerStyle, width: "60%", height: 14, marginBottom: 10 }} />
                <div style={{ ...shimmerStyle, width: "40%", height: 12, marginBottom: 8 }} />
                <div style={{ ...shimmerStyle, width: "80%", height: 10 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const containerPad = bp.isDesktop ? "24px 32px 48px" : bp.isTablet ? "28px 32px 60px" : "24px 20px 100px";
  const maxW = bp.isDesktop ? 1400 : bp.isTablet ? 680 : 480;

  return (
    <div style={{ maxWidth: maxW, margin: "0 auto", padding: containerPad }}>
      <PageHeader
        label="MY MATCHES"
        title="Your Matches"
        description={`Agent simulated ${matches.length} relationships for you.`}
        gradientAnimation
      />

      <div style={{ display: "flex", gap: bp.isDesktop ? 10 : 8, marginBottom: bp.isDesktop ? 28 : 20, overflowX: "auto", paddingBottom: 2, flexWrap: bp.isDesktop ? "wrap" : "nowrap" }}>
        {["All", "High Match", "Near Me", "New"].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{ padding: bp.isDesktop ? "9px 20px" : "7px 16px", borderRadius: 8, border: `1px solid ${filter === f ? T.rose : T.border}`, background: filter === f ? T.roseSoft : "transparent", color: filter === f ? T.rose : T.muted, fontSize: bp.isDesktop ? 13 : 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", transition: "all 0.2s" }}>
            {f}
          </button>
        ))}
      </div>

      {/* Upcoming Dates Schedule */}
      {upcomingDates.length > 0 && (
        <div style={{ marginBottom: bp.isDesktop ? 36 : 28 }}>
          <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.rose, fontWeight: 700, letterSpacing: "0.1em", marginBottom: bp.isDesktop ? 16 : 12 }}>UPCOMING DATES</div>
          <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "repeat(2, 1fr)" : "1fr", gap: bp.isDesktop ? 16 : 10 }}>
            {[...upcomingDates].sort((a, b) => new Date(a.date_time) - new Date(b.date_time)).map((d, i) => {
              const dt = new Date(d.date_time);
              const partnerName = d.matchName || d.user_a_name || d.user_b_name || "Your Match";
              const matchData = MATCHES.find(mx => mx.id === d.matchId) || MATCHES.find(mx => partnerName.includes(mx.name.split(" ")[0]));
              const status = d.status || "confirmed";
              const isPending = status === "pending";
              const statusColor = status === "confirmed" ? "#22c55e" : T.rose;
              const statusLabel = status === "confirmed" ? "CONFIRMED" : "AWAITING CONFIRMATION";
              const typeIcon = d.venue_type === "coffee" ? "☕" : d.venue_type === "dinner" ? "🍽️" : d.venue_type === "activity" ? "🎯" : d.venue_type === "drinks" ? "🍷" : d.venue_type === "lunch" ? "🥗" : "📅";
              return (
                <div key={i} style={{ background: T.card, border: `1px solid ${isPending ? `${T.rose}30` : `${T.border}`}`, borderRadius: 8, overflow: "hidden", animation: `fadeIn 0.4s ${i * 0.1}s ease both` }}>
                  <div style={{ padding: bp.isDesktop ? "18px 20px" : "14px 16px" }}>
                    {/* Status badge */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 8, background: `${statusColor}15`, color: statusColor, border: `1px solid ${statusColor}30`, letterSpacing: "0.05em" }}>
                        {statusLabel}
                      </div>
                      <span style={{ fontSize: bp.isDesktop ? 16 : 15 }}>{typeIcon}</span>
                    </div>

                    {/* Match info row */}
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                      {matchData && !blindMode ? (
                        <div style={{ position: "relative", width: bp.isDesktop ? 48 : 40, height: bp.isDesktop ? 48 : 40, flexShrink: 0 }}>
                          <div style={{ width: "100%", height: "100%", borderRadius: "50%", overflow: "hidden", border: `2px solid ${isPending ? T.rose : "#22c55e"}` }}>
                            <img src={matchData.photo} alt={partnerName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          </div>
                          <button onClick={e => handleOrbClick(e, matchData.id)} style={{ 
                            position: "absolute", 
                            bottom: -3, 
                            right: -3, 
                            width: 20, 
                            height: 20, 
                            borderRadius: "50%", 
                            background: playingMatchId === matchData.id ? T.rose : `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, 
                            border: "2px solid #fff", 
                            display: "flex", 
                            alignItems: "center", 
                            justifyContent: "center", 
                            cursor: "pointer",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            transition: "all 0.2s",
                            padding: 0
                          }}
                          onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.1)"; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}>
                            <span style={{ fontSize: 11, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginLeft: playingMatchId === matchData.id ? 0 : 1 }}>{playingMatchId === matchData.id ? "⏸" : <PlayTriangle size={11} />}</span>
                          </button>
                        </div>
                      ) : (
                        <div style={{ width: bp.isDesktop ? 48 : 40, height: bp.isDesktop ? 48 : 40, borderRadius: "50%", background: `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: `2px solid ${isPending ? T.rose : "#22c55e"}` }}>
                          <span style={{ fontSize: bp.isDesktop ? 18 : 15, color: "#fff" }}>♥</span>
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: bp.isDesktop ? 16 : 15, fontWeight: 700, color: T.text }}>{partnerName}{matchData ? `, ${matchData.age}` : ""}</div>
                        <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted }}>{matchData ? `${matchData.gender} · ${matchData.height}` : ""}</div>
                        {matchData && <div style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.muted }}><LocationIcon size={10} color={T.muted} /> {matchData.city}</div>}
                      </div>
                      {matchData && (
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <div style={{ fontSize: bp.isDesktop ? 22 : 18, fontWeight: 900, color: T.rose, fontFamily: "'Segoe UI', sans-serif", lineHeight: 1 }}>{matchData.compatibility || d.matchCompatibility}%</div>
                          <div style={{ fontSize: 11, color: T.muted }}>MATCH</div>
                        </div>
                      )}
                    </div>

                    {/* Venue & time */}
                    <div style={{ background: T.bg, borderRadius: 6, padding: bp.isDesktop ? "12px 14px" : "10px 12px" }}>
                      <div style={{ fontSize: bp.isDesktop ? 14 : 15, fontWeight: 700, color: T.text, marginBottom: 4 }}>{d.venue_name}</div>
                      <div style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.muted, marginBottom: 8 }}>{d.venue_address}</div>
                      <div style={{ display: "flex", gap: 16, fontSize: bp.isDesktop ? 12 : 13 }}>
                        <div>
                          <span style={{ color: T.muted }}>Date: </span>
                          <span style={{ color: T.text, fontWeight: 600 }}>{dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</span>
                        </div>
                        <div>
                          <span style={{ color: T.muted }}>Time: </span>
                          <span style={{ color: T.text, fontWeight: 600 }}>{dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</span>
                        </div>
                      </div>
                    </div>

                    {/* Pending message */}
                    {isPending && (
                      <div style={{ marginTop: 10, fontSize: bp.isDesktop ? 11 : 12, color: T.muted, textAlign: "center", fontStyle: "italic" }}>
                        Waiting for {partnerName.split(" ")[0]} to confirm this date
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "repeat(3, 1fr)" : bp.isTablet ? "repeat(2, 1fr)" : "1fr", gap: bp.isDesktop ? 24 : 14 }}>
        {matches.filter(m => !upcomingDates.some(d => d.matchId === m.id)).map((m, i) => (
          <div key={m.id}
            style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden", cursor: "pointer", transition: "all 0.2s", animation: `fadeIn 0.4s ${i * 0.1}s ease both` }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = T.borderHover; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.12)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}>

            {/* BLIND MODE: voicemail + stats only */}
            {blindMode ? (
              <div onClick={() => onSelectMatch(m)} style={{ padding: bp.isDesktop ? "20px 20px 18px" : "16px 16px 14px" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: bp.isDesktop ? 16 : 12, marginBottom: bp.isDesktop ? 16 : 12 }}>
                  {/* Animated orb play button */}
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                    <div onClick={e => handleOrbClick(e, m.id)}
                      style={{ position: "relative", width: bp.isDesktop ? 85 : 72, height: bp.isDesktop ? 85 : 72, flexShrink: 0, cursor: "pointer" }}>
                      <div style={{ position: "absolute", inset: -5, borderRadius: "50%", border: `2px solid ${T.rose}`, animation: "orbRing 1.8s ease-out infinite", opacity: 0.7 }} />
                      <div style={{ width: "100%", height: "100%", borderRadius: "50%", background: `linear-gradient(135deg, ${T.rose}, ${T.violet})`, display: "flex", alignItems: "center", justifyContent: "center", animation: "orbPulse 1.5s ease-in-out infinite alternate", boxShadow: `0 0 16px ${T.roseGlow}`, border: `3px solid ${T.rose}` }}>
                        <span style={{ fontSize: bp.isDesktop ? 28 : 22, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginLeft: playingMatchId === m.id ? 0 : 2 }}>{playingMatchId === m.id ? "⏸" : <PlayTriangle size={bp.isDesktop ? 32 : 26} />}</span>
                      </div>
                      {givenReactions[m.id] && !playingMatchId && (
                        <div style={{ position: "absolute", bottom: -2, right: -2, width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, lineHeight: 1 }}>
                          {givenReactions[m.id]}
                        </div>
                      )}
                    </div>
                    {/* Playback progress bar */}
                    {playingMatchId === m.id && (
                      <div style={{ width: bp.isDesktop ? 85 : 72, height: 3, borderRadius: 3, background: `${T.rose}30`, overflow: "hidden" }}>
                        <div style={{ height: "100%", background: `linear-gradient(90deg, ${T.rose}, ${T.violet})`, borderRadius: 3, animation: "voicemailProgress 60s linear forwards" }} />
                      </div>
                    )}
                    {/* Emoji reaction bar — visible during playback */}
                    {playingMatchId === m.id && (
                      <div onClick={e => e.stopPropagation()} style={{ display: "flex", gap: 4, animation: "reactionSlideUp 0.3s ease both" }}>
                        {REACTION_EMOJIS.map((emoji, ei) => (
                          <button key={emoji} onClick={e => { triggerRipple(e); handleReaction(e, m.id, emoji); }}
                            style={{ width: bp.isDesktop ? 32 : 28, height: bp.isDesktop ? 32 : 28, borderRadius: "50%", border: `1px solid ${T.border}`, background: givenReactions[m.id] === emoji ? `${T.rose}22` : T.dim, fontSize: bp.isDesktop ? 15 : 15, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", animation: `emojiPop 0.3s ${ei * 0.05}s ease both`, transition: "transform 0.15s", padding: 0 }}
                            onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.25)"; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}>
                            {emoji}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Name and match score */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: bp.isDesktop ? 19 : 20, fontWeight: 700, color: T.text, marginBottom: 2 }}>{m.name}, {m.age}</div>
                    <div style={{ fontSize: bp.isDesktop ? 12 : 14, color: T.muted, marginBottom: 2 }}>{m.gender} · {m.height}</div>
                    <div style={{ fontSize: bp.isDesktop ? 13 : 15, color: T.muted }}><LocationIcon size={bp.isDesktop ? 13 : 13} color={T.muted} /> {m.city}</div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: bp.isDesktop ? 28 : 26, fontWeight: 900, color: T.rose, fontFamily: "'Segoe UI', sans-serif", lineHeight: 1 }}>{m.compatibility}%</div>
                    <div style={{ fontSize: bp.isDesktop ? 10 : 12, color: T.muted }}>MATCH</div>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginBottom: bp.isDesktop ? 12 : 10 }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: bp.isDesktop ? 16 : 15, fontWeight: 800, color: T.rose }}>{m.personalityScore}%</div>
                      <div style={{ fontSize: bp.isDesktop ? 10 : 11, color: T.muted }}>Personality</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: bp.isDesktop ? 16 : 15, fontWeight: 800, color: T.teal }}>{m.valuesScore}%</div>
                      <div style={{ fontSize: bp.isDesktop ? 10 : 11, color: T.muted }}>Values</div>
                    </div>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: bp.isDesktop ? 12 : 10 }}>
                  {m.traits.slice(0, 2).map(t => <Pill key={t}>{t}</Pill>)}
                </div>
                <ProgressBar value={m.simulation.firstDate} color={T.rose} label="First Date Chemistry" />
                <div style={{ marginTop: bp.isDesktop ? 10 : 8, fontSize: bp.isDesktop ? 12 : 13, color: T.teal, fontWeight: 600 }}>✦ Intelligent simulation ready</div>
              </div>
            ) : (
            /* NORMAL MODE: photo + bio */
            <div onClick={() => onSelectMatch(m)} style={{ padding: bp.isDesktop ? "20px 20px 18px" : "16px 16px 14px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: bp.isDesktop ? 16 : 12, marginBottom: bp.isDesktop ? 16 : 12 }}>
                {/* Profile pic with audio play button */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                  <div style={{ position: "relative", width: bp.isDesktop ? 85 : 72, height: bp.isDesktop ? 85 : 72, flexShrink: 0 }}>
                    <div style={{ width: "100%", height: "100%", borderRadius: "50%", overflow: "hidden", border: `3px solid ${T.rose}` }}>
                      <img src={m.photo} alt={m.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                    </div>
                    <button onClick={e => handleOrbClick(e, m.id)} style={{ 
                      position: "absolute", 
                      bottom: -4, 
                      right: -4, 
                      width: bp.isDesktop ? 36 : 30, 
                      height: bp.isDesktop ? 36 : 30, 
                      borderRadius: "50%", 
                      background: playingMatchId === m.id ? T.rose : `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, 
                      border: "2px solid #fff", 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center", 
                      cursor: "pointer",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      transition: "all 0.2s",
                      padding: 0
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.1)"; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}>
                      <span style={{ fontSize: bp.isDesktop ? 14 : 13, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginLeft: playingMatchId === m.id ? 0 : 1 }}>{playingMatchId === m.id ? "⏸" : <PlayTriangle size={bp.isDesktop ? 16 : 14} />}</span>
                    </button>
                    {givenReactions[m.id] && !playingMatchId && (
                      <div style={{ position: "absolute", bottom: 8, right: -8, width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, lineHeight: 1, zIndex: 1 }}>
                        {givenReactions[m.id]}
                      </div>
                    )}
                  </div>
                  {playingMatchId === m.id && (
                    <div style={{ width: bp.isDesktop ? 85 : 72, height: 3, borderRadius: 3, background: `${T.rose}30`, overflow: "hidden" }}>
                      <div style={{ height: "100%", background: `linear-gradient(90deg, ${T.rose}, ${T.violet})`, borderRadius: 3, animation: "voicemailProgress 60s linear forwards" }} />
                    </div>
                  )}
                  {playingMatchId === m.id && (
                    <div onClick={e => e.stopPropagation()} style={{ display: "flex", gap: 4, animation: "reactionSlideUp 0.3s ease both" }}>
                      {REACTION_EMOJIS.map((emoji, ei) => (
                        <button key={emoji} onClick={e => { triggerRipple(e); handleReaction(e, m.id, emoji); }}
                          style={{ width: bp.isDesktop ? 32 : 28, height: bp.isDesktop ? 32 : 28, borderRadius: "50%", border: `1px solid ${T.border}`, background: givenReactions[m.id] === emoji ? `${T.rose}22` : T.dim, fontSize: bp.isDesktop ? 15 : 15, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", animation: `emojiPop 0.3s ${ei * 0.05}s ease both`, transition: "transform 0.15s", padding: 0 }}
                          onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.25)"; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}>
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Name and match score */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: bp.isDesktop ? 19 : 20, fontWeight: 700, color: T.text, marginBottom: 2 }}>{m.name}, {m.age}</div>
                  <div style={{ fontSize: bp.isDesktop ? 12 : 14, color: T.muted, marginBottom: 2 }}>{m.gender} · {m.height}</div>
                  <div style={{ fontSize: bp.isDesktop ? 13 : 15, color: T.muted }}><LocationIcon size={bp.isDesktop ? 13 : 13} color={T.muted} /> {m.city}</div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: bp.isDesktop ? 28 : 26, fontWeight: 900, color: T.rose, fontFamily: "'Segoe UI', sans-serif", lineHeight: 1 }}>{m.compatibility}%</div>
                  <div style={{ fontSize: bp.isDesktop ? 10 : 12, color: T.muted }}>MATCH</div>
                </div>
              </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginBottom: bp.isDesktop ? 12 : 10 }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: bp.isDesktop ? 16 : 15, fontWeight: 800, color: T.rose }}>{m.personalityScore}%</div>
                    <div style={{ fontSize: bp.isDesktop ? 10 : 11, color: T.muted }}>Personality</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: bp.isDesktop ? 16 : 15, fontWeight: 800, color: T.teal }}>{m.valuesScore}%</div>
                    <div style={{ fontSize: bp.isDesktop ? 10 : 11, color: T.muted }}>Values</div>
                  </div>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: bp.isDesktop ? 12 : 10 }}>
                  {m.traits.slice(0, 2).map(t => <Pill key={t}>{t}</Pill>)}
                </div>
                <ProgressBar value={m.simulation.firstDate} color={T.rose} label="First Date Chemistry" />
                <div style={{ marginTop: bp.isDesktop ? 10 : 8, fontSize: bp.isDesktop ? 12 : 13, color: T.teal, fontWeight: 600 }}>✦ Intelligent simulation ready</div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Communication Badge (count/status indicator on icon buttons) ──
function CommBadge({ count, isRead, color, isCheckmark, size = 'normal', onClick }) {
  if (!count && !isCheckmark) return null;
  const sz = size === 'small' ? { min: 16, font: 8, pad: '1px 4px' } : { min: 18, font: 9, pad: '2px 5px' };
  const solid = !isRead;
  return (
    <div onClick={onClick ? (e) => { e.stopPropagation(); onClick(e); } : undefined} style={{
      position: "absolute", top: size === 'small' ? 4 : 6, right: size === 'small' ? 4 : 6,
      minWidth: sz.min, height: sz.min, borderRadius: 9,
      background: solid ? color : "transparent",
      border: solid ? `2px solid ${color}` : `2px solid ${color}`,
      color: solid ? "#fff" : color,
      fontSize: sz.font, fontWeight: 800, fontFamily: "inherit",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: sz.pad, zIndex: 2, lineHeight: 1,
      boxShadow: solid ? `0 2px 8px ${color}40` : "none",
      transition: "all 0.3s ease",
      cursor: onClick ? "pointer" : undefined
    }}>
      {isCheckmark ? "✓" : count}
    </div>
  );
}

// ─── Connect Flyout Panel (CardNav-inspired GSAP animation) ──
function ConnectFlyout({
  isOpen, onClose, triggerRect, activeAction, expandReceived = false, matchName, bp,
  sentGiftReaction, setSentGiftReaction,
  pollQuestion, setPollQuestion, pollOptions, setPollOptions,
  pollSent, setPollSent, pollResult, setPollResult,
  phoneNumber, setPhoneNumber, phoneSent, setPhoneSent,
  isPremium, onPremiumClick,
  receivedGift, receivedPoll, receivedPollResponse, setReceivedPollResponse,
  receivedPhone, receivedMessage
}) {
  const panelRef = useRef(null);
  const backdropRef = useRef(null);
  const cardsRef = useRef([]);
  const headerRef = useRef(null);
  const tlRef = useRef(null);
  const [localGiftPick, setLocalGiftPick] = useState(null);
  const [localPollQ, setLocalPollQ] = useState('');
  const [localPollOpts, setLocalPollOpts] = useState(['', '']);
  const [localPhone, setLocalPhone] = useState('');
  const [sendExpanded, setSendExpanded] = useState(true);
  const [receivedExpanded, setReceivedExpanded] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLocalGiftPick(null);
      setLocalPollQ(pollQuestion || '');
      setLocalPollOpts(pollOptions?.length ? [...pollOptions] : ['', '']);
      setLocalPhone(phoneNumber || '');
      setSendExpanded(!expandReceived);
      setReceivedExpanded(!!expandReceived);
    }
  }, [isOpen, activeAction, expandReceived, pollQuestion, pollOptions, phoneNumber]);

  // Compute panel position anchored to the icon, clamped to viewport
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, originX: "50%", originY: "0%" });

  useLayoutEffect(() => {
    if (!panelRef.current || !backdropRef.current) return;
    if (tlRef.current) { tlRef.current.kill(); tlRef.current = null; }

    const panel = panelRef.current;
    const rect = triggerRect;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const pw = panel.offsetWidth;
    const ph = panel.offsetHeight;
    const pad = 12;

    let top, left, originX = "50%", originY = "0%";
    const isMobile = vw < 768;
    if (isMobile) {
      // Mobile: always center on viewport
      left = (vw - pw) / 2;
      top = (vh - ph) / 2;
      if (left < pad) left = pad;
      if (top < pad) top = pad;
      originX = "50%";
      originY = "50%";
    } else if (rect) {
      const iconCX = rect.left + rect.width / 2;
      const iconCY = rect.top + rect.height / 2;
      // Position panel so icon center is near the top-center of the panel
      left = iconCX - pw / 2;
      top = iconCY - 20; // slightly above icon center so panel appears below/behind icon
      // Clamp to viewport
      if (left < pad) left = pad;
      if (left + pw > vw - pad) left = vw - pad - pw;
      if (top < pad) top = pad;
      if (top + ph > vh - pad) top = vh - pad - ph;
      // transformOrigin = icon center relative to final panel position
      originX = (iconCX - left) + "px";
      originY = (iconCY - top) + "px";
    } else {
      // Fallback: center
      left = (vw - pw) / 2;
      top = (vh - ph) / 2;
    }
    setPanelPos({ top, left, originX, originY });

    const tl = gsap.timeline({ paused: true, onReverseComplete: onClose });
    tl.fromTo(backdropRef.current, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: "power2.out" }, 0);
    tl.fromTo(panel,
      { scale: 0.08, opacity: 0, transformOrigin: `${originX} ${originY}` },
      { scale: 1, opacity: 1, transformOrigin: `${originX} ${originY}`, duration: 0.42, ease: "back.out(1.2)" },
    0.05);
    if (headerRef.current) {
      tl.fromTo(headerRef.current, { y: 12, opacity: 0 }, { y: 0, opacity: 1, duration: 0.3, ease: "power3.out" }, 0.2);
    }
    const validCards = cardsRef.current.filter(Boolean);
    if (validCards.length) {
      tl.fromTo(validCards, { y: 30, opacity: 0, scale: 0.92 }, { y: 0, opacity: 1, scale: 1, duration: 0.38, ease: "power3.out", stagger: 0.08 }, 0.28);
    }
    tlRef.current = tl;

    if (isOpen) tl.play(0);
    return () => { tl.kill(); tlRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, triggerRect]);

  const handleClose = () => {
    if (tlRef.current) {
      tlRef.current.eventCallback('onReverseComplete', onClose);
      tlRef.current.reverse();
    } else {
      onClose();
    }
  };

  const setCardRef = (i) => (el) => { if (el) cardsRef.current[i] = el; };

  if (!isOpen) return null;

  const GIFT_EMOJIS = ["💝", "🌹", "✨", "🔥", "💫", "🦋", "💎", "🎀"];
  const isDark = T.bg !== "#fafafa";
  const panelBg = isDark ? "#0f0a1a" : "#f8f8fa";
  const subtleText = isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)";
  const subtleBorder = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)";
  const sectionBg = isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)";

  // Action-specific config
  const actionConfig = {
    gift: { label: "Send Gift", recvLabel: "Gifts Received", icon: <GiftBoxIcon size={bp.isDesktop ? 22 : 18} color="#fff" />, bg: `linear-gradient(135deg, ${T.rose}, ${T.roseDeep || "#c2185b"})`, color: T.rose },
    poll: { label: "Send Poll", recvLabel: "Polls Received", icon: <PollClipboardIcon size={bp.isDesktop ? 22 : 18} color="#fff" />, bg: `linear-gradient(135deg, ${T.teal}, #0d9488)`, color: T.teal },
    phone: { label: "Share Phone", recvLabel: "Phone Received", icon: <PhoneShareIcon size={bp.isDesktop ? 22 : 18} color="#fff" />, bg: `linear-gradient(135deg, ${T.violet}, #7c3aed)`, color: T.violet },
  };
  const cfg = actionConfig[activeAction] || actionConfig.gift;

  // Send content per action
  const sendContent = activeAction === 'gift' ? (
    <div>
      {sentGiftReaction ? (
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0" }}>
          <div style={{ fontSize: 36, animation: "emojiPop 0.5s ease both" }}>{sentGiftReaction}</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Gift sent!</div>
            <button onClick={() => { setSentGiftReaction(null); setLocalGiftPick(null); }}
              style={{ marginTop: 4, padding: "4px 12px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.3)", background: "rgba(255,255,255,0.1)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              Send Another
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", padding: "8px 0" }}>
          {GIFT_EMOJIS.map((emoji) => (
            <button key={emoji}
              onClick={() => { setLocalGiftPick(emoji); setSentGiftReaction(emoji); }}
              style={{
                width: 44, height: 44, borderRadius: "50%",
                border: localGiftPick === emoji ? "2px solid #fff" : "2px solid rgba(255,255,255,0.2)",
                background: localGiftPick === emoji ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.08)",
                cursor: "pointer", fontSize: 22, display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.2s"
              }}>
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  ) : activeAction === 'poll' ? (
    <div>
      {pollSent ? (
        <div style={{ padding: "8px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Poll sent!</span>
          </div>
          {pollResult && <div style={{ fontSize: 13, color: "rgba(255,255,255,0.8)" }}>Response: "{pollResult}"</div>}
        </div>
      ) : (
        <div style={{ padding: "8px 0", display: "flex", flexDirection: "column", gap: 6 }}>
          <input value={localPollQ} onChange={e => setLocalPollQ(e.target.value)} placeholder="Ask a question..."
            style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.1)", color: "#fff", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
          {localPollOpts.map((opt, i) => (
            <input key={i} value={opt} onChange={e => { const n = [...localPollOpts]; n[i] = e.target.value; setLocalPollOpts(n); }}
              placeholder={`Option ${i + 1}`}
              style={{ width: "100%", padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.06)", color: "#fff", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
          ))}
          <button
            onClick={() => {
              setPollQuestion(localPollQ);
              setPollOptions(localPollOpts);
              setPollSent(true);
              setTimeout(() => setPollResult(localPollOpts[Math.floor(Math.random() * localPollOpts.filter(Boolean).length)]), 3000 + Math.random() * 5000);
            }}
            disabled={!localPollQ || localPollOpts.filter(Boolean).length < 2}
            style={{
              padding: "8px 16px", borderRadius: 8, border: "none",
              background: localPollQ && localPollOpts.filter(Boolean).length >= 2 ? "#fff" : "rgba(255,255,255,0.15)",
              color: localPollQ && localPollOpts.filter(Boolean).length >= 2 ? T.teal : "rgba(255,255,255,0.4)",
              fontSize: 13, fontWeight: 700, cursor: localPollQ && localPollOpts.filter(Boolean).length >= 2 ? "pointer" : "not-allowed",
              fontFamily: "inherit", alignSelf: "flex-start", transition: "all 0.2s"
            }}>
            Send Poll
          </button>
        </div>
      )}
    </div>
  ) : activeAction === 'phone' ? (
    <div>
      {!isPremium ? (
        <div style={{ padding: "8px 0" }}>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", marginBottom: 8 }}>Premium feature</div>
          <button onClick={() => { handleClose(); setTimeout(() => onPremiumClick('Share Phone'), 400); }}
            style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: "#fff", color: T.violet, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            Upgrade to Unlock
          </button>
        </div>
      ) : phoneSent ? (
        <div style={{ padding: "8px 0", display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Phone number shared!</span>
        </div>
      ) : (
        <div style={{ padding: "8px 0", display: "flex", gap: 8, alignItems: "center" }}>
          <input value={localPhone} onChange={e => setLocalPhone(e.target.value.replace(/[^\d()\-+ ]/g, ''))} placeholder="Your phone number"
            style={{ flex: 1, padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.1)", color: "#fff", fontSize: 13, fontFamily: "inherit", outline: "none" }} />
          <button onClick={() => { setPhoneNumber(localPhone); setPhoneSent(true); }}
            disabled={!localPhone}
            style={{
              padding: "8px 16px", borderRadius: 8, border: "none",
              background: localPhone ? "#fff" : "rgba(255,255,255,0.15)",
              color: localPhone ? T.violet : "rgba(255,255,255,0.4)",
              fontSize: 13, fontWeight: 700, cursor: localPhone ? "pointer" : "not-allowed",
              fontFamily: "inherit", flexShrink: 0, transition: "all 0.2s"
            }}>
            Send
          </button>
        </div>
      )}
    </div>
  ) : null;

  // Received content per action
  const receivedContent = activeAction === 'gift' ? (
    receivedGift ? (
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0" }}>
        <div style={{ fontSize: 36 }}>{receivedGift}</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: isDark ? "#fff" : T.text }}>{matchName} sent you a gift!</div>
          <div style={{ fontSize: 13, color: subtleText, marginTop: 2 }}>Tap to react</div>
        </div>
      </div>
    ) : (
      <div style={{ padding: "8px 0", fontSize: 13, color: subtleText, fontStyle: "italic" }}>No gifts received yet</div>
    )
  ) : activeAction === 'poll' ? (
    receivedPoll ? (
      <div style={{ padding: "8px 0" }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: isDark ? "#fff" : T.text, marginBottom: 8 }}>{receivedPoll.question}</div>
        {receivedPollResponse ? (
          <div style={{ fontSize: 13, color: subtleText }}>You responded: "{receivedPollResponse}"</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {receivedPoll.options?.filter(Boolean).map((opt, i) => (
              <button key={i} onClick={() => setReceivedPollResponse(opt)}
                style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${cfg.color}40`, background: `${cfg.color}10`, color: isDark ? "#fff" : T.text, fontSize: 13, cursor: "pointer", fontFamily: "inherit", textAlign: "left", transition: "all 0.2s" }}>
                {opt}
              </button>
            ))}
          </div>
        )}
      </div>
    ) : (
      <div style={{ padding: "8px 0", fontSize: 13, color: subtleText, fontStyle: "italic" }}>No polls received yet</div>
    )
  ) : activeAction === 'phone' ? (
    receivedPhone ? (
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0" }}>
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: `${T.violet}20`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <PhoneShareIcon size={18} color={T.violet} />
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: isDark ? "#fff" : T.text }}>{matchName} shared their number</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: T.violet, marginTop: 2 }}>{receivedPhone}</div>
        </div>
      </div>
    ) : (
      <div style={{ padding: "8px 0", fontSize: 13, color: subtleText, fontStyle: "italic" }}>No phone number received yet</div>
    )
  ) : null;

  // Accordion section header renderer
  const AccordionHeader = ({ label, icon, expanded, onToggle, accentColor }) => (
    <div onClick={onToggle} style={{
      padding: bp.isDesktop ? "14px 16px" : "12px 14px",
      display: "flex", alignItems: "center", gap: 12, cursor: "pointer", userSelect: "none"
    }}>
      <div style={{
        width: bp.isDesktop ? 36 : 30, height: bp.isDesktop ? 36 : 30,
        borderRadius: 6, background: accentColor,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
      }}>
        {icon}
      </div>
      <div style={{ flex: 1, fontSize: bp.isDesktop ? 14 : 15, fontWeight: 700, color: isDark ? "#fff" : T.text }}>{label}</div>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={subtleText} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        style={{ transition: "transform 0.3s ease", transform: expanded ? "rotate(180deg)" : "rotate(0deg)", flexShrink: 0 }}>
        <polyline points="6 9 12 15 18 9"/>
      </svg>
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, pointerEvents: isOpen ? "auto" : "none" }}>
      {/* Backdrop */}
      <div ref={backdropRef} onClick={handleClose}
        style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }} />

      {/* Panel */}
      <div ref={panelRef} style={{
        position: "absolute", zIndex: 1,
        top: panelPos.top, left: panelPos.left,
        width: bp.isDesktop ? "min(480px, 88vw)" : "min(360px, 92vw)",
        maxHeight: bp.isDesktop ? "75vh" : "70vh",
        background: panelBg,
        borderRadius: 12,
        overflow: "hidden", display: "flex", flexDirection: "column",
        boxShadow: "0 8px 48px rgba(0,0,0,0.35), 0 0px 0px 1px rgba(255,255,255,0.05)"
      }}>
        {/* Header */}
        <div ref={headerRef} style={{
          padding: bp.isDesktop ? "20px 24px 12px" : "16px 20px 10px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          borderBottom: `1px solid ${subtleBorder}`
        }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: cfg.color, letterSpacing: "0.12em", marginBottom: 2 }}>{activeAction === 'gift' ? 'GIFT' : activeAction === 'poll' ? 'POLL' : activeAction === 'phone' ? 'PHONE' : 'CONNECT'}</div>
            <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 800, color: isDark ? "#fff" : "#1a1a1a", fontFamily: "'Segoe UI', sans-serif" }}>
              {matchName}
            </div>
          </div>
          <button onClick={handleClose} style={{
            width: 36, height: 36, borderRadius: "50%",
            border: "none", background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
            color: isDark ? "#aaa" : "#666",
            cursor: "pointer", fontSize: 18, fontWeight: 700, fontFamily: "inherit",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.2s"
          }}>
            ✕
          </button>
        </div>

        {/* Accordion Sections */}
        <div style={{
          padding: bp.isDesktop ? "12px 24px 24px" : "8px 16px 24px",
          overflowY: "auto", flex: 1,
          display: "flex", flexDirection: "column", gap: 10
        }}>
          {/* Section 1: Send Options */}
          <div ref={setCardRef(0)} style={{
            background: cfg.bg,
            borderRadius: 8, overflow: "hidden",
            boxShadow: sendExpanded ? `0 4px 20px ${cfg.color}40` : "0 2px 8px rgba(0,0,0,0.12)",
            transition: "box-shadow 0.3s ease"
          }}>
            <AccordionHeader label={cfg.label} icon={cfg.icon} expanded={sendExpanded} onToggle={() => setSendExpanded(v => !v)} accentColor="rgba(255,255,255,0.15)" />
            <div style={{
              maxHeight: sendExpanded ? 400 : 0,
              overflow: "hidden",
              transition: "max-height 0.35s ease",
              padding: sendExpanded ? `0 ${bp.isDesktop ? 16 : 14}px ${bp.isDesktop ? 16 : 12}px` : `0 ${bp.isDesktop ? 16 : 14}px 0`
            }}>
              {sendContent}
            </div>
          </div>

          {/* Section 2: Received Items */}
          <div ref={setCardRef(1)} style={{
            background: sectionBg,
            border: `1px solid ${subtleBorder}`,
            borderRadius: 8, overflow: "hidden",
            transition: "box-shadow 0.3s ease",
            boxShadow: receivedExpanded ? `0 4px 20px ${cfg.color}20` : "none"
          }}>
            <AccordionHeader label={cfg.recvLabel} icon={cfg.icon} expanded={receivedExpanded} onToggle={() => setReceivedExpanded(v => !v)} accentColor={`${cfg.color}20`} />
            <div style={{
              maxHeight: receivedExpanded ? 400 : 0,
              overflow: "hidden",
              transition: "max-height 0.35s ease",
              padding: receivedExpanded ? `0 ${bp.isDesktop ? 16 : 14}px ${bp.isDesktop ? 16 : 12}px` : `0 ${bp.isDesktop ? 16 : 14}px 0`
            }}>
              {receivedContent}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SCREEN 4: Match Detail ───────────────────────────────────
function MatchDetail({ match: m, onBack, onMessage, blindMode = false, onDateConfirmed, onNavigateToUpgrade }) {
  const { speak, speaking, stop } = useSpeechSynthesis();
  const [narrating, setNarrating] = useState(false);
  const [matchAction, setMatchAction] = useState(null); // null | 'accepted' | 'unmatched'
  const [dateOptions, setDateOptions] = useState(null);
  const [acceptedOptions, setAcceptedOptions] = useState([]);
  const [venueDeferred, setVenueDeferred] = useState(false);
  const [dateConfirmed, setDateConfirmed] = useState(null);
  const [showCustomSuggestion, setShowCustomSuggestion] = useState(false);
  const [customSuggestion, setCustomSuggestion] = useState({ venue: '', date: '', time: '' });
  const [isPlaying, setIsPlaying] = useState(false);
  const [givenReaction, setGivenReaction] = useState(() => _getGivenReactions()[m.id] || null);
  // Premium feature states
  const [userTier] = useState(() => getUserTier());
  const isPremium = userTier === "monthly" || userTier === "annual";
  const [sentGiftReaction, setSentGiftReaction] = useState(null);
  const [showGiftSelector, setShowGiftSelector] = useState(false);
  const [showPollCreator, setShowPollCreator] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [pollSent, setPollSent] = useState(false);
  const [pollResult, setPollResult] = useState(null);
  const [showPhoneShare, setShowPhoneShare] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneSent, setPhoneSent] = useState(false);
  const [messagingUnlocked, setMessagingUnlocked] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportSubmitted, setReportSubmitted] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  const [connectCollapsed, setConnectCollapsed] = useState(false);
  const [connectFlyoutOpen, setConnectFlyoutOpen] = useState(false);
  const [connectFlyoutRect, setConnectFlyoutRect] = useState(null);
  const [connectFlyoutAction, setConnectFlyoutAction] = useState(null);
  const [connectFlyoutExpandReceived, setConnectFlyoutExpandReceived] = useState(false);
  const openFlyout = (e, action, expandReceived = false) => { setConnectFlyoutRect(e.currentTarget.getBoundingClientRect()); setConnectFlyoutAction(action); setConnectFlyoutExpandReceived(expandReceived); setConnectFlyoutOpen(true); };
  // Received communications from matched user
  const [receivedGift, setReceivedGift] = useState(() => {
    const stored = localStorage.getItem(`received_gift_${m.id}`);
    return stored || null;
  });
  const [receivedPoll, setReceivedPoll] = useState(() => {
    const stored = localStorage.getItem(`received_poll_${m.id}`);
    return stored ? JSON.parse(stored) : null;
  });
  const [pollResponse, setPollResponse] = useState(null);
  const [receivedPhone, setReceivedPhone] = useState(() => {
    const stored = localStorage.getItem(`received_phone_${m.id}`);
    return stored || null;
  });
  const [receivedMessage, setReceivedMessage] = useState(() => {
    const stored = localStorage.getItem(`received_message_${m.id}`);
    return stored ? JSON.parse(stored) : null;
  });
  const [showReceivedPanel, setShowReceivedPanel] = useState(true);
  // Badge read-tracking: gifts
  const [giftsReceivedCount] = useState(() => receivedGift ? 1 : 0);
  const [giftsRead, setGiftsRead] = useState(false);
  // Badge read-tracking: polls
  const [pollsReceivedCount] = useState(() => receivedPoll ? 1 : 0);
  const pollsRead = !!pollResponse;
  // Badge read-tracking: phone shared (checkmark badge)
  const phoneSharedBadge = !!receivedPhone;
  // Badge read-tracking: messages
  const [unreadMsgCount] = useState(() => receivedMessage ? 1 : 0);
  const [messagesRead, setMessagesRead] = useState(false);
  const bp = useBreakpoint();
  const sim = m.simulation;
  const riskColor = sim.conflictRisk < 20 ? T.teal : sim.conflictRisk < 35 ? T.amber : T.rose;

  // Auto-collapse connect options on mobile when venue date options appear
  useEffect(() => {
    if (dateOptions && !bp.isDesktop) setConnectCollapsed(true);
  }, [dateOptions, bp.isDesktop]);

  const handleOrbClick = (e) => {
    e.stopPropagation();
    setIsPlaying(prev => !prev);
  };
  
  const handleReaction = (e, emoji) => {
    e.stopPropagation();
    _addVoicemailReaction(emoji);
    _saveGivenReaction(m.id, emoji);
    setGivenReaction(emoji);
    setIsPlaying(false);
    window.dispatchEvent(new Event('givenReactionsChanged'));
  };

  useEffect(() => {
    const sync = () => setGivenReaction(_getGivenReactions()[m.id] || null);
    window.addEventListener('givenReactionsChanged', sync);
    return () => window.removeEventListener('givenReactionsChanged', sync);
  }, [m.id]);

  const handlePremiumFeatureClick = (featureName) => {
    if (!isPremium) {
      setUpgradeFeature(featureName);
      setShowUpgradeModal(true);
    }
  };

  const handlePollResponse = (option) => {
    setPollResponse(option);
    localStorage.setItem(`poll_response_${m.id}`, option);
    savePollResponse(m.id, option);
  };

  const handleReportSubmit = () => {
    if (!reportReason.trim()) return;
    try {
      const report = {
        id: Date.now().toString(),
        reportedUserId: m.id,
        reportedUserName: m.name,
        reportedUserAge: m.age,
        reportedUserCity: m.city,
        reportedUserPhoto: m.photo,
        reason: reportReason,
        additionalInfo: '',
        reportedAt: new Date().toISOString(),
        reportedBy: 'current_user',
        status: 'pending'
      };
      saveUserReport(report); // Auto-syncs to Supabase
      setReportSubmitted(true);
      setTimeout(() => {
        setShowReportModal(false);
        setReportReason('');
        setReportSubmitted(false);
      }, 2000);
    } catch (e) {
      console.error('Failed to submit report:', e);
    }
  };

  // Venue pools for diverse date options
  const venuePools = {
    coffee: [
      { venue_name: "Blue Bottle Coffee", venue_address: "300 S Broadway, Los Angeles", venue_rating: 4.6 },
      { venue_name: "Intelligentsia Coffee", venue_address: "3922 W Sunset Blvd, Los Angeles", venue_rating: 4.5 },
      { venue_name: "Verve Coffee Roasters", venue_address: "8925 Melrose Ave, West Hollywood", venue_rating: 4.7 },
      { venue_name: "Alfred Coffee", venue_address: "8428 Melrose Pl, Los Angeles", venue_rating: 4.4 },
      { venue_name: "Café Gratitude", venue_address: "639 N Larchmont Blvd, Los Angeles", venue_rating: 4.5 },
      { venue_name: "Go Get Em Tiger", venue_address: "230 N Larchmont Blvd, Los Angeles", venue_rating: 4.6 },
      { venue_name: "Groundwork Coffee", venue_address: "671 Rose Ave, Venice", venue_rating: 4.5 },
      { venue_name: "The Conservatory", venue_address: "10117 Washington Blvd, Culver City", venue_rating: 4.6 }
    ],
    dinner: [
      { venue_name: "Bestia Restaurant", venue_address: "2121 E 7th Pl, Los Angeles", venue_rating: 4.7 },
      { venue_name: "Republique", venue_address: "624 S La Brea Ave, Los Angeles", venue_rating: 4.6 },
      { venue_name: "Guelaguetza", venue_address: "3014 W Olympic Blvd, Los Angeles", venue_rating: 4.5 },
      { venue_name: "Nobu Malibu", venue_address: "22706 Pacific Coast Hwy, Malibu", venue_rating: 4.7 },
      { venue_name: "Osteria Mozza", venue_address: "6602 Melrose Ave, Los Angeles", venue_rating: 4.6 },
      { venue_name: "Perch LA", venue_address: "448 S Hill St, Los Angeles", venue_rating: 4.5 },
      { venue_name: "Catch LA", venue_address: "8715 Melrose Ave, West Hollywood", venue_rating: 4.4 },
      { venue_name: "Mastro's Steakhouse", venue_address: "246 N Canon Dr, Beverly Hills", venue_rating: 4.6 },
      { venue_name: "Yamashiro", venue_address: "1999 N Sycamore Ave, Los Angeles", venue_rating: 4.5 },
      { venue_name: "71Above", venue_address: "633 W 5th St, Los Angeles", venue_rating: 4.4 }
    ],
    lunch: [
      { venue_name: "Republique", venue_address: "624 S La Brea Ave, Los Angeles", venue_rating: 4.5 },
      { venue_name: "Urth Caffé", venue_address: "267 S Beverly Dr, Beverly Hills", venue_rating: 4.4 },
      { venue_name: "The Ivy", venue_address: "113 N Robertson Blvd, Los Angeles", venue_rating: 4.3 },
      { venue_name: "Gjusta", venue_address: "320 Sunset Ave, Venice", venue_rating: 4.6 },
      { venue_name: "Sqirl", venue_address: "720 N Virgil Ave, Los Angeles", venue_rating: 4.5 },
      { venue_name: "Gracias Madre", venue_address: "8905 Melrose Ave, West Hollywood", venue_rating: 4.4 },
      { venue_name: "Joan's on Third", venue_address: "8350 W 3rd St, Los Angeles", venue_rating: 4.5 },
      { venue_name: "Blu Jam Café", venue_address: "7371 Melrose Ave, Los Angeles", venue_rating: 4.4 }
    ],
    activity: [
      { venue_name: "Griffith Park Hiking Trail", venue_address: "Griffith Park, Los Angeles", venue_rating: 4.8 },
      { venue_name: "The Getty Center", venue_address: "1200 Getty Center Dr, Los Angeles", venue_rating: 4.9 },
      { venue_name: "Santa Monica Pier", venue_address: "200 Santa Monica Pier, Santa Monica", venue_rating: 4.6 },
      { venue_name: "LACMA", venue_address: "5905 Wilshire Blvd, Los Angeles", venue_rating: 4.7 },
      { venue_name: "Venice Beach Boardwalk", venue_address: "1800 Ocean Front Walk, Venice", venue_rating: 4.5 },
      { venue_name: "Runyon Canyon Park", venue_address: "2000 N Fuller Ave, Los Angeles", venue_rating: 4.7 },
      { venue_name: "The Broad Museum", venue_address: "221 S Grand Ave, Los Angeles", venue_rating: 4.6 },
      { venue_name: "Descanso Gardens", venue_address: "1418 Descanso Dr, La Cañada Flintridge", venue_rating: 4.8 },
      { venue_name: "Huntington Library", venue_address: "1151 Oxford Rd, San Marino", venue_rating: 4.8 },
      { venue_name: "Malibu Wine Safari", venue_address: "32111 Mulholland Hwy, Malibu", venue_rating: 4.7 }
    ],
    drinks: [
      { venue_name: "The Varnish", venue_address: "118 E 6th St, Los Angeles", venue_rating: 4.6 },
      { venue_name: "Perch LA Rooftop", venue_address: "448 S Hill St, Los Angeles", venue_rating: 4.5 },
      { venue_name: "EP & LP", venue_address: "603 N La Cienega Blvd, West Hollywood", venue_rating: 4.4 },
      { venue_name: "The Rooftop by JG", venue_address: "9360 Wilshire Blvd, Beverly Hills", venue_rating: 4.5 },
      { venue_name: "Broken Shaker", venue_address: "8475 Santa Monica Blvd, West Hollywood", venue_rating: 4.6 },
      { venue_name: "The Bungalow", venue_address: "101 Wilshire Blvd, Santa Monica", venue_rating: 4.4 },
      { venue_name: "High Rooftop Lounge", venue_address: "465 S La Cienega Blvd, Los Angeles", venue_rating: 4.5 },
      { venue_name: "Employees Only", venue_address: "7953 Santa Monica Blvd, West Hollywood", venue_rating: 4.6 }
    ]
  };

  // Helper to randomly select venue from pool
  const getRandomVenue = (venueType) => {
    const pool = venuePools[venueType] || [];
    return pool[Math.floor(Math.random() * pool.length)];
  };

  // Helper to round time to nearest hour or half-hour
  const roundToHalfHour = (date) => {
    const d = new Date(date);
    const minutes = d.getMinutes();
    if (minutes < 15) {
      d.setMinutes(0);
    } else if (minutes < 45) {
      d.setMinutes(30);
    } else {
      d.setHours(d.getHours() + 1);
      d.setMinutes(0);
    }
    d.setSeconds(0);
    d.setMilliseconds(0);
    return d;
  };

  // Helper to generate time within business hours
  const getBusinessHourTime = (baseDate, venueType) => {
    const d = new Date(baseDate);
    let hour;
    
    // Set appropriate business hours based on venue type
    if (venueType === 'coffee') {
      hour = 9 + Math.floor(Math.random() * 6); // 9am-3pm
    } else if (venueType === 'dinner') {
      hour = 18 + Math.floor(Math.random() * 4); // 6pm-10pm
    } else if (venueType === 'lunch') {
      hour = 11 + Math.floor(Math.random() * 4); // 11am-3pm
    } else if (venueType === 'activity') {
      hour = 10 + Math.floor(Math.random() * 8); // 10am-6pm
    } else if (venueType === 'drinks') {
      hour = 17 + Math.floor(Math.random() * 5); // 5pm-10pm
    } else {
      hour = 12 + Math.floor(Math.random() * 8); // 12pm-8pm default
    }
    
    d.setHours(hour);
    d.setMinutes(Math.random() < 0.5 ? 0 : 30); // Either :00 or :30
    d.setSeconds(0);
    d.setMilliseconds(0);
    return d;
  };

  const handleAccept = () => {
    setMatchAction("accepted");
    // In production: call API POST /matches/:id/accept
    // Generate 6 preference-based date options with proper business hours
    setTimeout(() => {
      const now = Date.now();
      const coffeeVenue = getRandomVenue('coffee');
      const dinnerVenue = getRandomVenue('dinner');
      const activityVenue1 = getRandomVenue('activity');
      const lunchVenue = getRandomVenue('lunch');
      const drinksVenue = getRandomVenue('drinks');
      const activityVenue2 = getRandomVenue('activity');
      
      setDateOptions([
        { option_index: 0, date_time: getBusinessHourTime(new Date(now + 3 * 86400000), 'coffee').toISOString(), venue_name: coffeeVenue.venue_name, venue_address: coffeeVenue.venue_address, venue_type: "coffee", venue_rating: coffeeVenue.venue_rating, venue_maps_url: "https://maps.google.com" },
        { option_index: 1, date_time: getBusinessHourTime(new Date(now + 5 * 86400000), 'dinner').toISOString(), venue_name: dinnerVenue.venue_name, venue_address: dinnerVenue.venue_address, venue_type: "dinner", venue_rating: dinnerVenue.venue_rating, venue_maps_url: "https://maps.google.com" },
        { option_index: 2, date_time: getBusinessHourTime(new Date(now + 7 * 86400000), 'activity').toISOString(), venue_name: activityVenue1.venue_name, venue_address: activityVenue1.venue_address, venue_type: "activity", venue_rating: activityVenue1.venue_rating, venue_maps_url: "https://maps.google.com" },
        { option_index: 3, date_time: getBusinessHourTime(new Date(now + 4 * 86400000), 'lunch').toISOString(), venue_name: lunchVenue.venue_name, venue_address: lunchVenue.venue_address, venue_type: "lunch", venue_rating: lunchVenue.venue_rating, venue_maps_url: "https://maps.google.com" },
        { option_index: 4, date_time: getBusinessHourTime(new Date(now + 6 * 86400000), 'drinks').toISOString(), venue_name: drinksVenue.venue_name, venue_address: drinksVenue.venue_address, venue_type: "drinks", venue_rating: drinksVenue.venue_rating, venue_maps_url: "https://maps.google.com" },
        { option_index: 5, date_time: getBusinessHourTime(new Date(now + 8 * 86400000), 'activity').toISOString(), venue_name: activityVenue2.venue_name, venue_address: activityVenue2.venue_address, venue_type: "activity", venue_rating: activityVenue2.venue_rating, venue_maps_url: "https://maps.google.com" },
      ]);
    }, 1200);
  };

  const handleUnmatch = () => {
    // In production: call API POST /matches/:id/unmatch
    setMatchAction("unmatched");
    setTimeout(() => onBack(), 1800);
  };

  const handleConfirmDate = () => {
    if (!acceptedOptions.length || !dateOptions) return;
    const chosen = dateOptions[acceptedOptions[Math.floor(Math.random() * acceptedOptions.length)]];
    const dateWithMatch = {
      ...chosen,
      matchId: m.id,
      matchName: m.name,
      matchAge: m.age,
      matchCity: m.city,
      matchGender: m.gender,
      matchHeight: m.height,
      matchPhoto: m.photo,
      matchCompatibility: m.compatibility,
      status: "pending",
      user_a_name: m.name,
    };
    setDateConfirmed(dateWithMatch);
    if (onDateConfirmed) onDateConfirmed(dateWithMatch);
  };

  const narrateSimulation = () => {
    if (narrating) { stop(); setNarrating(false); return; }
    setNarrating(true);
    const text = `Your compatibility with ${m.name} is ${m.compatibility}%. ${sim.summary} Predicted first date chemistry: ${sim.firstDate}%. Long term probability: ${sim.longTerm}%. ${sim.conflict}`;
    speak(text, () => setNarrating(false));
  };

  // ─ Confirmed date splash screen
  if (dateConfirmed) {
    const d = new Date(dateConfirmed.date_time);
    const dateStr = d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
    const timeStr = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    const confettiColors = [T.rose, T.teal, "#f59e0b", "#8b5cf6", "#22c55e", "#ec4899"];
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "32px 24px", textAlign: "center", animation: "fadeIn 0.6s ease", position: "relative", overflow: "hidden" }}>
        {/* Confetti burst */}
        {[...Array(24)].map((_, i) => (
          <div key={i} style={{
            position: "absolute",
            top: `${Math.random() * 30}%`,
            left: `${Math.random() * 100}%`,
            width: Math.random() * 8 + 4,
            height: Math.random() * 8 + 4,
            borderRadius: Math.random() > 0.5 ? "50%" : "2px",
            background: confettiColors[i % confettiColors.length],
            animation: `confettiFall ${0.8 + Math.random() * 1.2}s ease ${Math.random() * 0.6}s both`,
            transform: `rotate(${Math.random() * 360}deg)`,
            opacity: 0.9,
            pointerEvents: "none"
          }} />
        ))}
        {/* Typing indicator before emoji */}
        <div style={{ display: "flex", gap: 4, marginBottom: 16, height: 24, alignItems: "center" }}>
          {[0,1,2].map(i => (
            <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: T.rose, animation: `typingDot 1.2s ease ${i * 0.2}s infinite` }} />
          ))}
        </div>
        <div style={{ fontSize: 72, marginBottom: 20 }}>💋</div>
        <h1 style={{ fontSize: 32, fontWeight: 900, fontFamily: "'Segoe UI', sans-serif", background: `linear-gradient(135deg, ${T.text}, ${T.rose})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", marginBottom: 8, lineHeight: 1.2 }}>Date scheduled!</h1>
        <p style={{ fontSize: 16, color: T.muted, marginBottom: 32, lineHeight: 1.7 }}>Waiting for {m.name} to confirm — you'll be notified once they do.</p>
        <div style={{ background: T.card, border: `1px solid ${T.rose}40`, borderRadius: 8, padding: "24px 28px", width: "100%", maxWidth: 360, marginBottom: 24 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.text, marginBottom: 6 }}>{dateConfirmed.venue_name}</div>
          <div style={{ fontSize: 14, color: T.muted, marginBottom: 16 }}>{dateConfirmed.venue_address}</div>
          <div style={{ display: "flex", justifyContent: "space-around" }}>
            <div><div style={{ fontSize: 13, color: T.rose, fontWeight: 700, marginBottom: 4 }}>DATE</div><div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{dateStr}</div></div>
            <div><div style={{ fontSize: 13, color: T.rose, fontWeight: 700, marginBottom: 4 }}>TIME</div><div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{timeStr}</div></div>
          </div>
        </div>
        {dateConfirmed.venue_maps_url && (
          <a href={dateConfirmed.venue_maps_url} target="_blank" rel="noreferrer" style={{ display: "block", padding: "12px 24px", borderRadius: 50, background: T.roseSoft, border: `1px solid ${T.rose}40`, color: T.rose, fontSize: 14, fontWeight: 700, textDecoration: "none", marginBottom: 16 }}>🗺️ Open in Maps</a>
        )}
        <p style={{ fontSize: 13, color: T.muted }}>🔔 You’ll get a reminder 24 hours and 3 hours before your date.</p>
        <button onClick={onBack} style={{ marginTop: 24, padding: "10px 24px", borderRadius: 50, background: "transparent", border: `1px solid ${T.border}`, color: T.muted, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Back to Matches</button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f5f7fa" }}>
      <div style={{ maxWidth: bp.isDesktop ? 1400 : bp.isTablet ? 680 : 480, margin: "0 auto", padding: bp.isDesktop ? "24px 32px 48px" : bp.isTablet ? "28px 32px 60px" : "24px 20px 100px" }}>
        
        {/* Header Actions */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <button onClick={onBack} style={{ padding: "8px 16px", borderRadius: 8, background: "transparent", border: `1px solid ${T.border}`, color: T.muted, fontSize: 15, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6 }}>
            ← Back to Matches
          </button>
          <button onClick={() => setShowReportModal(true)} style={{ padding: "8px 16px", borderRadius: 8, background: "transparent", border: `1px solid ${T.border}`, color: "#ef4444", fontSize: 14, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            Report User
          </button>
        </div>

        {/* Profile Header Card */}
        <div style={{ background: "#fff", borderRadius: 8, padding: bp.isDesktop ? 40 : 24, marginBottom: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
          <div style={{ display: bp.isDesktop ? "flex" : "block", gap: 32, alignItems: "stretch" }}>
            
            {/* Left Column: Profile Photo/Orb + User Details + Action Buttons */}
            <div style={{ flex: bp.isDesktop ? 1 : undefined, display: "flex", flexDirection: "column" }}>
              {/* Profile pic/orb and user details side by side */}
              <div style={{ display: "flex", gap: bp.isDesktop ? 24 : 16, alignItems: "flex-start", marginBottom: bp.isDesktop ? 24 : 10 }}>
                {/* Profile Photo / Audio Orb */}
                <div style={{ flexShrink: 0 }}>
                {blindMode ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
                    <div onClick={handleOrbClick} style={{ position: "relative", width: bp.isDesktop ? 160 : 120, height: bp.isDesktop ? 160 : 120, cursor: "pointer" }}>
                      <div style={{ position: "absolute", inset: -5, borderRadius: "50%", border: `2px solid ${T.rose}`, animation: "orbRing 1.8s ease-out infinite", opacity: 0.7 }} />
                      <div style={{ width: "100%", height: "100%", borderRadius: "50%", background: `linear-gradient(135deg, ${T.rose}, ${T.violet})`, display: "flex", alignItems: "center", justifyContent: "center", animation: "orbPulse 1.5s ease-in-out infinite alternate", border: `3px solid ${T.rose}` }}>
                        <span style={{ fontSize: bp.isDesktop ? 52 : 38, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginLeft: isPlaying ? 0 : 3 }}>{isPlaying ? "⏸" : <PlayTriangle size={bp.isDesktop ? 60 : 44} />}</span>
                      </div>
                    </div>
                    {/* Playback progress bar */}
                    {isPlaying && (
                      <div style={{ width: bp.isDesktop ? 160 : 120, height: 4, borderRadius: 3, background: `${T.rose}30`, overflow: "hidden" }}>
                        <div style={{ height: "100%", background: `linear-gradient(90deg, ${T.rose}, ${T.violet})`, borderRadius: 3, animation: "voicemailProgress 60s linear forwards" }} />
                      </div>
                    )}
                    {/* Emoji reaction bar */}
                    {isPlaying && (
                      <div onClick={e => e.stopPropagation()} style={{ display: "flex", gap: 6, animation: "reactionSlideUp 0.3s ease both" }}>
                        {REACTION_EMOJIS.map((emoji, ei) => (
                          <button key={emoji} onClick={e => { triggerRipple(e); handleReaction(e, emoji); }}
                            style={{ width: 36, height: 36, borderRadius: "50%", border: "none", background: T.card, cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s", animation: `emojiPop 0.4s ease ${ei * 0.05}s both` }}
                            onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.2)"; e.currentTarget.style.background = T.bg; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.background = T.card; }}>
                            {emoji}
                          </button>
                        ))}
                      </div>
                    )}
                    {givenReaction && (
                      <div style={{ fontSize: 28, animation: "emojiPop 0.5s ease both" }}>{givenReaction}</div>
                    )}
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
                    <div style={{ position: "relative", width: bp.isDesktop ? 160 : 120, height: bp.isDesktop ? 160 : 120, flexShrink: 0 }}>
                      <div style={{ width: "100%", height: "100%", borderRadius: "50%", overflow: "hidden", border: `3px solid ${T.rose}` }}>
                        <img src={m.photo} alt={m.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      </div>
                      <button onClick={handleOrbClick} style={{ 
                        position: "absolute", 
                        bottom: -4, 
                        right: -4, 
                        width: bp.isDesktop ? 48 : 38, 
                        height: bp.isDesktop ? 48 : 38, 
                        borderRadius: "50%", 
                        background: isPlaying ? T.rose : `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, 
                        border: "2px solid #fff", 
                        display: "flex", 
                        alignItems: "center", 
                        justifyContent: "center", 
                        cursor: "pointer",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        transition: "all 0.2s",
                        padding: 0
                      }}
                      onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.1)"; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}>
                        <span style={{ fontSize: bp.isDesktop ? 18 : 16, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginLeft: isPlaying ? 0 : 1 }}>{isPlaying ? "⏸" : <PlayTriangle size={bp.isDesktop ? 20 : 16} />}</span>
                      </button>
                    </div>
                    {isPlaying && (
                      <div style={{ width: bp.isDesktop ? 160 : 120, height: 4, borderRadius: 3, background: `${T.rose}30`, overflow: "hidden" }}>
                        <div style={{ height: "100%", background: `linear-gradient(90deg, ${T.rose}, ${T.violet})`, borderRadius: 3, animation: "voicemailProgress 60s linear forwards" }} />
                      </div>
                    )}
                    {isPlaying && (
                      <div onClick={e => e.stopPropagation()} style={{ display: "flex", gap: 6, animation: "reactionSlideUp 0.3s ease both" }}>
                        {REACTION_EMOJIS.map((emoji, ei) => (
                          <button key={emoji} onClick={e => { triggerRipple(e); handleReaction(e, emoji); }}
                            style={{ width: 36, height: 36, borderRadius: "50%", border: "none", background: T.card, cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s", animation: `emojiPop 0.4s ease ${ei * 0.05}s both` }}
                            onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.2)"; e.currentTarget.style.background = T.bg; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.background = T.card; }}>
                            {emoji}
                          </button>
                        ))}
                      </div>
                    )}
                    {givenReaction && !isPlaying && (
                      <div style={{ fontSize: 28, animation: "emojiPop 0.5s ease both" }}>{givenReaction}</div>
                    )}
                  </div>
                )}
                </div>

                {/* User Details */}
                <div style={{ flex: 1 }}>
                  <h1 style={{ fontSize: bp.isDesktop ? 28 : 24, fontWeight: 700, color: "#1a1a1a", marginBottom: 4, fontFamily: "inherit" }}>{m.name}, {m.age}</h1>
                  <div style={{ marginBottom: bp.isDesktop ? 16 : 4 }}>
                    <div style={{ fontSize: bp.isDesktop ? 14 : 15, color: T.muted, fontWeight: 500, marginBottom: 4 }}>
                      {m.gender} · {m.height}
                    </div>
                    <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, marginBottom: bp.isDesktop ? 8 : 0 }}>
                      <LocationIcon size={13} color={T.muted} /> {m.city}
                    </div>
                    {bp.isDesktop && (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                        {m.traits.slice(0, 3).map(t => <Pill key={t}>{t}</Pill>)}
                      </div>
                    )}
                  </div>

                  {/* Desktop: Compact Match Score inline */}
                  {bp.isDesktop && (
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <div style={{ padding: "8px 14px", background: `${T.rose}08`, border: `1px solid ${T.rose}20`, borderRadius: 6, textAlign: "center" }}>
                      <div style={{ fontSize: 22, fontWeight: 900, color: T.rose, fontFamily: "'Segoe UI', sans-serif", lineHeight: 1 }}>{m.compatibility}%</div>
                      <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, marginTop: 4 }}>MATCH</div>
                    </div>
                    <div style={{ padding: "8px 14px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, textAlign: "center" }}>
                      <div style={{ fontSize: 22, fontWeight: 900, color: T.rose, fontFamily: "'Segoe UI', sans-serif", lineHeight: 1 }}>{m.personalityScore}%</div>
                      <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, marginTop: 4 }}>PERSONALITY</div>
                    </div>
                    <div style={{ padding: "8px 14px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, textAlign: "center" }}>
                      <div style={{ fontSize: 22, fontWeight: 900, color: T.teal, fontFamily: "'Segoe UI', sans-serif", lineHeight: 1 }}>{m.valuesScore}%</div>
                      <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, marginTop: 4 }}>VALUES</div>
                    </div>
                  </div>
                  )}
                </div>
              </div>

              {/* Mobile: Traits row + divider + scores row (full width, condensed) */}
              {!bp.isDesktop && (<>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                  {m.traits.slice(0, 3).map(t => <Pill key={t}>{t}</Pill>)}
                </div>
                <div style={{ height: 1, background: `linear-gradient(90deg, ${T.rose}40, ${T.teal}40)`, marginBottom: 10 }} />
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ flex: 1, padding: "8px 0", background: `${T.rose}08`, border: `1px solid ${T.rose}20`, borderRadius: 6, textAlign: "center" }}>
                    <div style={{ fontSize: 18, fontWeight: 900, color: T.rose, fontFamily: "'Segoe UI', sans-serif", lineHeight: 1 }}>{m.valuesScore}%</div>
                    <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, marginTop: 4 }}>VALUES</div>
                  </div>
                  <div style={{ flex: 1, padding: "8px 0", background: `${T.rose}08`, border: `1px solid ${T.rose}20`, borderRadius: 6, textAlign: "center" }}>
                    <div style={{ fontSize: 18, fontWeight: 900, color: T.rose, fontFamily: "'Segoe UI', sans-serif", lineHeight: 1 }}>{m.compatibility}%</div>
                    <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, marginTop: 4 }}>MATCH</div>
                  </div>
                  <div style={{ flex: 1, padding: "8px 0", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, textAlign: "center" }}>
                    <div style={{ fontSize: 18, fontWeight: 900, color: T.rose, fontFamily: "'Segoe UI', sans-serif", lineHeight: 1 }}>{m.personalityScore}%</div>
                    <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, marginTop: 4 }}>PERSONALITY</div>
                  </div>
                </div>
              </>)}

              {/* Match Action Buttons - Span full left column */}
              {matchAction === null && (<>
                <div style={{ height: 1, background: `linear-gradient(90deg, ${T.rose}40, ${T.teal}40)`, marginTop: bp.isDesktop ? 16 : 12 }} />
                <div style={{ display: "flex", gap: bp.isDesktop ? 12 : 10, animation: "fadeIn 0.4s ease", paddingTop: bp.isDesktop ? 16 : 12, marginTop: "auto" }}>
                  <button onClick={handleUnmatch} 
                    style={{ 
                      flex: 1, 
                      padding: bp.isDesktop ? "14px 18px" : "12px 14px", 
                      borderRadius: 8, 
                      border: `2px solid ${T.border}`, 
                      background: T.card, 
                      color: T.muted, 
                      fontSize: bp.isDesktop ? 14 : 15, 
                      fontWeight: 600, 
                      cursor: "pointer", 
                      fontFamily: "inherit",
                      transition: "all 0.3s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = T.rose; e.currentTarget.style.color = T.rose; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 4px 16px ${T.rose}15`; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.muted; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}>
                    <span style={{ fontSize: bp.isDesktop ? 16 : 15 }}>✕</span> Unmatch
                  </button>
                  <button onClick={handleAccept} 
                    style={{ 
                      flex: 2, 
                      padding: bp.isDesktop ? "14px 20px" : "12px 16px", 
                      borderRadius: 8, 
                      background: `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, 
                      color: "#fff", 
                      fontSize: bp.isDesktop ? 15 : 15, 
                      fontWeight: 700, 
                      border: "none", 
                      cursor: "pointer", 
                      fontFamily: "inherit", 
                      boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
                      transition: "all 0.3s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      position: "relative",
                      overflow: "hidden",
                      animation: "acceptButtonBurst 2s ease-in-out infinite"
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px) scale(1.03)"; e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,0,0,0.25)"; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0) scale(1)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.2)"; }}>
                    {/* Shimmer effect */}
                    <div style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)", animation: "shimmer 1.5s infinite" }} />
                    {/* Glow pulse */}
                    <div style={{ position: "absolute", inset: -2, borderRadius: 8, background: `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, opacity: 0.5, filter: "blur(8px)", animation: "glowPulse 2s ease-in-out infinite" }} />
                    <span style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: bp.isDesktop ? 18 : 17, animation: "heartBeat 1.5s ease-in-out infinite" }}>💝</span> Accept Match <span style={{ fontSize: bp.isDesktop ? 16 : 15, animation: "sparkle 1.5s ease-in-out infinite" }}>✦</span>
                    </span>
                  </button>
                </div>
              </>)}
              {matchAction === "accepted" && !dateOptions && !venueDeferred && (
                <div style={{ textAlign: "center", padding: bp.isDesktop ? "16px" : "12px", color: T.muted, fontSize: bp.isDesktop ? 13 : 14, animation: "pulse 1.5s infinite", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: "auto" }}>
                  <VoiceOrb state="thinking" size={20} /> Finding date spots...
                </div>
              )}
              {matchAction === "accepted" && !dateOptions && venueDeferred && (
                <div style={{ display: "flex", gap: 10, paddingTop: bp.isDesktop ? 16 : 12, animation: "fadeIn 0.4s ease", marginTop: "auto" }}>
                  <button onClick={() => { setVenueDeferred(false); handleAccept(); }}
                    style={{ flex: 1, padding: bp.isDesktop ? "14px 20px" : "12px 16px", borderRadius: 8, background: `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, color: "#fff", fontSize: bp.isDesktop ? 15 : 15, fontWeight: 700, border: "none", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                    📅 Choose Venue Now
                  </button>
                </div>
              )}
              {matchAction === "unmatched" && (
                <div style={{ textAlign: "center", padding: bp.isDesktop ? "16px" : "12px", color: T.muted, fontSize: bp.isDesktop ? 13 : 14, animation: "fadeIn 0.3s ease", marginTop: "auto" }}>
                  Match removed. Returning...
                </div>
              )}
            </div>

            {/* Right Column: Connect Options */}
            {bp.isDesktop && (
              <div style={{ flex: 1, minWidth: 0, borderLeft: `1px solid ${T.border}`, paddingLeft: 32 }}>
                <div style={{ fontSize: 13, color: T.rose, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 16 }}>CONNECT WITH {m.name.toUpperCase()}</div>

                {/* Modern Icon Grid - Icons Only with Inline Interactions */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, marginBottom: 20, position: "relative" }}>
                  {/* Send Gift */}
                  {isFeatureEnabled("emoji_reactions") && (
                    <div>
                      {!sentGiftReaction ? (
                        <button onClick={(e) => openFlyout(e, 'gift')} style={{ padding: "16px 8px", border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 12, transition: "all 0.3s ease", position: "relative", animation: "iconOnload 0.6s ease-out", width: "100%" }}
                          onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-6px) scale(1.05)"; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0) scale(1)"; }}>
                          <CommBadge count={giftsReceivedCount} isRead={giftsRead} color={T.rose} onClick={(e) => { const btn = e.target.closest('button'); setConnectFlyoutRect(btn.getBoundingClientRect()); setConnectFlyoutAction('gift'); setConnectFlyoutExpandReceived(true); setConnectFlyoutOpen(true); }} />
                          <GiftBoxIcon size={64} color={T.rose} />
                          <div style={{ fontSize: 12, fontWeight: 600, color: T.text, textAlign: "center", textTransform: "uppercase", letterSpacing: "0.5px" }}>Send Gift</div>
                        </button>
                      ) : (
                        <div style={{ padding: "16px 8px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                          <div style={{ fontSize: 48, animation: "emojiPop 0.5s ease both" }}>{sentGiftReaction}</div>
                          <div style={{ fontSize: 11, color: T.muted, fontWeight: 600 }}>Sent!</div>
                          <button onClick={() => setSentGiftReaction(null)} style={{ padding: "4px 10px", borderRadius: 6, border: "none", background: T.rose, color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Send Another</button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Send Poll */}
                  {isFeatureEnabled("polls") && (
                    <div>
                      {!pollSent ? (
                        <button onClick={(e) => openFlyout(e, 'poll')} style={{ padding: "16px 8px", border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 12, transition: "all 0.3s ease", position: "relative", animation: "iconOnload 0.6s ease-out 0.1s backwards", width: "100%" }}
                          onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-6px) scale(1.05)"; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0) scale(1)"; }}>
                          <CommBadge count={pollsReceivedCount} isRead={pollsRead} color={T.teal} onClick={(e) => { const btn = e.target.closest('button'); setConnectFlyoutRect(btn.getBoundingClientRect()); setConnectFlyoutAction('poll'); setConnectFlyoutExpandReceived(true); setConnectFlyoutOpen(true); }} />
                          <PollClipboardIcon size={64} color={T.teal} />
                          <div style={{ fontSize: 12, fontWeight: 600, color: T.text, textAlign: "center", textTransform: "uppercase", letterSpacing: "0.5px" }}>Send Poll</div>
                        </button>
                      ) : (
                        <div style={{ padding: "16px 8px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                          <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, textAlign: "center" }}>Poll sent!</div>
                          {pollResult && <div style={{ fontSize: 12, fontWeight: 700, color: T.teal, textAlign: "center" }}>"{pollResult}"</div>}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Share Phone */}
                  {isFeatureEnabled("send_phone") && (
                    <div>
                      {!phoneSent ? (
                        <button onClick={(e) => isPremium ? openFlyout(e, 'phone') : handlePremiumFeatureClick('Share Phone Number')} style={{ padding: "16px 8px", border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 12, transition: "all 0.3s ease", position: "relative", animation: "iconOnload 0.6s ease-out 0.2s backwards", width: "100%" }}
                          onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-6px) scale(1.05)"; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0) scale(1)"; }}>
                          <CommBadge isCheckmark={phoneSharedBadge} isRead={true} color={T.violet} onClick={(e) => { if (!isPremium) { e.stopPropagation(); handlePremiumFeatureClick('Share Phone Number'); return; } const btn = e.target.closest('button'); setConnectFlyoutRect(btn.getBoundingClientRect()); setConnectFlyoutAction('phone'); setConnectFlyoutExpandReceived(true); setConnectFlyoutOpen(true); }} />
                          <PhoneShareIcon size={64} color={T.violet} />
                          <div style={{ fontSize: 12, fontWeight: 600, color: T.text, textAlign: "center", textTransform: "uppercase", letterSpacing: "0.5px" }}>Share Phone</div>
                        </button>
                      ) : (
                        <div style={{ padding: "16px 8px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                          <svg width="48" height="48" viewBox="0 0 24 24" fill={T.violet}><path d="M20 6L9 17l-5-5"/></svg>
                          <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, textAlign: "center" }}>Phone sent!</div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Send Message */}
                  {isFeatureEnabled("send_message") && (
                    <button onClick={() => (isPremium || messagingUnlocked) ? (onMessage && onMessage(m)) : handlePremiumFeatureClick('Chat & Images')} style={{ padding: "16px 8px", border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 12, transition: "all 0.3s ease", position: "relative", animation: "iconOnload 0.6s ease-out 0.3s backwards" }}
                      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-6px) scale(1.05)"; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0) scale(1)"; }}>
                      <CommBadge count={unreadMsgCount} isRead={messagesRead} color={T.teal} />
                      <ChatBubbleIcon size={64} color={T.teal} />
                      <div style={{ fontSize: 12, fontWeight: 600, color: T.text, textAlign: "center", textTransform: "uppercase", letterSpacing: "0.5px" }}>Message</div>
                    </button>
                  )}

                  {/* Call Match */}
                  {isFeatureEnabled("in_app_call") && (
                    <button onClick={() => !isPremium && handlePremiumFeatureClick('In-App Call')} style={{ padding: "16px 8px", border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 12, transition: "all 0.3s ease", position: "relative", animation: "iconOnload 0.6s ease-out 0.4s backwards" }}
                      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-6px) scale(1.05)"; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0) scale(1)"; }}>
                      <PhoneCallIcon size={64} color={T.amber} />
                      <div style={{ fontSize: 12, fontWeight: 600, color: T.text, textAlign: "center", textTransform: "uppercase", letterSpacing: "0.5px" }}>Call</div>
                    </button>
                  )}
                </div>

                {!isPremium && (
                  <div style={{ marginTop: 14, padding: "12px 16px", borderRadius: 8, background: `linear-gradient(135deg, ${T.rose}10, ${T.violet}10)`, border: `1px solid ${T.rose}25`, display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: `linear-gradient(135deg, ${T.rose}, ${T.violet})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, animation: "upgradePulse 2s ease-in-out infinite" }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Unlock Premium</div>
                      <div style={{ fontSize: 13, color: T.muted }}>Chat, call, and share phone</div>
                    </div>
                    <button onClick={() => onNavigateToUpgrade && onNavigateToUpgrade()}
                      style={{ padding: "8px 16px", borderRadius: 8, background: `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, border: "none", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", flexShrink: 0, animation: "upgradePulse 2s ease-in-out infinite" }}>
                      Upgrade
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile: Connect Options below profile */}
          {!bp.isDesktop && (
            <div style={{ marginTop: 20, borderTop: `1px solid ${T.border}`, paddingTop: 20 }}>
              <div 
                onClick={() => dateOptions && setConnectCollapsed(prev => !prev)}
                style={{ fontSize: 12, color: T.rose, fontWeight: 700, letterSpacing: "0.1em", marginBottom: connectCollapsed ? 0 : 16, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: dateOptions ? "pointer" : "default", transition: "margin-bottom 0.3s ease" }}>
                <span>CONNECT WITH {m.name.toUpperCase()}</span>
                {dateOptions && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.rose} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: "transform 0.3s ease", transform: connectCollapsed ? "rotate(0deg)" : "rotate(180deg)", flexShrink: 0 }}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                )}
              </div>

              <div style={{ maxHeight: connectCollapsed ? 0 : 600, overflow: "hidden", transition: "max-height 0.4s ease, opacity 0.3s ease", opacity: connectCollapsed ? 0 : 1 }}>
              {/* Modern Icon Grid - Mobile Version */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 20, paddingTop: connectCollapsed ? 0 : 0, position: "relative" }}>
                {/* Send Gift */}
                {isFeatureEnabled("emoji_reactions") && (
                  <div>
                    {!sentGiftReaction ? (
                      <button onClick={(e) => openFlyout(e, 'gift')} style={{ padding: "14px 6px", border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, transition: "all 0.3s ease", position: "relative", animation: "iconOnload 0.6s ease-out", width: "100%" }}>
                        <CommBadge count={giftsReceivedCount} isRead={giftsRead} color={T.rose} size="small" onClick={(e) => { const btn = e.target.closest('button'); setConnectFlyoutRect(btn.getBoundingClientRect()); setConnectFlyoutAction('gift'); setConnectFlyoutExpandReceived(true); setConnectFlyoutOpen(true); }} />
                        <GiftBoxIcon size={56} color={T.rose} />
                        <div style={{ fontSize: 11, fontWeight: 600, color: T.text, textAlign: "center", textTransform: "uppercase", letterSpacing: "0.5px" }}>Send Gift</div>
                      </button>
                    ) : (
                      <div style={{ padding: "14px 6px", display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                        <div style={{ fontSize: 42, animation: "emojiPop 0.5s ease both" }}>{sentGiftReaction}</div>
                        <div style={{ fontSize: 10, color: T.muted, fontWeight: 600 }}>Sent!</div>
                        <button onClick={() => setSentGiftReaction(null)} style={{ padding: "3px 8px", borderRadius: 5, border: "none", background: T.rose, color: "#fff", fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Send Another</button>
                      </div>
                    )}
                  </div>
                )}

                {/* Send Poll */}
                {isFeatureEnabled("polls") && (
                  <div>
                    {!pollSent ? (
                      <button onClick={(e) => openFlyout(e, 'poll')} style={{ padding: "14px 6px", border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, transition: "all 0.3s ease", position: "relative", animation: "iconOnload 0.6s ease-out 0.1s backwards", width: "100%" }}>
                        <CommBadge count={pollsReceivedCount} isRead={pollsRead} color={T.teal} size="small" onClick={(e) => { const btn = e.target.closest('button'); setConnectFlyoutRect(btn.getBoundingClientRect()); setConnectFlyoutAction('poll'); setConnectFlyoutExpandReceived(true); setConnectFlyoutOpen(true); }} />
                        <PollClipboardIcon size={56} color={T.teal} />
                        <div style={{ fontSize: 11, fontWeight: 600, color: T.text, textAlign: "center", textTransform: "uppercase", letterSpacing: "0.5px" }}>Send Poll</div>
                      </button>
                    ) : (
                      <div style={{ padding: "14px 6px", display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                        <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                        <div style={{ fontSize: 10, color: T.muted, fontWeight: 600, textAlign: "center" }}>Poll sent!</div>
                        {pollResult && <div style={{ fontSize: 11, fontWeight: 700, color: T.teal, textAlign: "center" }}>"{pollResult}"</div>}
                      </div>
                    )}
                  </div>
                )}

                {/* Share Phone */}
                {isFeatureEnabled("send_phone") && (
                  <div>
                    {!phoneSent ? (
                      <button onClick={(e) => isPremium ? openFlyout(e, 'phone') : handlePremiumFeatureClick('Share Phone Number')} style={{ padding: "14px 6px", border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, transition: "all 0.3s ease", position: "relative", animation: "iconOnload 0.6s ease-out 0.2s backwards", width: "100%" }}>
                        <CommBadge isCheckmark={phoneSharedBadge} isRead={true} color={T.violet} size="small" onClick={(e) => { if (!isPremium) { e.stopPropagation(); handlePremiumFeatureClick('Share Phone Number'); return; } const btn = e.target.closest('button'); setConnectFlyoutRect(btn.getBoundingClientRect()); setConnectFlyoutAction('phone'); setConnectFlyoutExpandReceived(true); setConnectFlyoutOpen(true); }} />
                        <PhoneShareIcon size={56} color={T.violet} />
                        <div style={{ fontSize: 11, fontWeight: 600, color: T.text, textAlign: "center", textTransform: "uppercase", letterSpacing: "0.5px" }}>Share Phone</div>
                      </button>
                    ) : (
                      <div style={{ padding: "14px 6px", display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                        <svg width="42" height="42" viewBox="0 0 24 24" fill={T.violet}><path d="M20 6L9 17l-5-5"/></svg>
                        <div style={{ fontSize: 10, color: T.muted, fontWeight: 600, textAlign: "center" }}>Phone sent!</div>
                      </div>
                    )}
                  </div>
                )}

                {/* Send Message */}
                {isFeatureEnabled("send_message") && (
                  <button onClick={() => (isPremium || messagingUnlocked) ? (onMessage && onMessage(m)) : handlePremiumFeatureClick('Chat & Images')} style={{ padding: "14px 6px", border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, transition: "all 0.3s ease", position: "relative", animation: "iconOnload 0.6s ease-out 0.3s backwards" }}>
                    <CommBadge count={unreadMsgCount} isRead={messagesRead} color={T.teal} size="small" />
                    <ChatBubbleIcon size={56} color={T.teal} />
                    <div style={{ fontSize: 11, fontWeight: 600, color: T.text, textAlign: "center", textTransform: "uppercase", letterSpacing: "0.5px" }}>Message</div>
                  </button>
                )}

                {/* Call Match */}
                {isFeatureEnabled("in_app_call") && (
                  <button onClick={() => !isPremium && handlePremiumFeatureClick('In-App Call')} style={{ padding: "14px 6px", border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, transition: "all 0.3s ease", position: "relative", animation: "iconOnload 0.6s ease-out 0.4s backwards" }}>
                    <PhoneCallIcon size={56} color={T.amber} />
                    <div style={{ fontSize: 11, fontWeight: 600, color: T.text, textAlign: "center", textTransform: "uppercase", letterSpacing: "0.5px" }}>Call</div>
                  </button>
                )}
              </div>

              {!isPremium && (
                <div style={{ marginTop: 14, padding: "12px 16px", borderRadius: 8, background: `linear-gradient(135deg, ${T.rose}10, ${T.violet}10)`, border: `1px solid ${T.rose}25`, display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 32, height: 32, borderRadius: "50%", background: `linear-gradient(135deg, ${T.rose}, ${T.violet})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, animation: "upgradePulse 2s ease-in-out infinite" }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Unlock Premium</div>
                    <div style={{ fontSize: 13, color: T.muted }}>Chat, call, and share phone</div>
                  </div>
                  <button onClick={() => onNavigateToUpgrade && onNavigateToUpgrade()}
                    style={{ padding: "8px 16px", borderRadius: 8, background: `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, border: "none", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", flexShrink: 0, animation: "upgradePulse 2s ease-in-out infinite" }}>
                    Upgrade
                  </button>
                </div>
              )}
              </div>{/* end accordion wrapper */}
            </div>
          )}
          {matchAction === "accepted" && dateOptions && (
            <div style={{ marginTop: 32, paddingTop: 32, borderTop: `1px solid ${T.border}`, animation: "fadeIn 0.5s ease" }}>
              <div style={{ fontSize: 13, color: T.rose, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 16 }}>📅 CHOOSE DATE OPTIONS (SELECT ALL THAT WORK)</div>
              <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "repeat(2, 1fr)" : "1fr", gap: 12, marginBottom: 16 }}>
                {dateOptions.map((opt, i) => {
                  const d = new Date(opt.date_time);
                  const selected = acceptedOptions.includes(i);
                  return (
                    <div key={i} onClick={() => setAcceptedOptions(prev => selected ? prev.filter(x => x !== i) : [...prev, i])}
                      style={{ padding: "14px 16px", borderRadius: 8, border: `2px solid ${selected ? T.rose : T.border}`, background: selected ? T.roseSoft : "transparent", cursor: "pointer", transition: "all 0.2s" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{opt.venue_name}</div>
                          <div style={{ fontSize: 13, color: T.muted, marginTop: 2 }}>{opt.venue_address}</div>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: selected ? T.rose : T.muted }}>{d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
                          <div style={{ fontSize: 13, color: T.muted }}>{d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</div>
                        </div>
                      </div>
                      {opt.venue_rating && <div style={{ fontSize: 13, color: T.amber, marginTop: 6 }}>★ {opt.venue_rating} · {opt.venue_type}</div>}
                      {opt.venue_maps_url && <a href={opt.venue_maps_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 13, color: T.teal, textDecoration: "none" }}>🗺 View on map</a>}
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button onClick={handleConfirmDate} disabled={!acceptedOptions.length}
                  style={{ flex: 2, minWidth: 140, padding: "14px 20px", borderRadius: 8, background: acceptedOptions.length ? `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})` : T.dim, color: "#fff", fontSize: 16, fontWeight: 700, border: "none", cursor: acceptedOptions.length ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
                  Confirm Date ✦
                </button>
                <button onClick={() => { setAcceptedOptions([]); setDateOptions(null); setTimeout(() => {
                  const now = Date.now();
                  const coffeeVenue = getRandomVenue('coffee');
                  const dinnerVenue = getRandomVenue('dinner');
                  const activityVenue1 = getRandomVenue('activity');
                  const lunchVenue = getRandomVenue('lunch');
                  const drinksVenue = getRandomVenue('drinks');
                  const activityVenue2 = getRandomVenue('activity');
                  setDateOptions([
                    { option_index: 0, date_time: getBusinessHourTime(new Date(now + 6 * 86400000), 'coffee').toISOString(), venue_name: coffeeVenue.venue_name, venue_address: coffeeVenue.venue_address, venue_type: "coffee", venue_rating: coffeeVenue.venue_rating, venue_maps_url: "https://maps.google.com" },
                    { option_index: 1, date_time: getBusinessHourTime(new Date(now + 8 * 86400000), 'activity').toISOString(), venue_name: activityVenue1.venue_name, venue_address: activityVenue1.venue_address, venue_type: "activity", venue_rating: activityVenue1.venue_rating, venue_maps_url: "https://maps.google.com" },
                    { option_index: 2, date_time: getBusinessHourTime(new Date(now + 10 * 86400000), 'dinner').toISOString(), venue_name: dinnerVenue.venue_name, venue_address: dinnerVenue.venue_address, venue_type: "dinner", venue_rating: dinnerVenue.venue_rating, venue_maps_url: "https://maps.google.com" },
                    { option_index: 3, date_time: getBusinessHourTime(new Date(now + 9 * 86400000), 'lunch').toISOString(), venue_name: lunchVenue.venue_name, venue_address: lunchVenue.venue_address, venue_type: "lunch", venue_rating: lunchVenue.venue_rating, venue_maps_url: "https://maps.google.com" },
                    { option_index: 4, date_time: getBusinessHourTime(new Date(now + 11 * 86400000), 'drinks').toISOString(), venue_name: drinksVenue.venue_name, venue_address: drinksVenue.venue_address, venue_type: "drinks", venue_rating: drinksVenue.venue_rating, venue_maps_url: "https://maps.google.com" },
                    { option_index: 5, date_time: getBusinessHourTime(new Date(now + 12 * 86400000), 'activity').toISOString(), venue_name: activityVenue2.venue_name, venue_address: activityVenue2.venue_address, venue_type: "activity", venue_rating: activityVenue2.venue_rating, venue_maps_url: "https://maps.google.com" },
                  ]);
                }, 600); }}
                  style={{ flex: 1, padding: "14px 20px", borderRadius: 8, background: T.card, border: `1px solid ${T.border}`, color: T.muted, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                  New Options
                </button>
                <button onClick={() => setShowCustomSuggestion(true)}
                  style={{ flex: 1, minWidth: 100, padding: "14px 20px", borderRadius: 8, background: "transparent", border: `1px dashed ${T.border}`, color: T.text, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                  ✨ Suggest
                </button>
                <button onClick={() => { setDateOptions(null); setAcceptedOptions([]); setVenueDeferred(true); }}
                  style={{ flex: bp.isDesktop ? 1 : "1 1 100%", minWidth: 100, padding: "14px 20px", borderRadius: 8, background: "transparent", border: `1px solid ${T.border}`, color: T.muted, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                  🕐 Choose Later
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ═══ Animated Widget Dashboard ═══ */}
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: bp.isDesktop ? "28px 32px" : "20px 18px", marginBottom: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>

          {/* ── SECTION 1: Profile Overview ── */}
          <div style={{ marginBottom: bp.isDesktop ? 32 : 20, animation: "widgetSlideUp 0.5s ease-out" }}>
            <div style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.rose, fontWeight: 700, letterSpacing: "0.14em", marginBottom: bp.isDesktop ? 16 : 12 }}>PROFILE OVERVIEW</div>
            <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "auto 1fr" : "1fr", gap: bp.isDesktop ? 28 : 16, alignItems: "start" }}>
              {/* Hero score - compact on desktop */}
              <div style={{ textAlign: "center", padding: bp.isDesktop ? "12px 24px" : "20px 0 12px" }}>
                <CountUpValue value={m.compatibility} duration={1600} suffix="%" style={{ fontSize: bp.isDesktop ? 72 : 64, fontWeight: 900, color: T.rose, fontFamily: "'Segoe UI', sans-serif", lineHeight: 1, letterSpacing: "-2px" }} />
                <div style={{ fontSize: 13, color: T.muted, fontWeight: 600, letterSpacing: "0.15em", marginTop: 6 }}>MATCH</div>
              </div>
              {/* Right side: About + Traits stacked */}
              <div style={{ display: "flex", flexDirection: "column", gap: bp.isDesktop ? 12 : 10 }}>
                {/* About */}
                <div style={{ background: T.bg, borderRadius: 10, padding: bp.isDesktop ? "16px 18px" : "14px 16px", border: `1px solid ${T.border}`, animation: "widgetSlideUp 0.5s ease-out 0.15s backwards" }}>
                  <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 6 }}>ABOUT {m.name.toUpperCase()}</div>
                  <div style={{ fontSize: bp.isDesktop ? 14 : 15, color: T.text, lineHeight: 1.7 }}>{m.intro}</div>
                </div>
                {/* Personality Traits */}
                <div style={{ background: T.bg, borderRadius: 10, padding: bp.isDesktop ? "16px 18px" : "14px 16px", border: `1px solid ${T.border}`, animation: "widgetSlideUp 0.5s ease-out 0.25s backwards" }}>
                  <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 8 }}>PERSONALITY TRAITS</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {m.traits.map((trait, i) => (
                      <div key={trait} style={{ 
                        background: `${T.rose}10`, color: T.rose, 
                        fontSize: bp.isDesktop ? 12 : 13, fontWeight: 600, 
                        padding: "6px 12px", borderRadius: 20,
                        animation: `widgetScale 0.4s ease-out ${0.35 + i * 0.04}s backwards`
                      }}>
                        {trait}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── SECTION 2: Compatibility Metrics ── */}
          <div style={{ marginBottom: bp.isDesktop ? 32 : 20, animation: "widgetSlideUp 0.5s ease-out 0.4s backwards" }}>
            <div style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.teal, fontWeight: 700, letterSpacing: "0.14em", marginBottom: bp.isDesktop ? 16 : 12 }}>COMPATIBILITY METRICS</div>
            <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "1fr 1fr" : "1fr", gap: bp.isDesktop ? 16 : 12 }}>
              {/* Left: Score Rings */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: bp.isDesktop ? 6 : 4 }}>
                {[
                  { label: "Personality", value: m.personalityScore, color: T.rose, delay: 0.45 },
                  { label: "Values", value: m.valuesScore, color: T.teal, delay: 0.55 },
                  { label: "Lifestyle", value: m.lifestyleScore, color: T.amber, delay: 0.65 },
                  { label: "First Date", value: sim.firstDate, color: T.violet, delay: 0.75 },
                ].map(s => (
                  <div key={s.label} style={{ 
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    padding: bp.isDesktop ? "16px 6px" : "14px 4px",
                    animation: `widgetSlideUp 0.5s ease-out ${s.delay}s backwards`,
                    transition: "transform 0.3s", cursor: "pointer"
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.06)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}>
                    <ScoreRing value={s.value} size={bp.isDesktop ? 80 : 66} color={s.color} />
                    <div style={{ fontSize: bp.isDesktop ? 10 : 11, color: T.muted, marginTop: 8, fontWeight: 700, letterSpacing: "0.1em", textAlign: "center" }}>{s.label.toUpperCase()}</div>
                  </div>
                ))}
              </div>
              {/* Right: Prediction Metrics + AI Simulation */}
              <div style={{ display: "flex", flexDirection: "column", gap: bp.isDesktop ? 12 : 10 }}>
                {/* Prediction Icons */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 4 }}>
                  {[
                    { l: "First Date", v: sim.firstDate, c: T.rose, Icon: HeartIcon, delay: 0.5 },
                    { l: "Conversation", v: sim.conversationFlow, c: T.teal, Icon: MessageIcon, delay: 0.6 },
                    { l: "Conflict Risk", v: sim.conflictRisk, c: riskColor, Icon: WarningIcon, delay: 0.7 },
                    { l: "Long-term", v: sim.longTerm, c: T.violet, Icon: HeartIcon, delay: 0.8 },
                  ].map(s => (
                    <div key={s.l} style={{ 
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                      padding: bp.isDesktop ? "14px 6px" : "12px 4px",
                      animation: `widgetSlideUp 0.5s ease-out ${s.delay}s backwards`,
                      transition: "transform 0.3s", cursor: "pointer"
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.06)"; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}>
                      <div style={{ marginBottom: 6 }}><s.Icon size={bp.isDesktop ? 26 : 22} color={s.c} /></div>
                      <CountUpValue value={s.v} duration={1200} suffix="%" style={{ fontSize: bp.isDesktop ? 34 : 28, fontWeight: 900, color: s.c, fontFamily: "'Segoe UI', sans-serif", lineHeight: 1, letterSpacing: "-1px" }} />
                      <div style={{ fontSize: bp.isDesktop ? 9 : 10, color: T.muted, marginTop: 4, fontWeight: 700, letterSpacing: "0.1em" }}>{s.l.toUpperCase()}</div>
                    </div>
                  ))}
                </div>
                {/* AI Simulation */}
                <div style={{ 
                  background: T.bg, borderRadius: 10, padding: bp.isDesktop ? "16px 18px" : "14px 16px",
                  border: `1px solid ${T.border}`,
                  animation: "widgetSlideUp 0.5s ease-out 0.85s backwards",
                  flex: 1
                }}>
                  <div style={{ fontSize: 11, color: T.rose, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 13 }}>✦</span> AI RELATIONSHIP SIMULATION
                  </div>
                  <p style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.text, lineHeight: 1.65, marginBottom: 10, fontWeight: 500 }}>{sim.summary}</p>
                  <div style={{ padding: "10px 12px", borderRadius: 6, background: `${T.amber}08`, borderLeft: `3px solid ${T.amber}`, marginBottom: 6 }}>
                    <div style={{ fontSize: 10, color: T.amber, fontWeight: 700, marginBottom: 2, letterSpacing: "0.08em" }}>💫 PREDICTED VIBE</div>
                    <p style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.text, fontStyle: "italic", margin: 0, lineHeight: 1.5 }}>"{sim.vibe}"</p>
                  </div>
                  <div style={{ padding: "10px 12px", borderRadius: 6, background: `${riskColor}08`, borderLeft: `3px solid ${riskColor}` }}>
                    <div style={{ fontSize: 10, color: riskColor, fontWeight: 700, marginBottom: 2, letterSpacing: "0.08em" }}>⚡ CONFLICT FORECAST</div>
                    <p style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.text, margin: 0, lineHeight: 1.5 }}>{sim.conflict}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── SECTION 3: Detailed Scores ── */}
          <div style={{ marginBottom: bp.isDesktop ? 20 : 16, animation: "widgetSlideUp 0.5s ease-out 0.95s backwards" }}>
            <div style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.violet, fontWeight: 700, letterSpacing: "0.14em", marginBottom: bp.isDesktop ? 16 : 12 }}>DETAILED SCORES</div>
            <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "1fr 1fr" : "1fr", gap: bp.isDesktop ? 16 : 10 }}>
              {/* Left column: Core scores */}
              <div style={{ background: T.bg, borderRadius: 10, padding: bp.isDesktop ? "18px 20px" : "14px 16px", border: `1px solid ${T.border}` }}>
                <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 14 }}>CORE ALIGNMENT</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <ProgressBar value={m.personalityScore} color={T.rose} label="Personality Alignment" />
                  <ProgressBar value={m.valuesScore} color={T.teal} label="Values Alignment" />
                  <ProgressBar value={m.lifestyleScore} color={T.amber} label="Lifestyle Overlap" />
                </div>
              </div>
              {/* Right column: Prediction scores */}
              <div style={{ background: T.bg, borderRadius: 10, padding: bp.isDesktop ? "18px 20px" : "14px 16px", border: `1px solid ${T.border}` }}>
                <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 14 }}>PREDICTED OUTCOMES</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <ProgressBar value={sim.firstDate} color={T.violet} label="First Date Chemistry" />
                  <ProgressBar value={sim.conversationFlow} color={T.teal} label="Conversation Flow" />
                  <ProgressBar value={sim.longTerm} color={T.rose} label="Long-term Probability" />
                </div>
              </div>
            </div>
          </div>

          {/* Voice Narration Button */}
          <button onClick={narrateSimulation} style={{ 
            width: "100%", padding: bp.isDesktop ? "14px 20px" : "12px 16px", borderRadius: 10, 
            background: narrating ? T.roseSoft : "transparent", 
            border: `1.5px solid ${narrating ? T.rose : T.border}`, 
            color: narrating ? T.rose : T.muted, 
            fontSize: bp.isDesktop ? 13 : 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10, 
            transition: "all 0.3s",
            animation: "widgetSlideUp 0.5s ease-out 1.1s backwards"
          }}>
            <span style={{ fontSize: bp.isDesktop ? 20 : 17 }}>{narrating ? "⏹" : "🔊"}</span>
            {narrating ? "Stop Narration" : "🎙 Hear AI Relationship Preview"}
            {narrating && <Waveform active bars={16} color={T.rose} />}
          </button>
        </div>

      {/* Custom Suggestion Modal */}
      {showCustomSuggestion && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }} onClick={() => setShowCustomSuggestion(false)}>
          <div style={{ background: T.card, borderRadius: 8, padding: 24, maxWidth: 400, width: "100%", animation: "fadeIn 0.3s ease" }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: T.text, marginBottom: 16, fontFamily: "'Segoe UI', sans-serif" }}>Suggest a Date</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 13, color: T.muted, fontWeight: 600, letterSpacing: "0.05em", marginBottom: 4, display: "block" }}>VENUE NAME</label>
                <input 
                  value={customSuggestion.venue} 
                  onChange={e => setCustomSuggestion(prev => ({ ...prev, venue: e.target.value }))}
                  placeholder="e.g., Blue Bottle Coffee"
                  style={{ width: "100%", padding: "12px 14px", borderRadius: 6, background: T.bg, border: `1px solid ${T.border}`, color: T.text, fontSize: 15, fontFamily: "inherit" }}
                />
              </div>
              <div>
                <label style={{ fontSize: 13, color: T.muted, fontWeight: 600, letterSpacing: "0.05em", marginBottom: 4, display: "block" }}>DATE</label>
                <input 
                  type="date"
                  value={customSuggestion.date} 
                  onChange={e => setCustomSuggestion(prev => ({ ...prev, date: e.target.value }))}
                  style={{ width: "100%", padding: "12px 14px", borderRadius: 6, background: T.bg, border: `1px solid ${T.border}`, color: T.text, fontSize: 15, fontFamily: "inherit" }}
                />
              </div>
              <div>
                <label style={{ fontSize: 13, color: T.muted, fontWeight: 600, letterSpacing: "0.05em", marginBottom: 4, display: "block" }}>TIME (rounded to hour or :30)</label>
                <input 
                  type="time"
                  value={customSuggestion.time} 
                  onChange={e => {
                    const time = e.target.value;
                    const [hours, minutes] = time.split(':');
                    const roundedMinutes = parseInt(minutes) < 15 ? '00' : parseInt(minutes) < 45 ? '30' : '00';
                    const roundedHours = parseInt(minutes) >= 45 ? String(parseInt(hours) + 1).padStart(2, '0') : hours;
                    setCustomSuggestion(prev => ({ ...prev, time: `${roundedHours}:${roundedMinutes}` }));
                  }}
                  step="1800"
                  style={{ width: "100%", padding: "12px 14px", borderRadius: 6, background: T.bg, border: `1px solid ${T.border}`, color: T.text, fontSize: 15, fontFamily: "inherit" }}
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setShowCustomSuggestion(false)}
                style={{ flex: 1, padding: "12px", borderRadius: 6, background: T.bg, border: `1px solid ${T.border}`, color: T.muted, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                Cancel
              </button>
              <button onClick={() => {
                if (customSuggestion.venue && customSuggestion.date && customSuggestion.time) {
                  const dateTime = new Date(`${customSuggestion.date}T${customSuggestion.time}`);
                  const newOption = {
                    option_index: dateOptions.length,
                    date_time: dateTime.toISOString(),
                    venue_name: customSuggestion.venue,
                    venue_address: "Custom suggestion",
                    venue_type: "custom",
                    venue_rating: null,
                    venue_maps_url: null
                  };
                  setDateOptions(prev => [...prev, newOption]);
                  setCustomSuggestion({ venue: '', date: '', time: '' });
                  setShowCustomSuggestion(false);
                }
              }}
                disabled={!customSuggestion.venue || !customSuggestion.date || !customSuggestion.time}
                style={{ flex: 1, padding: "12px", borderRadius: 6, background: customSuggestion.venue && customSuggestion.date && customSuggestion.time ? `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})` : T.dim, color: "#fff", fontSize: 15, fontWeight: 700, border: "none", cursor: customSuggestion.venue && customSuggestion.date && customSuggestion.time ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
                Add Suggestion
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom action bar - Enhanced */}
      {/* Message button shown after accepting match and date options are ready */}
      {matchAction === "accepted" && dateOptions && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, padding: bp.isDesktop ? "20px 32px 28px" : "16px 20px 24px", background: `linear-gradient(to top, ${T.dark} 70%, transparent)`, maxWidth: bp.isDesktop ? 900 : 480, margin: "0 auto", backdropFilter: "blur(10px)" }}>
          <button onClick={() => onMessage(m)} 
            style={{ 
              width: "100%", 
              padding: bp.isDesktop ? "16px 20px" : "14px 16px", 
              borderRadius: 8, 
              background: T.card, 
              border: `2px solid ${T.border}`, 
              color: T.text, 
              fontSize: bp.isDesktop ? 14 : 15, 
              fontWeight: 600, 
              cursor: "pointer", 
              fontFamily: "inherit",
              transition: "all 0.3s",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = T.teal; e.currentTarget.style.background = `${T.teal}08`; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = T.card; e.currentTarget.style.transform = "translateY(0)"; }}>
            <span style={{ fontSize: bp.isDesktop ? 18 : 17 }}>💬</span> Send a message while you wait
          </button>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 20, animation: "fadeIn 0.3s ease", overflow: "auto" }} onClick={() => setShowUpgradeModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: T.card, borderRadius: 12, padding: bp.isDesktop ? 32 : 24, maxWidth: 500, width: "100%", maxHeight: "calc(100vh - 40px)", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", animation: "modalSlideUp 0.3s ease" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: `linear-gradient(135deg, ${T.rose}, ${T.violet})`, display: "flex", alignItems: "center", justifyContent: "center", animation: "upgradePulse 2s ease-in-out infinite" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 2 }}>Upgrade to Premium</h3>
                <p style={{ fontSize: 14, color: T.muted }}>Unlock {upgradeFeature}</p>
              </div>
              <button onClick={() => setShowUpgradeModal(false)} style={{ width: 32, height: 32, borderRadius: "50%", border: "none", background: T.bg, color: T.muted, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <div style={{ marginBottom: 24 }}>
              <div style={{ background: `linear-gradient(135deg, ${T.rose}10, ${T.violet}10)`, border: `1px solid ${T.rose}25`, borderRadius: 8, padding: 20, marginBottom: 16 }}>
                <h4 style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 12 }}>Premium Features Include:</h4>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                    <span style={{ fontSize: 14, color: T.text }}>Unlimited messaging with matches</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                    <span style={{ fontSize: 14, color: T.text }}>Share phone numbers securely</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                    <span style={{ fontSize: 14, color: T.text }}>In-app voice & video calls</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                    <span style={{ fontSize: 14, color: T.text }}>Send photos & media</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                    <span style={{ fontSize: 14, color: T.text }}>Priority customer support</span>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={() => setShowUpgradeModal(false)} style={{ flex: 1, padding: "12px", borderRadius: 8, background: "transparent", border: `1px solid ${T.border}`, color: T.muted, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                Maybe Later
              </button>
              <button onClick={() => { setShowUpgradeModal(false); onNavigateToUpgrade && onNavigateToUpgrade(); }} style={{ flex: 2, padding: "12px", borderRadius: 8, background: `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, border: "none", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", animation: "upgradePulse 2s ease-in-out infinite" }}>
                View Plans
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Modal */}
      {showReportModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 20, animation: "fadeIn 0.3s ease", overflow: "auto" }} onClick={() => setShowReportModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: T.card, borderRadius: 12, padding: bp.isDesktop ? 32 : 24, maxWidth: 500, width: "100%", maxHeight: "calc(100vh - 40px)", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", animation: "modalSlideUp 0.3s ease" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 2 }}>Report {m.name}</h3>
                <p style={{ fontSize: 14, color: T.muted }}>Help us keep the community safe</p>
              </div>
              <button onClick={() => setShowReportModal(false)} style={{ width: 32, height: 32, borderRadius: "50%", border: "none", background: T.bg, color: T.muted, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {reportSubmitted ? (
              <div style={{ textAlign: "center", padding: "40px 20px" }}>
                <div style={{ width: 60, height: 60, borderRadius: "50%", background: "rgba(34,197,94,0.1)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                </div>
                <h4 style={{ fontSize: 17, fontWeight: 700, color: T.text, marginBottom: 8 }}>Report Submitted</h4>
                <p style={{ fontSize: 14, color: T.muted }}>Thank you for helping keep our community safe. We'll review this report shortly.</p>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 8 }}>Why are you reporting this user?</label>
                  <textarea
                    value={reportReason}
                    onChange={e => setReportReason(e.target.value)}
                    placeholder="Please provide details about why you're reporting this user..."
                    style={{ width: "100%", minHeight: 120, padding: 12, borderRadius: 8, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 15, fontFamily: "inherit", resize: "vertical" }}
                  />
                  <p style={{ fontSize: 13, color: T.muted, marginTop: 8 }}>Common reasons: inappropriate behavior, harassment, fake profile, spam, safety concerns</p>
                </div>

                <div style={{ display: "flex", gap: 12 }}>
                  <button onClick={() => setShowReportModal(false)} style={{ flex: 1, padding: "12px", borderRadius: 8, background: "transparent", border: `1px solid ${T.border}`, color: T.muted, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                    Cancel
                  </button>
                  <button onClick={handleReportSubmit} disabled={!reportReason.trim()} style={{ flex: 1, padding: "12px", borderRadius: 8, background: reportReason.trim() ? "#ef4444" : T.dim, border: "none", color: "#fff", fontSize: 15, fontWeight: 700, cursor: reportReason.trim() ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
                    Submit Report
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Connect Flyout Panel */}
      <ConnectFlyout
        isOpen={connectFlyoutOpen}
        onClose={() => setConnectFlyoutOpen(false)}
        triggerRect={connectFlyoutRect}
        activeAction={connectFlyoutAction}
        expandReceived={connectFlyoutExpandReceived}
        matchName={m.name}
        bp={bp}
        sentGiftReaction={sentGiftReaction}
        setSentGiftReaction={setSentGiftReaction}
        pollQuestion={pollQuestion}
        setPollQuestion={setPollQuestion}
        pollOptions={pollOptions}
        setPollOptions={setPollOptions}
        pollSent={pollSent}
        setPollSent={setPollSent}
        pollResult={pollResult}
        setPollResult={setPollResult}
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
        phoneSent={phoneSent}
        setPhoneSent={setPhoneSent}
        isPremium={isPremium}
        onPremiumClick={handlePremiumFeatureClick}
        receivedGift={receivedGift}
        receivedPoll={receivedPoll}
        receivedPollResponse={pollResponse}
        setReceivedPollResponse={setPollResponse}
        receivedPhone={receivedPhone}
        receivedMessage={receivedMessage}
      />
    </div>
  </div>
  );
}

// ─── SCREEN 4.5: Date Detail ──────────────────────────────────
function DateDetail({ dateItem, onBack, blindMode = false, onNavigateToUpgrade }) {
  const bp = useBreakpoint();
  const containerPad = bp.isDesktop ? "24px 32px 48px" : bp.isTablet ? "28px 32px 60px" : "24px 20px 100px";
  const maxW = bp.isDesktop ? 1400 : bp.isTablet ? 680 : 480;
  const [tab, setTab] = useState("overview");
  const { speak, speaking, stop } = useSpeechSynthesis();
  const [narrating, setNarrating] = useState(false);
  const [playingProfile, setPlayingProfile] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [rescheduleModal, setRescheduleModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduleStep, setRescheduleStep] = useState('reason');
  const [rescheduleData, setRescheduleData] = useState({ venue: '', date: '', time: '' });
  // Premium feature states
  const [ddUserTier] = useState(() => getUserTier());
  const ddIsPremium = ddUserTier === "monthly" || ddUserTier === "annual";
  const [ddSentGift, setDdSentGift] = useState(null);
  const [ddShowGiftSelector, setDdShowGiftSelector] = useState(false);
  const [ddShowPoll, setDdShowPoll] = useState(false);
  const [ddPollQ, setDdPollQ] = useState('');
  const [ddPollOpts, setDdPollOpts] = useState(['', '']);
  const [ddPollSent, setDdPollSent] = useState(false);
  const [ddPollResult, setDdPollResult] = useState(null);
  const [ddShowPhone, setDdShowPhone] = useState(false);
  const [ddPhone, setDdPhone] = useState('');
  const [ddPhoneSent, setDdPhoneSent] = useState(false);
  const [ddMsgUnlocked, setDdMsgUnlocked] = useState(false);
  const [ddConnectFlyoutOpen, setDdConnectFlyoutOpen] = useState(false);
  const [ddConnectFlyoutRect, setDdConnectFlyoutRect] = useState(null);
  const [ddConnectFlyoutAction, setDdConnectFlyoutAction] = useState(null);
  const [ddConnectFlyoutExpandReceived, setDdConnectFlyoutExpandReceived] = useState(false);
  const ddOpenFlyout = (e, action, expandReceived = false) => { setDdConnectFlyoutRect(e.currentTarget.getBoundingClientRect()); setDdConnectFlyoutAction(action); setDdConnectFlyoutExpandReceived(expandReceived); setDdConnectFlyoutOpen(true); };
  const [ddMsgNotification, setDdMsgNotification] = useState(false);
  const [ddShowReportModal, setDdShowReportModal] = useState(false);
  const [ddReportReason, setDdReportReason] = useState('');
  const [ddReportSubmitted, setDdReportSubmitted] = useState(false);
  const [ddShowUpgradeModal, setDdShowUpgradeModal] = useState(false);
  const [ddUpgradeFeature, setDdUpgradeFeature] = useState('');
  const [now, setNow] = useState(Date.now());

  const m = dateItem.match;
  
  // Convert 12-hour time to 24-hour format for proper date parsing
  let time24 = dateItem.time || '00:00';
  if (dateItem.time && (dateItem.time.includes('PM') || dateItem.time.includes('AM'))) {
    const [time, period] = dateItem.time.split(' ');
    const [hours, minutes] = time.split(':');
    let hour = parseInt(hours);
    if (period === 'PM' && hour !== 12) hour += 12;
    if (period === 'AM' && hour === 12) hour = 0;
    time24 = `${hour.toString().padStart(2, '0')}:${minutes}`;
  }
  
  const dt = new Date(`${dateItem.date}T${time24}`);

  // Countdown timer
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);

  const getDateMessage = () => {
    const diff = dt.getTime() - now;
    if (diff <= 0) return null;
    const nowDate = new Date(now);
    const todayStart = new Date(nowDate.getFullYear(), nowDate.getMonth(), nowDate.getDate());
    const targetStart = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    const dayDiff = Math.round((targetStart - todayStart) / 86400000);
    const totalHours = diff / 3600000;
    const isEvening = dt.getHours() >= 17;
    if (dayDiff === 0) {
      if (totalHours <= 1) return { message: "Right Now!", urgent: true };
      if (isEvening) return { message: "Tonight", urgent: true };
      return { message: "Today", urgent: true };
    }
    if (dayDiff === 1) {
      if (isEvening) return { message: "Tomorrow Night", urgent: false };
      return { message: "Tomorrow", urgent: false };
    }
    if (dayDiff >= 2 && dayDiff <= 6) {
      return { message: dt.toLocaleDateString('en-US', { weekday: 'long' }), urgent: false };
    }
    if (dayDiff >= 7 && dayDiff <= 13) return { message: "Next Week", urgent: false };
    if (dayDiff >= 14 && dayDiff <= 20) return { message: "In 2 Weeks", urgent: false };
    const weeks = Math.round(dayDiff / 7);
    if (dayDiff <= 30) return { message: `In ${weeks} Weeks`, urgent: false };
    return { message: `In ${Math.round(dayDiff / 30)} Month${Math.round(dayDiff / 30) > 1 ? 's' : ''}`, urgent: false };
  };

  const dateMsg = getDateMessage();

  // Received communications from matched user
  const [ddReceivedGift, setDdReceivedGift] = useState(() => {
    const stored = localStorage.getItem(`received_gift_${m.id}`);
    return stored || null;
  });
  const [ddReceivedPoll, setDdReceivedPoll] = useState(() => {
    const stored = localStorage.getItem(`received_poll_${m.id}`);
    return stored ? JSON.parse(stored) : null;
  });
  const [ddPollResponse, setDdPollResponse] = useState(null);
  const [ddReceivedPhone, setDdReceivedPhone] = useState(() => {
    const stored = localStorage.getItem(`received_phone_${m.id}`);
    return stored || null;
  });
  const [ddReceivedMessage, setDdReceivedMessage] = useState(() => {
    const stored = localStorage.getItem(`received_message_${m.id}`);
    return stored ? JSON.parse(stored) : null;
  });
  const [ddShowReceivedPanel, setDdShowReceivedPanel] = useState(true);
  // Badge read-tracking: gifts
  const [ddGiftsReceivedCount] = useState(() => ddReceivedGift ? 1 : 0);
  const [ddGiftsRead, setDdGiftsRead] = useState(false);
  // Badge read-tracking: polls
  const [ddPollsReceivedCount] = useState(() => ddReceivedPoll ? 1 : 0);
  const ddPollsRead = !!ddPollResponse;
  // Badge read-tracking: phone shared (checkmark badge)
  const ddPhoneSharedBadge = !!ddReceivedPhone;
  // Badge read-tracking: messages
  const [ddUnreadMsgCount] = useState(() => ddReceivedMessage ? 1 : 0);
  const [ddMessagesRead, setDdMessagesRead] = useState(false);
  const [ddGivenReaction, setDdGivenReaction] = useState(() => _getGivenReactions()[m.id] || null);

  const handleProfileOrbClick = () => {
    if (playingProfile || speaking) { stop(); setPlayingProfile(false); return; }
    setPlayingProfile(true);
    const text = `${m.name}, ${m.age}, from ${m.city}. ${m.compatibility}% compatible. ${m.traits.join(', ')}.`;
    speak(text, () => setPlayingProfile(false));
  };

  const handleDdReaction = (e, emoji) => {
    e.stopPropagation();
    _addVoicemailReaction(emoji);
    _saveGivenReaction(m.id, emoji);
    setDdGivenReaction(emoji);
    setPlayingProfile(false);
    // Dispatch event so other views can sync
    window.dispatchEvent(new Event('givenReactionsChanged'));
  };

  useEffect(() => {
    const sync = () => setDdGivenReaction(_getGivenReactions()[m.id] || null);
    window.addEventListener('givenReactionsChanged', sync);
    return () => window.removeEventListener('givenReactionsChanged', sync);
  }, [m.id]);

  // Check if messaging should be unlocked (20 min before date) for freemium users
  useEffect(() => {
    if (ddIsPremium) return;
    const checkUnlock = () => {
      const now = new Date();
      const diff = dt.getTime() - now.getTime();
      const twentyMin = 20 * 60 * 1000;
      if (diff <= twentyMin && diff > -60 * 60 * 1000) {
        if (!ddMsgUnlocked) {
          setDdMsgUnlocked(true);
          setDdMsgNotification(true);
          setTimeout(() => setDdMsgNotification(false), 8000);
        }
      }
    };
    checkUnlock();
    const interval = setInterval(checkUnlock, 30000);
    return () => clearInterval(interval);
  }, [dt, ddIsPremium, ddMsgUnlocked]);
  
  // Generate talking points based on compatibility and traits
  const talkingPoints = [
    { 
      topic: "Shared Values", 
      iconSvg: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={T.rose} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>,
      description: `You both prioritize ${m.traits[0]?.toLowerCase() || 'authenticity'} in relationships. Ask about what this means to them personally.` 
    },
    { 
      topic: "Personality Fit", 
      iconSvg: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={T.rose} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><path d="M9 3v18"/><path d="M15 3v18"/><path d="M3 9h18"/><path d="M3 15h18"/></svg>,
      description: `Your ${m.personalityScore}% personality match suggests natural chemistry. Discuss your communication styles and how you handle conflict.` 
    },
    { 
      topic: "Life Goals", 
      iconSvg: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={T.rose} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
      description: "Explore future aspirations - career ambitions, travel dreams, and lifestyle preferences." 
    },
    { 
      topic: "Emotional Connection", 
      iconSvg: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={T.rose} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
      description: `With ${m.valuesScore}% values alignment, dive into what matters most: family, independence, or adventure.` 
    }
  ];

  // Generate recommendations based on interview data
  const recommendations = [
    { title: "Start Light", tip: "Begin with the venue - compliment their choice or share why you're excited about this spot." },
    { title: "Ask Open Questions", tip: "Instead of yes/no questions, try 'What's been the highlight of your week?' or 'What are you passionate about lately?'" },
    { title: "Share Stories", tip: "Personal anecdotes create connection. Share a recent experience that reveals your personality." },
    { title: "Be Present", tip: "Put your phone away, maintain eye contact, and actively listen. Your compatibility score suggests great potential." },
    { title: "Follow Their Energy", tip: `They're described as ${m.traits.join(', ')}. Match their conversational pace and enthusiasm level.` }
  ];

  const handleDdPremiumFeatureClick = (featureName) => {
    if (!ddIsPremium) {
      setDdUpgradeFeature(featureName);
      setDdShowUpgradeModal(true);
    }
  };

  const handleDdPollResponse = (option) => {
    setDdPollResponse(option);
    localStorage.setItem(`poll_response_${m.id}`, option);
    savePollResponse(m.id, option);
  };

  const handleDdReportSubmit = () => {
    if (!ddReportReason.trim()) return;
    try {
      const report = {
        id: Date.now().toString(),
        reportedUserId: m.id,
        reportedUserName: m.name,
        reportedUserAge: m.age,
        reportedUserCity: m.city,
        reportedUserPhoto: m.photo,
        reason: ddReportReason,
        additionalInfo: '',
        reportedAt: new Date().toISOString(),
        reportedBy: 'current_user',
        status: 'pending'
      };
      saveUserReport(report); // Auto-syncs to Supabase
      setDdReportSubmitted(true);
      setTimeout(() => {
        setDdShowReportModal(false);
        setDdReportReason('');
        setDdReportSubmitted(false);
      }, 2000);
    } catch (e) {
      console.error('Failed to submit report:', e);
    }
  };

  const handleNarrate = () => {
    if (narrating || speaking) { stop(); setNarrating(false); return; }
    setNarrating(true);
    const text = `Date with ${m.name}, ${m.age}, from ${m.city}. Your compatibility is ${m.compatibility}%. The date is scheduled for ${dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} at ${dateItem.time} at ${dateItem.location}. ${m.name} is ${m.traits.join(', ')}. Your personality match is ${m.personalityScore}% and values alignment is ${m.valuesScore}%.`;
    speak(text, () => setNarrating(false));
  };

  const handleCancelDate = () => {
    setCancelModal(true);
  };

  const confirmCancel = () => {
    if (!cancelReason) return;
    console.log('Date cancelled:', dateItem.id, 'Reason:', cancelReason);
    setCancelModal(false);
    setCancelReason('');
    onBack();
  };

  const handleRescheduleDate = () => {
    setRescheduleModal(true);
    setRescheduleStep('reason');
  };

  const confirmReschedule = () => {
    console.log('Date rescheduled:', dateItem.id, 'Reason:', rescheduleReason, 'New details:', rescheduleData);
    setRescheduleModal(false);
    setRescheduleReason('');
    setRescheduleStep('reason');
    setRescheduleData({ venue: '', date: '', time: '' });
    onBack();
  };

  // Generate map links
  const venueAddress = encodeURIComponent(dateItem.location);
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${venueAddress}`;
  const appleMapsUrl = `http://maps.apple.com/?q=${venueAddress}`;

  return (
    <div style={{ maxWidth: maxW, margin: "0 auto", padding: containerPad }}>
      {/* Header */}
      <div style={{ marginBottom: bp.isDesktop ? 32 : 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", color: T.rose, fontSize: bp.isDesktop ? 14 : 15, fontWeight: 600, cursor: "pointer", padding: 0, fontFamily: "inherit" }}>
            <span style={{ fontSize: 18 }}>←</span> Back to Calendar
          </button>
          <button onClick={() => setDdShowReportModal(true)} style={{ padding: "8px 16px", borderRadius: 8, background: "transparent", border: `1px solid ${T.border}`, color: "#ef4444", fontSize: 14, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            Report User
          </button>
        </div>
        
        <h2 style={{ fontSize: bp.isDesktop ? 32 : 24, fontWeight: 900, color: T.text, marginBottom: 16, fontFamily: "'Segoe UI', sans-serif" }}>
          Date Details
        </h2>
      </div>


      {/* Match Profile Card */}
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: bp.isDesktop ? 40 : 24, marginBottom: bp.isDesktop ? 20 : 16 }}>
        <div style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.rose, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 12 }}>YOUR MATCH</div>
        
        <div style={{ display: bp.isDesktop ? "flex" : "block", alignItems: "flex-start", gap: 32 }}>
        <div style={{ flex: bp.isDesktop ? 1 : undefined }}>
        <div style={{ display: "flex", gap: bp.isDesktop ? 24 : 16, alignItems: "flex-start", marginBottom: bp.isDesktop ? 24 : 10 }}>
          {blindMode ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "center" }}>
              <div onClick={handleProfileOrbClick} style={{ position: "relative", width: bp.isDesktop ? 160 : 100, height: bp.isDesktop ? 160 : 100, flexShrink: 0, cursor: "pointer" }}>
                <div style={{ position: "absolute", inset: -5, borderRadius: "50%", border: `2px solid ${T.rose}`, animation: "orbRing 1.8s ease-out infinite", opacity: 0.7 }} />
                <div style={{ width: "100%", height: "100%", borderRadius: "50%", background: `linear-gradient(135deg, ${T.rose}, ${T.violet})`, display: "flex", alignItems: "center", justifyContent: "center", animation: "orbPulse 1.5s ease-in-out infinite alternate", border: `3px solid ${T.rose}` }}>
                  <span style={{ fontSize: bp.isDesktop ? 52 : 32, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginLeft: playingProfile ? 0 : 3 }}>{playingProfile ? "⏸" : <PlayTriangle size={bp.isDesktop ? 60 : 38} />}</span>
                </div>
              </div>
              {playingProfile && (
                <div style={{ width: bp.isDesktop ? 160 : 100, height: 4, borderRadius: 3, background: `${T.rose}30`, overflow: "hidden" }}>
                  <div style={{ height: "100%", background: `linear-gradient(90deg, ${T.rose}, ${T.violet})`, borderRadius: 3, animation: "voicemailProgress 10s linear forwards" }} />
                </div>
              )}
              {playingProfile && (
                <div onClick={e => e.stopPropagation()} style={{ display: "flex", gap: 6, animation: "reactionSlideUp 0.3s ease both" }}>
                  {REACTION_EMOJIS.map((emoji, ei) => (
                    <button key={emoji} onClick={e => handleDdReaction(e, emoji)}
                      style={{ width: 36, height: 36, borderRadius: "50%", border: "none", background: T.card, cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s", animation: `emojiPop 0.4s ease ${ei * 0.05}s both` }}
                      onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.2)"; e.currentTarget.style.background = T.bg; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.background = T.card; }}>
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
              {ddGivenReaction && !playingProfile && (
                <div style={{ fontSize: 28, animation: "emojiPop 0.5s ease both" }}>{ddGivenReaction}</div>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
              <div style={{ position: "relative", width: bp.isDesktop ? 160 : 100, height: bp.isDesktop ? 160 : 100, flexShrink: 0 }}>
                <div style={{ width: "100%", height: "100%", borderRadius: "50%", overflow: "hidden", border: `3px solid ${T.rose}` }}>
                  <img src={m.photo} alt={m.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
                <button onClick={handleProfileOrbClick} style={{ 
                  position: "absolute", 
                  bottom: -4, 
                  right: -4, 
                  width: bp.isDesktop ? 48 : 32, 
                  height: bp.isDesktop ? 48 : 32, 
                  borderRadius: "50%", 
                  background: playingProfile ? T.rose : `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, 
                  border: "2px solid #fff", 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center", 
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  transition: "all 0.2s",
                  padding: 0
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.1)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}>
                  <span style={{ fontSize: bp.isDesktop ? 18 : 14, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginLeft: playingProfile ? 0 : 1 }}>{playingProfile ? "⏸" : <PlayTriangle size={bp.isDesktop ? 20 : 14} />}</span>
                </button>
              </div>
              {playingProfile && (
                <div style={{ width: bp.isDesktop ? 160 : 100, height: 4, borderRadius: 3, background: `${T.rose}30`, overflow: "hidden" }}>
                  <div style={{ height: "100%", background: `linear-gradient(90deg, ${T.rose}, ${T.violet})`, borderRadius: 3, animation: "voicemailProgress 10s linear forwards" }} />
                </div>
              )}
              {playingProfile && (
                <div onClick={e => e.stopPropagation()} style={{ display: "flex", gap: 6, animation: "reactionSlideUp 0.3s ease both" }}>
                  {REACTION_EMOJIS.map((emoji, ei) => (
                    <button key={emoji} onClick={e => handleDdReaction(e, emoji)}
                      style={{ width: 36, height: 36, borderRadius: "50%", border: "none", background: T.card, cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s", animation: `emojiPop 0.4s ease ${ei * 0.05}s both` }}
                      onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.2)"; e.currentTarget.style.background = T.bg; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.background = T.card; }}>
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
              {ddGivenReaction && !playingProfile && (
                <div style={{ fontSize: 28, animation: "emojiPop 0.5s ease both" }}>{ddGivenReaction}</div>
              )}
            </div>
          )}
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: bp.isDesktop ? 28 : 24, fontWeight: 700, color: "#1a1a1a", marginBottom: 4, fontFamily: "inherit" }}>{m.name}, {m.age}</h1>
            <div style={{ marginBottom: bp.isDesktop ? 16 : 4 }}>
              <div style={{ fontSize: bp.isDesktop ? 14 : 15, color: T.muted, fontWeight: 500, marginBottom: 4 }}>
                {m.gender} · {m.height}
              </div>
              <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, marginBottom: bp.isDesktop ? 8 : 0 }}>
                <LocationIcon size={13} color={T.muted} /> {m.city}
              </div>
              {bp.isDesktop && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                  {m.traits.slice(0, 3).map(t => <Pill key={t}>{t}</Pill>)}
                </div>
              )}
            </div>

            {/* Desktop: Compact Match Score inline */}
            {bp.isDesktop && (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div style={{ padding: "8px 14px", background: `${T.rose}08`, border: `1px solid ${T.rose}20`, borderRadius: 6, textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: T.rose, fontFamily: "'Segoe UI', sans-serif", lineHeight: 1 }}>{m.compatibility}%</div>
                <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, marginTop: 4 }}>MATCH</div>
              </div>
              <div style={{ padding: "8px 14px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: T.rose, fontFamily: "'Segoe UI', sans-serif", lineHeight: 1 }}>{m.personalityScore}%</div>
                <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, marginTop: 4 }}>PERSONALITY</div>
              </div>
              <div style={{ padding: "8px 14px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: T.teal, fontFamily: "'Segoe UI', sans-serif", lineHeight: 1 }}>{m.valuesScore}%</div>
                <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, marginTop: 4 }}>VALUES</div>
              </div>
            </div>
            )}
          </div>
        </div>

        {/* Mobile: Traits row + divider + scores row (full width, condensed) */}
        {!bp.isDesktop && (<>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
            {m.traits.slice(0, 3).map(t => <Pill key={t}>{t}</Pill>)}
          </div>
          <div style={{ height: 1, background: `linear-gradient(90deg, ${T.rose}40, ${T.teal}40)`, marginBottom: 10 }} />
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1, padding: "8px 0", background: `${T.rose}08`, border: `1px solid ${T.rose}20`, borderRadius: 6, textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 900, color: T.rose, fontFamily: "'Segoe UI', sans-serif", lineHeight: 1 }}>{m.valuesScore}%</div>
              <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, marginTop: 4 }}>VALUES</div>
            </div>
            <div style={{ flex: 1, padding: "8px 0", background: `${T.rose}08`, border: `1px solid ${T.rose}20`, borderRadius: 6, textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 900, color: T.rose, fontFamily: "'Segoe UI', sans-serif", lineHeight: 1 }}>{m.compatibility}%</div>
              <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, marginTop: 4 }}>MATCH</div>
            </div>
            <div style={{ flex: 1, padding: "8px 0", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 900, color: T.rose, fontFamily: "'Segoe UI', sans-serif", lineHeight: 1 }}>{m.personalityScore}%</div>
              <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, marginTop: 4 }}>PERSONALITY</div>
            </div>
          </div>
        </>)}
        </div>

        {/* Right Column: Connect Options (Desktop) */}
        {bp.isDesktop && (
          <div style={{ flex: 1, minWidth: 0, borderLeft: `1px solid ${T.border}`, paddingLeft: 32 }}>
            <div style={{ fontSize: 13, color: T.rose, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 16 }}>CONNECT WITH {m.name.toUpperCase()}</div>

            {/* Modern Icon Grid - Icons Only with Inline Interactions */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, marginBottom: 20, position: "relative" }}>
              {/* Send Gift */}
              {isFeatureEnabled("emoji_reactions") && (
                <div>
                  {!ddSentGift ? (
                    <button onClick={(e) => ddOpenFlyout(e, 'gift')} style={{ padding: "16px 8px", border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 12, transition: "all 0.3s ease", position: "relative", animation: "iconOnload 0.6s ease-out", width: "100%" }}
                      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-6px) scale(1.05)"; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0) scale(1)"; }}>
                      <CommBadge count={ddGiftsReceivedCount} isRead={ddGiftsRead} color={T.rose} onClick={(e) => { const btn = e.target.closest('button'); setDdConnectFlyoutRect(btn.getBoundingClientRect()); setDdConnectFlyoutAction('gift'); setDdConnectFlyoutExpandReceived(true); setDdConnectFlyoutOpen(true); }} />
                      <GiftBoxIcon size={64} color={T.rose} />
                      <div style={{ fontSize: 12, fontWeight: 600, color: T.text, textAlign: "center", textTransform: "uppercase", letterSpacing: "0.5px" }}>Send Gift</div>
                    </button>
                  ) : (
                    <div style={{ padding: "16px 8px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                      <div style={{ fontSize: 48, animation: "emojiPop 0.5s ease both" }}>{ddSentGift}</div>
                      <div style={{ fontSize: 11, color: T.muted, fontWeight: 600 }}>Sent!</div>
                      <button onClick={() => setDdSentGift(null)} style={{ padding: "4px 10px", borderRadius: 6, border: "none", background: T.rose, color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Send Another</button>
                    </div>
                  )}
                </div>
              )}

              {/* Send Poll */}
              {isFeatureEnabled("polls") && (
                <div>
                  {!ddPollSent ? (
                    <button onClick={(e) => ddOpenFlyout(e, 'poll')} style={{ padding: "16px 8px", border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 12, transition: "all 0.3s ease", position: "relative", animation: "iconOnload 0.6s ease-out 0.1s backwards", width: "100%" }}
                      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-6px) scale(1.05)"; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0) scale(1)"; }}>
                      <CommBadge count={ddPollsReceivedCount} isRead={ddPollsRead} color={T.teal} onClick={(e) => { const btn = e.target.closest('button'); setDdConnectFlyoutRect(btn.getBoundingClientRect()); setDdConnectFlyoutAction('poll'); setDdConnectFlyoutExpandReceived(true); setDdConnectFlyoutOpen(true); }} />
                      <PollClipboardIcon size={64} color={T.teal} />
                      <div style={{ fontSize: 12, fontWeight: 600, color: T.text, textAlign: "center", textTransform: "uppercase", letterSpacing: "0.5px" }}>Send Poll</div>
                    </button>
                  ) : (
                    <div style={{ padding: "16px 8px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                      <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, textAlign: "center" }}>Poll sent!</div>
                      {ddPollResult && <div style={{ fontSize: 12, fontWeight: 700, color: T.teal, textAlign: "center" }}>"{ddPollResult}"</div>}
                    </div>
                  )}
                </div>
              )}

              {/* Share Phone */}
              {isFeatureEnabled("send_phone") && (
                <div>
                  {!ddPhoneSent ? (
                    <button onClick={(e) => ddIsPremium ? ddOpenFlyout(e, 'phone') : handleDdPremiumFeatureClick('Share Phone Number')} style={{ padding: "16px 8px", border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 12, transition: "all 0.3s ease", position: "relative", animation: "iconOnload 0.6s ease-out 0.2s backwards", width: "100%" }}
                      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-6px) scale(1.05)"; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0) scale(1)"; }}>
                      <CommBadge isCheckmark={ddPhoneSharedBadge} isRead={true} color={T.violet} onClick={(e) => { if (!ddIsPremium) { e.stopPropagation(); handleDdPremiumFeatureClick('Share Phone Number'); return; } const btn = e.target.closest('button'); setDdConnectFlyoutRect(btn.getBoundingClientRect()); setDdConnectFlyoutAction('phone'); setDdConnectFlyoutExpandReceived(true); setDdConnectFlyoutOpen(true); }} />
                      <PhoneShareIcon size={64} color={T.violet} />
                      <div style={{ fontSize: 12, fontWeight: 600, color: T.text, textAlign: "center", textTransform: "uppercase", letterSpacing: "0.5px" }}>Share Phone</div>
                    </button>
                  ) : (
                    <div style={{ padding: "16px 8px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill={T.violet}><path d="M20 6L9 17l-5-5"/></svg>
                      <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, textAlign: "center" }}>Phone sent!</div>
                    </div>
                  )}
                </div>
              )}

              {/* Send Message */}
              {isFeatureEnabled("send_message") && (
                <button onClick={() => !(ddIsPremium || ddMsgUnlocked) && handleDdPremiumFeatureClick('Chat & Images')} style={{ padding: "16px 8px", border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 12, transition: "all 0.3s ease", position: "relative", animation: "iconOnload 0.6s ease-out 0.3s backwards" }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-6px) scale(1.05)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0) scale(1)"; }}>
                  <CommBadge count={ddUnreadMsgCount} isRead={ddMessagesRead} color={T.teal} />
                  <ChatBubbleIcon size={64} color={T.teal} />
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.text, textAlign: "center", textTransform: "uppercase", letterSpacing: "0.5px" }}>Message</div>
                </button>
              )}

              {/* Call Match */}
              {isFeatureEnabled("in_app_call") && (
                <button onClick={() => !ddIsPremium && handleDdPremiumFeatureClick('In-App Call')} style={{ padding: "16px 8px", border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 12, transition: "all 0.3s ease", position: "relative", animation: "iconOnload 0.6s ease-out 0.4s backwards" }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-6px) scale(1.05)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0) scale(1)"; }}>
                  <PhoneCallIcon size={64} color={T.amber} />
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.text, textAlign: "center", textTransform: "uppercase", letterSpacing: "0.5px" }}>Call</div>
                </button>
              )}
            </div>

            {!ddIsPremium && (
              <div style={{ marginTop: 14, padding: "12px 16px", borderRadius: 8, background: `linear-gradient(135deg, ${T.rose}10, ${T.violet}10)`, border: `1px solid ${T.rose}25`, display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: `linear-gradient(135deg, ${T.rose}, ${T.violet})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, animation: "upgradePulse 2s ease-in-out infinite" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Unlock Premium</div>
                  <div style={{ fontSize: 13, color: T.muted }}>Chat, call, and share phone</div>
                </div>
                <button onClick={() => onNavigateToUpgrade && onNavigateToUpgrade()}
                  style={{ padding: "8px 16px", borderRadius: 8, background: `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, border: "none", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", flexShrink: 0, animation: "upgradePulse 2s ease-in-out infinite" }}>
                  Upgrade
                </button>
              </div>
            )}
          </div>
        )}
        </div>
      </div>

      {/* Mobile: Connect Options - separate card below match profile */}
      {!bp.isDesktop && (
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: 24, marginBottom: bp.isDesktop ? 20 : 16 }}>
          <div style={{ fontSize: 12, color: T.rose, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 16 }}>CONNECT WITH {m.name.toUpperCase()}</div>

          {/* Modern Icon Grid - Mobile Version */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 20, position: "relative" }}>
            {/* Send Gift */}
            {isFeatureEnabled("emoji_reactions") && (
              <div>
                {!ddSentGift ? (
                  <button onClick={(e) => ddOpenFlyout(e, 'gift')} style={{ padding: "14px 6px", border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, transition: "all 0.3s ease", position: "relative", animation: "iconOnload 0.6s ease-out", width: "100%" }}>
                    <CommBadge count={ddGiftsReceivedCount} isRead={ddGiftsRead} color={T.rose} size="small" onClick={(e) => { const btn = e.target.closest('button'); setDdConnectFlyoutRect(btn.getBoundingClientRect()); setDdConnectFlyoutAction('gift'); setDdConnectFlyoutExpandReceived(true); setDdConnectFlyoutOpen(true); }} />
                    <GiftBoxIcon size={56} color={T.rose} />
                    <div style={{ fontSize: 11, fontWeight: 600, color: T.text, textAlign: "center", textTransform: "uppercase", letterSpacing: "0.5px" }}>Send Gift</div>
                  </button>
                ) : (
                  <div style={{ padding: "14px 6px", display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                    <div style={{ fontSize: 42, animation: "emojiPop 0.5s ease both" }}>{ddSentGift}</div>
                    <div style={{ fontSize: 10, color: T.muted, fontWeight: 600 }}>Sent!</div>
                    <button onClick={() => setDdSentGift(null)} style={{ padding: "3px 8px", borderRadius: 5, border: "none", background: T.rose, color: "#fff", fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Send Another</button>
                  </div>
                )}
              </div>
            )}

            {/* Send Poll */}
            {isFeatureEnabled("polls") && (
              <div>
                {!ddPollSent ? (
                  <button onClick={(e) => ddOpenFlyout(e, 'poll')} style={{ padding: "14px 6px", border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, transition: "all 0.3s ease", position: "relative", animation: "iconOnload 0.6s ease-out 0.1s backwards", width: "100%" }}>
                    <CommBadge count={ddPollsReceivedCount} isRead={ddPollsRead} color={T.teal} size="small" onClick={(e) => { const btn = e.target.closest('button'); setDdConnectFlyoutRect(btn.getBoundingClientRect()); setDdConnectFlyoutAction('poll'); setDdConnectFlyoutExpandReceived(true); setDdConnectFlyoutOpen(true); }} />
                    <PollClipboardIcon size={56} color={T.teal} />
                    <div style={{ fontSize: 11, fontWeight: 600, color: T.text, textAlign: "center", textTransform: "uppercase", letterSpacing: "0.5px" }}>Send Poll</div>
                  </button>
                ) : (
                  <div style={{ padding: "14px 6px", display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                    <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                    <div style={{ fontSize: 10, color: T.muted, fontWeight: 600, textAlign: "center" }}>Poll sent!</div>
                    {ddPollResult && <div style={{ fontSize: 11, fontWeight: 700, color: T.teal, textAlign: "center" }}>"{ddPollResult}"</div>}
                  </div>
                )}
              </div>
            )}

            {/* Share Phone */}
            {isFeatureEnabled("send_phone") && (
              <div>
                {!ddPhoneSent ? (
                  <button onClick={(e) => ddIsPremium ? ddOpenFlyout(e, 'phone') : handleDdPremiumFeatureClick('Share Phone Number')} style={{ padding: "14px 6px", border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, transition: "all 0.3s ease", position: "relative", animation: "iconOnload 0.6s ease-out 0.2s backwards", width: "100%" }}>
                    <CommBadge isCheckmark={ddPhoneSharedBadge} isRead={true} color={T.violet} size="small" onClick={(e) => { if (!ddIsPremium) { e.stopPropagation(); handleDdPremiumFeatureClick('Share Phone Number'); return; } const btn = e.target.closest('button'); setDdConnectFlyoutRect(btn.getBoundingClientRect()); setDdConnectFlyoutAction('phone'); setDdConnectFlyoutExpandReceived(true); setDdConnectFlyoutOpen(true); }} />
                    <PhoneShareIcon size={56} color={T.violet} />
                    <div style={{ fontSize: 11, fontWeight: 600, color: T.text, textAlign: "center", textTransform: "uppercase", letterSpacing: "0.5px" }}>Share Phone</div>
                  </button>
                ) : (
                  <div style={{ padding: "14px 6px", display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                    <svg width="42" height="42" viewBox="0 0 24 24" fill={T.violet}><path d="M20 6L9 17l-5-5"/></svg>
                    <div style={{ fontSize: 10, color: T.muted, fontWeight: 600, textAlign: "center" }}>Phone sent!</div>
                  </div>
                )}
              </div>
            )}

            {/* Send Message */}
            {isFeatureEnabled("send_message") && (
              <button onClick={() => !(ddIsPremium || ddMsgUnlocked) && handleDdPremiumFeatureClick('Chat & Images')} style={{ padding: "14px 6px", border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, transition: "all 0.3s ease", position: "relative", animation: "iconOnload 0.6s ease-out 0.3s backwards" }}>
                <CommBadge count={ddUnreadMsgCount} isRead={ddMessagesRead} color={T.teal} size="small" />
                <ChatBubbleIcon size={56} color={T.teal} />
                <div style={{ fontSize: 11, fontWeight: 600, color: T.text, textAlign: "center", textTransform: "uppercase", letterSpacing: "0.5px" }}>Message</div>
              </button>
            )}

            {/* Call Match */}
            {isFeatureEnabled("in_app_call") && (
              <button onClick={() => !ddIsPremium && handleDdPremiumFeatureClick('In-App Call')} style={{ padding: "14px 6px", border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, transition: "all 0.3s ease", position: "relative", animation: "iconOnload 0.6s ease-out 0.4s backwards" }}>
                <PhoneCallIcon size={56} color={T.amber} />
                <div style={{ fontSize: 11, fontWeight: 600, color: T.text, textAlign: "center", textTransform: "uppercase", letterSpacing: "0.5px" }}>Call</div>
              </button>
            )}
          </div>

          {!ddIsPremium && (
            <div style={{ marginTop: 14, padding: "12px 16px", borderRadius: 8, background: `linear-gradient(135deg, ${T.rose}10, ${T.violet}10)`, border: `1px solid ${T.rose}25`, display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: `linear-gradient(135deg, ${T.rose}, ${T.violet})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, animation: "upgradePulse 2s ease-in-out infinite" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Unlock Premium</div>
                <div style={{ fontSize: 13, color: T.muted }}>Chat, call, and share phone</div>
              </div>
              <button onClick={() => onNavigateToUpgrade && onNavigateToUpgrade()}
                style={{ padding: "8px 16px", borderRadius: 8, background: `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, border: "none", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", flexShrink: 0, animation: "upgradePulse 2s ease-in-out infinite" }}>
                Upgrade
              </button>
            </div>
          )}
        </div>
      )}

      {/* Venue Card */}
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: bp.isDesktop ? 20 : 16, marginBottom: bp.isDesktop ? 20 : 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "1fr 1fr" : "1fr", gap: bp.isDesktop ? 20 : 16 }}>
          {/* Venue Details */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <div style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {dateItem.type === 'Dinner' ? (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T.rose} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 12h16"/><path d="M6 8v8"/><path d="M10 8v8"/><path d="M14 8v8"/><circle cx="12" cy="12" r="10"/></svg>
                ) : dateItem.type === 'Coffee' ? (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T.rose} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 8h1a4 4 0 1 1 0 8h-1"/><path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z"/><line x1="6" y1="2" x2="6" y2="4"/><line x1="10" y1="2" x2="10" y2="4"/><line x1="14" y1="2" x2="14" y2="4"/></svg>
                ) : dateItem.type === 'Activity' ? (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T.rose} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/><path d="m9 12 2 2 4-4"/></svg>
                ) : (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T.rose} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 2h8l4 10H4L8 2Z"/><path d="M12 15v5"/><path d="M8 20h8"/></svg>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: bp.isDesktop ? 10 : 11, color: T.rose, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 4 }}>{dateItem.type.toUpperCase()}</div>
                <h3 style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 800, color: T.text, lineHeight: 1.2 }}>{dateItem.location}</h3>
              </div>
            </div>
            
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ color: T.amber, fontSize: 17 }}>⭐</span>
              <span style={{ fontSize: bp.isDesktop ? 14 : 15, fontWeight: 700, color: T.text }}>{dateItem.rating}</span>
              <span style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted }}>({dateItem.reviewCount})</span>
            </div>
            
            <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, lineHeight: 1.5, marginBottom: 20 }}>{dateItem.notes}</div>
            
            {/* Status Badge and Action Buttons */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 14px", background: dateItem.status === 'confirmed' ? 'rgba(34,197,94,0.08)' : `${T.rose}08`, border: `1px solid ${dateItem.status === 'confirmed' ? 'rgba(34,197,94,0.2)' : `${T.rose}20`}`, borderRadius: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: dateItem.status === 'confirmed' ? '#22c55e' : T.rose, letterSpacing: "0.08em" }}>
                  {dateItem.status === 'confirmed' ? '✓ CONFIRMED' : '⏳ PENDING'}
                </span>
              </div>
              
              {(dateItem.status === 'confirmed' || dateItem.status === 'pending') && (
                <>
                  <button
                    onClick={handleRescheduleDate}
                    style={{
                      background: T.card,
                      border: `1px solid ${T.border}`,
                      borderRadius: 6,
                      padding: "8px 14px",
                      fontSize: 13,
                      fontWeight: 600,
                      color: T.rose,
                      cursor: "pointer",
                      transition: "all 0.2s",
                      fontFamily: "inherit"
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = T.bg; e.currentTarget.style.borderColor = T.rose; }}
                    onMouseLeave={e => { e.currentTarget.style.background = T.card; e.currentTarget.style.borderColor = T.border; }}>
                    Reschedule
                  </button>
                  <button
                    onClick={handleCancelDate}
                    style={{
                      background: T.card,
                      border: `1px solid ${T.border}`,
                      borderRadius: 6,
                      padding: "8px 14px",
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#ef4444",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      fontFamily: "inherit"
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = T.bg; e.currentTarget.style.borderColor = "#ef4444"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = T.card; e.currentTarget.style.borderColor = T.border; }}>
                    Cancel
                  </button>
                </>
              )}
            </div>

            {/* Date/Time and Date Message */}
            <div style={{ background: `linear-gradient(135deg, ${T.rose}08, ${T.violet}08)`, border: `1px solid ${T.rose}30`, borderRadius: 8, padding: bp.isDesktop ? 12 : 10, marginBottom: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.text, fontWeight: 600 }}>
                  {dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                </div>
                <div style={{ width: 1, height: 16, background: T.border }} />
                <div style={{ fontSize: bp.isDesktop ? 14 : 15, color: T.rose, fontWeight: 700 }}>
                  {dateItem.time}
                </div>
              </div>
              {dateMsg && (
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, textTransform: "uppercase", letterSpacing: "0.06em", lineHeight: 1 }}>YOUR DATE IS</div>
                  <div style={{ fontSize: bp.isDesktop ? 22 : 20, fontWeight: 800, background: `linear-gradient(90deg, ${T.rose}, ${T.violet}, ${T.rose})`, backgroundSize: '300% 100%', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', animation: 'aurora 6s ease-in-out infinite', lineHeight: 1.2, marginTop: 2 }}>{dateMsg.message}</div>
                </div>
              )}
            </div>
          </div>
          
          {/* Map Preview and Links */}
          <div>
            <div style={{ width: "100%", height: bp.isDesktop ? 240 : 200, borderRadius: 8, overflow: "hidden", border: `1px solid ${T.border}`, marginBottom: 12 }}>
              <iframe
                width="100%"
                height="100%"
                frameBorder="0"
                style={{ border: 0 }}
                src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${venueAddress}&zoom=15`}
                allowFullScreen
              />
            </div>
            
            {/* Map Links */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer" style={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center",
                gap: 6, 
                padding: "10px 12px", 
                background: `${T.rose}08`, 
                border: `1px solid ${T.rose}20`, 
                borderRadius: 6, 
                fontSize: bp.isDesktop ? 12 : 13, 
                fontWeight: 600, 
                color: T.rose, 
                textDecoration: "none",
                transition: "all 0.2s"
              }}
              onMouseEnter={e => { e.currentTarget.style.background = `${T.rose}15`; e.currentTarget.style.borderColor = `${T.rose}40`; }}
              onMouseLeave={e => { e.currentTarget.style.background = `${T.rose}08`; e.currentTarget.style.borderColor = `${T.rose}20`; }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                Google
              </a>
              <a href={appleMapsUrl} target="_blank" rel="noopener noreferrer" style={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center",
                gap: 6, 
                padding: "10px 12px", 
                background: T.bg, 
                border: `1px solid ${T.border}`, 
                borderRadius: 6, 
                fontSize: bp.isDesktop ? 12 : 13, 
                fontWeight: 600, 
                color: T.text, 
                textDecoration: "none",
                transition: "all 0.2s"
              }}
              onMouseEnter={e => { e.currentTarget.style.background = T.card; e.currentTarget.style.borderColor = T.rose; }}
              onMouseLeave={e => { e.currentTarget.style.background = T.bg; e.currentTarget.style.borderColor = T.border; }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                Apple
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Messaging Unlock Notification */}
      {ddMsgNotification && (
        <div style={{ marginBottom: bp.isDesktop ? 20 : 16, padding: "14px 18px", borderRadius: 8, background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)", display: "flex", alignItems: "center", gap: 12, animation: "fadeIn 0.4s ease" }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#22c55e" }}>Messaging Unlocked!</div>
            <div style={{ fontSize: 13, color: T.muted }}>Your date is starting soon — messaging with {m.name} is now free.</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: bp.isDesktop ? 24 : 20, borderBottom: `1px solid ${T.border}`, paddingBottom: 2 }}>
        {[
          { key: "overview", label: "Overview" },
          { key: "talking-points", label: "Talking Points" },
          { key: "tips", label: "Date Tips" }
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{ padding: "12px 20px", background: "none", border: "none", borderBottom: tab === t.key ? `2px solid ${T.rose}` : "2px solid transparent", color: tab === t.key ? T.rose : T.muted, fontSize: bp.isDesktop ? 14 : 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === "overview" && (
        <div>
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: bp.isDesktop ? 24 : 20, marginBottom: bp.isDesktop ? 20 : 16 }}>
            <div style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.rose, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 16 }}>WHY YOU'RE COMPATIBLE</div>
            <div style={{ fontSize: bp.isDesktop ? 14 : 15, color: T.text, lineHeight: 1.7, marginBottom: 16 }}>
              Based on your interview responses, you and {m.name} share remarkable alignment across multiple dimensions. Your {m.personalityScore}% personality compatibility suggests natural conversational flow and mutual understanding of each other's communication styles.
            </div>
            <div style={{ fontSize: bp.isDesktop ? 14 : 15, color: T.text, lineHeight: 1.7 }}>
              With {m.valuesScore}% values alignment, you both prioritize similar life goals and relationship dynamics. The traits you share—{m.traits.slice(0, 2).join(' and ')}—create a strong foundation for emotional connection and long-term compatibility.
            </div>
          </div>

          <div style={{ background: `${T.rose}08`, border: `1px solid ${T.rose}20`, borderRadius: 8, padding: bp.isDesktop ? 20 : 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={T.rose} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
              <div style={{ fontSize: bp.isDesktop ? 14 : 15, fontWeight: 700, color: T.rose }}>AI Insight</div>
            </div>
            <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.text, lineHeight: 1.6 }}>
              Your compatibility score of {m.compatibility}% places you in the top tier of matches. This suggests strong potential for both immediate chemistry and long-term relationship success. Focus on authentic conversation and let your natural compatibility shine through.
            </div>
          </div>
        </div>
      )}

      {tab === "talking-points" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {talkingPoints.map((point, i) => (
            <div key={i} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: bp.isDesktop ? 20 : 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                <div style={{ width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {point.iconSvg}
                </div>
                <h4 style={{ fontSize: bp.isDesktop ? 16 : 16, fontWeight: 800, color: T.text }}>{point.topic}</h4>
              </div>
              <p style={{ fontSize: bp.isDesktop ? 14 : 15, color: T.muted, lineHeight: 1.6 }}>
                {point.description}
              </p>
            </div>
          ))}
        </div>
      )}

      {tab === "tips" && (
        <div>
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: bp.isDesktop ? 24 : 20, marginBottom: 20 }}>
            <div style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.rose, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 16 }}>PERSONALIZED RECOMMENDATIONS</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {recommendations.map((rec, i) => (
                <div key={i} style={{ paddingBottom: i < recommendations.length - 1 ? 16 : 0, borderBottom: i < recommendations.length - 1 ? `1px solid ${T.border}` : "none" }}>
                  <div style={{ fontSize: bp.isDesktop ? 15 : 15, fontWeight: 700, color: T.text, marginBottom: 8 }}>{i + 1}. {rec.title}</div>
                  <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, lineHeight: 1.6 }}>{rec.tip}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: `${T.rose}08`, border: `1px solid ${T.rose}20`, borderRadius: 8, padding: bp.isDesktop ? 20 : 16 }}>
            <div style={{ fontSize: bp.isDesktop ? 13 : 14, fontWeight: 700, color: T.rose, marginBottom: 8 }}>Remember</div>
            <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.text, lineHeight: 1.6 }}>
              Your {m.compatibility}% compatibility is a strong indicator, but authentic connection happens in the moment. Be yourself, stay curious, and enjoy getting to know {m.name}.
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {cancelModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 20 }}>
          <div style={{ background: T.card, borderRadius: 12, padding: bp.isDesktop ? 32 : 24, maxWidth: 480, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <h3 style={{ fontSize: bp.isDesktop ? 22 : 20, fontWeight: 800, color: T.text, marginBottom: 12 }}>Cancel Date</h3>
            <p style={{ fontSize: bp.isDesktop ? 14 : 15, color: T.muted, marginBottom: 20, lineHeight: 1.6 }}>
              Let {m.name} know why you need to cancel. They'll be notified immediately.
            </p>
            
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 8 }}>Reason for cancellation</label>
              <textarea
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                placeholder="e.g., Something came up unexpectedly..."
                style={{
                  width: "100%",
                  minHeight: 100,
                  padding: "12px 14px",
                  borderRadius: 8,
                  border: `1px solid ${T.border}`,
                  background: T.bg,
                  color: T.text,
                  fontSize: 15,
                  fontFamily: "inherit",
                  resize: "vertical"
                }}
              />
            </div>

            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button onClick={() => { setCancelModal(false); setCancelReason(''); }} style={{ padding: "10px 20px", borderRadius: 8, background: T.card, border: `1px solid ${T.border}`, color: T.text, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                Keep Date
              </button>
              <button onClick={confirmCancel} disabled={!cancelReason} style={{ padding: "10px 20px", borderRadius: 8, background: !cancelReason ? T.muted : "#ef4444", border: "none", color: "#fff", fontSize: 15, fontWeight: 600, cursor: !cancelReason ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: !cancelReason ? 0.5 : 1 }}>
                Cancel Date
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {rescheduleModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 20 }}>
          <div style={{ background: T.card, borderRadius: 12, padding: bp.isDesktop ? 32 : 24, maxWidth: 480, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <h3 style={{ fontSize: bp.isDesktop ? 22 : 20, fontWeight: 800, color: T.text, marginBottom: 12 }}>Reschedule Date</h3>
            
            {rescheduleStep === 'reason' && (
              <>
                <p style={{ fontSize: bp.isDesktop ? 14 : 15, color: T.muted, marginBottom: 20, lineHeight: 1.6 }}>
                  Let {m.name} know why you need to reschedule. You can suggest a new time after.
                </p>
                
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 8 }}>Reason for rescheduling</label>
                  <textarea
                    value={rescheduleReason}
                    onChange={e => setRescheduleReason(e.target.value)}
                    placeholder="e.g., Work meeting got moved to that time..."
                    style={{
                      width: "100%",
                      minHeight: 100,
                      padding: "12px 14px",
                      borderRadius: 8,
                      border: `1px solid ${T.border}`,
                      background: T.bg,
                      color: T.text,
                      fontSize: 15,
                      fontFamily: "inherit",
                      resize: "vertical"
                    }}
                  />
                </div>

                <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                  <button onClick={() => { setRescheduleModal(false); setRescheduleReason(''); }} style={{ padding: "10px 20px", borderRadius: 8, background: T.card, border: `1px solid ${T.border}`, color: T.text, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                    Cancel
                  </button>
                  <button onClick={() => setRescheduleStep('datetime')} disabled={!rescheduleReason} style={{ padding: "10px 20px", borderRadius: 8, background: !rescheduleReason ? T.muted : T.rose, border: "none", color: "#fff", fontSize: 15, fontWeight: 600, cursor: !rescheduleReason ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: !rescheduleReason ? 0.5 : 1 }}>
                    Continue
                  </button>
                </div>
              </>
            )}

            {rescheduleStep === 'datetime' && (
              <>
                <p style={{ fontSize: bp.isDesktop ? 14 : 15, color: T.muted, marginBottom: 20, lineHeight: 1.6 }}>
                  Suggest a new date and time for your date with {m.name}.
                </p>
                
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 8 }}>New Date</label>
                  <input
                    type="date"
                    value={rescheduleData.date}
                    onChange={e => setRescheduleData(prev => ({ ...prev, date: e.target.value }))}
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      borderRadius: 8,
                      border: `1px solid ${T.border}`,
                      background: T.bg,
                      color: T.text,
                      fontSize: 15,
                      fontFamily: "inherit"
                    }}
                  />
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 8 }}>New Time</label>
                  <input
                    type="time"
                    value={rescheduleData.time}
                    onChange={e => setRescheduleData(prev => ({ ...prev, time: e.target.value }))}
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      borderRadius: 8,
                      border: `1px solid ${T.border}`,
                      background: T.bg,
                      color: T.text,
                      fontSize: 15,
                      fontFamily: "inherit"
                    }}
                  />
                </div>

                <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                  <button onClick={() => setRescheduleStep('reason')} style={{ padding: "10px 20px", borderRadius: 8, background: T.card, border: `1px solid ${T.border}`, color: T.text, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                    Back
                  </button>
                  <button onClick={confirmReschedule} disabled={!rescheduleData.date || !rescheduleData.time} style={{ padding: "10px 20px", borderRadius: 8, background: (!rescheduleData.date || !rescheduleData.time) ? T.muted : T.rose, border: "none", color: "#fff", fontSize: 15, fontWeight: 600, cursor: (!rescheduleData.date || !rescheduleData.time) ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: (!rescheduleData.date || !rescheduleData.time) ? 0.5 : 1 }}>
                    Send Request
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {ddShowUpgradeModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 20, animation: "fadeIn 0.3s ease", overflow: "auto" }} onClick={() => setDdShowUpgradeModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: T.card, borderRadius: 12, padding: bp.isDesktop ? 32 : 24, maxWidth: 500, width: "100%", maxHeight: "calc(100vh - 40px)", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", animation: "modalSlideUp 0.3s ease" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: `linear-gradient(135deg, ${T.rose}, ${T.violet})`, display: "flex", alignItems: "center", justifyContent: "center", animation: "upgradePulse 2s ease-in-out infinite" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 2 }}>Upgrade to Premium</h3>
                <p style={{ fontSize: 14, color: T.muted }}>Unlock {ddUpgradeFeature}</p>
              </div>
              <button onClick={() => setDdShowUpgradeModal(false)} style={{ width: 32, height: 32, borderRadius: "50%", border: "none", background: T.bg, color: T.muted, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <div style={{ marginBottom: 24 }}>
              <div style={{ background: `linear-gradient(135deg, ${T.rose}10, ${T.violet}10)`, border: `1px solid ${T.rose}25`, borderRadius: 8, padding: 20, marginBottom: 16 }}>
                <h4 style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 12 }}>Premium Features Include:</h4>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                    <span style={{ fontSize: 14, color: T.text }}>Unlimited messaging with matches</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                    <span style={{ fontSize: 14, color: T.text }}>Share phone numbers securely</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                    <span style={{ fontSize: 14, color: T.text }}>In-app voice & video calls</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                    <span style={{ fontSize: 14, color: T.text }}>Send photos & media</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                    <span style={{ fontSize: 14, color: T.text }}>Priority customer support</span>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={() => setDdShowUpgradeModal(false)} style={{ flex: 1, padding: "12px", borderRadius: 8, background: "transparent", border: `1px solid ${T.border}`, color: T.muted, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                Maybe Later
              </button>
              <button onClick={() => { setDdShowUpgradeModal(false); onNavigateToUpgrade && onNavigateToUpgrade(); }} style={{ flex: 2, padding: "12px", borderRadius: 8, background: `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, border: "none", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", animation: "upgradePulse 2s ease-in-out infinite" }}>
                View Plans
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Modal */}
      {ddShowReportModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 20, animation: "fadeIn 0.3s ease", overflow: "auto" }} onClick={() => setDdShowReportModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: T.card, borderRadius: 12, padding: bp.isDesktop ? 32 : 24, maxWidth: 500, width: "100%", maxHeight: "calc(100vh - 40px)", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", animation: "modalSlideUp 0.3s ease" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 2 }}>Report {m.name}</h3>
                <p style={{ fontSize: 14, color: T.muted }}>Help us keep the community safe</p>
              </div>
              <button onClick={() => setDdShowReportModal(false)} style={{ width: 32, height: 32, borderRadius: "50%", border: "none", background: T.bg, color: T.muted, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {ddReportSubmitted ? (
              <div style={{ textAlign: "center", padding: "40px 20px" }}>
                <div style={{ width: 60, height: 60, borderRadius: "50%", background: "rgba(34,197,94,0.1)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                </div>
                <h4 style={{ fontSize: 17, fontWeight: 700, color: T.text, marginBottom: 8 }}>Report Submitted</h4>
                <p style={{ fontSize: 14, color: T.muted }}>Thank you for helping keep our community safe. We'll review this report shortly.</p>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 8 }}>Why are you reporting this user?</label>
                  <textarea
                    value={ddReportReason}
                    onChange={e => setDdReportReason(e.target.value)}
                    placeholder="Please provide details about why you're reporting this user..."
                    style={{ width: "100%", minHeight: 120, padding: 12, borderRadius: 8, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 15, fontFamily: "inherit", resize: "vertical" }}
                  />
                  <p style={{ fontSize: 13, color: T.muted, marginTop: 8 }}>Common reasons: inappropriate behavior, harassment, fake profile, spam, safety concerns</p>
                </div>

                <div style={{ display: "flex", gap: 12 }}>
                  <button onClick={() => setDdShowReportModal(false)} style={{ flex: 1, padding: "12px", borderRadius: 8, background: "transparent", border: `1px solid ${T.border}`, color: T.muted, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                    Cancel
                  </button>
                  <button onClick={handleDdReportSubmit} disabled={!ddReportReason.trim()} style={{ flex: 1, padding: "12px", borderRadius: 8, background: ddReportReason.trim() ? "#ef4444" : T.dim, border: "none", color: "#fff", fontSize: 15, fontWeight: 700, cursor: ddReportReason.trim() ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
                    Submit Report
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Connect Flyout Panel */}
      <ConnectFlyout
        isOpen={ddConnectFlyoutOpen}
        onClose={() => setDdConnectFlyoutOpen(false)}
        triggerRect={ddConnectFlyoutRect}
        activeAction={ddConnectFlyoutAction}
        expandReceived={ddConnectFlyoutExpandReceived}
        matchName={m.name}
        bp={bp}
        sentGiftReaction={ddSentGift}
        setSentGiftReaction={setDdSentGift}
        pollQuestion={ddPollQ}
        setPollQuestion={setDdPollQ}
        pollOptions={ddPollOpts}
        setPollOptions={setDdPollOpts}
        pollSent={ddPollSent}
        setPollSent={setDdPollSent}
        pollResult={ddPollResult}
        setPollResult={setDdPollResult}
        phoneNumber={ddPhone}
        setPhoneNumber={setDdPhone}
        phoneSent={ddPhoneSent}
        setPhoneSent={setDdPhoneSent}
        isPremium={ddIsPremium}
        onPremiumClick={handleDdPremiumFeatureClick}
        receivedGift={ddReceivedGift}
        receivedPoll={ddReceivedPoll}
        receivedPollResponse={ddPollResponse}
        setReceivedPollResponse={setDdPollResponse}
        receivedPhone={ddReceivedPhone}
        receivedMessage={ddReceivedMessage}
      />
    </div>
  );
}

// ─── SCREEN 5: Voice Chat ─────────────────────────────────────
function VoiceChat({ match, onBack, blindMode = false }) {
  const [messages, setMessages] = useState([
    { role: "ai", text: `I've introduced you to ${match.name}. Your compatibility is ${match.compatibility}%. Based on your profiles, ask about travel — it's something you both deeply value. Tap the orb to send a voice message, or type below.` }
  ]);
  const [orbState, setOrbState] = useState("idle");
  const [liveT, setLiveT] = useState("");
  const [textInput, setTextInput] = useState("");
  const [inputMode, setInputMode] = useState("voice");
  const endRef = useRef(null);

  const { speak, speaking, stop } = useSpeechSynthesis();
  const { start, stop: stopRec, listening, transcript } = useSpeechRecognition();

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  useEffect(() => { if (speaking) setOrbState("speaking"); }, [speaking]);
  useEffect(() => { setLiveT(transcript); }, [transcript]);

  // Auto-read first AI message
  useEffect(() => {
    setTimeout(() => {
      speak(messages[0].text, () => setOrbState("idle"));
    }, 500);
  }, []);

  const handleOrbTap = () => {
    if (orbState === "speaking") { stop(); setOrbState("idle"); return; }
    if (orbState === "listening") {
      stopRec();
      if (liveT.trim()) sendMessage(liveT);
      setOrbState("idle");
    } else {
      setLiveT("");
      setOrbState("listening");
      start(t => setLiveT(t), () => {
        if (liveT.trim()) sendMessage(liveT);
        setOrbState("idle");
      });
    }
  };

  const sendMessage = (text) => {
    if (!text.trim()) return;
    setMessages(prev => [...prev, { role: "user", text }]);
    setLiveT("");
    setOrbState("thinking");
    setTimeout(() => {
      const reply = "That sounds amazing — I'd love to explore that together. Maybe over coffee sometime? ☕";
      setMessages(prev => [...prev, { role: "match", text: reply, name: match.name }]);
      setOrbState("speaking");
      speak(reply, () => setOrbState("idle"));
    }, 1400);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", maxWidth: 480, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 12, background: T.dark, backdropFilter: "blur(10px)", flexShrink: 0 }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: T.muted, fontSize: 22, cursor: "pointer" }}>←</button>
        {blindMode ? (
          <div style={{ width: 40, height: 40, borderRadius: "50%", background: `linear-gradient(135deg, ${T.rose}, ${T.violet})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: `2px solid ${T.rose}` }}>
            <span style={{ fontSize: 17, color: "#fff" }}>♥</span>
          </div>
        ) : (
          <img src={match.photo} style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }} alt="" />
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{match.name}</div>
          <div style={{ fontSize: 13, color: T.teal }}>✦ {match.compatibility}% match</div>
        </div>
        <div style={{ opacity: orbState === "speaking" ? 1 : 0, transition: "opacity 0.3s" }}>
          <Waveform active={orbState === "speaking"} bars={14} color={T.teal} />
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
        {messages.map((msg, i) => (
          <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start", animation: "fadeIn 0.3s ease" }}>
            {msg.role === "ai" && (
              <div style={{ background: `linear-gradient(135deg, ${T.roseSoft}, rgba(20,184,166,0.08))`, border: `1px solid ${T.rose}25`, borderRadius: "4px 16px 16px 16px", padding: "12px 16px", maxWidth: "85%", fontSize: 14, color: T.text, lineHeight: 1.7 }}>
                <div style={{ fontSize: 12, color: T.rose, fontWeight: 700, marginBottom: 5, letterSpacing: "0.08em" }}>✦ SETTUP COACHING</div>
                {msg.text}
              </div>
            )}
            {msg.role === "user" && (
              <div style={{ background: `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, borderRadius: "16px 4px 16px 16px", padding: "12px 16px", maxWidth: "75%", fontSize: 15, color: "#fff", lineHeight: 1.6 }}>
                {msg.text}
              </div>
            )}
            {msg.role === "match" && (
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end", maxWidth: "80%" }}>
                {blindMode ? (
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: `linear-gradient(135deg, ${T.rose}, ${T.violet})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: `1.5px solid ${T.rose}` }}>
                    <span style={{ fontSize: 13, color: "#fff" }}>♥</span>
                  </div>
                ) : (
                  <img src={match.photo} style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} alt="" />
                )}
                <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: "4px 16px 16px 16px", padding: "12px 16px", fontSize: 15, color: T.text, lineHeight: 1.6 }}>
                  {msg.text}
                </div>
              </div>
            )}
          </div>
        ))}
        {liveT && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <div style={{ background: `${T.rose}25`, border: `1px solid ${T.rose}40`, borderRadius: "16px 4px 16px 16px", padding: "10px 14px", maxWidth: "75%", fontSize: 15, color: T.muted, fontStyle: "italic" }}>
              {liveT}...
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Voice orb input area */}
      <div style={{ padding: "12px 18px 24px", borderTop: `1px solid ${T.border}`, background: T.dark, flexShrink: 0 }}>
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 14 }}>
          {["voice", "text"].map(m => (
            <button key={m} onClick={() => setInputMode(m)} style={{ padding: "5px 14px", borderRadius: 8, border: `1px solid ${inputMode === m ? T.rose : T.border}`, background: inputMode === m ? T.roseSoft : "transparent", color: inputMode === m ? T.rose : T.muted, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}>
              {m === "voice" ? "🎙 Voice" : "⌨ Type"}
            </button>
          ))}
        </div>

        {inputMode === "voice" ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
            <div onClick={handleOrbTap} style={{ cursor: "pointer" }}>
              <VoiceOrb state={orbState} size={80} />
            </div>
            <div style={{ height: 16 }} />
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, alignItems: "center", background: T.card, border: `1px solid ${T.border}`, borderRadius: 25, padding: "8px 8px 8px 16px" }}>
            <input value={textInput} onChange={e => setTextInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { sendMessage(textInput); setTextInput(""); }}}
              placeholder="Message..." style={{ flex: 1, background: "none", border: "none", outline: "none", color: T.text, fontSize: 16, fontFamily: "inherit" }} />
            <button onClick={() => { sendMessage(textInput); setTextInput(""); }} style={{ width: 38, height: 38, borderRadius: "50%", background: textInput.trim() ? `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})` : T.dim, border: "none", cursor: "pointer", color: "#fff", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center" }}>↑</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Helper: Get State Abbreviation ──────────────────────────────
function getStateAbbreviation(stateName) {
  const stateMap = {
    'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
    'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
    'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
    'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
    'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
    'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
    'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
    'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
    'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
    'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY'
  };
  return stateMap[stateName] || stateName;
}

// ─── App Navigation (Responsive) ──────────────────────────────
function AppNav({ screen, onNav, onLogout }) {
  const bp = useBreakpoint();
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [currentCity, setCurrentCity] = useState(() => {
    return localStorage.getItem("soul_user_location") || "Los Angeles, CA";
  });
  const [tempCity, setTempCity] = useState("");
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [appSwitcherOpen, setAppSwitcherOpen] = useState(false);
  const [activeApp, setActiveApp] = useState("dating");

  useEffect(() => {
    const handler = () => setAppSwitcherOpen(true);
    window.addEventListener('openAppSwitcher', handler);
    return () => window.removeEventListener('openAppSwitcher', handler);
  }, []);
  const appVariants = [
    { id: "dating", subtitle: "COMPATIBILITY AI MATCHMAKING", desc: "Find your perfect match through deep compatibility analysis", grad1: T.rose, grad2: T.violet,
      iconPaths: <><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" /></> },
    { id: "friendships", subtitle: "MAKE MEANINGFUL FRIENDSHIPS", desc: "Connect with like-minded people for genuine friendships", grad1: "#ffb400", grad2: "#ff8c00",
      iconPaths: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></> },
    { id: "professional", subtitle: "PROFESSIONAL VERTICAL CONNECTIONS", desc: "Build your professional network with aligned career minds", grad1: "#0096FF", grad2: "#00d4ff",
      iconPaths: <><path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /><rect width="20" height="14" x="2" y="6" rx="2" /></> },
    { id: "marketplace", subtitle: "BUY & SELL MARKETPLACE", desc: "A marketplace to buy and sell with trusted connections", grad1: "#00BB77", grad2: "#00e68a",
      iconPaths: <><path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7" /><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" /><path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4" /><rect width="20" height="5" x="2" y="7" rx="1" /></> },
  ];
  const activeAppData = appVariants.find(a => a.id === activeApp) || appVariants[0];
  const appModalBackdropRef = useRef(null);
  const appModalPanelRef = useRef(null);
  const appModalHeaderRef = useRef(null);
  const appModalCardsRef = useRef([]);
  const appModalTlRef = useRef(null);
  const setAppCardRef = (i) => (el) => { if (el) appModalCardsRef.current[i] = el; };

  useLayoutEffect(() => {
    if (!appModalPanelRef.current || !appModalBackdropRef.current) return;
    if (appModalTlRef.current) { appModalTlRef.current.kill(); appModalTlRef.current = null; }
    const tl = gsap.timeline({ paused: true, onReverseComplete: () => setAppSwitcherOpen(false) });
    tl.fromTo(appModalBackdropRef.current, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: "power2.out" }, 0);
    tl.fromTo(appModalPanelRef.current, { scale: 0.08, opacity: 0, y: 40 }, { scale: 1, opacity: 1, y: 0, duration: 0.45, ease: "back.out(1.4)" }, 0.05);
    if (appModalHeaderRef.current) {
      tl.fromTo(appModalHeaderRef.current, { y: 16, opacity: 0 }, { y: 0, opacity: 1, duration: 0.3, ease: "power3.out" }, 0.22);
    }
    const validCards = appModalCardsRef.current.filter(Boolean);
    if (validCards.length) {
      tl.fromTo(validCards, { y: 30, opacity: 0, scale: 0.92 }, { y: 0, opacity: 1, scale: 1, duration: 0.38, ease: "power3.out", stagger: 0.1 }, 0.3);
    }
    appModalTlRef.current = tl;
    if (appSwitcherOpen) tl.play(0);
    return () => { tl.kill(); appModalTlRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSwitcherOpen]);

  const handleAppModalClose = () => {
    if (appModalTlRef.current) {
      appModalTlRef.current.eventCallback('onReverseComplete', () => setAppSwitcherOpen(false));
      appModalTlRef.current.reverse();
    } else {
      setAppSwitcherOpen(false);
    }
  };

  const handleGetLocation = async () => {
    setLoadingLocation(true);
    
    if ("geolocation" in navigator) {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true, // Use GPS + WiFi + Cellular for best accuracy
            timeout: 10000,
            maximumAge: 0
          });
        });

        const { latitude, longitude } = position.coords;
        
        // Reverse geocode to get city name
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await response.json();
          
          // Extract location components with multiple fallbacks
          const city = data.address.city || 
                      data.address.town || 
                      data.address.village || 
                      data.address.municipality || 
                      data.address.county || "";
          const state = data.address.state || "";
          const stateAbbr = state ? getStateAbbreviation(state) : "";
          
          // Always prefer city, state format with abbreviation
          let locationString;
          if (city && stateAbbr) {
            locationString = `${city}, ${stateAbbr}`;
          } else if (city) {
            locationString = city;
          } else if (stateAbbr) {
            locationString = stateAbbr;
          } else {
            // If all else fails, use a generic message
            locationString = "Location detected";
            alert("We detected your location but couldn't determine the city name. Please enter it manually.");
          }
          
          setTempCity(locationString);
          setCurrentCity(locationString);
          localStorage.setItem("soul_user_location", locationString);
          saveDatingPreferences({ user_location: locationString });
          setShowLocationModal(false);
        } catch (geocodeError) {
          alert("Unable to determine city name from your location. Please enter it manually.");
          console.error("Geocoding error:", geocodeError);
          setLoadingLocation(false);
          return;
        }
      } catch (error) {
        console.error("Geolocation error:", error);
        // Allow manual entry on failure
        setShowLocationModal(true);
      }
    } else {
      // No geolocation — allow manual entry
      setShowLocationModal(true);
    }
    
    setLoadingLocation(false);
  };
  
  if (["landing", "interview"].includes(screen)) return null;
  const tabs = [
    { id: "city", Icon: AnimMapPinIcon, label: "My City", isSpecial: true },
    { id: "profile",  Icon: AnimDashboardIcon, label: "Dashboard"  },
    { id: "myprofile", Icon: AnimUserIcon, label: "Profile" },
    { id: "matches",  Icon: AnimSparklesIcon, label: "Matches"  },
    { id: "calendar", Icon: AnimCalendarIcon, label: "Calendar" },
    { id: "settings", Icon: AnimSettingsIcon, label: "Settings" },
  ];
  const isMobile = !bp.isDesktop && !bp.isTablet;
  const activeId = screen === "detail" ? "matches" : screen === "chat" ? "messages" : screen;
  const navBg = T.bg === "#fafafa" ? "#ffffff" : "#080610";

  // ── Desktop: left sidebar ──────────────────────────────────────
  if (bp.isDesktop) {
    return (
      <nav style={{ position: "fixed", top: 0, left: 0, bottom: 0, width: 300, background: navBg, borderRight: `1px solid ${T.border}`, backdropFilter: "blur(20px)", display: "flex", flexDirection: "column", zIndex: 100, padding: "28px 24px" }}>
        <div style={{ marginBottom: 32, paddingLeft: 8 }}>
          <div onClick={() => setAppSwitcherOpen(true)} style={{ cursor: "pointer", transition: "opacity 0.2s" }}
            onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}>
            <div style={{ fontSize: 28, fontWeight: 900, fontFamily: "'MBF Canno', 'Segoe UI', sans-serif", background: `linear-gradient(90deg, ${activeAppData.grad1}, ${activeAppData.grad2}, ${activeAppData.grad1})`, backgroundSize: "300% 100%", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "aurora 6s ease-in-out infinite", textTransform: "lowercase", padding: "4px 0", overflow: "visible", lineHeight: 1.1 }}>settup</div>
            <div style={{ fontSize: 12, color: T.muted, letterSpacing: "0.1em", fontWeight: 700 }}>{activeAppData.subtitle}</div>
          </div>
        </div>
        {appSwitcherOpen && ReactDOM.createPortal(
          <div style={{ position: "fixed", inset: 0, zIndex: 2147483647, fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
            <div ref={appModalBackdropRef} onClick={handleAppModalClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(8px)" }} />
            <div ref={appModalPanelRef} style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 420, maxWidth: "92vw", background: T.card, borderRadius: 20, border: `1px solid ${T.border}`, boxShadow: "0 24px 80px rgba(0,0,0,0.25)", padding: "32px 28px", transformOrigin: "50% 50%" }}>
              <div ref={appModalHeaderRef}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
                  <div>
                    <div style={{ fontSize: 13, color: T.muted, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 4 }}>SWITCH APP</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: T.text }}>Choose your experience</div>
                  </div>
                  <button onClick={handleAppModalClose} style={{ width: 36, height: 36, borderRadius: "50%", border: `1px solid ${T.border}`, background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.2s", flexShrink: 0 }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = T.rose; e.currentTarget.style.background = T.roseSoft; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = T.bg; }}>
                    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                  </button>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {appVariants.map((app, i) => {
                  const isActive = app.id === activeApp;
                  return (
                    <div key={app.id} ref={setAppCardRef(i)} onClick={() => { setActiveApp(app.id); handleAppModalClose(); }}
                      style={{ padding: "18px 20px", borderRadius: 14, cursor: "pointer", background: isActive ? `${app.grad1}14` : T.bg, border: isActive ? `1.5px solid ${app.grad1}40` : `1.5px solid ${T.border}`, transition: "all 0.2s", position: "relative" }}
                      onMouseEnter={e => { if (!isActive) { e.currentTarget.style.borderColor = `${app.grad1}60`; e.currentTarget.style.background = `${app.grad1}0a`; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 8px 24px ${app.grad1}15`; } }}
                      onMouseLeave={e => { if (!isActive) { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = T.bg; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; } }}>
                      {isActive && <div style={{ position: "absolute", top: 16, right: 16, width: 24, height: 24, borderRadius: "50%", background: app.grad1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                      </div>}
                      <div style={{ display: "flex", alignItems: "flex-end", gap: 6, marginBottom: -8 }}>
                        <div style={{ display: "inline-flex", alignItems: "center", flexShrink: 0, marginBottom: 2 }}><svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={app.grad1} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ filter: `drop-shadow(0 0 3px ${app.grad1}40)` }}>{app.iconPaths}</svg></div>
                        <div style={{ fontSize: 28, fontWeight: 900, fontFamily: "'MBF Canno', 'Segoe UI', sans-serif", background: `linear-gradient(90deg, ${app.grad1}, ${app.grad2}, ${app.grad1})`, backgroundSize: "300% 100%", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "aurora 6s ease-in-out infinite", textTransform: "lowercase", padding: "2px 0", overflow: "visible", lineHeight: 1.2 }}>settup</div>
                      </div>
                      <div style={{ fontSize: 11, color: isActive ? app.grad1 : T.muted, letterSpacing: "0.1em", fontWeight: 700, marginTop: 8 }}>{app.subtitle}</div>
                      <div style={{ fontSize: 13, color: T.muted, marginTop: 6, lineHeight: 1.5 }}>{app.desc}</div>
                      {app.id !== "dating" && <div style={{ display: "inline-block", marginTop: 8, padding: "3px 10px", borderRadius: 20, background: `${app.grad1}15`, color: app.grad1, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em" }}>COMING SOON</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body
        )}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
          {tabs.map(t => {
            if (t.isSpecial) {
              // Special location menu item - navigates to settings
              return (
                <div key={t.id} style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 13, color: T.muted, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 6, paddingLeft: 8 }}>LOCATION</div>
                  <div style={{ 
                    background: T.bg, 
                    border: `1px solid ${T.border}`, 
                    borderRadius: 6, 
                    padding: "12px 14px",
                    cursor: "pointer",
                    transition: "all 0.2s"
                  }}
                  onClick={() => onNav("settings")}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = T.rose; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <t.Icon size={16} color={T.rose} />
                      <div style={{ fontSize: 13, color: T.text }}>{currentCity}</div>
                    </div>
                  </div>
                </div>
              );
            }
            
            const isActive = activeId === t.id;
            return (
              <button key={t.id} onClick={() => onNav(t.id)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 6, background: isActive ? T.roseSoft : "transparent", border: isActive ? `1px solid ${T.borderHover}` : "1px solid transparent", cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s", textAlign: "left" }}>
                <t.Icon size={18} color={isActive ? T.rose : T.muted} style={{ filter: isActive ? `drop-shadow(0 0 6px ${T.rose})` : "none", transition: "all 0.2s" }} />
                <span style={{ fontSize: 15, fontWeight: isActive ? 700 : 500, color: isActive ? T.text : T.muted, transition: "all 0.2s" }}>{t.label}</span>
              </button>
            );
          })}
        </div>
        <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 12, marginBottom: 8 }}>
          {/* Admin Panel Button - only show if user is admin */}
          {(() => {
            try {
              const t = localStorage.getItem("soul_session_token");
              if (!t) return null;
              const payload = JSON.parse(atob(t.split('.')[1]));
              if (!payload.isAdmin) return null;
              return (
                <button onClick={() => onNav("admin")} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 6, background: "transparent", border: "1px solid transparent", cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s", width: "100%", textAlign: "left", marginBottom: 4 }}
                  onMouseEnter={e => { e.currentTarget.style.background = T.roseSoft; e.currentTarget.style.borderColor = T.borderHover; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "transparent"; }}>
                  <AnimLockIcon size={16} color={T.muted} />
                  <span style={{ fontSize: 15, fontWeight: 500, color: T.muted }}>Admin Panel</span>
                </button>
              );
            } catch {
              return null;
            }
          })()}
        </div>
        <div style={{ fontSize: 12, color: T.muted, paddingLeft: 8, lineHeight: 1.6 }}>© 2026 <span style={{ fontSize: 15 }}>settup.ai</span></div>
      </nav>
    );
  }

  // ── Tablet: top bar ────────────────────────────────────────────
  if (bp.isTablet) {
    return (
      <nav style={{ position: "fixed", top: 0, left: 0, right: 0, height: 56, background: navBg, borderBottom: `1px solid ${T.border}`, backdropFilter: "blur(20px)", display: "flex", alignItems: "center", zIndex: 100, padding: "0 24px", gap: 8 }}>
        <div onClick={() => setAppSwitcherOpen(true)} style={{ marginRight: "auto", fontSize: 24, fontWeight: 900, fontFamily: "'MBF Canno', 'Segoe UI', sans-serif", background: `linear-gradient(90deg, ${activeAppData.grad1}, ${activeAppData.grad2}, ${activeAppData.grad1})`, backgroundSize: "300% 100%", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "aurora 6s ease-in-out infinite", textTransform: "lowercase", padding: "4px 0", overflow: "visible", lineHeight: 1.1, cursor: "pointer", transition: "opacity 0.2s" }}
          onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}>settup</div>
        {appSwitcherOpen && ReactDOM.createPortal(
          <div style={{ position: "fixed", inset: 0, zIndex: 2147483647, fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
            <div ref={appModalBackdropRef} onClick={handleAppModalClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(8px)" }} />
            <div ref={appModalPanelRef} style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 420, maxWidth: "92vw", background: T.card, borderRadius: 20, border: `1px solid ${T.border}`, boxShadow: "0 24px 80px rgba(0,0,0,0.25)", padding: "32px 28px", transformOrigin: "50% 50%" }}>
              <div ref={appModalHeaderRef}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
                  <div>
                    <div style={{ fontSize: 13, color: T.muted, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 4 }}>SWITCH APP</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: T.text }}>Choose your experience</div>
                  </div>
                  <button onClick={handleAppModalClose} style={{ width: 36, height: 36, borderRadius: "50%", border: `1px solid ${T.border}`, background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.2s", flexShrink: 0 }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = T.rose; e.currentTarget.style.background = T.roseSoft; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = T.bg; }}>
                    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                  </button>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {appVariants.map((app, i) => {
                  const isActive = app.id === activeApp;
                  return (
                    <div key={app.id} ref={setAppCardRef(i)} onClick={() => { setActiveApp(app.id); handleAppModalClose(); }}
                      style={{ padding: "18px 20px", borderRadius: 14, cursor: "pointer", background: isActive ? `${app.grad1}14` : T.bg, border: isActive ? `1.5px solid ${app.grad1}40` : `1.5px solid ${T.border}`, transition: "all 0.2s", position: "relative" }}
                      onMouseEnter={e => { if (!isActive) { e.currentTarget.style.borderColor = `${app.grad1}60`; e.currentTarget.style.background = `${app.grad1}0a`; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 8px 24px ${app.grad1}15`; } }}
                      onMouseLeave={e => { if (!isActive) { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = T.bg; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; } }}>
                      {isActive && <div style={{ position: "absolute", top: 16, right: 16, width: 24, height: 24, borderRadius: "50%", background: app.grad1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                      </div>}
                      <div style={{ display: "flex", alignItems: "flex-end", gap: 6, marginBottom: -8 }}>
                        <div style={{ display: "inline-flex", alignItems: "center", flexShrink: 0, marginBottom: 2 }}><svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={app.grad1} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ filter: `drop-shadow(0 0 3px ${app.grad1}40)` }}>{app.iconPaths}</svg></div>
                        <div style={{ fontSize: 28, fontWeight: 900, fontFamily: "'MBF Canno', 'Segoe UI', sans-serif", background: `linear-gradient(90deg, ${app.grad1}, ${app.grad2}, ${app.grad1})`, backgroundSize: "300% 100%", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "aurora 6s ease-in-out infinite", textTransform: "lowercase", padding: "2px 0", overflow: "visible", lineHeight: 1.2 }}>settup</div>
                      </div>
                      <div style={{ fontSize: 11, color: isActive ? app.grad1 : T.muted, letterSpacing: "0.1em", fontWeight: 700, marginTop: 8 }}>{app.subtitle}</div>
                      <div style={{ fontSize: 13, color: T.muted, marginTop: 6, lineHeight: 1.5 }}>{app.desc}</div>
                      {app.id !== "dating" && <div style={{ display: "inline-block", marginTop: 8, padding: "3px 10px", borderRadius: 20, background: `${app.grad1}15`, color: app.grad1, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em" }}>COMING SOON</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body
        )}
        {tabs.map(t => {
          const isActive = activeId === t.id;
          return (
            <button key={t.id} onClick={() => onNav(t.id)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, background: isActive ? T.roseSoft : "transparent", border: isActive ? `1px solid ${T.borderHover}` : "1px solid transparent", cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}>
              <t.Icon size={15} color={isActive ? T.rose : T.muted} />
              <span style={{ fontSize: 14, fontWeight: isActive ? 700 : 500, color: isActive ? T.text : T.muted }}>{t.label}</span>
            </button>
          );
        })}
        <button onClick={onLogout} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, background: "transparent", border: `1px solid ${T.border}`, cursor: "pointer", fontFamily: "inherit", marginLeft: 4, transition: "all 0.2s", color: T.muted, fontSize: 14, fontWeight: 500 }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(239,68,68,0.4)"; e.currentTarget.style.color = "#ef4444"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.muted; }}>
          ↩ Log Out
        </button>
      </nav>
    );
  }

  // ── Mobile: bottom bar (portal to body to guarantee position:fixed) ──
  const mobileTabs = [
    { id: "profile",  Icon: AnimDashboardIcon, label: "Dashboard"  },
    { id: "matches",  Icon: AnimSparklesIcon, label: "Matches"  },
    { id: "calendar", Icon: AnimCalendarIcon, label: "Calendar" },
    { id: "myprofile", Icon: AnimUserIcon, label: "Profile" },
  ];
  return <>
    {ReactDOM.createPortal(
      <nav style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: navBg, borderTop: `1px solid ${T.border}`, display: "flex", zIndex: 2147483647, fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
        {mobileTabs.map(t => {
          const isActive = activeId === t.id;
          return (
            <button key={t.id} onClick={() => onNav(t.id)} style={{ flex: 1, padding: "11px 0 14px", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 3, transition: "none" }}>
              <t.Icon size={20} color={isActive ? T.rose : T.dim} style={{ transition: "all 0.2s", filter: isActive ? `drop-shadow(0 0 6px ${T.rose})` : "none" }} />
              <span style={{ fontSize: 11, color: isActive ? T.rose : T.dim, fontWeight: 700, letterSpacing: "0.06em" }}>{t.label.toUpperCase()}</span>
            </button>
          );
        })}
      </nav>,
      document.body
    )}
    {appSwitcherOpen && ReactDOM.createPortal(
      <div style={{ position: "fixed", inset: 0, zIndex: 2147483647, fontFamily: "'DM Sans', 'Segoe UI', sans-serif", display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}>
        <div ref={appModalBackdropRef} onClick={handleAppModalClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(8px)" }} />
        <div ref={appModalPanelRef} style={{ position: "relative", width: 420, maxWidth: "92vw", maxHeight: "90vh", overflowY: "auto", background: T.card, borderRadius: 20, border: `1px solid ${T.border}`, boxShadow: "0 24px 80px rgba(0,0,0,0.25)", padding: "24px 18px", transformOrigin: "50% 50%" }}>
          <div ref={appModalHeaderRef}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 3 }}>SWITCH APP</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>Choose your experience</div>
              </div>
              <button onClick={handleAppModalClose} style={{ width: 34, height: 34, borderRadius: "50%", border: `1px solid ${T.border}`, background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {appVariants.map((app, i) => {
              const isActive = app.id === activeApp;
              return (
                <div key={app.id} ref={setAppCardRef(i)} onClick={() => { setActiveApp(app.id); handleAppModalClose(); }}
                  style={{ padding: "12px 14px", borderRadius: 12, cursor: "pointer", background: isActive ? `${app.grad1}14` : T.bg, border: isActive ? `1.5px solid ${app.grad1}40` : `1.5px solid ${T.border}`, transition: "all 0.2s", position: "relative" }}>
                  {isActive && <div style={{ position: "absolute", top: 12, right: 12, width: 20, height: 20, borderRadius: "50%", background: app.grad1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                  </div>}
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 5, marginBottom: -6 }}>
                    <div style={{ display: "inline-flex", alignItems: "center", flexShrink: 0, marginBottom: 1 }}><svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={app.grad1} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ filter: `drop-shadow(0 0 3px ${app.grad1}40)` }}>{app.iconPaths}</svg></div>
                    <div style={{ fontSize: 20, fontWeight: 900, fontFamily: "'MBF Canno', 'Segoe UI', sans-serif", background: `linear-gradient(90deg, ${app.grad1}, ${app.grad2}, ${app.grad1})`, backgroundSize: "300% 100%", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "aurora 6s ease-in-out infinite", textTransform: "lowercase", padding: "2px 0", overflow: "visible", lineHeight: 1.2 }}>settup</div>
                  </div>
                  <div style={{ fontSize: 9, color: isActive ? app.grad1 : T.muted, letterSpacing: "0.1em", fontWeight: 700, marginTop: 6 }}>{app.subtitle}</div>
                  <div style={{ fontSize: 11, color: T.muted, marginTop: 3, lineHeight: 1.4 }}>{app.desc}</div>
                  {app.id !== "dating" && <div style={{ display: "inline-block", marginTop: 4, padding: "2px 8px", borderRadius: 20, background: `${app.grad1}15`, color: app.grad1, fontSize: 9, fontWeight: 700, letterSpacing: "0.06em" }}>COMING SOON</div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>,
      document.body
    )}
  </>;

  // Location Change Modal
  if (showLocationModal) {
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 2000, backdropFilter: "blur(8px)" }}>
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: 32, maxWidth: 400, width: "100%", boxShadow: `0 40px 120px rgba(0,0,0,0.4)` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
            <div>
              <h3 style={{ fontSize: 22, fontWeight: 800, color: T.text, fontFamily: "'Segoe UI', sans-serif" }}>Change Location</h3>
              <p style={{ fontSize: 14, color: T.muted, marginTop: 4 }}>Update your city for better matches</p>
            </div>
            <button onClick={() => setShowLocationModal(false)} style={{ background: T.dim, border: "none", color: T.muted, fontSize: 18, cursor: "pointer", fontFamily: "inherit", width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontSize: 13, color: T.muted, marginBottom: 4 }}>Current location</div>
            <div style={{ padding: "12px 16px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, fontSize: 15, fontWeight: 600 }}>
              📍 {currentCity}
            </div>
            
            {/* GPS Location Button */}
            <button 
              onClick={handleGetLocation}
              disabled={loadingLocation}
              style={{ 
                padding: "14px", 
                borderRadius: 6, 
                background: loadingLocation ? T.dim : `linear-gradient(135deg, ${T.teal}, ${T.teal}dd)`,
                color: "#fff", 
                fontSize: 15, 
                fontWeight: 700, 
                border: "none", 
                cursor: loadingLocation ? "wait" : "pointer", 
                fontFamily: "inherit",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                opacity: loadingLocation ? 0.6 : 1,
                transition: "all 0.2s"
              }}>
              📍 {loadingLocation ? "Getting your location..." : "Use My Current Location"}
            </button>
            
            <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "8px 0" }}>
              <div style={{ flex: 1, height: 1, background: T.border }} />
              <span style={{ fontSize: 13, color: T.muted, fontWeight: 600 }}>OR</span>
              <div style={{ flex: 1, height: 1, background: T.border }} />
            </div>
            
            <div style={{ fontSize: 13, color: T.muted, marginBottom: 4 }}>Enter location manually</div>
            <input 
              type="text" 
              placeholder="Enter city name..."
              value={tempCity}
              onChange={e => setTempCity(e.target.value)}
              style={{ 
                padding: "13px 16px", 
                borderRadius: 6, 
                background: T.bg, 
                border: `1px solid ${T.border}`, 
                color: T.text, 
                fontSize: 15, 
                fontFamily: "inherit", 
                outline: "none",
                width: "100%"
              }} />
            <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
              <button 
                onClick={() => setShowLocationModal(false)}
                style={{ 
                  flex: 1, 
                  padding: "14px", 
                  borderRadius: 6, 
                  background: "transparent", 
                  border: `1px solid ${T.border}`, 
                  color: T.muted, 
                  fontSize: 16, 
                  fontWeight: 600, 
                  cursor: "pointer", 
                  fontFamily: "inherit" 
                }}>
                Cancel
              </button>
              <button 
                onClick={() => {
                  if (tempCity.trim()) {
                    const location = tempCity.trim();
                    setCurrentCity(location);
                    localStorage.setItem("soul_user_location", location);
                    saveDatingPreferences({ user_location: location });
                    setTempCity("");
                    setShowLocationModal(false);
                  }
                }}
                disabled={!tempCity.trim()}
                style={{ 
                  flex: 1, 
                  padding: "14px", 
                  borderRadius: 6, 
                  background: tempCity.trim() ? `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})` : T.dim, 
                  color: "#fff", 
                  fontSize: 16, 
                  fontWeight: 700, 
                  border: "none", 
                  cursor: tempCity.trim() ? "pointer" : "not-allowed", 
                  fontFamily: "inherit", 
                  boxShadow: tempCity.trim() ? "0 4px 12px rgba(0,0,0,0.15)" : "none" 
                }}>
                Update Location
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

// ─── SampleQTypewriter: typewriter + blur-fade, with stagger support ───
function SampleQTypewriter({ questions = [], color = "#888", fontSize = 14, interval = 5000, staggerDelay = 0 }) {
  const [started, setStarted] = useState(false);
  const [qIdx, setQIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [phase, setPhase] = useState("typing"); // "typing" | "hold" | "out"
  const len = questions.length;
  const text = len > 0 ? questions[qIdx] : "";

  // Stagger: delay initial start per card
  useEffect(() => {
    const t = setTimeout(() => setStarted(true), staggerDelay);
    return () => clearTimeout(t);
  }, [staggerDelay]);

  // Typewriter: reveal one character at a time
  useEffect(() => {
    if (!started || len === 0 || phase !== "typing") return;
    if (charIdx >= text.length) { setPhase("hold"); return; }
    const t = setTimeout(() => setCharIdx(prev => prev + 1), 35);
    return () => clearTimeout(t);
  }, [started, phase, text, len, charIdx]);

  // Hold visible, then blur-out
  useEffect(() => {
    if (phase !== "hold") return;
    const holdTime = Math.max(interval - 2000, 1500);
    const t = setTimeout(() => setPhase("out"), holdTime);
    return () => clearTimeout(t);
  }, [phase, interval]);

  // After out phase (fade-out), advance to next question
  useEffect(() => {
    if (phase !== "out") return;
    const t = setTimeout(() => {
      setQIdx(prev => (prev + 1) % len);
      setCharIdx(0);
      setPhase("typing");
    }, 600);
    return () => clearTimeout(t);
  }, [phase, len]);

  if (len === 0 || !started) return <div style={{ height: "100%" }} />;

  const displayed = text.slice(0, charIdx);
  const showCursor = phase === "typing";

  return (
    <div style={{ height: "100%", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, animation: phase === "out" ? "qBlurOut 0.6s ease forwards" : "qBlurIn 0.4s ease", transition: "opacity 0.4s ease" }}>
        <span style={{ fontSize, color, fontStyle: "italic", fontWeight: 500, lineHeight: 1.6 }}>
          {'\u201c'}{displayed}
          {showCursor && (
            <span style={{ display: "inline-block", width: 2, height: "1em", background: color, marginLeft: 1, verticalAlign: "text-bottom", animation: "blink 0.8s step-end infinite", opacity: 0.7 }} />
          )}
          {!showCursor && '\u201d'}
        </span>
      </div>
    </div>
  );
}

// ─── SCREEN: Personality Snapshot (after Stage 1) ─────────────
function PersonalitySnapshot({ profile, onContinue }) {
  const [vis, setVis] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  useEffect(() => { setTimeout(() => setVis(true), 100); }, []);

  const { encouragement: enc, bigFive, mbti, mbtiProfile, zodiacSign, zodiacInfo, attachmentStyle } = profile;

  const BigFiveBar = ({ label, value, color }) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 13, color: T.muted }}>{label}</span>
        <span style={{ fontSize: 13, color, fontWeight: 700 }}>{value}</span>
      </div>
      <div style={{ height: 5, borderRadius: 3, background: T.dim }}>
        <div style={{ height: "100%", width: `${value}%`, background: `linear-gradient(90deg, ${color}, ${color}80)`, borderRadius: 3, boxShadow: `0 0 6px ${color}50`, transition: "width 1.2s ease" }} />
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", overflowY: "auto", padding: "32px 24px 120px", maxWidth: 520, margin: "0 auto" }}>
      <div style={{ opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(20px)", transition: "all 0.8s cubic-bezier(0.16,1,0.3,1)" }}>

        {/* Header badge */}
        <div style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 14px", borderRadius: 8, border: `1px solid ${T.rose}40`, background: T.roseSoft, marginBottom: 24 }}>
          <span style={{ width: 5, height: 5, borderRadius: "50%", background: T.rose, boxShadow: `0 0 6px ${T.rose}`, display: "inline-block" }} />
          <span style={{ fontSize: 12, color: T.rose, letterSpacing: "0.12em", fontWeight: 700 }}>YOUR SOUL PROFILE · STAGE 1 COMPLETE</span>
        </div>

        {/* Archetype hero */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ fontSize: 52, marginBottom: 10 }}>{enc.headline.split(" ")[0]}</div>
          <h1 style={{ fontSize: "clamp(26px,6vw,38px)", fontWeight: 900, fontFamily: "'Segoe UI', sans-serif", background: `linear-gradient(135deg, ${T.text}, ${T.rose})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", marginBottom: 12, lineHeight: 1.1 }}>
            {enc.headline.split(" ").slice(1).join(" ")}
          </h1>
          <p style={{ fontSize: 16, color: T.muted, lineHeight: 1.7, maxWidth: 380, margin: "0 auto" }}>{enc.tagline}</p>
        </div>

        {/* MBTI + Attachment cards */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "16px 14px", animation: "fadeIn 0.5s 0.2s both" }}>
            <div style={{ fontSize: 12, color: T.rose, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 6 }}>PERSONALITY TYPE</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: T.text, marginBottom: 4 }}>{mbti}</div>
            <div style={{ fontSize: 13, color: T.rose, fontWeight: 600, marginBottom: 6 }}>{mbtiProfile?.name}</div>
            <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.5 }}>{mbtiProfile?.description}</div>
          </div>
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "16px 14px", animation: "fadeIn 0.5s 0.3s both" }}>
            <div style={{ fontSize: 12, color: T.teal, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 6 }}>ATTACHMENT STYLE</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: T.text, marginBottom: 6 }}>{enc.attachmentLabel}</div>
            <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.5 }}>{enc.attachmentDescription}</div>
          </div>
        </div>

        {/* Zodiac card */}
        {zodiacSign && zodiacInfo && (
          <div style={{ background: `linear-gradient(135deg, rgba(139,92,246,0.12), rgba(244,63,94,0.08))`, border: `1px solid rgba(139,92,246,0.25)`, borderRadius: 8, padding: "16px 18px", marginBottom: 20, animation: "fadeIn 0.5s 0.4s both" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 22 }}>✦</span>
              <div>
                <div style={{ fontSize: 12, color: T.violet, fontWeight: 700, letterSpacing: "0.1em" }}>ASTROLOGICAL PROFILE</div>
                <div style={{ fontSize: 17, fontWeight: 800, color: T.text }}>{zodiacSign} · {zodiacInfo.keywords?.join(" · ")}</div>
              </div>
            </div>
            <p style={{ fontSize: 13, color: T.muted, lineHeight: 1.6 }}>{zodiacInfo.strengths} <span style={{ color: T.violet, fontSize: 13 }}>(10% of your compatibility score)</span></p>
          </div>
        )}

        {/* Big Five */}
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "18px 18px", marginBottom: 20, animation: "fadeIn 0.5s 0.5s both" }}>
          <div style={{ fontSize: 12, color: T.amber, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 14 }}>BIG FIVE PERSONALITY (OCEAN MODEL)</div>
          <BigFiveBar label="Openness to Experience"   value={bigFive.openness}          color={T.violet} />
          <BigFiveBar label="Conscientiousness"        value={bigFive.conscientiousness}  color={T.teal} />
          <BigFiveBar label="Extraversion"             value={bigFive.extraversion}       color={T.amber} />
          <BigFiveBar label="Agreeableness"            value={bigFive.agreeableness}      color={T.rose} />
          <BigFiveBar label="Emotional Stability"      value={100 - bigFive.neuroticism}  color="#22c55e" />
          <p style={{ fontSize: 12, color: T.muted, opacity: 0.7, marginTop: 10, lineHeight: 1.5 }}>
            ✦ Preliminary profile from Stage 1 — deepens significantly as you complete more stages.
          </p>
        </div>

        {/* Teaser lines */}
        <div style={{ marginBottom: 28, animation: "fadeIn 0.5s 0.6s both" }}>
          {enc.teaser?.map((line, i) => (
            <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
              <span style={{ color: T.rose, fontSize: 15, flexShrink: 0, marginTop: 1 }}>✦</span>
              <p style={{ fontSize: 14, color: T.muted, lineHeight: 1.6 }}>{line}</p>
            </div>
          ))}
        </div>

        {/* Stage gate message */}
        <div style={{ background: `linear-gradient(135deg, ${T.roseSoft}, rgba(20,184,166,0.06))`, border: `1px solid ${T.rose}30`, borderRadius: 8, padding: "18px 18px", marginBottom: 28, animation: "fadeIn 0.5s 0.7s both" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 8 }}>🔓 Unlock Your Full Relationship Score</div>
          <p style={{ fontSize: 13, color: T.muted, lineHeight: 1.7, marginBottom: 0 }}>
            Create a free account to complete Stages 2–14, get your full <strong style={{ color: T.rose }}>relationship success probability</strong>, 
            access <strong style={{ color: T.teal }}>AI simulated relationship previews</strong>, and receive your curated matches.
          </p>
        </div>

        {/* Sign-up options */}
        {!showSignUp ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, animation: "fadeIn 0.5s 0.8s both" }}>
            <button onClick={() => setShowSignUp(true)} style={{ padding: "15px 24px", borderRadius: 50, background: `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, color: "#fff", fontSize: 16, fontWeight: 700, border: "none", cursor: "pointer", boxShadow: "0 4px 14px rgba(0,0,0,0.15)", fontFamily: "inherit", transition: "all 0.2s" }}
              onMouseEnter={e => e.target.style.transform = "translateY(-2px)"}
              onMouseLeave={e => e.target.style.transform = "translateY(0)"}>
              Create Free Account — Save My Profile
            </button>
            <button onClick={() => onContinue("guest")} style={{ padding: "13px 24px", borderRadius: 50, background: "transparent", color: T.muted, fontSize: 14, fontWeight: 600, border: `1px solid ${T.border}`, cursor: "pointer", fontFamily: "inherit" }}>
              Continue as Guest — See Preview Matches
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, animation: "fadeIn 0.3s ease both" }}>
            <div style={{ fontSize: 13, color: T.muted, textAlign: "center", marginBottom: 4 }}>Your Stage 1 profile will be saved automatically</div>
            <button onClick={() => onContinue("google")} style={{ padding: "13px 20px", borderRadius: 50, background: "#fff", color: "#1a1a1a", fontSize: 15, fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
              <span style={{ fontSize: 17, fontWeight: 900, color: "#4285F4" }}>G</span> Continue with Google
            </button>
            <button onClick={() => onContinue("facebook")} style={{ padding: "13px 20px", borderRadius: 50, background: "#1877F2", color: "#fff", fontSize: 15, fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
              <span style={{ fontSize: 17, fontWeight: 900 }}>f</span> Continue with Facebook
            </button>
            <button onClick={() => onContinue("instagram")} style={{ padding: "13px 20px", borderRadius: 50, background: "linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)", color: "#fff", fontSize: 15, fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
              <span style={{ fontSize: 16 }}>📷</span> Continue with Instagram
            </button>
            <button onClick={() => { setShowSignUp(false); onContinue("guest"); }} style={{ padding: "11px 20px", borderRadius: 50, background: "transparent", color: T.dim, fontSize: 13, border: "none", cursor: "pointer", fontFamily: "inherit" }}>
              Skip for now
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Stage Ring (per-stage progress circle) ─────────────────
function StageRing({ value, size, color, children, glow, dotColor }) {
  const stroke = size > 80 ? 8 : size > 60 ? 6 : 4;
  const r = (size - stroke * 2) / 2;
  const circ = 2 * Math.PI * r;
  const [visible, setVisible] = React.useState(false);
  const ringRef = React.useRef(null);
  React.useEffect(() => {
    const el = ringRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  const offset = visible ? circ - (value / 100) * circ : circ;
  const dotAngle = ((visible ? value : 0) / 100) * 360 - 90;
  const dotRad = (dotAngle * Math.PI) / 180;
  const dotX = size / 2 + r * Math.cos(dotRad);
  const dotY = size / 2 + r * Math.sin(dotRad);
  const dotR = stroke > 5 ? 5 : 3.5;
  return (
    <div ref={ringRef} style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)", position: "absolute", inset: 0 }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={`${T.dim}`} strokeWidth={stroke} opacity={0.5} />
        {value > 0 && (
          <>
            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
              strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
              style={{ transition: visible ? `stroke-dashoffset 1.4s cubic-bezier(0.22, 1, 0.36, 1)` : "none", filter: `drop-shadow(0 0 ${size > 60 ? 4 : 2}px ${color}60)` }} />
          </>
        )}
      </svg>
      <svg width={size} height={size} style={{ position: "absolute", inset: 0 }}>
        <circle cx={dotX} cy={dotY} r={dotR} fill={dotColor || color} style={{ filter: `drop-shadow(0 0 3px ${(dotColor || color)}80)`, transition: visible ? "cx 1.4s cubic-bezier(0.22, 1, 0.36, 1), cy 1.4s cubic-bezier(0.22, 1, 0.36, 1)" : "none" }} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
        background: value === 100 && visible ? `radial-gradient(circle, ${color}22 0%, transparent 70%)` : "transparent",
        borderRadius: "50%",
        opacity: visible ? 1 : 0, transition: "opacity 0.4s ease 0.2s" }}>
        {children}
      </div>
    </div>
  );
}

// ─── Shared: Reusable Parallax Drag/Scroll Slider ──────────────
function ParallaxSlider({ items, itemWidth, itemGap, sliderHeight, nudgeCount, renderItem, arrowColor }) {
  const bp = useBreakpoint();
  const containerRef = React.useRef(null);
  const itemsRef = React.useRef([]);
  const stateRef = React.useRef({ scrollY: 0, y: 0, oldY: 0, speed: 0, isDragging: false, touchStart: 0, dragMoved: false });
  const rafRef = React.useRef(null);

  const totalW = items.length * (itemWidth + itemGap);
  const lerp = (a, b, t) => a * (1 - t) + b * t;
  const ac = arrowColor || T.muted;

  const dispose = React.useCallback(() => {
    const els = itemsRef.current;
    if (!els.length) return;
    const st = stateRef.current;
    els.forEach((el, i) => {
      if (!el) return;
      const x = i * (itemWidth + itemGap) + st.y;
      const skewX = -st.speed * 0.15;
      const rotate = st.speed * 0.008;
      const scale = 1 - Math.min(100, Math.abs(st.speed)) * 0.002;
      el.style.transform = `translateX(${x}px) skewX(${skewX}deg) rotate(${rotate}deg) scale(${scale})`;
    });
  }, [itemWidth, itemGap, totalW]);

  const clampScroll = React.useCallback(() => {
    const st = stateRef.current;
    const containerW = containerRef.current ? containerRef.current.clientWidth : 400;
    const minScroll = -(totalW - containerW + itemGap);
    if (st.scrollY > 0) st.scrollY = 0;
    if (st.scrollY < minScroll) st.scrollY = Math.min(0, minScroll);
  }, [totalW, itemGap]);

  React.useEffect(() => {
    const render = () => {
      const st = stateRef.current;
      clampScroll();
      st.y = lerp(st.y, st.scrollY, 0.1);
      st.speed = st.y - st.oldY;
      st.oldY = st.y;
      dispose();
      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [dispose, clampScroll]);

  const onWheel = React.useCallback((e) => {
    e.preventDefault();
    stateRef.current.scrollY -= (Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY) * 0.8;
  }, []);
  const getClientX = (e) => (e.touches && e.touches.length > 0) ? e.touches[0].clientX : e.clientX;
  const onPointerDown = React.useCallback((e) => {
    const st = stateRef.current;
    st.isDragging = true; st.dragMoved = false;
    st.touchStart = getClientX(e) || 0;
    if (containerRef.current) containerRef.current.style.cursor = "grabbing";
  }, []);
  const onPointerMove = React.useCallback((e) => {
    const st = stateRef.current;
    if (!st.isDragging) return;
    const clientX = getClientX(e) || 0;
    const delta = clientX - st.touchStart;
    if (Math.abs(delta) > 3) {
      st.dragMoved = true;
      if (e.cancelable) e.preventDefault();
    }
    st.scrollY += delta * 2.0;
    st.touchStart = clientX;
  }, []);
  const onPointerUp = React.useCallback(() => {
    stateRef.current.isDragging = false;
    if (containerRef.current) containerRef.current.style.cursor = "grab";
  }, []);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("mousedown", onPointerDown);
    el.addEventListener("mousemove", onPointerMove);
    el.addEventListener("mouseup", onPointerUp);
    el.addEventListener("mouseleave", onPointerUp);
    el.addEventListener("touchstart", onPointerDown, { passive: false });
    el.addEventListener("touchmove", onPointerMove, { passive: false });
    el.addEventListener("touchend", onPointerUp);
    el.addEventListener("touchcancel", onPointerUp);
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("mousedown", onPointerDown);
      el.removeEventListener("mousemove", onPointerMove);
      el.removeEventListener("mouseup", onPointerUp);
      el.removeEventListener("mouseleave", onPointerUp);
      el.removeEventListener("touchstart", onPointerDown);
      el.removeEventListener("touchmove", onPointerMove);
      el.removeEventListener("touchend", onPointerUp);
      el.removeEventListener("touchcancel", onPointerUp);
    };
  }, [onWheel, onPointerDown, onPointerMove, onPointerUp]);

  const nc = nudgeCount || (bp.isDesktop ? 3 : bp.isTablet ? 2 : 1);
  const nudge = (dir) => { stateRef.current.scrollY += dir * (itemWidth + itemGap) * nc; };
  const arrowSize = bp.isDesktop ? 36 : 30;
  const getDragMoved = () => stateRef.current.dragMoved;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
      <button onClick={() => nudge(1)}
        style={{
          width: arrowSize, height: arrowSize, minWidth: arrowSize,
          border: "none", background: "none", color: ac,
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all 0.2s", flexShrink: 0, outline: "none", marginLeft: bp.isDesktop ? 4 : 2,
          zIndex: 2, fontSize: 0, padding: 0, opacity: 0.6,
        }}
        onMouseEnter={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "scale(1.15)"; }}
        onMouseLeave={e => { e.currentTarget.style.opacity = "0.6"; e.currentTarget.style.transform = "scale(1)"; }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
      </button>
      <div ref={containerRef}
        style={{
          flex: 1, overflow: "hidden", position: "relative", height: sliderHeight,
          cursor: "grab", userSelect: "none", WebkitUserSelect: "none",
          touchAction: "pan-y",
        }}
        onSelectStart={e => e.preventDefault()}>
        {items.map((item, i) => (
          <div key={item.key || i} ref={el => itemsRef.current[i] = el}
            style={{
              position: "absolute", top: 0, left: 0, width: itemWidth, height: sliderHeight,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              willChange: "transform",
            }}>
            {renderItem(item, i, getDragMoved)}
          </div>
        ))}
      </div>
      <button onClick={() => nudge(-1)}
        style={{
          width: arrowSize, height: arrowSize, minWidth: arrowSize,
          border: "none", background: "none", color: ac,
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all 0.2s", flexShrink: 0, outline: "none", marginRight: bp.isDesktop ? 4 : 2,
          zIndex: 2, fontSize: 0, padding: 0, opacity: 0.6,
        }}
        onMouseEnter={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "scale(1.15)"; }}
        onMouseLeave={e => { e.currentTarget.style.opacity = "0.6"; e.currentTarget.style.transform = "scale(1)"; }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
      </button>
    </div>
  );
}

// ─── Shared: Interview Stages Panel (Parallax Slider) ───────────
function InterviewStagesPanel({ completedStages, onStartStage }) {
  const bp = useBreakpoint();
  const doneSet = completedStages instanceof Set ? completedStages : new Set();
  const doneCount = doneSet.size;
  const overallPct = Math.round((doneCount / STAGE_DEFS.length) * 100);

  const containerRef = React.useRef(null);
  const itemsRef = React.useRef([]);
  const stateRef = React.useRef({ scrollY: 0, y: 0, oldY: 0, speed: 0, isDragging: false, touchStart: 0, dragMoved: false });
  const rafRef = React.useRef(null);

  const itemW = bp.isDesktop ? 170 : bp.isTablet ? 120 : 130;
  const gap = bp.isDesktop ? 24 : bp.isTablet ? 16 : 16;
  const sliderH = bp.isDesktop ? 220 : 200;
  const ringSize = bp.isDesktop ? 100 : bp.isTablet ? 68 : 110;
  const totalW = STAGE_DEFS.length * (itemW + gap);

  const lerp = (a, b, t) => a * (1 - t) + b * t;

  const dispose = React.useCallback(() => {
    const items = itemsRef.current;
    if (!items.length) return;
    const st = stateRef.current;
    items.forEach((el, i) => {
      if (!el) return;
      let x = i * (itemW + gap) + st.y;
      const skewX = -st.speed * 0.15;
      const rotate = st.speed * 0.008;
      const scale = 1 - Math.min(100, Math.abs(st.speed)) * 0.002;
      el.style.transform = `translateX(${x}px) skewX(${skewX}deg) rotate(${rotate}deg) scale(${scale})`;
    });
  }, [itemW, gap, totalW]);

  // Clamp scroll to bounded range (no infinite wrap)
  const clampScroll = React.useCallback(() => {
    const st = stateRef.current;
    const containerW = containerRef.current ? containerRef.current.clientWidth : 400;
    const maxScroll = 0;
    const minScroll = -(totalW - containerW + gap);
    if (st.scrollY > maxScroll) st.scrollY = maxScroll;
    if (st.scrollY < minScroll) st.scrollY = minScroll;
  }, [totalW, gap]);

  React.useEffect(() => {
    const render = () => {
      const st = stateRef.current;
      clampScroll();
      st.y = lerp(st.y, st.scrollY, 0.1);
      st.speed = st.y - st.oldY;
      st.oldY = st.y;
      dispose();
      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [dispose, clampScroll]);

  // Mouse/touch handlers
  const onWheel = React.useCallback((e) => {
    e.preventDefault();
    stateRef.current.scrollY -= (Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY) * 0.8;
  }, []);

  const getClientX2 = (e) => (e.touches && e.touches.length > 0) ? e.touches[0].clientX : e.clientX;
  const onPointerDown = React.useCallback((e) => {
    const st = stateRef.current;
    st.isDragging = true;
    st.dragMoved = false;
    st.touchStart = getClientX2(e) || 0;
    if (containerRef.current) containerRef.current.style.cursor = "grabbing";
  }, []);

  const onPointerMove = React.useCallback((e) => {
    const st = stateRef.current;
    if (!st.isDragging) return;
    const clientX = getClientX2(e) || 0;
    const delta = clientX - st.touchStart;
    if (Math.abs(delta) > 3) {
      st.dragMoved = true;
      if (e.cancelable) e.preventDefault();
    }
    st.scrollY += delta * 2.0;
    st.touchStart = clientX;
  }, []);

  const onPointerUp = React.useCallback(() => {
    stateRef.current.isDragging = false;
    if (containerRef.current) containerRef.current.style.cursor = "grab";
  }, []);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("mousedown", onPointerDown);
    el.addEventListener("mousemove", onPointerMove);
    el.addEventListener("mouseup", onPointerUp);
    el.addEventListener("mouseleave", onPointerUp);
    el.addEventListener("touchstart", onPointerDown, { passive: false });
    el.addEventListener("touchmove", onPointerMove, { passive: false });
    el.addEventListener("touchend", onPointerUp);
    el.addEventListener("touchcancel", onPointerUp);
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("mousedown", onPointerDown);
      el.removeEventListener("mousemove", onPointerMove);
      el.removeEventListener("mouseup", onPointerUp);
      el.removeEventListener("mouseleave", onPointerUp);
      el.removeEventListener("touchstart", onPointerDown);
      el.removeEventListener("touchmove", onPointerMove);
      el.removeEventListener("touchend", onPointerUp);
      el.removeEventListener("touchcancel", onPointerUp);
    };
  }, [onWheel, onPointerDown, onPointerMove, onPointerUp]);

  const nudge = (dir) => { stateRef.current.scrollY += dir * (itemW + gap) * (bp.isDesktop ? 4 : bp.isTablet ? 2 : 1); };

  const arrowSize = bp.isDesktop ? 36 : 30;

  // ── Mobile: individual cards layout ──
  if (!bp.isDesktop) {
    return (
      <div style={{ marginBottom: 24 }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 13, color: T.rose, fontWeight: 700, letterSpacing: "0.1em" }}>INTERVIEW STAGES</div>
        </div>

        {/* Horizontal scroll of individual cards */}
        <div onTouchStart={e => e.stopPropagation()} onTouchEnd={e => e.stopPropagation()} style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 8, WebkitOverflowScrolling: "touch", scrollSnapType: "x mandatory", msOverflowStyle: "none", scrollbarWidth: "none" }}>
          {STAGE_DEFS.map((s) => {
            const n = s.n;
            const done = doneSet.has(n);
            const stageTotal = AI_QUESTIONS.filter(q => q.stage === n).length;
            let savedStep = -1;
            try { const raw = localStorage.getItem("soul_interview_progress"); if (raw) { const p = JSON.parse(raw); if (p.stage === n && p.step >= 0) savedStep = p.step; } } catch {}
            const hasProg = !done && savedStep >= 0;
            const stagePct = done ? 100 : hasProg ? Math.round(((savedStep + 1) / stageTotal) * 100) : 0;
            const ringColor = done ? T.rose : hasProg ? T.amber : T.border;
            const statusLabel = done ? "Complete" : hasProg ? "Resume" : "Start";
            const statusColor = done ? T.rose : hasProg ? T.amber : T.muted;

            return (
              <div key={n}
                onClick={() => onStartStage && onStartStage(n)}
                style={{
                  minWidth: 144, maxWidth: 144,
                  background: T.card, border: `1px solid ${done ? T.rose + "40" : T.border}`, borderRadius: 14,
                  padding: "18px 12px 16px",
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
                  cursor: "pointer", scrollSnapAlign: "start", flexShrink: 0,
                  transition: "all 0.2s",
                }}>
                <div style={{ animation: stagePct > 0 ? "stageProgressPulse 2s ease-in-out infinite" : "none" }}>
                  <StageRing value={stagePct} size={86} color={ringColor} dotColor={T.rose}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                      <div style={{ lineHeight: 1 }}>
                        {done ? <s.Icon size={24} color={ringColor} /> : hasProg ? <span style={{ fontSize: 19 }}>⏸</span> : <s.AnimIcon size={26} color={T.muted} />}
                      </div>
                      {stagePct > 0 && (
                        <span style={{ fontSize: 10, fontWeight: 800, color: done ? T.rose : T.amber, lineHeight: 1 }}>
                          {stagePct}%
                        </span>
                      )}
                    </div>
                  </StageRing>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text, lineHeight: 1.2, marginBottom: 3 }}>
                    {s.name}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: statusColor, letterSpacing: "0.04em" }}>
                    {statusLabel}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Progress labels + bar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: T.muted }}>{doneCount} of {STAGE_DEFS.length} complete</span>
          <span style={{ fontSize: 11, color: T.rose, fontWeight: 700 }}>{overallPct}%</span>
        </div>
        <div style={{ height: 6, borderRadius: 3, background: T.dim }}>
          <div style={{ height: "100%", width: `${overallPct}%`, background: `linear-gradient(90deg, ${T.rose}, ${T.amber})`, borderRadius: 3, transition: "width 1s ease" }} />
        </div>

      </div>
    );
  }

  // ── Desktop: original parallax slider layout ──
  return (
    <div style={{ marginBottom: 32 }}>
      {/* Header (outside panel) */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: T.rose, fontWeight: 700, letterSpacing: "0.1em" }}>INTERVIEW STAGES</div>
      </div>
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: 20, overflow: "hidden" }}>

      {/* Slider area with side arrows */}
      <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
        {/* Left arrow */}
        <button onClick={() => nudge(1)}
          style={{
            width: arrowSize, height: arrowSize, minWidth: arrowSize,
            border: "none", background: "none", color: T.muted,
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.2s", flexShrink: 0, outline: "none", marginLeft: 4,
            zIndex: 2, fontSize: 0, padding: 0, opacity: 0.6,
          }}
          onMouseEnter={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "scale(1.15)"; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = "0.6"; e.currentTarget.style.transform = "scale(1)"; }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        </button>

        {/* Infinite slider track */}
        <div ref={containerRef}
          style={{
            flex: 1, overflow: "hidden", position: "relative", height: sliderH,
            cursor: "grab", userSelect: "none", WebkitUserSelect: "none",
            touchAction: "pan-y",
          }}
          onSelectStart={e => e.preventDefault()}>
          {STAGE_DEFS.map((s, i) => {
            const n = s.n;
            const done = doneSet.has(n);
            const stageTotal = AI_QUESTIONS.filter(q => q.stage === n).length;
            let savedStep = -1;
            try { const raw = localStorage.getItem("soul_interview_progress"); if (raw) { const p = JSON.parse(raw); if (p.stage === n && p.step >= 0) savedStep = p.step; } } catch {}
            const hasProg = !done && savedStep >= 0;
            const stagePct = done ? 100 : hasProg ? Math.round(((savedStep + 1) / stageTotal) * 100) : 0;
            const ringColor = done ? T.rose : hasProg ? T.amber : T.border;
            const statusLabel = done ? "Complete" : hasProg ? "Resume" : "";
            const statusColor = done ? T.rose : hasProg ? T.amber : T.muted;

            return (
              <div key={n} ref={el => itemsRef.current[i] = el}
                style={{
                  position: "absolute", top: 0, left: 0, width: itemW, height: sliderH,
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  gap: 8, willChange: "transform",
                }}>
                <button
                  onClick={(e) => { if (!stateRef.current.dragMoved) { onStartStage && onStartStage(n); } }}
                  title={`Stage ${n}: ${s.name}\n${done ? '✓ Complete — click to redo' : hasProg ? `⏸ ${stagePct}% — click to resume` : 'Click to start'}`}
                  style={{
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
                    background: "none", border: "none", cursor: "pointer",
                    padding: 0, borderRadius: 8, outline: "none", width: "100%",
                    transition: "filter 0.2s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.filter = "brightness(1.15)"; }}
                  onMouseLeave={e => { e.currentTarget.style.filter = "brightness(1)"; }}>
                  <div style={{ animation: stagePct > 0 ? "stageProgressPulse 2s ease-in-out infinite" : "none" }}>
                    <StageRing value={stagePct} size={ringSize} color={ringColor} dotColor={T.rose}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                        <div style={{ lineHeight: 1 }}>
                          {done ? <s.Icon size={28} color={ringColor} /> : hasProg ? <span style={{ fontSize: 20 }}>⏸</span> : <s.AnimIcon size={28} color={T.muted} />}
                        </div>
                        {stagePct > 0 && (
                          <span style={{ fontSize: 10, fontWeight: 800, color: done ? T.rose : T.amber, lineHeight: 1 }}>
                            {stagePct}%
                          </span>
                        )}
                      </div>
                    </StageRing>
                  </div>
                  <div style={{ textAlign: "center", maxWidth: itemW - 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.text, lineHeight: 1.2, marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {s.name}
                    </div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: statusColor, letterSpacing: "0.04em" }}>
                      {statusLabel}
                    </div>
                  </div>
                </button>
              </div>
            );
          })}
        </div>

        {/* Right arrow */}
        <button onClick={() => nudge(-1)}
          style={{
            width: arrowSize, height: arrowSize, minWidth: arrowSize,
            border: "none", background: "none", color: T.muted,
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.2s", flexShrink: 0, outline: "none", marginRight: 4,
            zIndex: 2, fontSize: 0, padding: 0, opacity: 0.6,
          }}
          onMouseEnter={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "scale(1.15)"; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = "0.6"; e.currentTarget.style.transform = "scale(1)"; }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
        </button>
      </div>

      {/* Progress labels + bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "16px 20px 5px" }}>
        <span style={{ fontSize: 11, color: T.muted }}>{doneCount} of {STAGE_DEFS.length} complete</span>
        <span style={{ fontSize: 11, color: T.rose, fontWeight: 700 }}>{overallPct}%</span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: T.dim, margin: "0 20px 0" }}>
        <div style={{ height: "100%", width: `${overallPct}%`, background: `linear-gradient(90deg, ${T.rose}, ${T.amber})`, borderRadius: 3, transition: "width 1s ease" }} />
      </div>
      
      </div>
    </div>
  );
}

// ─── Animated Stage Icons (lucide-animated style, MIT) ──────────
const AnimUserIcon = ({ size = 28, color }) => {
  const c = useAnimation();
  return (<div onMouseEnter={() => c.start("animate")} onMouseLeave={() => c.start("normal")} style={{ color, display: "inline-flex" }}>
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <motion.circle cx="12" cy="8" r="5" animate={c} variants={{ normal: { pathLength: 1, opacity: 1, pathOffset: 0 }, animate: { pathLength: [0, 1], pathOffset: [1, 0], scale: [0.5, 1] } }} />
      <motion.path d="M20 21a8 8 0 0 0-16 0" animate={c} transition={{ delay: 0.2, duration: 0.4 }} variants={{ normal: { pathLength: 1, opacity: 1, pathOffset: 0 }, animate: { pathLength: [0, 1], opacity: [0, 1], pathOffset: [1, 0] } }} />
    </svg>
  </div>);
};

const AnimBrainIcon = ({ size = 28, color }) => {
  const c = useAnimation();
  const stemV = { normal: { pathLength: 1, pathOffset: 0 }, animate: { pathLength: [1, 0.4, 1], pathOffset: [0, 0.25, 0], transition: { duration: 1.4, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" } } };
  const sideV = { normal: { pathLength: 1, pathOffset: 0 }, animate: { pathLength: [1, 0.5, 1], pathOffset: [0, 0.25, 0], transition: { duration: 1.4, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" } } };
  const topV = { normal: { pathLength: 1, pathOffset: 0 }, animate: { pathLength: [1, 0.8, 1], pathOffset: [0, 0.07, 0], transition: { duration: 1.4, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" } } };
  const lowV = { normal: { pathLength: 1, pathOffset: 0 }, animate: { pathLength: [1, 0.8, 1], pathOffset: [0, 0.14, 0], transition: { duration: 1.4, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" } } };
  return (<div onMouseEnter={() => c.start("animate")} onMouseLeave={() => c.start("normal")} style={{ color, display: "inline-flex" }}>
    <motion.svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" animate={c} variants={{ normal: { scale: 1 }, animate: { scale: [1, 1.08, 1], transition: { duration: 1.4, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" } } }}>
      <motion.path d="M12 18V5" animate={c} variants={stemV} />
      <motion.path d="M15 13a4.17 4.17 0 0 1-3-4 4.17 4.17 0 0 1-3 4" animate={c} variants={sideV} />
      <motion.path d="M12 5A3 3 0 1 1 17.598 6.5" animate={c} variants={topV} />
      <motion.path d="M12 5A3 3 0 1 0 6.402 6.5" animate={c} variants={topV} />
      <path d="M17.997 5.125a4 4 0 0 1 2.526 5.77" />
      <motion.path d="M18 18a4 4 0 0 0 2-7.464" animate={c} variants={lowV} />
      <path d="M19.967 17.483A4 4 0 1 1 12 18a4 4 0 1 1-7.967-.517" />
      <motion.path d="M6 18a4 4 0 0 1-2-7.464" animate={c} variants={lowV} />
      <path d="M6.003 5.125a4 4 0 0 0-2.526 5.77" />
    </motion.svg>
  </div>);
};

const AnimGavelIcon = ({ size = 28, color }) => {
  const c = useAnimation();
  return (<div onMouseEnter={() => c.start("animate")} onMouseLeave={() => c.start("normal")} style={{ color, display: "inline-flex" }}>
    <motion.svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" animate={c} initial="normal" style={{ transformOrigin: "0% 100%", transformBox: "fill-box" }} variants={{ normal: { rotate: 0, transition: { duration: 0.3, ease: "easeOut" } }, animate: { rotate: [0, -20, 25, 0], transition: { duration: 0.8, times: [0, 0.6, 0.8, 1] } } }}>
      <path d="m14 13-8.381 8.38a1 1 0 0 1-3.001-3l8.384-8.381" />
      <path d="m16 16 6-6" /><path d="m21.5 10.5-8-8" /><path d="m8 8 6-6" /><path d="m8.5 7.5 8 8" />
    </motion.svg>
  </div>);
};

const AnimHeartIcon = ({ size = 28, color }) => {
  const c = useAnimation();
  return (<div onMouseEnter={() => c.start("animate")} onMouseLeave={() => c.start("normal")} style={{ color, display: "inline-flex" }}>
    <motion.svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" animate={c} variants={{ normal: { scale: 1 }, animate: { scale: [1, 1.08, 1] } }} transition={{ duration: 0.45, repeat: 2 }}>
      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
    </motion.svg>
  </div>);
};

const AnimSunIcon = ({ size = 28, color }) => {
  const c = useAnimation();
  const pv = { normal: { opacity: 1 }, animate: (i) => ({ opacity: [0, 1], transition: { delay: i * 0.1, duration: 0.3 } }) };
  const rays = ["M12 2v2","m19.07 4.93-1.41 1.41","M20 12h2","m17.66 17.66 1.41 1.41","M12 20v2","m6.34 17.66-1.41 1.41","M2 12h2","m4.93 4.93 1.41 1.41"];
  return (<div onMouseEnter={() => c.start("animate")} onMouseLeave={() => c.start("normal")} style={{ color, display: "inline-flex" }}>
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4" />
      {rays.map((d, i) => <motion.path key={d} d={d} animate={c} custom={i + 1} variants={pv} />)}
    </svg>
  </div>);
};

const AnimHeartPulseIcon = ({ size = 28, color }) => {
  const hDraw = useAnimation(); const hPulse = useAnimation(); const lc = useAnimation();
  const start = useCallback(async () => {
    hDraw.start("hidden", { duration: 0 });
    await lc.start("animate", { duration: 0.6, ease: "linear", opacity: { duration: 0.1 } });
    await hDraw.start("draw", { duration: 0.5, ease: "easeOut", opacity: { duration: 0.1 } });
    hPulse.start("pulse", { duration: 0.9, repeat: 1, ease: "easeInOut" });
  }, [hDraw, hPulse, lc]);
  const stop = useCallback(() => { hDraw.start("normal", { duration: 0.3 }); hPulse.start("normal", { duration: 0.3 }); lc.start("normal", { duration: 0.3 }); }, [hDraw, hPulse, lc]);
  return (<div onMouseEnter={start} onMouseLeave={stop} style={{ color, display: "inline-flex" }}>
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <motion.g animate={hPulse} style={{ originX: "12px", originY: "12px" }} variants={{ normal: { scale: 1 }, pulse: { scale: [1, 1.08, 1] } }}>
        <motion.path d="M2 9.5a5.5 5.5 0 0 1 9.591-3.676.56.56 0 0 0 .818 0A5.49 5.49 0 0 1 22 9.5c0 2.29-1.5 4-3 5.5l-5.492 5.313a2 2 0 0 1-3 .019L5 15c-1.5-1.5-3-3.2-3-5.5" animate={hDraw} variants={{ normal: { pathLength: 1, opacity: 1 }, hidden: { pathLength: 0, opacity: 0 }, draw: { pathLength: [0, 1], opacity: [0, 1] } }} />
      </motion.g>
      <motion.path d="M3.22 13H9.5l.5-1 2 4.5 2-7 1.5 3.5h5.27" animate={lc} variants={{ normal: { pathLength: 1, pathOffset: 0, opacity: 1 }, animate: { pathLength: [0, 1], pathOffset: [1, 0], opacity: [0, 1] } }} />
    </svg>
  </div>);
};

const AnimCupSodaIcon = ({ size = 28, color }) => {
  const c = useAnimation();
  const strawV = { normal: { y: 0, scaleY: 1 }, animate: { y: [0, -0.85, 0.15, 0], scaleY: [1, 1.06, 0.99, 1], transition: { duration: 0.5, ease: [0.34, 1.56, 0.64, 1], times: [0, 0.35, 0.65, 1] } } };
  const waveV = { normal: { y: 0 }, animate: { y: [0, -1, 0], transition: { duration: 1.8, repeat: Infinity, ease: "easeInOut" } } };
  return (<div onMouseEnter={() => c.start("animate")} onMouseLeave={() => c.start("normal")} style={{ color, display: "inline-flex" }}>
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m6 8 1.75 12.28a2 2 0 0 0 2 1.72h4.54a2 2 0 0 0 2-1.72L18 8" /><path d="M5 8h14" />
      <motion.path d="M7 15a6.47 6.47 0 0 1 5 0 6.47 6.47 0 0 0 5 0" animate={c} variants={waveV} />
      <motion.path d="m12 8 1-6h2" animate={c} style={{ transformBox: "fill-box", originX: "50%", originY: "100%" }} variants={strawV} />
    </svg>
  </div>);
};

const AnimSparklesIcon = ({ size = 28, color }) => {
  const star = useAnimation(); const sparkle = useAnimation();
  const sparkleV = { initial: { y: 0, fill: "none" }, hover: { y: [0, -1, 0, 0], fill: "currentColor", transition: { duration: 1, bounce: 0.3 } } };
  const starV = { initial: { opacity: 1 }, blink: { opacity: [0, 1, 0, 0, 0, 0, 1], transition: { duration: 2, type: "spring", stiffness: 70, damping: 10, mass: 0.4 } } };
  return (<div onMouseEnter={() => { sparkle.start("hover"); star.start("blink", { delay: 1 }); }} onMouseLeave={() => { sparkle.start("initial"); star.start("initial"); }} style={{ color, display: "inline-flex" }}>
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <motion.path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" animate={sparkle} variants={sparkleV} />
      <motion.path d="M20 3v4" animate={star} variants={starV} />
      <motion.path d="M22 5h-4" animate={star} variants={starV} />
      <motion.path d="M4 17v2" animate={star} variants={starV} />
      <motion.path d="M5 18H3" animate={star} variants={starV} />
    </svg>
  </div>);
};

const AnimCookingPotIcon = ({ size = 28, color }) => {
  const c = useAnimation();
  const lidV = { normal: { rotate: 0 }, animate: { rotate: [0, -14, 14, -10, 10, -6, 6, 0], transition: { duration: 0.9, ease: "easeInOut" } } };
  const potV = { normal: { scale: 1 }, animate: { scale: [1, 1.08, 1], transition: { duration: 0.95, ease: "easeInOut" } } };
  return (<div onMouseEnter={() => c.start("animate")} onMouseLeave={() => c.start("normal")} style={{ color, display: "inline-flex" }}>
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <motion.g animate={c} initial="normal" style={{ transformOrigin: "12px 16px" }} variants={potV}>
        <path d="M2 12h20" /><path d="M20 12v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8" />
      </motion.g>
      <motion.g animate={c} initial="normal" style={{ transformOrigin: "18px 6px" }} variants={lidV}>
        <path d="m4 8 16-4" /><path d="m8.86 6.78-.45-1.81a2 2 0 0 1 1.45-2.43l1.94-.48a2 2 0 0 1 2.43 1.46l.45 1.8" />
      </motion.g>
    </svg>
  </div>);
};

const AnimSmileIcon = ({ size = 28, color }) => {
  const c = useAnimation();
  const faceV = { normal: { scale: 1, rotate: 0, transition: { duration: 0.3 } }, animate: { scale: [1, 1.15, 1.05, 1.1], rotate: [0, -3, 3, 0], transition: { duration: 0.8, times: [0, 0.3, 0.6, 1] } } };
  const mouthV = { normal: { d: "M8 14s1.5 2 4 2 4-2 4-2", transition: { duration: 0.3 } }, animate: { d: "M7 13.5s2.5 3.5 5 3.5 5-3.5 5-3.5", transition: { duration: 0.4, delay: 0.1 } } };
  const eyeV = { normal: { scale: 1, transition: { duration: 0.3 } }, animate: { scale: [1, 1.5, 0.8, 1.2], transition: { duration: 0.5, times: [0, 0.3, 0.6, 1] } } };
  return (<div onMouseEnter={() => c.start("animate")} onMouseLeave={() => c.start("normal")} style={{ color, display: "inline-flex" }}>
    <motion.svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" animate={c} initial="normal" variants={faceV}>
      <circle cx="12" cy="12" r="10" />
      <motion.path d="M8 14s1.5 2 4 2 4-2 4-2" animate={c} initial="normal" variants={mouthV} />
      <motion.line x1="9" x2="9.01" y1="9" y2="9" animate={c} initial="normal" variants={eyeV} />
      <motion.line x1="15" x2="15.01" y1="9" y2="9" animate={c} initial="normal" variants={eyeV} />
    </motion.svg>
  </div>);
};

const AnimMoonIcon = ({ size = 28, color }) => {
  const c = useAnimation();
  return (<div onMouseEnter={() => c.start("animate")} onMouseLeave={() => c.start("normal")} style={{ color, display: "inline-flex" }}>
    <motion.svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" animate={c} variants={{ normal: { rotate: 0 }, animate: { rotate: [0, -10, 10, -5, 5, 0] } }} transition={{ duration: 1.2, ease: "easeInOut" }}>
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
    </motion.svg>
  </div>);
};

const AnimTelescopeIcon = ({ size = 28, color }) => {
  const c = useAnimation();
  const scopeV = { normal: { rotate: 0, transition: { duration: 0.6, ease: "easeInOut" } }, animate: { rotate: -15, transition: { duration: 0.8, ease: "easeInOut" } } };
  return (<div onMouseEnter={() => c.start("animate")} onMouseLeave={() => c.start("normal")} style={{ color, display: "inline-flex" }}>
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <motion.g animate={c} style={{ transformOrigin: "12px 13px" }} variants={scopeV}>
        <path d="m10.065 12.493-6.18 1.318a.934.934 0 0 1-1.108-.702l-.537-2.15a1.07 1.07 0 0 1 .691-1.265l13.504-4.44" />
        <path d="m13.56 11.747 4.332-.924" />
        <path d="M16.485 5.94a2 2 0 0 1 1.455-2.425l1.09-.272a1 1 0 0 1 1.212.727l1.515 6.06a1 1 0 0 1-.727 1.213l-1.09.272a2 2 0 0 1-2.425-1.455z" />
        <path d="m6.158 8.633 1.114 4.456" />
      </motion.g>
      <path d="m16 21-3.105-6.21" /><path d="m8 21 3.105-6.21" /><circle cx="12" cy="13" r="2" />
    </svg>
  </div>);
};

const AnimBanIcon = ({ size = 28, color }) => {
  const c = useAnimation();
  const circV = { normal: { opacity: 1, pathLength: 1, transition: { duration: 0.3 } }, animate: { opacity: [0, 1], pathLength: [0, 1], transition: { duration: 0.4 } } };
  const lineV = { normal: { opacity: 1, pathLength: 1, transition: { duration: 0.3 } }, slash: { opacity: [0, 1], pathLength: [0, 1], transition: { duration: 0.4 } } };
  return (<div onMouseEnter={() => { c.start("animate"); c.start("slash", { delay: 0.5 }); }} onMouseLeave={() => c.start("normal")} style={{ color, display: "inline-flex" }}>
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <motion.circle cx="12" cy="12" r="10" animate={c} initial="normal" variants={circV} />
      <motion.path d="m4.9 4.9 14.2 14.2" animate={c} initial="normal" variants={lineV} />
    </svg>
  </div>);
};

const AnimMicIcon = ({ size = 28, color }) => {
  const c = useAnimation();
  const capsV = { normal: { y: 0 }, animate: { y: [0, -3, 0, -2, 0], transition: { duration: 0.6, ease: "easeInOut" } } };
  return (<div onMouseEnter={() => c.start("animate")} onMouseLeave={() => c.start("normal")} style={{ color, display: "inline-flex" }}>
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" overflow="visible">
      <path d="M12 19v3" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <motion.rect width="6" height="13" x="9" y="2" rx="3" animate={c} variants={capsV} />
    </svg>
  </div>);
};

const AnimChartIcon = ({ size = 28, color }) => {
  const c = useAnimation();
  const barV = (delay) => ({ normal: { scaleY: 1, transition: { duration: 0.3 } }, animate: { scaleY: [0, 1], transition: { duration: 0.4, delay, ease: "easeOut" } } });
  return (<div onMouseEnter={() => c.start("animate")} onMouseLeave={() => c.start("normal")} style={{ color, display: "inline-flex" }}>
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <motion.line x1="18" y1="20" x2="18" y2="10" animate={c} variants={barV(0)} style={{ transformOrigin: "bottom" }} />
      <motion.line x1="12" y1="20" x2="12" y2="4" animate={c} variants={barV(0.1)} style={{ transformOrigin: "bottom" }} />
      <motion.line x1="6" y1="20" x2="6" y2="14" animate={c} variants={barV(0.2)} style={{ transformOrigin: "bottom" }} />
    </svg>
  </div>);
};

const AnimUsersIcon = ({ size = 28, color }) => {
  const c = useAnimation();
  const user1V = { normal: { x: 0, opacity: 1, transition: { duration: 0.3 } }, animate: { x: [-4, 0], opacity: [0, 1], transition: { duration: 0.4 } } };
  const user2V = { normal: { x: 0, opacity: 1, transition: { duration: 0.3 } }, animate: { x: [4, 0], opacity: [0, 1], transition: { duration: 0.4, delay: 0.15 } } };
  return (<div onMouseEnter={() => c.start("animate")} onMouseLeave={() => c.start("normal")} style={{ color, display: "inline-flex" }}>
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <motion.g animate={c} variants={user1V}>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
      </motion.g>
      <motion.g animate={c} variants={user2V}>
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </motion.g>
    </svg>
  </div>);
};

const AnimCoupleIcon = ({ size = 28, color }) => {
  const c = useAnimation();
  const heartV = { normal: { scale: 1, y: 0, transition: { duration: 0.3 } }, animate: { scale: [1, 1.25, 1], y: [0, -2, 0], transition: { duration: 0.6, ease: "easeInOut" } } };
  const leftV = { normal: { x: 0, transition: { duration: 0.3 } }, animate: { x: [0, 1, 0], transition: { duration: 0.5, delay: 0.1 } } };
  const rightV = { normal: { x: 0, transition: { duration: 0.3 } }, animate: { x: [0, -1, 0], transition: { duration: 0.5, delay: 0.1 } } };
  return (<div onMouseEnter={() => c.start("animate")} onMouseLeave={() => c.start("normal")} style={{ color, display: "inline-flex" }}>
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <motion.g animate={c} variants={leftV}><circle cx="6" cy="8" r="3" /><path d="M2 21v-2a4 4 0 0 1 4-4h1" /></motion.g>
      <motion.g animate={c} variants={rightV}><circle cx="18" cy="8" r="3" /><path d="M22 21v-2a4 4 0 0 0-4-4h-1" /></motion.g>
      <motion.path d="M12 11l-1.5 1.5a2.12 2.12 0 1 0 3 0L12 11z" animate={c} variants={heartV} style={{ transformOrigin: "center" }} />
    </svg>
  </div>);
};

const AnimDashboardIcon = ({ size = 28, color }) => {
  const c = useAnimation();
  const boxV = (delay) => ({ normal: { scale: 1, opacity: 1, transition: { duration: 0.3 } }, animate: { scale: [0, 1], opacity: [0, 1], transition: { duration: 0.35, delay, ease: [0.34, 1.56, 0.64, 1] } } });
  return (<div onMouseEnter={() => c.start("animate")} onMouseLeave={() => c.start("normal")} style={{ color, display: "inline-flex" }}>
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <motion.rect x="3" y="3" width="7" height="9" rx="1" animate={c} variants={boxV(0)} style={{ transformOrigin: "6.5px 7.5px" }} />
      <motion.rect x="14" y="3" width="7" height="5" rx="1" animate={c} variants={boxV(0.08)} style={{ transformOrigin: "17.5px 5.5px" }} />
      <motion.rect x="14" y="12" width="7" height="9" rx="1" animate={c} variants={boxV(0.16)} style={{ transformOrigin: "17.5px 16.5px" }} />
      <motion.rect x="3" y="16" width="7" height="5" rx="1" animate={c} variants={boxV(0.24)} style={{ transformOrigin: "6.5px 18.5px" }} />
    </svg>
  </div>);
};

const AnimCalendarIcon = ({ size = 28, color }) => {
  const c = useAnimation();
  const bodyV = { normal: { scale: 1, transition: { duration: 0.3 } }, animate: { scale: [1, 0.95, 1.02, 1], transition: { duration: 0.5, ease: "easeInOut" } } };
  const pegV = (delay) => ({ normal: { y: 0, scaleY: 1, transition: { duration: 0.3 } }, animate: { y: [0, -2, 0], scaleY: [1, 1.2, 1], transition: { duration: 0.4, delay, ease: [0.34, 1.56, 0.64, 1] } } });
  return (<div onMouseEnter={() => c.start("animate")} onMouseLeave={() => c.start("normal")} style={{ color, display: "inline-flex" }}>
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <motion.g animate={c} variants={bodyV} style={{ transformOrigin: "12px 14px" }}>
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M3 10h18" />
      </motion.g>
      <motion.path d="M16 2v4" animate={c} variants={pegV(0)} style={{ transformOrigin: "16px 4px" }} />
      <motion.path d="M8 2v4" animate={c} variants={pegV(0.1)} style={{ transformOrigin: "8px 4px" }} />
    </svg>
  </div>);
};

const AnimMapPinIcon = ({ size = 28, color }) => {
  const c = useAnimation();
  const pinV = { normal: { y: 0, scale: 1, transition: { duration: 0.3 } }, animate: { y: [0, -3, 0, -1.5, 0], scale: [1, 1.1, 1, 1.05, 1], transition: { duration: 0.7, ease: "easeInOut" } } };
  return (<div onMouseEnter={() => c.start("animate")} onMouseLeave={() => c.start("normal")} style={{ color, display: "inline-flex" }}>
    <motion.svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" animate={c} variants={pinV}>
      <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
      <circle cx="12" cy="10" r="3" />
    </motion.svg>
  </div>);
};

const AnimLockIcon = ({ size = 28, color }) => {
  const c = useAnimation();
  const shackleV = { normal: { y: 0, transition: { duration: 0.3 } }, animate: { y: [0, -2, 0], transition: { duration: 0.5, ease: [0.34, 1.56, 0.64, 1] } } };
  const bodyV = { normal: { scale: 1, transition: { duration: 0.3 } }, animate: { scale: [1, 1.05, 1], transition: { duration: 0.4, delay: 0.15 } } };
  return (<div onMouseEnter={() => c.start("animate")} onMouseLeave={() => c.start("normal")} style={{ color, display: "inline-flex" }}>
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <motion.rect x="3" y="11" width="18" height="11" rx="2" ry="2" animate={c} variants={bodyV} style={{ transformOrigin: "12px 16.5px" }} />
      <motion.path d="M7 11V7a5 5 0 0 1 10 0v4" animate={c} variants={shackleV} />
    </svg>
  </div>);
};

const AnimSettingsIcon = ({ size = 28, color }) => {
  const c = useAnimation();
  const gearV = { normal: { rotate: 0, transition: { duration: 0.5, ease: "easeOut" } }, animate: { rotate: 180, transition: { duration: 0.8, ease: [0.34, 1.56, 0.64, 1] } } };
  return (<div onMouseEnter={() => c.start("animate")} onMouseLeave={() => c.start("normal")} style={{ color, display: "inline-flex" }}>
    <motion.svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" animate={c} variants={gearV} style={{ transformOrigin: "center" }}>
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </motion.svg>
  </div>);
};

// ─── App Connection → Snapshot Stage Mapping ────────────────────
// Maps each connected app's data to the snapshot stage it enriches
const APP_INSIGHT_MAP = {
  linkedin:    { stage: 5, label: "LinkedIn", connectedData: ['Industry: Tech / Product Design', 'Career focus: UX research, human-centered design', 'Values: Work-life balance, social impact, continuous learning'] },
  spotify:     { stage: 6, label: "Spotify", connectedData: ['Top genres: Indie folk, alternative rock, lo-fi hip hop', 'Favorite artists: Bon Iver, Phoebe Bridgers, The National', 'Listening habits: Evening listener, creates mood playlists'] },
  apple_music: { stage: 6, label: "Apple Music", connectedData: ['Top genres: Jazz, soul, R&B', 'Favorite artists: Norah Jones, D\'Angelo, Erykah Badu', 'Listening habits: Creates curated playlists, explores new releases'] },
  strava:      { stage: 6, label: "Strava", connectedData: ['Activities: Trail running, yoga, cycling', 'Weekly average: 4 workouts, 15 miles running', 'Preferred time: Early morning workouts, weekend hikes'] },
  netflix:     { stage: 6, label: "Netflix", connectedData: ['Favorite genres: Psychological thrillers, documentaries, indie dramas', 'Recently watched: The Crown, Dark, Chef\'s Table', 'Viewing pattern: Binge-watches on weekends, prefers subtitles'] },
  goodreads:   { stage: 6, label: "Goodreads", connectedData: ['Favorite genres: Literary fiction, philosophy, memoirs', 'Currently reading: "The Overstory" by Richard Powers', 'Reading pace: 2-3 books/month, writes detailed reviews'] },
  facebook:    { stage: 6, label: "Facebook", connectedData: ['Member of: Hiking Enthusiasts LA, Book Club Downtown', 'Interests: Travel photography, sustainable living, indie films', 'Recent activity: Shared 3 articles on climate change'] },
  tiktok:      { stage: 9, label: "TikTok", connectedData: ['Content preferences: Cooking tutorials, travel vlogs, comedy sketches', 'Humor style: Dry humor, wordplay, observational comedy', 'Engagement: Saves recipes, shares travel tips'] },
  instagram:   { stage: 10, label: "Instagram", connectedData: ['Top themes: Nature photography, coffee culture, minimalist design', 'Active hashtags: #hikingadventures #coffeelover #minimalism', 'Engagement style: Thoughtful comments, saves travel content'] },
  google:      { stage: 10, label: "Google", connectedData: ['Frequent locations: Coffee shops, hiking trails, art galleries', 'Search interests: Sustainable living, travel destinations, cooking', 'Activity pattern: Weekend explorer, values local experiences'] },
  pinterest:   { stage: 10, label: "Pinterest", connectedData: ['Top boards: Home design, travel inspiration, healthy recipes', 'Style: Scandinavian minimalism, earthy tones, natural materials', 'Interests: DIY projects, plant care, sustainable fashion'] },
  twitter:     { stage: 11, label: "Twitter/X", connectedData: ['Topics: Tech ethics, environmental policy, urban planning', 'Tone: Thoughtful, balanced, occasionally witty', 'Engagement: Retweets research, engages in civil discussions'] },
};

// Helper: get connected app insights for a given stage
function getAppInsightsForStage(stageNum, appConnections) {
  if (!appConnections) return [];
  const insights = [];
  Object.entries(APP_INSIGHT_MAP).forEach(([appId, info]) => {
    if (info.stage === stageNum && appConnections[appId]) {
      insights.push({ label: info.label, data: info.connectedData });
    }
  });
  return insights;
}

// ─── Pill Icon SVGs for Snapshot Header ─────────────────────────
const PillIcon = ({ type, size = 14, color = "#888" }) => {
  const s = { width: size, height: size, flexShrink: 0 };
  const p = { fill: "none", stroke: color, strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (type) {
    case "ruler": return <svg viewBox="0 0 24 24" style={s} {...p}><path d="M2 12h20M6 8v8M10 10v4M14 8v8M18 10v4"/></svg>;
    case "user": return <svg viewBox="0 0 24 24" style={s} {...p}><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 4-6 8-6s8 2 8 6"/></svg>;
    case "globe": return <svg viewBox="0 0 24 24" style={s} {...p}><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2c3 3 4.5 6 4.5 10s-1.5 7-4.5 10c-3-3-4.5-6-4.5-10S9 5 12 2"/></svg>;
    case "baby": return <svg viewBox="0 0 24 24" style={s} {...p}><circle cx="12" cy="10" r="6"/><path d="M8 14c1.5 2 6.5 2 8 0M9 9h.01M15 9h.01M12 16v4M8 20h8"/></svg>;
    case "briefcase": return <svg viewBox="0 0 24 24" style={s} {...p}><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>;
    case "pray": return <svg viewBox="0 0 24 24" style={s} {...p}><path d="M12 2L8 8l4 4 4-4-4-6zM8 8l-4 8h16l-4-8M12 12v10"/></svg>;
    case "runner": return <svg viewBox="0 0 24 24" style={s} {...p}><circle cx="14" cy="4" r="2"/><path d="M6 20l4-8 3 2 3-4 2 6M4 16l3-6"/></svg>;
    case "sun": return <svg viewBox="0 0 24 24" style={s} {...p}><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>;
    case "heart": return <svg viewBox="0 0 24 24" style={s} {...p}><path d="M12 21C12 21 4 13.5 4 8.5C4 5.5 6.5 3 9.5 3c1.74 0 3.41.81 4.5 2.09A5.99 5.99 0 0 1 18.5 3C21.5 3 24 5.5 24 8.5c0 5-8 12.5-8 12.5" transform="translate(-2,0)"/></svg>;
    case "target": return <svg viewBox="0 0 24 24" style={s} {...p}><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>;
    case "family": return <svg viewBox="0 0 24 24" style={s} {...p}><circle cx="8" cy="6" r="3"/><circle cx="16" cy="6" r="3"/><path d="M2 20c0-3 3-5 6-5s6 2 6 5M10 20c0-3 3-5 6-5s6 2 6 5"/></svg>;
    case "chat": return <svg viewBox="0 0 24 24" style={s} {...p}><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>;
    case "plane": return <svg viewBox="0 0 24 24" style={s} {...p}><path d="M17.8 19.2L16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8l-8.2-1.8c-.4-.1-.8.1-.9.5L3 9l6 3 3 6 2.3-.9c.4-.1.6-.5.5-.9z"/></svg>;
    case "star": return <svg viewBox="0 0 24 24" style={s} {...p}><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>;
    case "cigarette": return <svg viewBox="0 0 24 24" style={s} {...p}><rect x="2" y="16" width="16" height="4" rx="1"/><path d="M22 16v4M18 8c0-3-2-4-4-4M20 8c0-3-2-4-4-4"/></svg>;
    case "x-circle": return <svg viewBox="0 0 24 24" style={s} {...p}><circle cx="12" cy="12" r="10"/><path d="M15 9l-6 6M9 9l6 6"/></svg>;
    case "ban": return <svg viewBox="0 0 24 24" style={s} {...p}><circle cx="12" cy="12" r="10"/><path d="M4.93 4.93l14.14 14.14"/></svg>;
    case "scale": return <svg viewBox="0 0 24 24" style={s} {...p}><path d="M12 3v18M4 7l8-4 8 4M4 7c0 3 3.5 5.5 8 5.5S20 10 20 7"/></svg>;
    case "wine": return <svg viewBox="0 0 24 24" style={s} {...p}><path d="M8 2h8l-1 9a5 5 0 0 1-6 0L8 2zM12 11v8M8 19h8"/></svg>;
    case "ring": return <svg viewBox="0 0 24 24" style={s} {...p}><circle cx="12" cy="14" r="6"/><path d="M12 8V2M9 4l3-2 3 2"/></svg>;
    case "body": return <svg viewBox="0 0 24 24" style={s} {...p}><circle cx="12" cy="4" r="2"/><path d="M8 8h8M12 6v8M8 22l2-8M16 22l-2-8"/></svg>;
    case "eye": return <svg viewBox="0 0 24 24" style={s} {...p}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
    case "coffee": return <svg viewBox="0 0 24 24" style={s} {...p}><path d="M18 8h1a4 4 0 0 1 0 8h-1M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/></svg>;
    case "laugh": return <svg viewBox="0 0 24 24" style={s} {...p}><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01"/></svg>;
    default: return <svg viewBox="0 0 24 24" style={s} {...p}><circle cx="12" cy="12" r="10"/></svg>;
  }
};

// ─── Extract structured data from userAnswers for snapshot header ──
function extractBasics(a) {
  const items = [];
  if (a.height) items.push({ icon: "ruler", text: `${a.height}${a.gender ? ` ${a.gender}` : ""}` });
  if (a.ethnicity) items.push({ icon: "globe", text: a.ethnicity });
  if (a.birth_country) items.push({ icon: "globe", text: `Family: ${a.birth_country.split(",").map(s => s.trim().split(" ").pop()).join(" ")}` });
  if (a.children) items.push({ icon: "baby", text: a.children.length > 50 ? (a.children.includes("want") ? "Want Kids: Yes" : a.children.substring(0, 40)) : a.children });
  if (a.employment) items.push({ icon: "briefcase", text: `Industry: ${a.employment.split("—")[0].split("–")[0].trim().substring(0, 40)}` });
  if (a.religion) items.push({ icon: "pray", text: a.religion.split(".")[0].substring(0, 40) });
  if (a.fitness) items.push({ icon: "runner", text: "Active" });
  if (a.daily_rhythm) items.push({ icon: "sun", text: a.daily_rhythm.toLowerCase().includes("morning") ? "Early Bird" : a.daily_rhythm.toLowerCase().includes("night") ? "Night Owl" : a.daily_rhythm.split(".")[0].substring(0, 25) });
  return items;
}

function extractLookingFor(a) {
  const items = [];
  if (a.relationship_intent) items.push({ icon: "ring", text: a.relationship_intent.toLowerCase().includes("marriage") ? "Long Term" : a.relationship_intent.toLowerCase().includes("serious") ? "Serious" : a.relationship_intent.split(".")[0].substring(0, 30) });
  if (a.height_preference) items.push({ icon: "ruler", text: `~ ${a.height_preference}` });
  if (a.family_closeness) items.push({ icon: "family", text: a.family_closeness.toLowerCase().includes("close") || a.family_closeness.toLowerCase().includes("very") ? "Big on family" : "Family values" });
  if (a.cultural_traditions) items.push({ icon: "globe", text: "Observes holidays with family" });
  if (a.core_values && a.core_values.toLowerCase().includes("communic")) items.push({ icon: "chat", text: "Good communication a must" });
  if (a.fitness) items.push({ icon: "runner", text: "Active and fit" });
  if (a.travel_importance && a.travel_importance.toLowerCase().includes("important")) items.push({ icon: "plane", text: "Travel Partner" });
  if (a.must_haves) {
    const must = a.must_haves.toLowerCase();
    if (must.includes("loyal") || must.includes("funny")) items.push({ icon: "star", text: "Loyal and Funny" });
    if (must.includes("kind") || must.includes("empath")) items.push({ icon: "heart", text: "Kind & Empathetic" });
  }
  return items;
}

function extractNotInterestedIn(a) {
  const items = [];
  const parse = (text) => {
    if (!text) return [];
    const parts = [];
    const t = text.toLowerCase();
    if (t.includes("tattoo")) parts.push({ icon: "ban", text: "Heavy Tattoos" });
    if (t.includes("cheat")) parts.push({ icon: "x-circle", text: "Cheaters" });
    if (t.includes("addict")) parts.push({ icon: "x-circle", text: "Addictions" });
    if (t.includes("heavy drink") || t.includes("alcoholi")) parts.push({ icon: "wine", text: "Heavy Drinkers" });
    if (t.includes("smok")) parts.push({ icon: "cigarette", text: "Smoking" });
    if (t.includes("kid") && (t.includes("no") || t.includes("doesn't"))) parts.push({ icon: "baby", text: "Doesn't want kids" });
    if (t.includes("travel") && (t.includes("unwill") || t.includes("never"))) parts.push({ icon: "plane", text: "Unwillingness to travel" });
    if (t.includes("lazy") || t.includes("no ambition") || t.includes("lack of ambition")) parts.push({ icon: "x-circle", text: "Lack of ambition" });
    if (t.includes("dishonest") || t.includes("game")) parts.push({ icon: "x-circle", text: "Dishonesty" });
    if (t.includes("phone") && t.includes("addict")) parts.push({ icon: "x-circle", text: "Phone addiction" });
    if (t.includes("passive")) parts.push({ icon: "x-circle", text: "Passive-aggressive" });
    return parts;
  };
  const seen = new Set();
  [a.hard_dealbreakers, a.lifestyle_dealbreakers, a.dealbreakers, a.substance_preferences].forEach(txt => {
    parse(txt).forEach(item => {
      if (!seen.has(item.text)) { seen.add(item.text); items.push(item); }
    });
  });
  // fitness reference in dealbreakers
  if (a.fitness && !seen.has("Active")) items.push({ icon: "runner", text: "Active" });
  return items;
}

function extractKeywords(a, userContext) {
  const words = new Set();
  // Pull short keywords from various answer fields
  const addWords = (text) => {
    if (!text) return;
    // Extract short phrases (1-3 words that feel like tags)
    const lowered = text.toLowerCase();
    const kwMap = [
      "funny", "country music", "photography", "ceramics", "cooking", "gym", "hiking",
      "yoga", "running", "reading", "travel", "coffee", "wine", "sushi", "japanese food",
      "mexican food", "mediterranean", "family", "kids", "personal growth", "product design",
      "early bird", "night owl", "coffee date", "action movies", "indie films", "documentaries",
      "communication is key", "dostoevsky", "minimalism", "sustainable living", "plant care",
      "basketball", "cycling", "trail running"
    ];
    kwMap.forEach(kw => { if (lowered.includes(kw)) words.add(kw); });
  };
  addWords(a.hobbies); addWords(a.food_preferences); addWords(a.drink_preferences);
  addWords(a.social_activities); addWords(a.core_values); addWords(a.employment);
  addWords(a.self_description); addWords(a.love_language); addWords(a.daily_rhythm);
  addWords(a.weekend_lifestyle); addWords(a.travel_history); addWords(a.date_preferences);
  // Add context keywords from all stages (except suppression list)
  if (userContext) {
    Object.entries(userContext).forEach(([k, stageCtx]) => {
      if (k === "kw_hidden") return;
      if (Array.isArray(stageCtx)) stageCtx.forEach(c => words.add(c));
    });
  }
  const hidden = Array.isArray(userContext?.kw_hidden) ? userContext.kw_hidden : [];
  return [...words].filter(w => !hidden.includes(w));
}

// ─── Add Context Panel (Mobile Drawer / Desktop Dropdown) ───────
const addCtxDrawerVariants = {
  hidden: { y: "100%", opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 320, damping: 30, mass: 0.8, staggerChildren: 0.06, delayChildren: 0.1 } },
  exit: { y: "100%", opacity: 0, transition: { type: "spring", stiffness: 300, damping: 30 } },
};
const addCtxDropdownVariants = {
  hidden: { opacity: 0, scale: 0.92, y: -4 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { type: "spring", stiffness: 400, damping: 28, mass: 0.6, staggerChildren: 0.05, delayChildren: 0.06 } },
  exit: { opacity: 0, scale: 0.92, y: -4, transition: { duration: 0.15 } },
};
const addCtxItemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 340, damping: 26 } },
};

function AddContextPanel({ open, onClose, accentColor, contextVisibility, setContextVisibility, contextText, setContextText, onSave, label, isExact, setIsExact }) {
  const bp = useBreakpoint();
  const panelRef = useRef(null);
  const backdropRef = useRef(null);
  const inputRef = useRef(null);
  const tlRef = useRef(null);
  // Desktop: keep panel mounted during GSAP exit animation
  const [desktopRendered, setDesktopRendered] = useState(open && !bp.isMobile);

  useEffect(() => {
    if (open && inputRef.current) {
      const t = setTimeout(() => inputRef.current?.focus(), bp.isMobile ? 350 : 120);
      return () => clearTimeout(t);
    }
  }, [open, bp.isMobile]);

  // Desktop: manage mount/unmount around GSAP animation
  useEffect(() => {
    if (bp.isMobile) return;
    if (open) {
      setDesktopRendered(true);
    } else if (desktopRendered && tlRef.current) {
      tlRef.current.eventCallback('onReverseComplete', () => setDesktopRendered(false));
      tlRef.current.reverse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bp.isMobile]);

  // Desktop: GSAP timeline (ConnectFlyout-style scale + stagger)
  useLayoutEffect(() => {
    if (bp.isMobile || !desktopRendered || !panelRef.current) return;
    const panel = panelRef.current;
    const items = panel.querySelectorAll(':scope > [data-ctx-item]');
    if (tlRef.current) { tlRef.current.kill(); tlRef.current = null; }
    const tl = gsap.timeline({ paused: true });
    if (backdropRef.current) {
      tl.fromTo(backdropRef.current, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: "power2.out" }, 0);
    }
    tl.fromTo(panel,
      { scale: 0.08, opacity: 0, transformOrigin: "50% 50%" },
      { scale: 1, opacity: 1, transformOrigin: "50% 50%", duration: 0.42, ease: "back.out(1.2)" }, 0.05);
    if (items.length) {
      tl.fromTo(items,
        { y: 30, opacity: 0, scale: 0.92 },
        { y: 0, opacity: 1, scale: 1, duration: 0.38, ease: "power3.out", stagger: 0.08 }, 0.28);
    }
    tlRef.current = tl;
    tl.play(0);
    return () => { tl.kill(); tlRef.current = null; };
  }, [desktopRendered, bp.isMobile]);

  // Mobile: hide bottom nav bar so it cannot overlap the drawer
  useEffect(() => {
    if (!bp.isMobile || !open) return;
    const nav = document.body.querySelector('nav');
    if (!nav) return;
    nav.style.display = 'none';
    return () => { nav.style.display = 'flex'; };
  }, [open, bp.isMobile]);

  // Desktop: close on outside click
  useEffect(() => {
    if (!open || bp.isMobile) return;
    const handler = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) onClose(); };
    const t = setTimeout(() => document.addEventListener("mousedown", handler), 10);
    return () => { clearTimeout(t); document.removeEventListener("mousedown", handler); };
  }, [open, bp.isMobile, onClose]);

  // Desktop: close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const secretColor = "#7c3aed";
  const activeColor = contextVisibility === "secret" ? secretColor : accentColor;

  const content = (
    <>
      <motion.div data-ctx-item variants={addCtxItemVariants} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, letterSpacing: "-0.01em" }}>{label || "Add Context"}</div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => { setContextVisibility("public"); setTimeout(() => inputRef.current?.focus(), 50); }}
            style={{ padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700, border: `1.5px solid ${contextVisibility === "public" ? accentColor : T.border}`, background: contextVisibility === "public" ? `${accentColor}15` : "transparent", color: contextVisibility === "public" ? accentColor : T.muted, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}>
            Public
          </button>
          <button onClick={() => { setContextVisibility("secret"); setTimeout(() => inputRef.current?.focus(), 50); }}
            style={{ padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700, border: `1.5px solid ${contextVisibility === "secret" ? secretColor : T.border}`, background: contextVisibility === "secret" ? `${secretColor}15` : "transparent", color: contextVisibility === "secret" ? secretColor : T.muted, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}>
            Secret
          </button>
        </div>
      </motion.div>
      <div data-ctx-item>
        <AnimatePresence mode="wait">
          {contextVisibility === "secret" ? (
            <motion.div key="secret-hint" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }}
              style={{ fontSize: 11, color: secretColor, fontWeight: 500, marginBottom: 10, lineHeight: 1.5, padding: "8px 12px", background: `${secretColor}08`, borderRadius: 8, border: `1px solid ${secretColor}20`, overflow: "hidden" }}>
              🔒 No one will ever see this. It will only be used to improve your matches and will be kept completely private.
            </motion.div>
          ) : (
            <motion.div key="public-hint" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }}
              style={{ fontSize: 11, color: T.muted, fontWeight: 500, marginBottom: 10, lineHeight: 1.5, overflow: "hidden" }}>
              This will appear on your public profile as a keyword or added to your snapshot descriptions.
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <motion.div data-ctx-item variants={addCtxItemVariants} style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input ref={inputRef} value={contextText} onChange={e => setContextText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && contextText.trim()) onSave(); }}
          placeholder={contextVisibility === "secret" ? "Add a private preference or criteria..." : "Add an interest, keyword, or detail..."}
          style={{ flex: 1, padding: "10px 14px", borderRadius: 10, border: `1.5px solid ${activeColor}40`, background: T.bg, color: T.text, fontSize: 14, fontFamily: "inherit", outline: "none", transition: "border-color 0.2s" }} />
      </motion.div>
      {typeof setIsExact === "function" && (
        <motion.div data-ctx-item variants={addCtxItemVariants} style={{ marginTop: 10 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}>
            <input type="checkbox" checked={!!isExact} onChange={e => setIsExact(e.target.checked)}
              style={{ width: 16, height: 16, accentColor: "#ef4444", cursor: "pointer" }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>Must match exactly</span>
          </label>
          <AnimatePresence>
            {isExact && (
              <motion.div key="exact-warn" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }}
                style={{ marginTop: 8, padding: "8px 12px", background: "#ef444410", border: "1px solid #ef444430", borderRadius: 8, fontSize: 11, color: "#b91c1c", fontWeight: 500, lineHeight: 1.5, overflow: "hidden" }}>
                ⚠️ <b>Strongly discouraged.</b> You will only be matched with users whose profile contains this exact phrase (capitalization ignored). This greatly reduces your matches.
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
      <motion.div data-ctx-item variants={addCtxItemVariants} style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button onClick={() => { if (contextText.trim()) onSave(); }}
          style={{ flex: 1, padding: "11px 0", borderRadius: 10, background: activeColor, color: "#fff", fontSize: 13, fontWeight: 700, border: "none", cursor: "pointer", fontFamily: "inherit", transition: "background 0.2s", boxShadow: `0 2px 12px ${activeColor}30` }}>
          Save
        </button>
        <button onClick={onClose}
          style={{ padding: "11px 20px", borderRadius: 10, background: "transparent", color: T.muted, fontSize: 13, fontWeight: 600, border: `1.5px solid ${T.border}`, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}>
          Cancel
        </button>
      </motion.div>
    </>
  );

  if (bp.isMobile) {
    // Mobile: bottom sheet drawer
    return ReactDOM.createPortal(
      <AnimatePresence>
        {open && (
          <>
            <motion.div key="ctx-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
              onClick={onClose}
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", zIndex: 2147483647 }} />
            <motion.div key="ctx-drawer" variants={addCtxDrawerVariants} initial="hidden" animate="visible" exit="exit"
              style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 2147483647, background: T.card, borderRadius: "20px 20px 0 0", padding: "20px 20px calc(28px + env(safe-area-inset-bottom, 0px))", boxShadow: "0 -8px 40px rgba(0,0,0,0.25)", maxHeight: "85vh", overflowY: "auto" }}>
              {/* Drag handle */}
              <motion.div variants={addCtxItemVariants} style={{ width: 36, height: 4, borderRadius: 2, background: T.border, margin: "0 auto 18px" }} />
              {content}
            </motion.div>
          </>
        )}
      </AnimatePresence>,
      document.body
    );
  }

  // Desktop: centered modal with darkened backdrop (matches ConnectFlyout style)
  if (!desktopRendered) return null;
  return ReactDOM.createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 2000 }} onClick={onClose}>
      <div ref={backdropRef} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", opacity: 0 }} />
      <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", height: "100%", padding: 20 }}>
        <div ref={panelRef} onClick={e => e.stopPropagation()}
          style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: "22px 22px 18px", width: "100%", maxWidth: 460, boxShadow: "0 20px 60px rgba(0,0,0,0.25), 0 1px 3px rgba(0,0,0,0.08)", opacity: 0, willChange: "transform, opacity" }}>
          {content}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Snapshot Header Pill Component ─────────────────────────────
const SnapPill = ({ icon, text, variant = "basic", size = "sm" }) => {
  const colors = variant === "positive" ? { bg: "#22c55e12", border: "#22c55e40", dot: "#22c55e", text: T.text }
    : variant === "negative" ? { bg: "#ef444412", border: "#ef444440", dot: "#ef4444", text: T.text }
    : { bg: "transparent", border: T.border, dot: T.muted, text: T.text };
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: size === "sm" ? "3px 10px 3px 7px" : "4px 12px 4px 8px", borderRadius: 20, background: colors.bg, border: `1px solid ${colors.border}`, fontSize: size === "sm" ? 12 : 13, fontWeight: 600, color: colors.text, lineHeight: 1.3 }}>
      <PillIcon type={icon} size={size === "sm" ? 13 : 14} color={colors.dot} />
      {text}
    </div>
  );
};

// ─── SCREEN: User Profile + Stage Progress ─────────────────────
const STAGE_DEFS = [
  { n: 1,  name: "Identity & Basics",      Icon: UserIcon,     AnimIcon: AnimUserIcon },
  { n: 2,  name: "Personality Deep Dive",  Icon: BrainIcon,    AnimIcon: AnimBrainIcon },
  { n: 3,  name: "Values & Beliefs",       Icon: ScaleIcon,    AnimIcon: AnimGavelIcon },
  { n: 4,  name: "Relationship Goals",     Icon: HeartIcon,    AnimIcon: AnimHeartIcon },
  { n: 5,  name: "Lifestyle & Schedule",   Icon: LeafIcon,     AnimIcon: AnimSunIcon },
  { n: 6,  name: "Emotional Intelligence", Icon: HeartIcon,    AnimIcon: AnimHeartPulseIcon },
  { n: 7,  name: "Date Preferences",       Icon: WineIcon,     AnimIcon: AnimCupSodaIcon },
  { n: 8,  name: "Physical Attraction",    Icon: SparklesIcon, AnimIcon: AnimSparklesIcon },
  { n: 9,  name: "Food & Social Life",     Icon: RestaurantIcon, AnimIcon: AnimCookingPotIcon },
  { n: 10, name: "Humor & Vibe",           Icon: SmileIcon,    AnimIcon: AnimSmileIcon },
  { n: 11, name: "Spirituality",           Icon: MoonIcon,     AnimIcon: AnimMoonIcon },
  { n: 12, name: "Future Vision",          Icon: CrystalBallIcon, AnimIcon: AnimTelescopeIcon },
  { n: 13, name: "Dealbreakers",           Icon: BlockIcon,    AnimIcon: AnimBanIcon },
  { n: 14, name: "Voicemail Recording",    Icon: MicIcon,      AnimIcon: AnimMicIcon },
];

function UserProfile({ profile, completedStage = 0, completedStages, isPremium = false, onBack, onStartAdmin, onStartStage, onEditPreferences, onSelectMatch, onLogout, isDarkMode, toggleTheme, colorScheme, changeColorScheme, blindMode = false, onNavigateToSettings }) {
  const [showTravel, setShowTravel] = useState(false);
  const [travelCity, setTravelCity] = useState("");
  const [travelSaved, setTravelSaved] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [vmPlaying, setVmPlaying] = useState(false);
  const [vmReactions, setVmReactions] = useState(_getVoicemailReactions);
  const [playingMatchId, setPlayingMatchId] = useState(null);
  const [givenReactions, setGivenReactions] = useState(_getGivenReactions);
  const [receivedVmReactions, setReceivedVmReactions] = useState(() => {
    try { return JSON.parse(localStorage.getItem('soul_received_vm_reactions') || '[]'); } catch { return []; }
  });
  const [appConnections, setAppConnections] = useState(() => {
    try { const s = localStorage.getItem('app_connections'); return s ? JSON.parse(s) : {}; } catch { return {}; }
  });
  const [svcLoginModal, setSvcLoginModal] = useState(null);
  const [snapName, setSnapName] = useState(() => {
    try { const u = JSON.parse(localStorage.getItem("soul_user") || "{}"); if (u.name) return u.name; if (u.first_name) return u.first_name; } catch {}
    try { const ans = JSON.parse(localStorage.getItem("soul_user_answers") || "{}"); if (ans.first_name) return ans.first_name; } catch {}
    return "User";
  });
  const [userAnswers, setUserAnswers] = useState(() => {
    try { return JSON.parse(localStorage.getItem("soul_user_answers") || "{}"); } catch { return {}; }
  });
  // Re-read userAnswers and snapName after server sync completes (cross-device sync)
  useEffect(() => {
    const onSync = () => {
      try {
        const ans = JSON.parse(localStorage.getItem("soul_user_answers") || "{}");
        setUserAnswers(ans);
        const name = ans.first_name || (() => { try { const u = JSON.parse(localStorage.getItem("soul_user") || "{}"); return u.name || u.first_name; } catch { return null; } })() || "User";
        setSnapName(name);
      } catch {}
    };
    window.addEventListener("soul_sync_complete", onSync);
    return () => window.removeEventListener("soul_sync_complete", onSync);
  }, []);
  const [addContextStage, setAddContextStage] = useState(null);
  const [contextText, setContextText] = useState("");
  const [contextVisibility, setContextVisibility] = useState("public"); // "public" or "secret"
  const [isExact, setIsExact] = useState(false);
  const [expandedCtxBtn, setExpandedCtxBtn] = useState(null);
  const [editingCtx, setEditingCtx] = useState(null);
  const [editingKw, setEditingKw] = useState(null);
  const [showReactionsModal, setShowReactionsModal] = useState(false);
  const [userContext, setUserContext] = useState(() => {
    try { return JSON.parse(localStorage.getItem("soul_user_context") || "{}"); } catch { return {}; }
  });
  const [secretContext, setSecretContext] = useState(() => {
    try { return JSON.parse(localStorage.getItem("soul_secret_context") || "[]"); } catch { return []; }
  });
  const [exactContext, setExactContext] = useState(() => {
    try { return JSON.parse(localStorage.getItem("soul_exact_context") || "[]"); } catch { return []; }
  });
  const handleSvcConnect = (appId) => {
    setAppConnections(prev => {
      const updated = { ...prev, [appId]: true };
      localStorage.setItem('app_connections', JSON.stringify(updated));
      syncAppConnections(updated);
      window.dispatchEvent(new Event('app_connections_changed'));
      return updated;
    });
  };
  const handleSvcDisconnect = (appId) => {
    setAppConnections(prev => {
      const updated = { ...prev, [appId]: false };
      localStorage.setItem('app_connections', JSON.stringify(updated));
      syncAppConnections(updated);
      window.dispatchEvent(new Event('app_connections_changed'));
      return updated;
    });
  };

  const handleOrbClick = (e, matchId) => {
    e.stopPropagation();
    setPlayingMatchId(prev => prev === matchId ? null : matchId);
  };
  const handleReaction = (e, matchId, emoji) => {
    e.stopPropagation();
    _addVoicemailReaction(emoji);
    _saveGivenReaction(matchId, emoji);
    setGivenReactions(prev => ({ ...prev, [matchId]: emoji }));
    setPlayingMatchId(null);
    window.dispatchEvent(new Event('givenReactionsChanged'));
  };

  useEffect(() => {
    const sync = () => setGivenReactions(_getGivenReactions());
    window.addEventListener('givenReactionsChanged', sync);
    return () => window.removeEventListener('givenReactionsChanged', sync);
  }, []);

  useEffect(() => {
    const handler = () => setVmReactions(_getVoicemailReactions());
    window.addEventListener("voicemailReaction", handler);
    return () => window.removeEventListener("voicemailReaction", handler);
  }, []);

  useEffect(() => {
    fetchReceivedReactions().then(r => { if (r && r.length) setReceivedVmReactions(r); });
  }, []);

  useEffect(() => {
    const syncConns = () => {
      try { const s = localStorage.getItem('app_connections'); setAppConnections(s ? JSON.parse(s) : {}); } catch {}
    };
    window.addEventListener('app_connections_changed', syncConns);
    return () => window.removeEventListener('app_connections_changed', syncConns);
  }, []);

  useEffect(() => {
    if (!vmPlaying) return;
    const t = setTimeout(() => setVmPlaying(false), 60000);
    return () => clearTimeout(t);
  }, [vmPlaying]);
  const doneSet = completedStages instanceof Set ? completedStages : new Set();
  const doneCount = doneSet.size;
  const overallPct = Math.round((doneCount / STAGE_DEFS.length) * 100);

  const handleSaveTravel = async () => {
    if (!travelCity.trim()) return;
    setGettingLocation(true);
    try {
      // Geocode city name → lat/lng using browser geolocation or nominatim fallback
      const resp = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(travelCity)}&format=json&limit=1`);
      const data = await resp.json();
      if (data.length) {
        await fetch(`${API_BASE}/api/user/travel-location`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("soul_session_token")}` },
          body: JSON.stringify({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon), city: travelCity }),
        });
        setTravelSaved(true);
        setTimeout(() => { setShowTravel(false); setTravelSaved(false); }, 2000);
      }
    } catch {}
    setGettingLocation(false);
  };

  const bp = useBreakpoint();
  const containerPad = bp.isDesktop ? "24px 32px 48px" : bp.isTablet ? "28px 32px 60px" : "24px 20px 100px";
  const maxW = bp.isDesktop ? 1400 : bp.isTablet ? 680 : 480;

  // ─── Snapshot section definitions: each stage → section title, sample Qs, completed data ───
  // Helper to build a creative summary sentence from answer fragments
  const a = userAnswers;
  const ac = appConnections; // shorthand for app connections
  const SNAPSHOT_SECTIONS = [
    {
      stage: 1, title: "IDENTITY & BASICS", color: T.rose,
      sampleQs: ["What's your first name?", "How old are you?", "What type of relationship are you looking for?"],
      getInsight: () => {
        const name = a.first_name || "Someone";
        const age = a.age || "";
        const loc = a.location || "";
        const intent = a.relationship_intent || "";
        const dist = a.travel_distance || "";
        const gender = a.gender || "";
        const pref = a.dating_gender_preference || "";
        const status = a.relationship_status || "";
        const single = a.single_duration || "";
        const intentShort = intent.toLowerCase().includes("marriage") ? "looking for something real — ideally leading to marriage" : intent.toLowerCase().includes("serious") ? "looking for a meaningful, long-term connection" : intent.toLowerCase().includes("casual") ? "keeping things light and open for now" : "exploring what feels right";
        const distShort = dist.toLowerCase().includes("anywhere") ? "and open to love wherever it finds them" : dist.toLowerCase().includes("local") ? "focused on connecting locally" : "willing to go the distance for the right person";
        const summary = `${name}, ${age}${loc ? ` from ${loc}` : ""}. ${gender}${pref ? `, interested in ${pref.toLowerCase()}` : ""}. ${status}${single ? ` for ${single.toLowerCase()}` : ""} — ${intentShort}, ${distShort}.`;
        return { type: "text", label: "WHO YOU ARE", content: summary };
      }
    },
    {
      stage: 2, title: "PHYSICAL PROFILE", color: T.rose,
      sampleQs: ["How tall are you?", "How would you describe your body type?", "What physical traits attract you?"],
      getInsight: () => {
        const h = a.height || ""; const bt = a.body_type || ""; const eth = a.ethnicity || "";
        const phys = a.physical_preferences || ""; const hp = a.height_preference || ""; const btp = a.body_type_preference || "";
        const parts = [];
        if (h || bt) parts.push(`${h}${bt ? `, ${bt.toLowerCase()} build` : ""}`);
        if (eth) parts.push(eth);
        const prefParts = [];
        if (hp) prefParts.push(`prefers someone ${hp}`);
        if (btp) prefParts.push(btp.toLowerCase());
        if (phys) prefParts.push(phys);
        const summary = (parts.length ? parts.join(". ") + "." : "") + (prefParts.length ? " Attracted to " + prefParts.join(" — ") + "." : "");
        return { type: "text", label: "PHYSICAL PROFILE", content: summary || "Physical profile captured." };
      }
    },
    {
      stage: 3, title: "CULTURE & BACKGROUND", color: T.violet,
      sampleQs: ["What languages do you speak fluently?", "How important is cultural compatibility?", "Any cultural traditions important to you?"],
      getInsight: () => {
        const lang = a.languages || ""; const born = a.birth_country || "";
        const imp = a.cultural_importance || ""; const open = a.cultural_openness || ""; const trad = a.cultural_traditions || "";
        const parts = [];
        if (lang) parts.push(`Speaks ${lang}`);
        if (born) parts.push(born);
        if (trad) parts.push(trad);
        if (imp) parts.push(imp);
        if (open && (open.toLowerCase().includes("yes") || open.toLowerCase().includes("absolutely"))) parts.push("Open to dating across cultures.");
        return { type: "text", label: "CULTURAL IDENTITY", content: parts.join(". ") || "Cultural background captured." };
      }
    },
    {
      stage: 4, title: "FAMILY & RELATIONSHIP HISTORY", color: T.teal,
      sampleQs: ["How close are you with your family?", "Do you have or want children?", "What did your last relationship teach you?"],
      getInsight: () => {
        const fam = a.family_closeness || ""; const kids = a.children || "";
        const lesson = a.relationship_lesson || ""; const last = a.last_relationship || ""; const reason = a.breakup_reason || "";
        const parts = [];
        if (fam) parts.push(fam);
        if (kids) parts.push(kids);
        if (lesson) parts.push(`Biggest takeaway: "${lesson}"`);
        return { type: "text", label: "FAMILY & HISTORY", content: parts.join(". ") || "Family and relationship history captured." };
      }
    },
    {
      stage: 5, title: "CAREER & STABILITY", color: T.amber,
      sampleQs: ["What line of work are you in?", "Where do you see yourself in 10 years?", "What days are you free for dates?"],
      getInsight: () => {
        const job = a.employment || ""; const sched = a.work_schedule || "";
        const near = a.near_future_plans || ""; const long = a.long_term_goals || ""; const avail = a.schedule_availability || "";
        const parts = [];
        if (job) parts.push(job);
        if (near) parts.push(`Near-term: ${near}`);
        if (long) parts.push(`Long-term vision: ${long}`);
        if (avail) parts.push(`Free for dates: ${avail}`);
        const appIns5 = getAppInsightsForStage(5, ac);
        if (appIns5.length) parts.push(...appIns5.flatMap(i => i.data.map(d => `[${i.label}] ${d}`)));
        return { type: "text", label: "CAREER & AMBITION", content: parts.join(". ") || "Career and goals captured." };
      }
    },
    {
      stage: 6, title: "LIFESTYLE & DAILY LIFE", color: T.teal,
      sampleQs: ["Are you a morning person or night owl?", "How often do you exercise?", "What hobbies mean the most to you?"],
      getInsight: () => {
        const wk = a.weekend_lifestyle || ""; const rhythm = a.daily_rhythm || "";
        const fit = a.fitness || ""; const social = a.social_activities || ""; const hobby = a.hobbies || "";
        const parts = [];
        if (rhythm) parts.push(rhythm);
        if (fit) parts.push(fit);
        if (wk) parts.push(`Weekends: ${wk}`);
        if (hobby) parts.push(`Passions: ${hobby}`);
        if (social) parts.push(social);
        const appIns6 = getAppInsightsForStage(6, ac);
        if (appIns6.length) parts.push(...appIns6.flatMap(i => i.data.map(d => `[${i.label}] ${d}`)));
        return { type: "text", label: "DAY-TO-DAY LIFE", content: parts.join(". ") || "Lifestyle captured." };
      }
    },
    {
      stage: 7, title: "DATING STYLE", color: T.rose,
      sampleQs: ["Coffee, dinner, or a day activity for a first date?", "What atmosphere do you enjoy on dates?", "How often would you like to see a partner?"],
      getInsight: () => {
        const pref = a.date_preferences || ""; const atm = a.date_atmosphere || "";
        const freq = a.date_frequency || ""; const ideal = a.ideal_date_vision || "";
        const parts = [];
        if (pref) parts.push(`First date style: ${pref}`);
        if (atm) parts.push(`Best atmosphere: ${atm}`);
        if (freq) parts.push(`Ideal frequency: ${freq}`);
        if (ideal) parts.push(`Dream date: "${ideal}"`);
        return { type: "text", label: "HOW YOU DATE", content: parts.join(". ") || "Dating style captured." };
      }
    },
    {
      stage: 8, title: "LOGISTICS & MONEY", color: T.amber,
      sampleQs: ["What's your typical budget for a date?", "How do you feel about splitting the bill?", "How far will you travel for a relationship?"],
      getInsight: () => {
        const budget = a.date_budget || ""; const fin = a.financial_preferences || ""; const dist = a.relationship_distance || "";
        const parts = [];
        if (budget) parts.push(`Date budget: ${budget}`);
        if (fin) parts.push(fin);
        if (dist) parts.push(`Distance comfort: ${dist}`);
        return { type: "text", label: "PRACTICAL SIDE", content: parts.join(". ") || "Logistics captured." };
      }
    },
    {
      stage: 9, title: "FOOD & SOCIAL HABITS", color: T.teal,
      sampleQs: ["What are your top three cuisines?", "How do you take your coffee?", "Do you smoke or use recreational substances?"],
      getInsight: () => {
        const food = a.food_preferences || ""; const dislike = a.disliked_foods || "";
        const drink = a.drink_preferences || ""; const sub = a.substance_preferences || "";
        const parts = [];
        if (food) parts.push(food);
        if (dislike) parts.push(`Can't do: ${dislike}`);
        if (drink) parts.push(drink);
        if (sub) parts.push(sub);
        const appIns9 = getAppInsightsForStage(9, ac);
        if (appIns9.length) parts.push(...appIns9.flatMap(i => i.data.map(d => `[${i.label}] ${d}`)));
        return { type: "text", label: "FOOD & SOCIAL LIFE", content: parts.join(". ") || "Social habits captured." };
      }
    },
    {
      stage: 10, title: "TRAVEL & EXPERIENCES", color: T.violet,
      sampleQs: ["What countries have you traveled to?", "How important is travel in your life?"],
      getInsight: () => {
        const hist = a.travel_history || ""; const imp = a.travel_importance || "";
        const parts = [];
        if (hist) parts.push(hist);
        if (imp) parts.push(imp);
        const appIns10 = getAppInsightsForStage(10, ac);
        if (appIns10.length) parts.push(...appIns10.flatMap(i => i.data.map(d => `[${i.label}] ${d}`)));
        return { type: "text", label: "WANDERLUST", content: parts.join(". ") || "Travel profile captured." };
      }
    },
    {
      stage: 11, title: "VALUES & BELIEFS", color: T.rose,
      sampleQs: ["How would you describe your political views?", "How important is religion or spirituality?", "Any core values essential in a relationship?"],
      getInsight: () => {
        const pol = a.political_views || ""; const rel = a.religion || ""; const val = a.core_values || "";
        const parts = [];
        if (val) parts.push(val);
        if (rel) parts.push(rel);
        if (pol) parts.push(pol);
        const appIns11 = getAppInsightsForStage(11, ac);
        if (appIns11.length) parts.push(...appIns11.flatMap(i => i.data.map(d => `[${i.label}] ${d}`)));
        return { type: "text", label: "WHAT YOU STAND FOR", content: parts.join(". ") || "Values captured." };
      }
    },
    {
      stage: 12, title: "RELATIONSHIP PSYCHOLOGY", color: T.teal,
      sampleQs: ["What qualities do you value most in a relationship?", "How do you handle conflict?", "What's your primary love language?"],
      getInsight: () => {
        const rv = a.relationship_values || ""; const db = a.dealbreakers || "";
        const conf = a.conflict_resolution || ""; const stress = a.stress_needs || "";
        const love = a.love_language || ""; const app = a.appreciation_style || "";
        const parts = [];
        if (rv) parts.push(`Values most: ${rv}`);
        if (love) parts.push(`Love language: ${love}`);
        if (conf) parts.push(`Handles conflict by: ${conf}`);
        if (stress) parts.push(`When stressed, needs: ${stress}`);
        if (app) parts.push(`Feels loved when: ${app}`);
        return { type: "text", label: "EMOTIONAL WIRING", content: parts.join(". ") || "Relationship psychology captured." };
      }
    },
    {
      stage: 13, title: "SELF AWARENESS", color: T.violet,
      sampleQs: ["What are your biggest pet peeves in relationships?", "What do people misunderstand about you?", "What does a healthy relationship look like to you?"],
      getInsight: () => {
        const pet = a.pet_peeves || ""; const first = a.first_impression || "";
        const self = a.self_description || ""; const ideal = a.ideal_partner_effect || "";
        const vision = a.healthy_relationship_vision || "";
        const parts = [];
        if (self) parts.push(`Describes themselves as: ${self}`);
        if (first) parts.push(first);
        if (ideal) parts.push(`The right person makes them: ${ideal}`);
        if (vision) parts.push(`Healthy love looks like: "${vision}"`);
        if (pet) parts.push(`Pet peeves: ${pet}`);
        return { type: "text", label: "SELF PORTRAIT", content: parts.join(". ") || "Self-awareness captured." };
      }
    },
    {
      stage: 14, title: "MUST-HAVES & DEALBREAKERS", color: T.rose,
      sampleQs: ["What qualities are absolute must-haves?", "What behaviors are relationship impossibilities?", "Any lifestyle choices that rule someone out?"],
      getInsight: () => {
        const must = a.must_haves || ""; const hard = a.hard_dealbreakers || ""; const life = a.lifestyle_dealbreakers || "";
        const parts = [];
        if (must) parts.push(`Must-haves: ${must}`);
        if (hard) parts.push(`Absolute dealbreakers: ${hard}`);
        if (life) parts.push(`Lifestyle non-negotiables: ${life}`);
        return { type: "text", label: "NON-NEGOTIABLES", content: parts.join(". ") || "Dealbreakers captured." };
      }
    },
  ];

  // Helper: get stage progress info
  const getStageProgress = (stageNum) => {
    const done = doneSet.has(stageNum);
    const stageTotal = AI_QUESTIONS.filter(q => q.stage === stageNum).length;
    let savedStep = -1;
    try { const raw = localStorage.getItem("soul_interview_progress"); if (raw) { const p = JSON.parse(raw); if (p.stage === stageNum && p.step >= 0) savedStep = p.step; } } catch {}
    const hasPartial = !done && savedStep >= 0;
    const pct = done ? 100 : hasPartial ? Math.round(((savedStep + 1) / stageTotal) * 100) : 0;
    return { done, hasPartial, pct, savedStep, stageTotal };
  };

  // Legacy detail level for header badge
  const getDetailLevel = () => {
    const d = doneSet;
    if (d.has(12) || d.has(13) || d.has(14)) return 'complete';
    if (d.has(8) || d.has(9) || d.has(10) || d.has(11)) return 'advanced';
    if (d.has(4) || d.has(5) || d.has(6) || d.has(7)) return 'intermediate';
    return 'basic';
  };
  const detailLevel = getDetailLevel();

  return (
    <div style={{ maxWidth: maxW, margin: "0 auto", padding: bp.isDesktop ? containerPad : bp.isTablet ? "12px 32px 60px" : "10px 20px 100px", overflowX: bp.isDesktop ? "clip" : "visible", overflow: bp.isDesktop ? undefined : "visible" }}>
      {/* ── Mobile/Tablet Header ── */}
      {!bp.isDesktop && <>
        {/* DASHBOARD label + streak + progress circle */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <p style={{ fontSize: 13, color: T.rose, fontWeight: 700, letterSpacing: "0.12em", margin: 0 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>DASHBOARD <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "2px 8px", borderRadius: 4,
              background: "#f59e0b18", border: "1px solid #f59e0b40",
              animation: "streakGlow 2.5s ease-in-out infinite",
              fontSize: 13, fontWeight: 700, color: "#f59e0b", letterSpacing: "normal"
            }}>🔥 {doneCount + 1} day streak</span></span>
          </p>
          <div style={{ textAlign: "center", flexShrink: 0 }}>
            <div style={{ fontSize: 11, color: T.muted, marginBottom: 2 }}>Profile Complete</div>
            {(() => { const s = 90; const pad = 2; const full = s + pad * 2; return (
            <div style={{ position: "relative", display: "inline-block", width: full, height: full }}>
              <svg width={full} height={full} style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}>
                <circle cx={full/2} cy={full/2} r="0" fill="none" stroke={T.rose} strokeWidth="3">
                  <animate attributeName="r" calcMode="spline" dur="2.4s" keySplines=".52,.6,.25,.99" repeatCount="indefinite" values={`0;${full/2}`} />
                  <animate attributeName="opacity" calcMode="spline" dur="2.4s" keySplines=".52,.6,.25,.99" repeatCount="indefinite" values="0.5;0" />
                </circle>
                <circle cx={full/2} cy={full/2} r="0" fill="none" stroke={T.rose} strokeWidth="2">
                  <animate attributeName="r" calcMode="spline" dur="2.4s" keySplines=".52,.6,.25,.99" repeatCount="indefinite" values={`0;${full/2}`} begin="0.6s" />
                  <animate attributeName="opacity" calcMode="spline" dur="2.4s" keySplines=".52,.6,.25,.99" repeatCount="indefinite" values="0.35;0" begin="0.6s" />
                </circle>
                <circle cx={full/2} cy={full/2} r="0" fill="none" stroke={T.rose} strokeWidth="1.5">
                  <animate attributeName="r" calcMode="spline" dur="2.4s" keySplines=".52,.6,.25,.99" repeatCount="indefinite" values={`0;${full/2}`} begin="1.2s" />
                  <animate attributeName="opacity" calcMode="spline" dur="2.4s" keySplines=".52,.6,.25,.99" repeatCount="indefinite" values="0.2;0" begin="1.2s" />
                </circle>
              </svg>
              <div style={{ position: "absolute", top: pad, left: pad }}>
                <ScoreRing value={overallPct} size={s} color={T.rose} />
              </div>
            </div>); })()}
          </div>
        </div>
        {/* settup text — sticky, pulled up to sit right under DASHBOARD label */}
        <div style={{ position: "sticky", top: 0, zIndex: 50, marginTop: -95, marginBottom: 4, paddingBottom: 14, overflow: "visible", pointerEvents: "none" }}>
          <div onClick={() => { if (window.innerWidth < 768) window.dispatchEvent(new Event('openAppSwitcher')); }} style={{ fontFamily: "'MBF Canno', 'Segoe UI', sans-serif", textTransform: "lowercase", fontSize: 68, fontWeight: 900, background: `linear-gradient(90deg, ${T.rose}, ${T.violet}, ${T.rose})`, backgroundSize: "300% 100%", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "aurora 6s ease-in-out infinite", filter: `drop-shadow(0 0 8px ${T.rose}30) drop-shadow(0 0 20px ${T.rose}1a)`, lineHeight: 1.2, padding: "8px 4px 12px", margin: "-8px -4px -12px", pointerEvents: "auto", overflow: "visible", cursor: window.innerWidth < 768 ? "pointer" : undefined }}>settup</div>
        </div>
        {profile?.mbti && <p style={{ fontSize: 13, color: T.muted, marginTop: 0, marginBottom: 4 }}>{profile.mbti} · {profile.zodiacSign || "—"}</p>}
      </>}
      {/* ── Desktop Header ── */}
      {bp.isDesktop && (
      <PageHeader
        label={<span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>DASHBOARD <span style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          padding: "2px 8px", borderRadius: 4,
          background: "#f59e0b18", border: "1px solid #f59e0b40",
          animation: "streakGlow 2.5s ease-in-out infinite",
          fontSize: 13, fontWeight: 700, color: "#f59e0b", letterSpacing: "normal"
        }}>🔥 {doneCount + 1} day streak</span></span>}
        title="settup"
        titleStyle={{ fontFamily: "'MBF Canno', 'Segoe UI', sans-serif", textTransform: "lowercase", fontSize: 96, lineHeight: 1.2, padding: "6px 0 8px", background: `linear-gradient(90deg, ${T.rose}, ${T.violet}, ${T.rose})`, backgroundSize: "300% 100%", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "aurora 6s ease-in-out infinite", filter: `drop-shadow(0 0 8px ${T.rose}30) drop-shadow(0 0 20px ${T.rose}1a)` }}
        description={profile?.mbti ? `${profile.mbti} · ${profile.zodiacSign || "—"}` : undefined}
        alignTop
        rightContent={
          <div style={{ display: "flex", alignItems: "flex-start", gap: 24 }}>
            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "24px 32px", textAlign: "center" }}>
              <div style={{ fontSize: 13, color: T.rose, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 8 }}>COMPLETED</div>
              <div style={{ fontSize: 40, fontWeight: 900, color: T.text, fontFamily: "'Segoe UI', sans-serif" }}>{doneCount}/{STAGE_DEFS.length}</div>
              <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>Interview Stages</div>
            </div>
            <div style={{ textAlign: "center", flexShrink: 0 }}>
              <div style={{ fontSize: 13, color: T.muted, marginBottom: 6 }}>Profile Complete</div>
              {(() => { const s = 120; const pad = 10; const full = s + pad * 2; return (
              <div style={{ position: "relative", display: "inline-block", width: full, height: full }}>
                <svg width={full} height={full} style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}>
                  <circle cx={full/2} cy={full/2} r="0" fill="none" stroke={T.rose} strokeWidth="3">
                    <animate attributeName="r" calcMode="spline" dur="2.4s" keySplines=".52,.6,.25,.99" repeatCount="indefinite" values={`0;${full/2}`} />
                    <animate attributeName="opacity" calcMode="spline" dur="2.4s" keySplines=".52,.6,.25,.99" repeatCount="indefinite" values="0.5;0" />
                  </circle>
                  <circle cx={full/2} cy={full/2} r="0" fill="none" stroke={T.rose} strokeWidth="2">
                    <animate attributeName="r" calcMode="spline" dur="2.4s" keySplines=".52,.6,.25,.99" repeatCount="indefinite" values={`0;${full/2}`} begin="0.6s" />
                    <animate attributeName="opacity" calcMode="spline" dur="2.4s" keySplines=".52,.6,.25,.99" repeatCount="indefinite" values="0.35;0" begin="0.6s" />
                  </circle>
                  <circle cx={full/2} cy={full/2} r="0" fill="none" stroke={T.rose} strokeWidth="1.5">
                    <animate attributeName="r" calcMode="spline" dur="2.4s" keySplines=".52,.6,.25,.99" repeatCount="indefinite" values={`0;${full/2}`} begin="1.2s" />
                    <animate attributeName="opacity" calcMode="spline" dur="2.4s" keySplines=".52,.6,.25,.99" repeatCount="indefinite" values="0.2;0" begin="1.2s" />
                  </circle>
                </svg>
                <div style={{ position: "absolute", top: pad, left: pad }}>
                  <ScoreRing value={overallPct} size={s} color={T.rose} />
                </div>
              </div>); })()}
            </div>
          </div>
        }
      />
      )}

      {/* Match Preview Panel */}
      <div style={{ marginBottom: bp.isDesktop ? 32 : 24, marginTop: bp.isDesktop ? 0 : 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: bp.isDesktop ? 20 : 14 }}>
          <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.rose, fontWeight: 700, letterSpacing: "0.1em" }}>YOUR MATCHES</div>
          <div style={{ fontSize: bp.isDesktop ? 12 : 12, color: T.muted }}>Top {Math.min(3, MATCHES.length)} of {MATCHES.length}</div>
        </div>
        
        {/* Top 3 Matches — MiniMatchCard on mobile, full card on desktop */}
        {bp.isMobile ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
            {MATCHES.slice(0, 3).map((m, i) => (
              <div key={m.id}
                onClick={() => onSelectMatch && onSelectMatch(m)}
                style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: "12px 14px", cursor: "pointer", transition: "all 0.2s", animation: `slideStackIn 0.45s ${i * 0.12}s cubic-bezier(0.22,1,0.36,1) both` }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
                  {/* Photo + play button */}
                  {blindMode ? (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                      <div onClick={e => handleOrbClick(e, m.id)}
                        style={{ position: "relative", width: 70, height: 70, flexShrink: 0, cursor: "pointer" }}>
                        <div style={{ width: "100%", height: "100%", borderRadius: "50%", background: `linear-gradient(135deg, ${T.rose}, ${T.violet})`, display: "flex", alignItems: "center", justifyContent: "center", border: `2.5px solid ${T.rose}` }}>
                          <span style={{ fontSize: 24, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginLeft: playingMatchId === m.id ? 0 : 2 }}>{playingMatchId === m.id ? "⏸" : <PlayTriangle size={24} />}</span>
                        </div>
                        {givenReactions[m.id] && !playingMatchId && (
                          <div style={{ position: "absolute", bottom: -2, right: -2, width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, lineHeight: 1 }}>
                            {givenReactions[m.id]}
                          </div>
                        )}
                      </div>
                      {playingMatchId === m.id && (
                        <div style={{ width: 70, height: 3, borderRadius: 3, background: `${T.rose}30`, overflow: "hidden" }}>
                          <div style={{ height: "100%", background: `linear-gradient(90deg, ${T.rose}, ${T.violet})`, borderRadius: 3, animation: "voicemailProgress 60s linear forwards" }} />
                        </div>
                      )}
                      {playingMatchId === m.id && (
                        <div onClick={e => e.stopPropagation()} style={{ display: "flex", gap: 3, animation: "reactionSlideUp 0.3s ease both" }}>
                          {REACTION_EMOJIS.map((emoji, ei) => (
                            <button key={emoji} onClick={e => { triggerRipple(e); handleReaction(e, m.id, emoji); }}
                              style={{ width: 24, height: 24, borderRadius: "50%", border: `1px solid ${T.border}`, background: givenReactions[m.id] === emoji ? `${T.rose}22` : T.dim, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", animation: `emojiPop 0.3s ${ei * 0.05}s ease both`, transition: "transform 0.15s", padding: 0 }}
                              onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.25)"; }}
                              onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}>
                              {emoji}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                      <div style={{ position: "relative", width: 70, height: 70, flexShrink: 0 }}>
                        <div style={{ width: "100%", height: "100%", borderRadius: "50%", overflow: "hidden", border: `2.5px solid ${T.rose}` }}>
                          <img src={m.photo} alt={m.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                        </div>
                        <button onClick={e => handleOrbClick(e, m.id)} style={{
                          position: "absolute", bottom: -3, right: -3, width: 28, height: 28, borderRadius: "50%",
                          background: playingMatchId === m.id ? T.rose : `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`,
                          border: "2px solid #fff", display: "flex", alignItems: "center", justifyContent: "center",
                          cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,0.15)", padding: 0, transition: "all 0.2s"
                        }}>
                          <span style={{ fontSize: 11, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginLeft: playingMatchId === m.id ? 0 : 1 }}>{playingMatchId === m.id ? "⏸" : <PlayTriangle size={12} />}</span>
                        </button>
                        {givenReactions[m.id] && !playingMatchId && (
                          <div style={{ position: "absolute", bottom: 8, right: -8, width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, lineHeight: 1, zIndex: 1 }}>
                            {givenReactions[m.id]}
                          </div>
                        )}
                      </div>
                      {playingMatchId === m.id && (
                        <div style={{ width: 70, height: 3, borderRadius: 3, background: `${T.rose}30`, overflow: "hidden" }}>
                          <div style={{ height: "100%", background: `linear-gradient(90deg, ${T.rose}, ${T.violet})`, borderRadius: 3, animation: "voicemailProgress 60s linear forwards" }} />
                        </div>
                      )}
                      {playingMatchId === m.id && (
                        <div onClick={e => e.stopPropagation()} style={{ display: "flex", gap: 3, animation: "reactionSlideUp 0.3s ease both" }}>
                          {REACTION_EMOJIS.map((emoji, ei) => (
                            <button key={emoji} onClick={e => { triggerRipple(e); handleReaction(e, m.id, emoji); }}
                              style={{ width: 24, height: 24, borderRadius: "50%", border: `1px solid ${T.border}`, background: givenReactions[m.id] === emoji ? `${T.rose}22` : T.dim, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", animation: `emojiPop 0.3s ${ei * 0.05}s ease both`, transition: "transform 0.15s", padding: 0 }}
                              onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.25)"; }}
                              onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}>
                              {emoji}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Name, details, location */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 17, fontWeight: 700, color: T.text, lineHeight: 1.2 }}>{m.name}, {m.age}</div>
                    <div style={{ fontSize: 13, color: T.muted, marginTop: 1 }}>{m.gender} · {m.height}</div>
                    <div style={{ fontSize: 13, color: T.muted, marginTop: 1 }}><LocationIcon size={11} color={T.muted} /> {m.city}</div>
                  </div>
                  {/* Match % */}
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 22, fontWeight: 900, color: T.rose, fontFamily: "'Segoe UI', sans-serif", lineHeight: 1 }}>{m.compatibility}%</div>
                    <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, letterSpacing: "0.03em" }}>MATCH</div>
                  </div>
                </div>
                <div style={{ marginTop: 8 }}>
                  <ProgressBar value={m.simulation.firstDate} color={T.rose} label="First Date Chemistry" />
                </div>
              </div>
            ))}
          </div>
        ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
          {MATCHES.slice(0, 3).map((m, i) => (
            <div key={m.id}
              style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden", cursor: "pointer", transition: "all 0.2s" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = T.borderHover; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.12)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}>

              {/* BLIND MODE: voicemail + stats only */}
              {blindMode ? (
                <div onClick={() => onSelectMatch && onSelectMatch(m)} style={{ padding: "20px 20px 18px" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 16 }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "center" }}>
                      <div onClick={e => handleOrbClick(e, m.id)}
                        style={{ position: "relative", width: 85, height: 85, flexShrink: 0, cursor: "pointer" }}>
                        <div style={{ position: "absolute", inset: -5, borderRadius: "50%", border: `2px solid ${T.rose}`, animation: "orbRing 1.8s ease-out infinite", opacity: 0.7 }} />
                        <div style={{ width: "100%", height: "100%", borderRadius: "50%", background: `linear-gradient(135deg, ${T.rose}, ${T.violet})`, display: "flex", alignItems: "center", justifyContent: "center", animation: "orbPulse 1.5s ease-in-out infinite alternate", border: `3px solid ${T.rose}` }}>
                          <span style={{ fontSize: 28, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginLeft: playingMatchId === m.id ? 0 : 2 }}>{playingMatchId === m.id ? "⏸" : <PlayTriangle size={32} />}</span>
                        </div>
                        {givenReactions[m.id] && !playingMatchId && (
                          <div style={{ position: "absolute", bottom: -2, right: -2, width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, lineHeight: 1 }}>
                            {givenReactions[m.id]}
                          </div>
                        )}
                      </div>
                      {playingMatchId === m.id && (
                        <div style={{ width: 85, height: 3, borderRadius: 3, background: `${T.rose}30`, overflow: "hidden" }}>
                          <div style={{ height: "100%", background: `linear-gradient(90deg, ${T.rose}, ${T.violet})`, borderRadius: 3, animation: "voicemailProgress 60s linear forwards" }} />
                        </div>
                      )}
                      {playingMatchId === m.id && (
                        <div onClick={e => e.stopPropagation()} style={{ display: "flex", gap: 4, animation: "reactionSlideUp 0.3s ease both" }}>
                          {REACTION_EMOJIS.map((emoji, ei) => (
                            <button key={emoji} onClick={(e) => { triggerRipple(e); handleReaction(e, m.id, emoji); }}
                              style={{ width: 28, height: 28, borderRadius: "50%", border: "none", background: T.card, cursor: "pointer", fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s", animation: `emojiPop 0.4s ease ${ei * 0.05}s both` }}
                              onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.2)"; e.currentTarget.style.background = T.bg; }}
                              onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.background = T.card; }}>
                              {emoji}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 19, fontWeight: 700, color: T.text, marginBottom: 2 }}>{m.name}, {m.age}</div>
                      <div style={{ fontSize: 12, color: T.muted, marginBottom: 2 }}>{m.gender} · {m.height}</div>
                      <div style={{ fontSize: 13, color: T.muted }}><LocationIcon size={13} color={T.muted} /> {m.city}</div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: 28, fontWeight: 900, color: T.rose, fontFamily: "'Segoe UI', sans-serif", lineHeight: 1 }}>{m.compatibility}%</div>
                      <div style={{ fontSize: 10, color: T.muted }}>MATCH</div>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginBottom: 12 }}>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: T.rose }}>{m.personalityScore}%</div>
                        <div style={{ fontSize: 10, color: T.muted }}>Personality</div>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: T.teal }}>{m.valuesScore}%</div>
                        <div style={{ fontSize: 10, color: T.muted }}>Values</div>
                      </div>
                    </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 12 }}>
                    {m.traits.slice(0, 2).map(t => <Pill key={t}>{t}</Pill>)}
                  </div>
                  <ProgressBar value={m.simulation.firstDate} color={T.rose} label="First Date Chemistry" />
                  <div style={{ marginTop: 10, fontSize: 12, color: T.teal, fontWeight: 600 }}>✦ Intelligent simulation ready</div>
                </div>
              ) : (
              /* NORMAL MODE: photo + bio */
              <div onClick={() => onSelectMatch && onSelectMatch(m)} style={{ padding: "20px 20px 18px" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 16 }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "center" }}>
                    <div style={{ position: "relative", width: 85, height: 85, flexShrink: 0 }}>
                      <div style={{ width: "100%", height: "100%", borderRadius: "50%", overflow: "hidden", border: `3px solid ${T.rose}` }}>
                        <img src={m.photo} alt={m.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                      </div>
                      <button onClick={e => handleOrbClick(e, m.id)} style={{
                        position: "absolute", bottom: -4, right: -4, width: 36, height: 36, borderRadius: "50%",
                        background: playingMatchId === m.id ? T.rose : `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`,
                        border: "2px solid #fff", display: "flex", alignItems: "center", justifyContent: "center",
                        cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", transition: "all 0.2s", padding: 0
                      }}
                      onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.1)"; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}>
                        <span style={{ fontSize: 14, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginLeft: playingMatchId === m.id ? 0 : 1 }}>{playingMatchId === m.id ? "⏸" : <PlayTriangle size={18} />}</span>
                      </button>
                      {givenReactions[m.id] && !playingMatchId && (
                        <div style={{ position: "absolute", bottom: 8, right: -8, width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, lineHeight: 1, zIndex: 1 }}>
                          {givenReactions[m.id]}
                        </div>
                      )}
                    </div>
                    {playingMatchId === m.id && (
                      <div style={{ width: 85, height: 3, borderRadius: 3, background: `${T.rose}30`, overflow: "hidden" }}>
                        <div style={{ height: "100%", background: `linear-gradient(90deg, ${T.rose}, ${T.violet})`, borderRadius: 3, animation: "voicemailProgress 60s linear forwards" }} />
                      </div>
                    )}
                    {playingMatchId === m.id && (
                      <div onClick={e => e.stopPropagation()} style={{ display: "flex", gap: 4, animation: "reactionSlideUp 0.3s ease both" }}>
                        {REACTION_EMOJIS.map((emoji, ei) => (
                          <button key={emoji} onClick={e => { triggerRipple(e); handleReaction(e, m.id, emoji); }}
                            style={{ width: 28, height: 28, borderRadius: "50%", border: "none", background: T.card, cursor: "pointer", fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s", animation: `emojiPop 0.4s ease ${ei * 0.05}s both` }}
                            onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.2)"; e.currentTarget.style.background = T.bg; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.background = T.card; }}>
                            {emoji}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 19, fontWeight: 700, color: T.text, marginBottom: 2 }}>{m.name}, {m.age}</div>
                    <div style={{ fontSize: 12, color: T.muted, marginBottom: 2 }}>{m.gender} · {m.height}</div>
                    <div style={{ fontSize: 13, color: T.muted }}><LocationIcon size={13} color={T.muted} /> {m.city}</div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 28, fontWeight: 900, color: T.rose, fontFamily: "'Segoe UI', sans-serif", lineHeight: 1 }}>{m.compatibility}%</div>
                    <div style={{ fontSize: 10, color: T.muted }}>MATCH</div>
                  </div>
                </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginBottom: 12 }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: T.rose }}>{m.personalityScore}%</div>
                      <div style={{ fontSize: 10, color: T.muted }}>Personality</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: T.teal }}>{m.valuesScore}%</div>
                      <div style={{ fontSize: 10, color: T.muted }}>Values</div>
                    </div>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 12 }}>
                  {m.traits.slice(0, 2).map(t => <Pill key={t}>{t}</Pill>)}
                </div>
                <ProgressBar value={m.simulation.firstDate} color={T.rose} label="First Date Chemistry" />
                <div style={{ marginTop: 10, fontSize: 12, color: T.teal, fontWeight: 600 }}>✦ Intelligent simulation ready</div>
              </div>
              )}
            </div>
          ))}
        </div>
        )}
        
      </div>

      {!bp.isDesktop && <div style={{ borderBottom: '1px solid rgba(0,0,0,0.08)', margin: '0 0 24px' }} />}

      <InterviewStagesPanel completedStages={completedStages} onStartStage={onStartStage} />

      {!bp.isDesktop && <div style={{ borderBottom: '1px solid rgba(0,0,0,0.08)', margin: '0 0 24px' }} />}

      {/* App Connections Panel */}
      {bp.isDesktop ? (
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: T.rose, fontWeight: 700, letterSpacing: "0.1em" }}>APP CONNECTIONS</div>
          </div>
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: 20 }}>
          <ParallaxSlider
            items={[
              { key: 'spotify', id: 'spotify', name: 'Spotify', Icon: MusicIcon, color: '#1DB954' },
              { key: 'instagram', id: 'instagram', name: 'Instagram', Icon: CameraIcon, color: '#E4405F' },
              { key: 'netflix', id: 'netflix', name: 'Netflix', Icon: MovieIcon, color: '#E50914' },
              { key: 'goodreads', id: 'goodreads', name: 'Goodreads', Icon: BookIcon, color: '#553B08' },
              { key: 'linkedin', id: 'linkedin', name: 'LinkedIn', Icon: BriefcaseIcon, color: '#0A66C2' },
              { key: 'strava', id: 'strava', name: 'Strava', Icon: RunIcon, color: '#FC4C02' },
              { key: 'pinterest', id: 'pinterest', name: 'Pinterest', Icon: PinIcon, color: '#E60023' },
              { key: 'facebook', id: 'facebook', name: 'Facebook', Icon: FacebookIcon, color: '#1877F2' }
            ]}
            itemWidth={84}
            itemGap={16}
            sliderHeight={100}
            nudgeCount={4}
            renderItem={(app, i, getDragMoved) => {
              const connected = appConnections[app.id] || false;
              return (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, width: "100%" }}>
                  <div
                    onClick={() => { if (!getDragMoved()) setSvcLoginModal(app.id); }}
                    style={{ 
                      width: 64, height: 64, borderRadius: "50%", 
                      background: `${app.color}12`, border: "none",
                      display: "flex", alignItems: "center", justifyContent: "center", 
                      transition: "all 0.2s", cursor: "pointer", position: "relative"
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.08)"; e.currentTarget.style.background = `${app.color}22`; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.background = `${app.color}12`; }}>
                    <app.Icon size={32} color={app.color} />
                    <div style={{ 
                      position: "absolute", bottom: -1, right: -1, width: 18, height: 18, 
                      borderRadius: "50%", background: connected ? "#22c55e" : T.dim, 
                      border: `2px solid ${T.card}`,
                      display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.3s"
                    }}>
                      {connected ? (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17L4 12" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      ) : (
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6L18 18" stroke={T.muted} strokeWidth="3" strokeLinecap="round"/></svg>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: 10, color: T.text, fontWeight: 600, textAlign: "center", lineHeight: 1.2, maxWidth: 64 }}>
                    {app.name}
                  </div>
                </div>
              );
            }}
          />
          </div>
        </div>
      ) : (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ fontSize: 13, color: T.rose, fontWeight: 700, letterSpacing: "0.1em" }}>APP CONNECTIONS</div>
          </div>
          {/* Mobile: horizontal scroll of individual cards */}
          <div onTouchStart={e => e.stopPropagation()} onTouchEnd={e => e.stopPropagation()} style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 8, WebkitOverflowScrolling: "touch", scrollSnapType: "x mandatory", msOverflowStyle: "none", scrollbarWidth: "none" }}>
            {[
              { id: 'spotify', name: 'Spotify', Icon: MusicIcon, color: '#1DB954' },
              { id: 'instagram', name: 'Instagram', Icon: CameraIcon, color: '#E4405F' },
              { id: 'netflix', name: 'Netflix', Icon: MovieIcon, color: '#E50914' },
              { id: 'goodreads', name: 'Goodreads', Icon: BookIcon, color: '#553B08' },
              { id: 'linkedin', name: 'LinkedIn', Icon: BriefcaseIcon, color: '#0A66C2' },
              { id: 'strava', name: 'Strava', Icon: RunIcon, color: '#FC4C02' },
              { id: 'pinterest', name: 'Pinterest', Icon: PinIcon, color: '#E60023' },
              { id: 'facebook', name: 'Facebook', Icon: FacebookIcon, color: '#1877F2' }
            ].map(app => {
              const connected = appConnections[app.id] || false;
              return (
                <div key={app.id}
                  onClick={() => setSvcLoginModal(app.id)}
                  style={{
                    minWidth: 106, maxWidth: 106,
                    background: T.card, border: `1px solid ${connected ? app.color + "40" : T.border}`, borderRadius: 14,
                    padding: "16px 10px 14px",
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
                    cursor: "pointer", scrollSnapAlign: "start", flexShrink: 0,
                    transition: "all 0.2s",
                  }}>
                  <div style={{ 
                    width: 62, height: 62, borderRadius: "50%", 
                    background: `${app.color}12`, 
                    display: "flex", alignItems: "center", justifyContent: "center", 
                    position: "relative"
                  }}>
                    <app.Icon size={34} color={app.color} />
                    <div style={{ 
                      position: "absolute", bottom: -1, right: -1, width: 18, height: 18, 
                      borderRadius: "50%", background: connected ? "#22c55e" : T.dim, 
                      border: `2px solid ${T.card}`,
                      display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.3s"
                    }}>
                      {connected ? (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17L4 12" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      ) : (
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6L18 18" stroke={T.muted} strokeWidth="3" strokeLinecap="round"/></svg>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: 13, color: T.text, fontWeight: 600, textAlign: "center", lineHeight: 1.2 }}>
                    {app.name}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Service Login Modal (from profile preview) */}
      {svcLoginModal && (
        <ServiceLoginModal
          serviceId={svcLoginModal}
          isConnected={appConnections[svcLoginModal] || false}
          onClose={() => setSvcLoginModal(null)}
          onConnect={handleSvcConnect}
          onDisconnect={handleSvcDisconnect}
        />
      )}

      {!bp.isDesktop && <div style={{ borderBottom: '1px solid rgba(0,0,0,0.08)', margin: '0 0 24px' }} />}

      {/* Personality Snapshot Title (outside panel) */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: bp.isDesktop ? 12 : 10 }}>
        <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.rose, fontWeight: 700, letterSpacing: "0.1em" }}>PERSONALITY SNAPSHOT</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: bp.isDesktop ? 11 : 12, color: T.muted, cursor: "pointer" }} onClick={() => { if (navigator.share) navigator.share({ title: "My settup Profile", text: "Check out my personality snapshot on settup!" }).catch(() => {}); else { navigator.clipboard.writeText(window.location.href); alert("Link copied!"); } }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
          Share with friends
        </div>
      </div>

      {/* Personality Snapshot Panel */}
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: bp.isDesktop ? 24 : 20, marginBottom: bp.isDesktop ? 32 : 24 }}>

          {/* ═══ NEW 4-COLUMN SNAPSHOT HEADER ═══ */}
          {(() => {
            const basics = extractBasics(userAnswers);
            const lookingFor = extractLookingFor(userAnswers);
            const notInterested = extractNotInterestedIn(userAnswers);
            const keywords = extractKeywords(userAnswers, userContext);
            const removeKeyword = (kw) => {
              const updated = { ...userContext };
              let found = false;
              Object.keys(updated).forEach(k => {
                if (k === "kw_hidden") return;
                if (Array.isArray(updated[k])) {
                  const filtered = updated[k].filter(item => item !== kw);
                  if (filtered.length !== updated[k].length) { updated[k] = filtered; found = true; }
                }
              });
              if (!found) updated.kw_hidden = [...(updated.kw_hidden || []), kw];
              setUserContext(updated);
              localStorage.setItem("soul_user_context", JSON.stringify(updated));
              syncUserContext(updated);
            };
            const totalReactions = Object.values(vmReactions).reduce((s, v) => s + (v || 0), 0);
            const sortedEmojis = [...REACTION_EMOJIS].sort((a, b) => (vmReactions[b] || 0) - (vmReactions[a] || 0));
            const userAge = userAnswers.age || "";
            const userLoc = userAnswers.location || "";

            return (
              <div style={{ marginBottom: bp.isDesktop ? 24 : 20, paddingBottom: bp.isDesktop ? 24 : 20, borderBottom: `1px solid ${T.border}` }}>
                {/* 4-Column Grid: Desktop=4col, Tablet=2col, Mobile=1col */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: bp.isDesktop ? "minmax(180px, 1fr) minmax(180px, 1.2fr) minmax(180px, 1.2fr) minmax(180px, 1.2fr)" : bp.isTablet ? "1fr 1fr" : "1fr",
                  gap: bp.isDesktop ? 24 : 16,
                  alignItems: "start",
                }}>
                  {/* ── COL 1: Name, Location, VM Orb, Stacked Reactions ── */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <div>
                      <div style={{ fontSize: bp.isDesktop ? 20 : 18, fontWeight: 800, color: T.text, lineHeight: 1.2 }}>{snapName}{userAge ? `, ${userAge}` : ""}</div>
                      {userLoc && <div style={{ fontSize: 12, color: T.muted, marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                        {userLoc}
                      </div>}
                    </div>
                    {/* VM Orb */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 6 }}>
                      <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: "0.08em" }}>MY VOICEMAIL</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div onClick={() => setVmPlaying(p => !p)}
                          style={{ position: "relative", width: 56, height: 56, cursor: "pointer", flexShrink: 0 }}>
                          <div style={{ position: "absolute", inset: -4, borderRadius: "50%", border: `2px solid ${T.rose}`, animation: "orbRing 1.8s ease-out infinite", opacity: 0.7 }} />
                          <div style={{ width: "100%", height: "100%", borderRadius: "50%", background: `linear-gradient(135deg, ${T.rose}, ${T.violet})`, display: "flex", alignItems: "center", justifyContent: "center", animation: "orbPulse 1.5s ease-in-out infinite alternate", boxShadow: `0 0 12px ${T.roseGlow}`, border: `2px solid ${T.rose}` }}>
                            <span style={{ fontSize: 20, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginLeft: vmPlaying ? 0 : 2 }}>{vmPlaying ? "⏸" : <PlayTriangle size={22} />}</span>
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{vmPlaying ? "Playing..." : "Tap to listen"}</div>
                          <div style={{ fontSize: 11, color: T.muted }}>Recorded voicemail</div>
                        </div>
                      </div>
                      {vmPlaying && (
                        <div style={{ width: "100%", height: 3, borderRadius: 3, background: `${T.rose}30`, overflow: "hidden" }}>
                          <div style={{ height: "100%", background: `linear-gradient(90deg, ${T.rose}, ${T.violet})`, borderRadius: 3, animation: "voicemailProgress 60s linear forwards" }} />
                        </div>
                      )}
                    </div>
                    {/* Stacked Reactions */}
                    <div onClick={() => setShowReactionsModal(true)} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", marginTop: 2 }}>
                      <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: "0.06em" }}>REACTIONS RECEIVED</div>
                    </div>
                    <div onClick={() => setShowReactionsModal(true)} style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        {sortedEmojis.filter(e => (vmReactions[e] || 0) > 0).slice(0, 5).map((emoji, i) => (
                          <span key={emoji} style={{ fontSize: 20, marginLeft: i > 0 ? -4 : 0, zIndex: 10 - i, filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.15))" }}>{emoji}</span>
                        ))}
                      </div>
                      {totalReactions > 0 && <span style={{ fontSize: 13, fontWeight: 700, color: T.rose, marginLeft: 8 }}>{totalReactions}</span>}
                    </div>
                  </div>

                  {/* ── COL 2: BASICS ── */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: T.text, letterSpacing: "0.06em", marginBottom: 8 }}>BASICS:</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      {basics.map((b, i) => <SnapPill key={i} icon={b.icon} text={b.text} variant="basic" />)}
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                      <div className="add-ctx-btn" onClick={() => { setAddContextStage("basics"); setContextText(""); setContextVisibility("public"); }}
                        style={{ "--btn-color": T.rose, "--btn-color-bg": `${T.rose}15`, "--btn-color-border": `${T.rose}35` }}>
                        <span className="add-ctx-btn__plus">+</span>
                        <span className="add-ctx-btn__static">Add</span>
                        <span className="add-ctx-btn__label">context</span>
                      </div>
                    </div>
                  </div>

                  {/* ── COL 3: LOOKING FOR ── */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#22c55e", letterSpacing: "0.06em", marginBottom: 8 }}>LOOKING FOR:</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      {lookingFor.map((b, i) => <SnapPill key={i} icon={b.icon} text={b.text} variant="positive" />)}
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                      <div className="add-ctx-btn" onClick={() => { setAddContextStage("looking"); setContextText(""); setContextVisibility("public"); }}
                        style={{ "--btn-color": T.rose, "--btn-color-bg": `${T.rose}15`, "--btn-color-border": `${T.rose}35` }}>
                        <span className="add-ctx-btn__plus">+</span>
                        <span className="add-ctx-btn__static">Add</span>
                        <span className="add-ctx-btn__label">context</span>
                      </div>
                    </div>
                  </div>

                  {/* ── COL 4: NOT INTERESTED IN ── */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#ef4444", letterSpacing: "0.06em", marginBottom: 8 }}>NOT INTERESTED IN:</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      {notInterested.map((b, i) => <SnapPill key={i} icon={b.icon} text={b.text} variant="negative" />)}
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                      <div className="add-ctx-btn" onClick={() => { setAddContextStage("dealbreakers"); setContextText(""); setContextVisibility("public"); }}
                        style={{ "--btn-color": T.rose, "--btn-color-bg": `${T.rose}15`, "--btn-color-border": `${T.rose}35` }}>
                        <span className="add-ctx-btn__plus">+</span>
                        <span className="add-ctx-btn__static">Add</span>
                        <span className="add-ctx-btn__label">context</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Keyword Cluster ── */}
                {keywords.length > 0 && (
                  <div style={{ marginTop: bp.isDesktop ? 20 : 16, paddingTop: bp.isDesktop ? 16 : 12, borderTop: `1px solid ${T.border}` }}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: bp.isDesktop ? "8px 14px" : "6px 10px", justifyContent: "center", lineHeight: 1.8 }}>
                      {keywords.map((kw, i) => (
                        editingKw?.kw === kw ? (
                          <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: `${T.rose}10`, border: `1px solid ${T.rose}35`, borderRadius: 20, padding: "4px 12px", position: "relative", zIndex: 101 }}>
                            {editingKw.confirmDelete ? (
                              <>
                                <span style={{ fontSize: 11, color: T.text, fontWeight: 500 }}>Remove?</span>
                                <span onClick={() => { removeKeyword(kw); setEditingKw(null); }} style={{ fontSize: 11, fontWeight: 700, color: "#ef4444", cursor: "pointer", userSelect: "none" }}>Yes</span>
                                <span onClick={() => setEditingKw(p => ({ ...p, confirmDelete: false }))} style={{ fontSize: 11, color: T.muted, cursor: "pointer", userSelect: "none" }}>No</span>
                              </>
                            ) : (
                              <>
                                <input autoFocus value={editingKw.text} onChange={e => setEditingKw(p => ({ ...p, text: e.target.value }))}
                                  onKeyDown={e => {
                                    if (e.key === "Enter") { const t = editingKw.text.trim(); if (t) { const upd = { ...userContext }; Object.keys(upd).forEach(k => { if (Array.isArray(upd[k])) { const idx = upd[k].indexOf(kw); if (idx !== -1) { const a = [...upd[k]]; a[idx] = t; upd[k] = a; } } }); setUserContext(upd); localStorage.setItem("soul_user_context", JSON.stringify(upd)); syncUserContext(upd); } setEditingKw(null); }
                                    if (e.key === "Escape") setEditingKw(null);
                                  }}
                                  style={{ border: "none", outline: "none", background: "transparent", fontSize: [11, 13, 12, 14, 11, 13, 12][i % 7], fontWeight: 600, color: T.muted, minWidth: 40, maxWidth: 140 }} />
                                <span onClick={() => { const t = editingKw.text.trim(); if (t) { const upd = { ...userContext }; Object.keys(upd).forEach(k => { if (Array.isArray(upd[k])) { const idx = upd[k].indexOf(kw); if (idx !== -1) { const a = [...upd[k]]; a[idx] = t; upd[k] = a; } } }); setUserContext(upd); localStorage.setItem("soul_user_context", JSON.stringify(upd)); syncUserContext(upd); } setEditingKw(null); }} style={{ fontSize: 14, color: T.rose, cursor: "pointer", fontWeight: 700, userSelect: "none" }}>✓</span>
                                <span onClick={() => setEditingKw(p => ({ ...p, confirmDelete: true }))} title="Remove" style={{ fontSize: 13, color: T.muted, cursor: "pointer", opacity: 0.5, userSelect: "none" }} onMouseEnter={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.color = "#ef4444"; }} onMouseLeave={e => { e.currentTarget.style.opacity = "0.5"; e.currentTarget.style.color = T.muted; }}>🗑</span>
                                <span onClick={() => setEditingKw(null)} style={{ fontSize: 13, color: T.muted, cursor: "pointer", opacity: 0.5, userSelect: "none" }}>✕</span>
                              </>
                            )}
                          </span>
                        ) : (
                          <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                            <span style={{ fontSize: [11, 13, 12, 14, 11, 13, 12][i % 7], fontWeight: [500, 600, 500, 700, 500, 600, 500][i % 7], color: T.muted, opacity: [0.7, 0.85, 0.6, 0.9, 0.65, 0.8, 0.55][i % 7], cursor: "default" }}>{kw}</span>
                            <span onClick={() => setEditingKw({ kw, text: kw, confirmDelete: false })} title="Edit" style={{ fontSize: 10, color: T.muted, opacity: 0.3, cursor: "pointer", lineHeight: 1, userSelect: "none", transition: "opacity 0.15s, color 0.15s" }}
                              onMouseEnter={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.color = T.rose; }}
                              onMouseLeave={e => { e.currentTarget.style.opacity = "0.3"; e.currentTarget.style.color = T.muted; }}>✏</span>
                          </span>
                        )
                      ))}
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                      <div className="add-ctx-btn" onClick={() => { setAddContextStage("keywords"); setContextText(""); setContextVisibility("public"); }}
                        style={{ "--btn-color": T.rose, "--btn-color-bg": `${T.rose}15`, "--btn-color-border": `${T.rose}35` }}>
                        <span className="add-ctx-btn__plus">+</span>
                        <span className="add-ctx-btn__static">Add</span>
                        <span className="add-ctx-btn__label">context</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Add Context Panel (for header columns) ── */}
                <div style={{ position: "relative" }}>
                  <AddContextPanel
                    open={addContextStage === "basics" || addContextStage === "looking" || addContextStage === "dealbreakers" || addContextStage === "keywords"}
                    onClose={() => { setAddContextStage(null); setContextText(""); setIsExact(false); }}
                    accentColor={T.rose}
                    contextVisibility={contextVisibility}
                    setContextVisibility={setContextVisibility}
                    contextText={contextText}
                    setContextText={setContextText}
                    isExact={isExact}
                    setIsExact={setIsExact}
                    label={addContextStage === "basics" ? "Add to Profile" : addContextStage === "looking" ? "Add to Looking For" : addContextStage === "dealbreakers" ? "Add to Deal Breakers" : "Add Keyword"}
                    onSave={() => {
                      const trimmed = contextText.trim();
                      if (contextVisibility === "secret") {
                        const updated = [...secretContext, { text: trimmed, category: addContextStage, createdAt: Date.now(), exact: !!isExact }];
                        setSecretContext(updated);
                        localStorage.setItem("soul_secret_context", JSON.stringify(updated));
                        syncSecretContext(updated);
                      } else {
                        const stageKey = addContextStage === "keywords" ? "kw" : addContextStage;
                        const updated = { ...userContext, [stageKey]: [...(userContext[stageKey] || []), trimmed] };
                        setUserContext(updated);
                        localStorage.setItem("soul_user_context", JSON.stringify(updated));
                        syncUserContext(updated);
                      }
                      if (isExact) {
                        const norm = trimmed.toLowerCase();
                        const next = exactContext.includes(norm) ? exactContext : [...exactContext, norm];
                        setExactContext(next);
                        localStorage.setItem("soul_exact_context", JSON.stringify(next));
                        syncExactContext(next);
                      }
                      setContextText(""); setAddContextStage(null); setIsExact(false);
                    }}
                  />
                </div>
              </div>
            );
          })()}

          {/* ── My Private Context Management ── */}
          <div style={{ marginBottom: bp.isDesktop ? 24 : 18 }}>
            <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: "#7c3aed", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
              🔒 MY PRIVATE CONTEXT
              <span style={{ fontSize: 11, fontWeight: 500, color: T.muted, letterSpacing: "normal" }}>· only visible to you</span>
            </div>
            {secretContext.length === 0 ? (
              <div style={{ padding: "12px 14px", background: "#7c3aed06", border: "1px dashed #7c3aed30", borderRadius: 10, fontSize: 12, color: T.muted, lineHeight: 1.5 }}>
                No private context yet. When you add context and choose <b>Secret</b>, it will appear here for you to manage.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {secretContext.map((item, i) => (
                  editingCtx?.type === "secret" && editingCtx?.idx === i ? (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "10px 14px", background: "#7c3aed12", border: "1px solid #7c3aed50", borderRadius: 10, position: "relative", zIndex: 101 }}>
                      {editingCtx.confirmDelete ? (
                        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 13, color: T.text, fontWeight: 500 }}>Remove this item?</span>
                          <button onClick={() => { const updated = secretContext.filter((_, idx) => idx !== i); setSecretContext(updated); localStorage.setItem("soul_secret_context", JSON.stringify(updated)); syncSecretContext(updated); if (item.exact) { const norm = item.text.trim().toLowerCase(); const updatedExact = exactContext.filter(e => e !== norm); setExactContext(updatedExact); localStorage.setItem("soul_exact_context", JSON.stringify(updatedExact)); syncExactContext(updatedExact); } setEditingCtx(null); }} style={{ fontSize: 12, fontWeight: 700, color: "#ef4444", background: "#ef444415", border: "1px solid #ef444430", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontFamily: "inherit" }}>Yes, remove</button>
                          <button onClick={() => setEditingCtx(p => ({ ...p, confirmDelete: false }))} style={{ fontSize: 12, color: T.muted, background: T.hover, border: `1px solid ${T.border}`, borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                        </div>
                      ) : (
                        <>
                          <textarea autoFocus value={editingCtx.text}
                            onChange={e => setEditingCtx(p => ({ ...p, text: e.target.value }))}
                            onKeyDown={e => {
                              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); const t = editingCtx.text.trim(); if (t) { const a = secretContext.map((x, xi) => xi === i ? { ...x, text: t } : x); setSecretContext(a); localStorage.setItem("soul_secret_context", JSON.stringify(a)); syncSecretContext(a); } setEditingCtx(null); }
                              if (e.key === "Escape") setEditingCtx(null);
                            }}
                            style={{ flex: 1, resize: "none", minHeight: 50, border: "none", outline: "none", background: "transparent", fontSize: 13, fontWeight: 600, color: T.text, fontFamily: "inherit", lineHeight: 1.4 }} />
                          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                            <button onClick={() => { const t = editingCtx.text.trim(); if (t) { const a = secretContext.map((x, xi) => xi === i ? { ...x, text: t } : x); setSecretContext(a); localStorage.setItem("soul_secret_context", JSON.stringify(a)); syncSecretContext(a); } setEditingCtx(null); }} style={{ width: 28, height: 28, borderRadius: "50%", background: "#7c3aed20", border: "1px solid #7c3aed40", color: "#7c3aed", fontSize: 15, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}>✓</button>
                            <button onClick={() => setEditingCtx(p => ({ ...p, confirmDelete: true }))} title="Remove" style={{ width: 28, height: 28, borderRadius: "50%", background: "#ef444410", border: "1px solid #ef444430", color: "#ef4444", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit", opacity: 0.6 }} onMouseEnter={e => e.currentTarget.style.opacity = "1"} onMouseLeave={e => e.currentTarget.style.opacity = "0.6"}>🗑</button>
                            <button onClick={() => setEditingCtx(null)} style={{ width: 28, height: 28, borderRadius: "50%", background: T.hover, border: `1px solid ${T.border}`, color: T.muted, fontSize: 15, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}>✕</button>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                  <div key={i} onClick={() => setEditingCtx({ type: "secret", idx: i, text: item.text, confirmDelete: false })} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: "#7c3aed08", border: "1px solid #7c3aed20", borderRadius: 10, cursor: "pointer" }}>
                    <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{item.text}</span>
                      {item.exact && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: "#ef4444", background: "#ef444415", border: "1px solid #ef444430", borderRadius: 4, padding: "1px 6px", flexShrink: 0 }}>EXACT</span>
                      )}
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#7c3aed", opacity: 0.65, flexShrink: 0 }}>
                        {typeof item.category === "number" ? `Stage ${item.category}` : item.category === "kw" ? "Keyword" : item.category ? item.category.charAt(0).toUpperCase() + item.category.slice(1) : "General"}
                      </span>
                    </div>
                    <span style={{ fontSize: 12, color: "#7c3aed", opacity: 0.4, marginLeft: 12, flexShrink: 0 }}>✏</span>
                  </div>
                  )
                ))}
              </div>
            )}
          </div>

          {(editingKw || editingCtx) && (
            <div onClick={() => { setEditingKw(null); setEditingCtx(null); }} style={{ position: "fixed", inset: 0, zIndex: 100 }} />
          )}

          {/* ── Reactions Detail Modal ── */}
          {showReactionsModal && (
            <div onClick={() => setShowReactionsModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
              <div onClick={e => e.stopPropagation()} style={{ background: T.card, borderRadius: 12, padding: "24px 28px", maxWidth: 340, width: "100%", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 16 }}>Reactions Received</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {REACTION_EMOJIS.filter(e => (vmReactions[e] || 0) > 0).sort((a, b) => (vmReactions[b] || 0) - (vmReactions[a] || 0)).map(emoji => (
                    <div key={emoji} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: `${T.rose}08`, borderRadius: 8 }}>
                      <span style={{ fontSize: 22 }}>{emoji}</span>
                      <span style={{ fontSize: 16, fontWeight: 800, color: T.rose }}>{vmReactions[emoji] || 0}</span>
                    </div>
                  ))}
                </div>
                {receivedVmReactions.length > 0 && (
                  <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, letterSpacing: "0.06em", marginBottom: 8 }}>FROM YOUR MATCHES</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {receivedVmReactions.map((r, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 4, background: `${T.rose}10`, border: `1px solid ${T.rose}25`, borderRadius: 8, padding: "3px 10px" }}>
                          <span style={{ fontSize: 14 }}>{r.emoji}</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{r.reactorName || "Match"}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <button onClick={() => setShowReactionsModal(false)}
                  style={{ marginTop: 16, width: "100%", padding: "10px 0", borderRadius: 8, background: T.rose, color: "#fff", fontSize: 13, fontWeight: 700, border: "none", cursor: "pointer", fontFamily: "inherit" }}>Close</button>
              </div>
            </div>
          )}

          {/* ─── Per-Stage Snapshot Sections (completed only) ─── */}
          {SNAPSHOT_SECTIONS.filter(sec => getStageProgress(sec.stage).done).map((sec, si) => {
            const insight = sec.getInsight();
            const stageCtx = userContext[sec.stage] || [];
            const stageQs = AI_QUESTIONS.filter(q => q.stage === sec.stage);
            const filled = stageQs.filter(q => userAnswers[q.key] && String(userAnswers[q.key]).trim()).length;
            const fillPct = stageQs.length ? Math.round((filled / stageQs.length) * 100) : 100;
            return (
              <div key={sec.stage} style={{ marginBottom: bp.isDesktop ? 20 : 16, paddingBottom: bp.isDesktop ? 20 : 16, borderBottom: `1px solid ${T.border}` }}>
                {/* Section Header + Insight merged */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 6 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: insight?.content ? 5 : 0 }}>
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: sec.color, flexShrink: 0 }} />
                      <div style={{ fontSize: bp.isDesktop ? 11 : 12, color: sec.color, fontWeight: 700, letterSpacing: "0.08em" }}>
                        STAGE {sec.stage} · {sec.title}
                      </div>
                    </div>
                    {insight?.content && (
                      <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, lineHeight: 1.6, paddingLeft: 14 }}>
                        {insight.content}
                      </div>
                    )}
                  </div>
                  <div style={{ flexShrink: 0, width: 60, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3, marginTop: 2 }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: sec.color, lineHeight: 1 }}>{fillPct}%</div>
                    <div style={{ width: 60, height: 3, borderRadius: 99, background: `${sec.color}20`, overflow: "hidden" }}>
                      <div style={{ height: "100%", borderRadius: 99, background: sec.color, width: `${fillPct}%`, transition: "width 0.9s cubic-bezier(0.22,1,0.36,1)" }} />
                    </div>
                  </div>
                </div>
                {/* User-added context tags */}
                {stageCtx.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                    {stageCtx.map((ctx, ci) => (
                      editingCtx?.type === "stage" && editingCtx?.key === sec.stage && editingCtx?.idx === ci ? (
                        <span key={ci} style={{ display: "inline-flex", alignItems: "center", gap: 5, background: `${sec.color}18`, border: `1px solid ${sec.color}50`, borderRadius: 20, padding: "3px 10px", position: "relative", zIndex: 101 }}>
                          {editingCtx.confirmDelete ? (
                            <>
                              <span style={{ fontSize: 11, color: T.text, fontWeight: 500 }}>Remove?</span>
                              <span onClick={() => { const u = { ...userContext, [sec.stage]: stageCtx.filter((_, j) => j !== ci) }; setUserContext(u); localStorage.setItem("soul_user_context", JSON.stringify(u)); syncUserContext(u); setEditingCtx(null); }} style={{ fontSize: 11, fontWeight: 700, color: "#ef4444", cursor: "pointer", userSelect: "none" }}>Yes</span>
                              <span onClick={() => setEditingCtx(p => ({ ...p, confirmDelete: false }))} style={{ fontSize: 11, color: T.muted, cursor: "pointer", userSelect: "none" }}>No</span>
                            </>
                          ) : (
                            <>
                              <input autoFocus value={editingCtx.text}
                                onChange={e => setEditingCtx(p => ({ ...p, text: e.target.value }))}
                                onKeyDown={e => {
                                  if (e.key === "Enter") { const t = editingCtx.text.trim(); if (t) { const a = [...(userContext[sec.stage] || [])]; a[ci] = t; const u = { ...userContext, [sec.stage]: a }; setUserContext(u); localStorage.setItem("soul_user_context", JSON.stringify(u)); syncUserContext(u); } setEditingCtx(null); }
                                  if (e.key === "Escape") setEditingCtx(null);
                                }}
                                style={{ border: "none", outline: "none", background: "transparent", fontSize: 12, fontWeight: 600, color: sec.color, minWidth: 60, maxWidth: 220 }} />
                              <span onClick={() => { const t = editingCtx.text.trim(); if (t) { const a = [...(userContext[sec.stage] || [])]; a[ci] = t; const u = { ...userContext, [sec.stage]: a }; setUserContext(u); localStorage.setItem("soul_user_context", JSON.stringify(u)); syncUserContext(u); } setEditingCtx(null); }} style={{ fontSize: 13, color: sec.color, cursor: "pointer", fontWeight: 700 }}>✓</span>
                              <span onClick={() => setEditingCtx(p => ({ ...p, confirmDelete: true }))} style={{ fontSize: 12, color: T.muted, cursor: "pointer", opacity: 0.5 }} onMouseEnter={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.color = "#ef4444"; }} onMouseLeave={e => { e.currentTarget.style.opacity = "0.5"; e.currentTarget.style.color = T.muted; }}>🗑</span>
                              <span onClick={() => setEditingCtx(null)} style={{ fontSize: 13, color: T.muted, cursor: "pointer", opacity: 0.5 }}>✕</span>
                            </>
                          )}
                        </span>
                      ) : (
                        <div key={ci} onClick={() => setEditingCtx({ type: "stage", key: sec.stage, idx: ci, text: ctx, confirmDelete: false })} style={{ display: "inline-flex", alignItems: "center", gap: 5, background: `${sec.color}10`, border: `1px solid ${sec.color}25`, color: sec.color, fontSize: 12, fontWeight: 600, padding: "4px 10px", borderRadius: 20, cursor: "pointer" }}>
                          <span>{ctx}</span>
                          <span style={{ fontSize: 10, opacity: 0.4, lineHeight: 1 }}>✏</span>
                        </div>
                      )
                    ))}
                  </div>
                )}
                {/* Add Context button + Panel */}
                <div style={{ position: "relative", marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
                  <div
                    className="add-ctx-btn"
                    onClick={() => { setAddContextStage(sec.stage); setContextText(""); setContextVisibility("public"); }}
                    title="Add context"
                    style={{ "--btn-color": T.rose, "--btn-color-bg": `${T.rose}15`, "--btn-color-border": `${T.rose}35` }}>
                    <span className="add-ctx-btn__plus">+</span>
                    <span className="add-ctx-btn__static">Add</span>
                    <span className="add-ctx-btn__label">context</span>
                  </div>
                  <AddContextPanel
                    open={addContextStage === sec.stage}
                    onClose={() => { setAddContextStage(null); setContextText(""); setIsExact(false); }}
                    accentColor={sec.color}
                    contextVisibility={contextVisibility}
                    setContextVisibility={setContextVisibility}
                    contextText={contextText}
                    setContextText={setContextText}
                    isExact={isExact}
                    setIsExact={setIsExact}
                    label={`Stage ${sec.stage} · ${sec.title}`}
                    onSave={() => {
                      const trimmed = contextText.trim();
                      if (contextVisibility === "secret") {
                        const updated = [...secretContext, { text: trimmed, category: String(sec.stage), createdAt: Date.now(), exact: !!isExact }];
                        setSecretContext(updated);
                        localStorage.setItem("soul_secret_context", JSON.stringify(updated));
                        syncSecretContext(updated);
                      } else {
                        const updated = { ...userContext, [sec.stage]: [...(userContext[sec.stage] || []), trimmed] };
                        setUserContext(updated);
                        localStorage.setItem("soul_user_context", JSON.stringify(updated));
                        syncUserContext(updated);
                      }
                      if (isExact) {
                        const norm = trimmed.toLowerCase();
                        const next = exactContext.includes(norm) ? exactContext : [...exactContext, norm];
                        setExactContext(next);
                        localStorage.setItem("soul_exact_context", JSON.stringify(next));
                        syncExactContext(next);
                      }
                      setContextText(""); setAddContextStage(null); setIsExact(false);
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

      {!bp.isDesktop && <div style={{ borderBottom: '1px solid rgba(0,0,0,0.08)', margin: '0 0 24px' }} />}

      {/* ── INCOMPLETE STAGES: Sample Questions (outside panel) ── */}
      {(() => {
        const incompleteSections = SNAPSHOT_SECTIONS.filter(sec => !getStageProgress(sec.stage).done);
        if (incompleteSections.length === 0) return null;
        return (
          <div style={{ marginBottom: bp.isDesktop ? 32 : 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: bp.isDesktop ? 12 : 10 }}>
              <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.rose, fontWeight: 700, letterSpacing: "0.1em" }}>SAMPLE QUESTIONS</div>
              <div style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.muted }}>{incompleteSections.length} stages remaining</div>
            </div>
            {bp.isMobile ? (
              <div onTouchStart={e => e.stopPropagation()} onTouchEnd={e => e.stopPropagation()} style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 8, WebkitOverflowScrolling: "touch", scrollSnapType: "x mandatory", msOverflowStyle: "none", scrollbarWidth: "none" }}>
                {incompleteSections.map((sec, cardIdx) => {
                  const prog = getStageProgress(sec.stage);
                  const isPartial = prog.hasPartial;
                  const badgeColor = isPartial ? T.amber : sec.color;
                  const ringSize = 38;
                  const ringStroke = 2.5;
                  const ringR = (ringSize - ringStroke * 2) / 2;
                  const ringCirc = 2 * Math.PI * ringR;
                  const ringOffset = ringCirc - (prog.pct / 100) * ringCirc;
                  return (
                    <div key={sec.stage} onClick={() => onStartStage && onStartStage(sec.stage)} style={{
                      minWidth: 220, maxWidth: 220,
                      background: T.card, border: `1px solid ${T.border}`, borderRadius: 14,
                      padding: "14px 14px 12px",
                      display: "flex", flexDirection: "column", gap: 0,
                      cursor: "pointer", scrollSnapAlign: "start", flexShrink: 0,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    }}>
                      {/* Row 1: Ring + Title */}
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                        <div style={{ position: "relative", width: ringSize, height: ringSize, flexShrink: 0 }}>
                          <svg width={ringSize} height={ringSize} style={{ transform: "rotate(-90deg)", position: "absolute", inset: 0 }}>
                            <circle cx={ringSize/2} cy={ringSize/2} r={ringR} fill="none" stroke={`${sec.color}20`} strokeWidth={ringStroke} />
                            {prog.pct > 0 && (
                              <circle cx={ringSize/2} cy={ringSize/2} r={ringR} fill="none" stroke={sec.color} strokeWidth={ringStroke}
                                strokeDasharray={ringCirc} strokeDashoffset={ringOffset} strokeLinecap="round"
                                style={{ transition: "stroke-dashoffset 0.8s ease", filter: `drop-shadow(0 0 3px ${sec.color}50)` }} />
                            )}
                          </svg>
                          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: `${sec.color}15`, borderRadius: "50%" }}>
                            {(() => { const sd = STAGE_DEFS.find(d => d.n === sec.stage); return sd?.AnimIcon ? <sd.AnimIcon size={18} color={sec.color} /> : <span style={{ fontSize: 14, fontWeight: 800, color: sec.color }}>{sec.stage}</span>; })()}
                          </div>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{STAGES[sec.stage]?.name || sec.title}</div>
                          <div style={{ fontSize: 11, color: T.muted, marginTop: 2, lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{STAGES[sec.stage]?.desc || ""}</div>
                        </div>
                      </div>
                      {/* Row 2: Progress bar */}
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                        <div style={{ flex: 1, height: 4, background: `${sec.color}15`, borderRadius: 2, overflow: "hidden" }}>
                          <div style={{ width: `${prog.pct}%`, height: "100%", background: sec.color, borderRadius: 2, transition: "width 0.6s ease" }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: isPartial ? sec.color : T.muted, flexShrink: 0 }}>{prog.pct}%</span>
                      </div>
                      {/* Row 3: Sample Q */}
                      <div style={{ flex: 1, overflow: "hidden", fontSize: 13, color: T.muted, lineHeight: 1.5, fontStyle: "italic", marginBottom: 6, minHeight: 40 }}>
                        <SampleQTypewriter questions={sec.sampleQs} color={T.muted} fontSize={11} interval={5000} staggerDelay={cardIdx * 600} />
                      </div>
                      {/* Row 4: CTA */}
                      <button onClick={(e) => { e.stopPropagation(); onStartStage && onStartStage(sec.stage); }}
                        style={{
                          width: "100%", padding: "7px 0", borderRadius: 6, background: badgeColor, color: "#fff",
                          fontSize: 12, fontWeight: 700, border: "none", cursor: "pointer",
                          fontFamily: "inherit", transition: "all 0.15s", flexShrink: 0
                        }}>
                        {isPartial ? "▶ Resume" : "▶ Start"}
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <ParallaxSlider
                items={incompleteSections.map(sec => ({ ...sec, key: sec.stage }))}
                itemWidth={bp.isDesktop ? 280 : 250}
                itemGap={bp.isDesktop ? 16 : 12}
                sliderHeight={bp.isDesktop ? 200 : 185}
                nudgeCount={bp.isDesktop ? 3 : 2}
                renderItem={(sec, cardIdx, getDragMoved) => {
                  const prog = getStageProgress(sec.stage);
                  const isPartial = prog.hasPartial;
                  const badgeColor = isPartial ? T.amber : sec.color;
                  const iW = bp.isDesktop ? 280 : 250;
                  const ringSize = bp.isDesktop ? 42 : 38;
                  const ringStroke = 2.5;
                  const ringR = (ringSize - ringStroke * 2) / 2;
                  const ringCirc = 2 * Math.PI * ringR;
                  const ringOffset = ringCirc - (prog.pct / 100) * ringCirc;
                  return (
                    <div style={{
                      width: iW, background: T.card, border: `1px solid ${T.border}`, borderRadius: 8,
                      padding: bp.isDesktop ? "14px 16px" : "12px 14px",
                      display: "flex", flexDirection: "column", gap: 0,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)", height: "100%", boxSizing: "border-box", overflow: "hidden",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                        <div style={{ position: "relative", width: ringSize, height: ringSize, flexShrink: 0 }}>
                          <svg width={ringSize} height={ringSize} style={{ transform: "rotate(-90deg)", position: "absolute", inset: 0 }}>
                            <circle cx={ringSize/2} cy={ringSize/2} r={ringR} fill="none" stroke={`${sec.color}20`} strokeWidth={ringStroke} />
                            {prog.pct > 0 && (
                              <circle cx={ringSize/2} cy={ringSize/2} r={ringR} fill="none" stroke={sec.color} strokeWidth={ringStroke}
                                strokeDasharray={ringCirc} strokeDashoffset={ringOffset} strokeLinecap="round"
                                style={{ transition: "stroke-dashoffset 0.8s ease", filter: `drop-shadow(0 0 3px ${sec.color}50)` }} />
                            )}
                          </svg>
                          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: `${sec.color}15`, borderRadius: "50%" }}>
                            {(() => { const sd = STAGE_DEFS.find(d => d.n === sec.stage); return sd?.AnimIcon ? <sd.AnimIcon size={bp.isDesktop ? 20 : 18} color={sec.color} /> : <span style={{ fontSize: 14, fontWeight: 800, color: sec.color }}>{sec.stage}</span>; })()}
                          </div>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: bp.isDesktop ? 13 : 14, fontWeight: 700, color: T.text, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{STAGES[sec.stage]?.name || sec.title}</div>
                          <div style={{ fontSize: bp.isDesktop ? 10 : 11, color: T.muted, marginTop: 2, lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{STAGES[sec.stage]?.desc || ""}</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                        <div style={{ flex: 1, height: 4, background: `${sec.color}15`, borderRadius: 2, overflow: "hidden" }}>
                          <div style={{ width: `${prog.pct}%`, height: "100%", background: sec.color, borderRadius: 2, transition: "width 0.6s ease" }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: isPartial ? sec.color : T.muted, flexShrink: 0 }}>{prog.pct}%</span>
                      </div>
                      <div style={{ flex: 1, overflow: "hidden", fontSize: bp.isDesktop ? 12 : 13, color: T.muted, lineHeight: 1.5, fontStyle: "italic" }}>
                        <SampleQTypewriter questions={sec.sampleQs} color={T.muted} fontSize={bp.isDesktop ? 12 : 11} interval={5000} staggerDelay={cardIdx * 600} />
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); if (!getDragMoved()) onStartStage && onStartStage(sec.stage); }}
                        style={{
                          width: "100%", padding: "6px 0", borderRadius: 5, background: badgeColor, color: "#fff",
                          fontSize: bp.isDesktop ? 11 : 12, fontWeight: 700, border: "none", cursor: "pointer",
                          fontFamily: "inherit", transition: "all 0.15s", marginTop: 6, flexShrink: 0
                        }}
                        onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.02)"; e.currentTarget.style.filter = "brightness(1.1)"; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.filter = "none"; }}>
                        {isPartial ? "▶ Resume" : "▶ Start"}
                      </button>
                    </div>
                  );
                }}
              />
            )}

            {/* Overall Progress Footer */}
            {doneCount < STAGE_DEFS.length && (
              <div style={{ 
                marginTop: 12, 
                padding: bp.isDesktop ? "14px 16px" : "12px 14px", 
                background: `${T.amber}08`,
                border: `1px dashed ${T.amber}40`,
                borderRadius: 6,
                textAlign: "center"
              }}>
                <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.amber, fontWeight: 600 }}>
                  💡 {doneCount} of {STAGE_DEFS.length} stages complete — each stage deepens your personality insights and improves match accuracy
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Settings link — mobile only */}
      {!bp.isDesktop && onNavigateToSettings && (
        <button onClick={onNavigateToSettings} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", padding: "14px 0", marginTop: 8, marginBottom: 8, background: "none", border: `1px solid ${T.border}`, borderRadius: 8, cursor: "pointer", fontFamily: "inherit" }}>
          <SettingsIcon size={16} color={T.muted} />
          <span style={{ fontSize: 14, fontWeight: 600, color: T.muted }}>Settings</span>
        </button>
      )}

      {/* Footer — mobile/tablet only */}
      {!bp.isDesktop && (
        <div style={{ textAlign: "center", fontSize: 12, color: T.muted, lineHeight: 1.6, paddingTop: 12, paddingBottom: 8 }}>© 2026 <span style={{ fontSize: 15 }}>settup.ai</span></div>
      )}
      
    </div>
  );
}

// ─── SCREEN: My Profile (Interview Stages + Personality Snapshot) ──
function MyProfilePage({ profile, completedStages, onStartStage, onBack }) {
  const bp = useBreakpoint();
  const containerPad = bp.isDesktop ? "24px 32px 48px" : bp.isTablet ? "28px 32px 60px" : "24px 20px 100px";
  const maxW = bp.isDesktop ? 1400 : bp.isTablet ? 680 : 480;

  const [vmPlaying, setVmPlaying] = useState(false);
  const [vmReactions, setVmReactions] = useState(_getVoicemailReactions);
  const [receivedVmReactions, setReceivedVmReactions] = useState(() => {
    try { return JSON.parse(localStorage.getItem('soul_received_vm_reactions') || '[]'); } catch { return []; }
  });
  const [appConnections, setAppConnections] = useState(() => {
    try { const s = localStorage.getItem('app_connections'); return s ? JSON.parse(s) : {}; } catch { return {}; }
  });
  const [svcLoginModal, setSvcLoginModal] = useState(null);
  const [snapName, setSnapName] = useState(() => {
    try { const u = JSON.parse(localStorage.getItem("soul_user") || "{}"); if (u.name) return u.name; if (u.first_name) return u.first_name; } catch {}
    try { const ans = JSON.parse(localStorage.getItem("soul_user_answers") || "{}"); if (ans.first_name) return ans.first_name; } catch {}
    return "User";
  });
  const [userAnswers, setUserAnswers] = useState(() => {
    try { return JSON.parse(localStorage.getItem("soul_user_answers") || "{}"); } catch { return {}; }
  });
  // Re-read userAnswers and snapName after server sync completes (cross-device sync)
  useEffect(() => {
    const onSync = () => {
      try {
        const ans = JSON.parse(localStorage.getItem("soul_user_answers") || "{}");
        setUserAnswers(ans);
        const name = ans.first_name || (() => { try { const u = JSON.parse(localStorage.getItem("soul_user") || "{}"); return u.name || u.first_name; } catch { return null; } })() || "User";
        setSnapName(name);
      } catch {}
    };
    window.addEventListener("soul_sync_complete", onSync);
    return () => window.removeEventListener("soul_sync_complete", onSync);
  }, []);
  const [addContextStage, setAddContextStage] = useState(null);
  const [contextText, setContextText] = useState("");
  const [contextVisibility, setContextVisibility] = useState("public");
  const [isExact, setIsExact] = useState(false);
  const [expandedCtxBtn, setExpandedCtxBtn] = useState(null);
  const [editingCtx, setEditingCtx] = useState(null);
  const [editingKw, setEditingKw] = useState(null);
  const [showReactionsModal, setShowReactionsModal] = useState(false);
  const [userContext, setUserContext] = useState(() => {
    try { return JSON.parse(localStorage.getItem("soul_user_context") || "{}"); } catch { return {}; }
  });
  const [secretContext, setSecretContext] = useState(() => {
    try { return JSON.parse(localStorage.getItem("soul_secret_context") || "[]"); } catch { return []; }
  });
  const [exactContext, setExactContext] = useState(() => {
    try { return JSON.parse(localStorage.getItem("soul_exact_context") || "[]"); } catch { return []; }
  });

  useEffect(() => {
    const handler = () => setVmReactions(_getVoicemailReactions());
    window.addEventListener("voicemailReaction", handler);
    return () => window.removeEventListener("voicemailReaction", handler);
  }, []);

  useEffect(() => {
    fetchReceivedReactions().then(r => { if (r && r.length) setReceivedVmReactions(r); });
  }, []);

  useEffect(() => {
    const syncConns = () => {
      try { const s = localStorage.getItem('app_connections'); setAppConnections(s ? JSON.parse(s) : {}); } catch {}
    };
    window.addEventListener('app_connections_changed', syncConns);
    return () => window.removeEventListener('app_connections_changed', syncConns);
  }, []);

  useEffect(() => {
    if (!vmPlaying) return;
    const t = setTimeout(() => setVmPlaying(false), 60000);
    return () => clearTimeout(t);
  }, [vmPlaying]);

  const handleSvcConnect = (appId) => {
    setAppConnections(prev => {
      const updated = { ...prev, [appId]: true };
      localStorage.setItem('app_connections', JSON.stringify(updated));
      syncAppConnections(updated);
      window.dispatchEvent(new Event('app_connections_changed'));
      return updated;
    });
  };
  const handleSvcDisconnect = (appId) => {
    setAppConnections(prev => {
      const updated = { ...prev, [appId]: false };
      localStorage.setItem('app_connections', JSON.stringify(updated));
      syncAppConnections(updated);
      window.dispatchEvent(new Event('app_connections_changed'));
      return updated;
    });
  };

  const APP_DATA = [
    { id: 'facebook', name: 'Facebook', Icon: FacebookIcon, color: '#1877F2', description: 'Connect to analyze interests, groups, and social activity', benefit: 'Social interests & lifestyle', connectedData: ['Member of: Hiking Enthusiasts LA, Book Club Downtown', 'Interests: Travel photography, sustainable living, indie films', 'Recent activity: Shared 3 articles on climate change'] },
    { id: 'instagram', name: 'Instagram', Icon: CameraIcon, color: '#E4405F', description: 'Connect to understand visual preferences and lifestyle', benefit: 'Aesthetic preferences & hobbies', connectedData: ['Top themes: Nature photography, coffee culture, minimalist design', 'Active hashtags: #hikingadventures #coffeelover #minimalism', 'Engagement style: Thoughtful comments, saves travel content'] },
    { id: 'spotify', name: 'Spotify', Icon: MusicIcon, color: '#1DB954', description: 'Connect to match music taste and listening habits', benefit: 'Music compatibility', connectedData: ['Top genres: Indie folk, alternative rock, lo-fi hip hop', 'Favorite artists: Bon Iver, Phoebe Bridgers, The National', 'Listening habits: Evening listener, creates mood playlists'] },
    { id: 'netflix', name: 'Netflix', Icon: MovieIcon, color: '#E50914', description: 'Connect to analyze entertainment preferences', benefit: 'Movie & TV compatibility', connectedData: ['Favorite genres: Psychological thrillers, documentaries, indie dramas', 'Recently watched: The Crown, Dark, Chef\'s Table', 'Viewing pattern: Binge-watches on weekends, prefers subtitles'] },
    { id: 'goodreads', name: 'Goodreads', Icon: BookIcon, color: '#553B08', description: 'Connect to match reading preferences and literary taste', benefit: 'Reading compatibility', connectedData: ['Favorite genres: Literary fiction, philosophy, memoirs', 'Currently reading: "The Overstory" by Richard Powers', 'Reading pace: 2-3 books/month, writes detailed reviews'] },
    { id: 'linkedin', name: 'LinkedIn', Icon: BriefcaseIcon, color: '#0A66C2', description: 'Connect to understand career goals and professional interests', benefit: 'Career & ambition alignment', connectedData: ['Industry: Tech / Product Design', 'Career focus: UX research, human-centered design', 'Values: Work-life balance, social impact, continuous learning'] },
    { id: 'strava', name: 'Strava', Icon: RunIcon, color: '#FC4C02', description: 'Connect to match fitness habits and activity levels', benefit: 'Fitness & lifestyle compatibility', connectedData: ['Activities: Trail running, yoga, cycling', 'Weekly average: 4 workouts, 15 miles running', 'Preferred time: Early morning workouts, weekend hikes'] },
    { id: 'pinterest', name: 'Pinterest', Icon: PinIcon, color: '#E60023', description: 'Connect to understand aesthetic preferences and interests', benefit: 'Style & interest alignment', connectedData: ['Top boards: Home design, travel inspiration, healthy recipes', 'Style: Scandinavian minimalism, earthy tones, natural materials', 'Interests: DIY projects, plant care, sustainable fashion'] },
    { id: 'twitter', name: 'Twitter/X', Icon: TwitterIcon, color: '#1DA1F2', description: 'Connect to analyze opinions and communication style', benefit: 'Values & communication style', connectedData: ['Topics: Tech ethics, environmental policy, urban planning', 'Tone: Thoughtful, balanced, occasionally witty', 'Engagement: Retweets research, engages in civil discussions'] },
    { id: 'tiktok', name: 'TikTok', Icon: TikTokIcon, color: '#000000', description: 'Connect to understand humor and content preferences', benefit: 'Humor & entertainment style', connectedData: ['Content preferences: Cooking tutorials, travel vlogs, comedy sketches', 'Humor style: Dry humor, wordplay, observational comedy', 'Engagement: Saves recipes, shares travel tips'] },
    { id: 'apple_music', name: 'Apple Music', Icon: AppleMusicIcon, color: '#FA243C', description: 'Connect to match music taste and listening habits', benefit: 'Music compatibility', connectedData: ['Top genres: Jazz, soul, R&B', 'Favorite artists: Norah Jones, D\'Angelo, Erykah Badu', 'Listening habits: Creates curated playlists, explores new releases'] },
    { id: 'google', name: 'Google', Icon: SearchIcon, color: '#4285F4', description: 'Connect to enhance profile with location and activity data', benefit: 'Location & activity insights', connectedData: ['Frequent locations: Coffee shops, hiking trails, art galleries', 'Search interests: Sustainable living, travel destinations, cooking', 'Activity pattern: Weekend explorer, values local experiences'] },
  ];
  const connectedCount = Object.values(appConnections).filter(Boolean).length;

  const doneSet = completedStages instanceof Set ? completedStages : new Set();
  const doneCount = doneSet.size;
  const overallPct = Math.round((doneCount / STAGE_DEFS.length) * 100);

  const getDetailLevel = () => {
    const d = doneSet;
    if (d.has(12) || d.has(13) || d.has(14)) return 'complete';
    if (d.has(8) || d.has(9) || d.has(10) || d.has(11)) return 'advanced';
    if (d.has(4) || d.has(5) || d.has(6) || d.has(7)) return 'intermediate';
    return 'basic';
  };
  const detailLevel = getDetailLevel();

  const getStageProgress = (stageNum) => {
    const done = doneSet.has(stageNum);
    const stageTotal = AI_QUESTIONS.filter(q => q.stage === stageNum).length;
    let savedStep = -1;
    try { const raw = localStorage.getItem("soul_interview_progress"); if (raw) { const p = JSON.parse(raw); if (p.stage === stageNum && p.step >= 0) savedStep = p.step; } } catch {}
    const hasPartial = !done && savedStep >= 0;
    const pct = done ? 100 : hasPartial ? Math.round(((savedStep + 1) / stageTotal) * 100) : 0;
    return { done, hasPartial, pct, savedStep, stageTotal };
  };

  // ─── Snapshot section definitions (same as UserProfile) ───
  const a = userAnswers;
  const ac = appConnections; // shorthand for app connections
  const SNAPSHOT_SECTIONS = [
    {
      stage: 1, title: "IDENTITY & BASICS", color: T.rose,
      sampleQs: ["What's your first name?", "How old are you?", "What type of relationship are you looking for?"],
      getInsight: () => {
        const name = a.first_name || "Someone";
        const age = a.age || "";
        const loc = a.location || "";
        const intent = a.relationship_intent || "";
        const dist = a.travel_distance || "";
        const gender = a.gender || "";
        const pref = a.dating_gender_preference || "";
        const status = a.relationship_status || "";
        const single = a.single_duration || "";
        const intentShort = intent.toLowerCase().includes("marriage") ? "looking for something real — ideally leading to marriage" : intent.toLowerCase().includes("serious") ? "looking for a meaningful, long-term connection" : intent.toLowerCase().includes("casual") ? "keeping things light and open for now" : "exploring what feels right";
        const distShort = dist.toLowerCase().includes("anywhere") ? "and open to love wherever it finds them" : dist.toLowerCase().includes("local") ? "focused on connecting locally" : "willing to go the distance for the right person";
        const summary = `${name}, ${age}${loc ? ` from ${loc}` : ""}. ${gender}${pref ? `, interested in ${pref.toLowerCase()}` : ""}. ${status}${single ? ` for ${single.toLowerCase()}` : ""} — ${intentShort}, ${distShort}.`;
        return { type: "text", label: "WHO YOU ARE", content: summary };
      }
    },
    {
      stage: 2, title: "PHYSICAL PROFILE", color: T.rose,
      sampleQs: ["How tall are you?", "How would you describe your body type?", "What physical traits attract you?"],
      getInsight: () => {
        const h = a.height || ""; const bt = a.body_type || ""; const eth = a.ethnicity || "";
        const phys = a.physical_preferences || ""; const hp = a.height_preference || ""; const btp = a.body_type_preference || "";
        const parts = [];
        if (h || bt) parts.push(`${h}${bt ? `, ${bt.toLowerCase()} build` : ""}`);
        if (eth) parts.push(eth);
        const prefParts = [];
        if (hp) prefParts.push(`prefers someone ${hp}`);
        if (btp) prefParts.push(btp.toLowerCase());
        if (phys) prefParts.push(phys);
        const summary = (parts.length ? parts.join(". ") + "." : "") + (prefParts.length ? " Attracted to " + prefParts.join(" — ") + "." : "");
        return { type: "text", label: "PHYSICAL PROFILE", content: summary || "Physical profile captured." };
      }
    },
    {
      stage: 3, title: "CULTURE & BACKGROUND", color: T.violet,
      sampleQs: ["What languages do you speak fluently?", "How important is cultural compatibility?", "Any cultural traditions important to you?"],
      getInsight: () => {
        const lang = a.languages || ""; const born = a.birth_country || "";
        const imp = a.cultural_importance || ""; const open = a.cultural_openness || ""; const trad = a.cultural_traditions || "";
        const parts = [];
        if (lang) parts.push(`Speaks ${lang}`);
        if (born) parts.push(born);
        if (trad) parts.push(trad);
        if (imp) parts.push(imp);
        if (open && (open.toLowerCase().includes("yes") || open.toLowerCase().includes("absolutely"))) parts.push("Open to dating across cultures.");
        return { type: "text", label: "CULTURAL IDENTITY", content: parts.join(". ") || "Cultural background captured." };
      }
    },
    {
      stage: 4, title: "FAMILY & RELATIONSHIP HISTORY", color: T.teal,
      sampleQs: ["How close are you with your family?", "Do you have or want children?", "What did your last relationship teach you?"],
      getInsight: () => {
        const fam = a.family_closeness || ""; const kids = a.children || "";
        const lesson = a.relationship_lesson || "";
        const parts = [];
        if (fam) parts.push(fam);
        if (kids) parts.push(kids);
        if (lesson) parts.push(`Biggest takeaway: "${lesson}"`);
        return { type: "text", label: "FAMILY & HISTORY", content: parts.join(". ") || "Family and relationship history captured." };
      }
    },
    {
      stage: 5, title: "CAREER & STABILITY", color: T.amber,
      sampleQs: ["What line of work are you in?", "Where do you see yourself in 10 years?", "What days are you free for dates?"],
      getInsight: () => {
        const job = a.employment || "";
        const near = a.near_future_plans || ""; const long = a.long_term_goals || ""; const avail = a.schedule_availability || "";
        const parts = [];
        if (job) parts.push(job);
        if (near) parts.push(`Near-term: ${near}`);
        if (long) parts.push(`Long-term vision: ${long}`);
        if (avail) parts.push(`Free for dates: ${avail}`);
        const appIns5 = getAppInsightsForStage(5, ac);
        if (appIns5.length) parts.push(...appIns5.flatMap(i => i.data.map(d => `[${i.label}] ${d}`)));
        return { type: "text", label: "CAREER & AMBITION", content: parts.join(". ") || "Career and goals captured." };
      }
    },
    {
      stage: 6, title: "LIFESTYLE & DAILY LIFE", color: T.teal,
      sampleQs: ["Are you a morning person or night owl?", "How often do you exercise?", "What hobbies mean the most to you?"],
      getInsight: () => {
        const wk = a.weekend_lifestyle || ""; const rhythm = a.daily_rhythm || "";
        const fit = a.fitness || ""; const social = a.social_activities || ""; const hobby = a.hobbies || "";
        const parts = [];
        if (rhythm) parts.push(rhythm);
        if (fit) parts.push(fit);
        if (wk) parts.push(`Weekends: ${wk}`);
        if (hobby) parts.push(`Passions: ${hobby}`);
        if (social) parts.push(social);
        const appIns6 = getAppInsightsForStage(6, ac);
        if (appIns6.length) parts.push(...appIns6.flatMap(i => i.data.map(d => `[${i.label}] ${d}`)));
        return { type: "text", label: "DAY-TO-DAY LIFE", content: parts.join(". ") || "Lifestyle captured." };
      }
    },
    {
      stage: 7, title: "DATING STYLE", color: T.rose,
      sampleQs: ["Coffee, dinner, or a day activity for a first date?", "What atmosphere do you enjoy on dates?", "How often would you like to see a partner?"],
      getInsight: () => {
        const pref = a.date_preferences || ""; const atm = a.date_atmosphere || "";
        const freq = a.date_frequency || ""; const ideal = a.ideal_date_vision || "";
        const parts = [];
        if (pref) parts.push(`First date style: ${pref}`);
        if (atm) parts.push(`Best atmosphere: ${atm}`);
        if (freq) parts.push(`Ideal frequency: ${freq}`);
        if (ideal) parts.push(`Dream date: "${ideal}"`);
        return { type: "text", label: "HOW YOU DATE", content: parts.join(". ") || "Dating style captured." };
      }
    },
    {
      stage: 8, title: "LOGISTICS & MONEY", color: T.amber,
      sampleQs: ["What's your typical budget for a date?", "How do you feel about splitting the bill?", "How far will you travel for a relationship?"],
      getInsight: () => {
        const budget = a.date_budget || ""; const fin = a.financial_preferences || ""; const dist = a.relationship_distance || "";
        const parts = [];
        if (budget) parts.push(`Date budget: ${budget}`);
        if (fin) parts.push(fin);
        if (dist) parts.push(`Distance comfort: ${dist}`);
        return { type: "text", label: "PRACTICAL SIDE", content: parts.join(". ") || "Logistics captured." };
      }
    },
    {
      stage: 9, title: "FOOD & SOCIAL HABITS", color: T.teal,
      sampleQs: ["What are your top three cuisines?", "How do you take your coffee?", "Do you smoke or use recreational substances?"],
      getInsight: () => {
        const food = a.food_preferences || ""; const dislike = a.disliked_foods || "";
        const drink = a.drink_preferences || ""; const sub = a.substance_preferences || "";
        const parts = [];
        if (food) parts.push(food);
        if (dislike) parts.push(`Can't do: ${dislike}`);
        if (drink) parts.push(drink);
        if (sub) parts.push(sub);
        const appIns9 = getAppInsightsForStage(9, ac);
        if (appIns9.length) parts.push(...appIns9.flatMap(i => i.data.map(d => `[${i.label}] ${d}`)));
        return { type: "text", label: "FOOD & SOCIAL LIFE", content: parts.join(". ") || "Social habits captured." };
      }
    },
    {
      stage: 10, title: "TRAVEL & EXPERIENCES", color: T.violet,
      sampleQs: ["What countries have you traveled to?", "How important is travel in your life?"],
      getInsight: () => {
        const hist = a.travel_history || ""; const imp = a.travel_importance || "";
        const parts = [];
        if (hist) parts.push(hist);
        if (imp) parts.push(imp);
        const appIns10 = getAppInsightsForStage(10, ac);
        if (appIns10.length) parts.push(...appIns10.flatMap(i => i.data.map(d => `[${i.label}] ${d}`)));
        return { type: "text", label: "WANDERLUST", content: parts.join(". ") || "Travel profile captured." };
      }
    },
    {
      stage: 11, title: "VALUES & BELIEFS", color: T.rose,
      sampleQs: ["How would you describe your political views?", "How important is religion or spirituality?", "Any core values essential in a relationship?"],
      getInsight: () => {
        const pol = a.political_views || ""; const rel = a.religion || ""; const val = a.core_values || "";
        const parts = [];
        if (val) parts.push(val);
        if (rel) parts.push(rel);
        if (pol) parts.push(pol);
        const appIns11 = getAppInsightsForStage(11, ac);
        if (appIns11.length) parts.push(...appIns11.flatMap(i => i.data.map(d => `[${i.label}] ${d}`)));
        return { type: "text", label: "WHAT YOU STAND FOR", content: parts.join(". ") || "Values captured." };
      }
    },
    {
      stage: 12, title: "RELATIONSHIP PSYCHOLOGY", color: T.teal,
      sampleQs: ["What qualities do you value most in a relationship?", "How do you handle conflict?", "What's your primary love language?"],
      getInsight: () => {
        const rv = a.relationship_values || "";
        const conf = a.conflict_resolution || ""; const stress = a.stress_needs || "";
        const love = a.love_language || ""; const app = a.appreciation_style || "";
        const parts = [];
        if (rv) parts.push(`Values most: ${rv}`);
        if (love) parts.push(`Love language: ${love}`);
        if (conf) parts.push(`Handles conflict by: ${conf}`);
        if (stress) parts.push(`When stressed, needs: ${stress}`);
        if (app) parts.push(`Feels loved when: ${app}`);
        return { type: "text", label: "EMOTIONAL WIRING", content: parts.join(". ") || "Relationship psychology captured." };
      }
    },
    {
      stage: 13, title: "SELF AWARENESS", color: T.violet,
      sampleQs: ["What are your biggest pet peeves in relationships?", "What do people misunderstand about you?", "What does a healthy relationship look like to you?"],
      getInsight: () => {
        const pet = a.pet_peeves || ""; const first = a.first_impression || "";
        const self = a.self_description || ""; const ideal = a.ideal_partner_effect || "";
        const vision = a.healthy_relationship_vision || "";
        const parts = [];
        if (self) parts.push(`Describes themselves as: ${self}`);
        if (first) parts.push(first);
        if (ideal) parts.push(`The right person makes them: ${ideal}`);
        if (vision) parts.push(`Healthy love looks like: "${vision}"`);
        if (pet) parts.push(`Pet peeves: ${pet}`);
        return { type: "text", label: "SELF PORTRAIT", content: parts.join(". ") || "Self-awareness captured." };
      }
    },
    {
      stage: 14, title: "MUST-HAVES & DEALBREAKERS", color: T.rose,
      sampleQs: ["What qualities are absolute must-haves?", "What behaviors are relationship impossibilities?", "Any lifestyle choices that rule someone out?"],
      getInsight: () => {
        const must = a.must_haves || ""; const hard = a.hard_dealbreakers || ""; const life = a.lifestyle_dealbreakers || "";
        const parts = [];
        if (must) parts.push(`Must-haves: ${must}`);
        if (hard) parts.push(`Absolute dealbreakers: ${hard}`);
        if (life) parts.push(`Lifestyle non-negotiables: ${life}`);
        return { type: "text", label: "NON-NEGOTIABLES", content: parts.join(". ") || "Dealbreakers captured." };
      }
    },
  ];

  return (
    <div style={{ maxWidth: maxW, margin: "0 auto", padding: containerPad }}>
      {/* Header */}
      <PageHeader
        label="MY PROFILE"
        title="Interview & Insights"
        description="Your voice interview progress and personality analysis"
        gradientAnimation
      />

      <InterviewStagesPanel completedStages={completedStages} onStartStage={onStartStage} />

      {!bp.isDesktop && <div style={{ borderBottom: '1px solid rgba(0,0,0,0.08)', margin: '0 0 24px' }} />}

      {/* Personality Snapshot Title (outside panel) */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: bp.isDesktop ? 12 : 10 }}>
        <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.rose, fontWeight: 700, letterSpacing: "0.1em" }}>PERSONALITY SNAPSHOT</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: bp.isDesktop ? 11 : 12, color: T.muted, cursor: "pointer" }} onClick={() => { if (navigator.share) navigator.share({ title: "My settup Profile", text: "Check out my personality snapshot on settup!" }).catch(() => {}); else { navigator.clipboard.writeText(window.location.href); alert("Link copied!"); } }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
          Share with friends
        </div>
      </div>

      {/* Personality Snapshot Panel */}
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: bp.isDesktop ? 24 : 20, marginBottom: bp.isDesktop ? 32 : 24 }}>

          {/* ═══ NEW 4-COLUMN SNAPSHOT HEADER (MyProfilePage) ═══ */}
          {(() => {
            const basics = extractBasics(userAnswers);
            const lookingFor = extractLookingFor(userAnswers);
            const notInterested = extractNotInterestedIn(userAnswers);
            const keywords = extractKeywords(userAnswers, userContext);
            const removeKeyword = (kw) => {
              const updated = { ...userContext };
              let found = false;
              Object.keys(updated).forEach(k => {
                if (k === "kw_hidden") return;
                if (Array.isArray(updated[k])) {
                  const filtered = updated[k].filter(item => item !== kw);
                  if (filtered.length !== updated[k].length) { updated[k] = filtered; found = true; }
                }
              });
              if (!found) updated.kw_hidden = [...(updated.kw_hidden || []), kw];
              setUserContext(updated);
              localStorage.setItem("soul_user_context", JSON.stringify(updated));
              syncUserContext(updated);
            };
            const totalReactions = Object.values(vmReactions).reduce((s, v) => s + (v || 0), 0);
            const sortedEmojis = [...REACTION_EMOJIS].sort((a, b) => (vmReactions[b] || 0) - (vmReactions[a] || 0));
            const userAge = userAnswers.age || "";
            const userLoc = userAnswers.location || "";

            return (
              <div style={{ marginBottom: bp.isDesktop ? 24 : 20, paddingBottom: bp.isDesktop ? 24 : 20, borderBottom: `1px solid ${T.border}` }}>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: bp.isDesktop ? "minmax(180px, 1fr) minmax(180px, 1.2fr) minmax(180px, 1.2fr) minmax(180px, 1.2fr)" : bp.isTablet ? "1fr 1fr" : "1fr",
                  gap: bp.isDesktop ? 24 : 16,
                  alignItems: "start",
                }}>
                  {/* ── COL 1: Name, Location, VM Orb, Stacked Reactions ── */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <div>
                      <div style={{ fontSize: bp.isDesktop ? 20 : 18, fontWeight: 800, color: T.text, lineHeight: 1.2 }}>{snapName}{userAge ? `, ${userAge}` : ""}</div>
                      {userLoc && <div style={{ fontSize: 12, color: T.muted, marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                        {userLoc}
                      </div>}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 6 }}>
                      <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: "0.08em" }}>MY VOICEMAIL</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div onClick={() => setVmPlaying(p => !p)}
                          style={{ position: "relative", width: 56, height: 56, cursor: "pointer", flexShrink: 0 }}>
                          <div style={{ position: "absolute", inset: -4, borderRadius: "50%", border: `2px solid ${T.rose}`, animation: "orbRing 1.8s ease-out infinite", opacity: 0.7 }} />
                          <div style={{ width: "100%", height: "100%", borderRadius: "50%", background: `linear-gradient(135deg, ${T.rose}, ${T.violet})`, display: "flex", alignItems: "center", justifyContent: "center", animation: "orbPulse 1.5s ease-in-out infinite alternate", boxShadow: `0 0 12px ${T.roseGlow}`, border: `2px solid ${T.rose}` }}>
                            <span style={{ fontSize: 20, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginLeft: vmPlaying ? 0 : 2 }}>{vmPlaying ? "⏸" : <PlayTriangle size={22} />}</span>
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{vmPlaying ? "Playing..." : "Tap to listen"}</div>
                          <div style={{ fontSize: 11, color: T.muted }}>Recorded voicemail</div>
                        </div>
                      </div>
                      {vmPlaying && (
                        <div style={{ width: "100%", height: 3, borderRadius: 3, background: `${T.rose}30`, overflow: "hidden" }}>
                          <div style={{ height: "100%", background: `linear-gradient(90deg, ${T.rose}, ${T.violet})`, borderRadius: 3, animation: "voicemailProgress 60s linear forwards" }} />
                        </div>
                      )}
                    </div>
                    <div onClick={() => setShowReactionsModal(true)} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", marginTop: 2 }}>
                      <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, letterSpacing: "0.06em" }}>REACTIONS RECEIVED</div>
                    </div>
                    <div onClick={() => setShowReactionsModal(true)} style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        {sortedEmojis.filter(e => (vmReactions[e] || 0) > 0).slice(0, 5).map((emoji, i) => (
                          <span key={emoji} style={{ fontSize: 20, marginLeft: i > 0 ? -4 : 0, zIndex: 10 - i, filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.15))" }}>{emoji}</span>
                        ))}
                      </div>
                      {totalReactions > 0 && <span style={{ fontSize: 13, fontWeight: 700, color: T.rose, marginLeft: 8 }}>{totalReactions}</span>}
                    </div>
                  </div>

                  {/* ── COL 2: BASICS ── */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: T.text, letterSpacing: "0.06em", marginBottom: 8 }}>BASICS:</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      {basics.map((b, i) => <SnapPill key={i} icon={b.icon} text={b.text} variant="basic" />)}
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                      <div className="add-ctx-btn" onClick={() => { setAddContextStage("basics"); setContextText(""); setContextVisibility("public"); }}
                        style={{ "--btn-color": T.rose, "--btn-color-bg": `${T.rose}15`, "--btn-color-border": `${T.rose}35` }}>
                        <span className="add-ctx-btn__plus">+</span>
                        <span className="add-ctx-btn__static">Add</span>
                        <span className="add-ctx-btn__label">context</span>
                      </div>
                    </div>
                  </div>

                  {/* ── COL 3: LOOKING FOR ── */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#22c55e", letterSpacing: "0.06em", marginBottom: 8 }}>LOOKING FOR:</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      {lookingFor.map((b, i) => <SnapPill key={i} icon={b.icon} text={b.text} variant="positive" />)}
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                      <div className="add-ctx-btn" onClick={() => { setAddContextStage("looking"); setContextText(""); setContextVisibility("public"); }}
                        style={{ "--btn-color": T.rose, "--btn-color-bg": `${T.rose}15`, "--btn-color-border": `${T.rose}35` }}>
                        <span className="add-ctx-btn__plus">+</span>
                        <span className="add-ctx-btn__static">Add</span>
                        <span className="add-ctx-btn__label">context</span>
                      </div>
                    </div>
                  </div>

                  {/* ── COL 4: NOT INTERESTED IN ── */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#ef4444", letterSpacing: "0.06em", marginBottom: 8 }}>NOT INTERESTED IN:</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      {notInterested.map((b, i) => <SnapPill key={i} icon={b.icon} text={b.text} variant="negative" />)}
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                      <div className="add-ctx-btn" onClick={() => { setAddContextStage("dealbreakers"); setContextText(""); setContextVisibility("public"); }}
                        style={{ "--btn-color": T.rose, "--btn-color-bg": `${T.rose}15`, "--btn-color-border": `${T.rose}35` }}>
                        <span className="add-ctx-btn__plus">+</span>
                        <span className="add-ctx-btn__static">Add</span>
                        <span className="add-ctx-btn__label">context</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Keyword Cluster ── */}
                {keywords.length > 0 && (
                  <div style={{ marginTop: bp.isDesktop ? 20 : 16, paddingTop: bp.isDesktop ? 16 : 12, borderTop: `1px solid ${T.border}` }}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: bp.isDesktop ? "8px 14px" : "6px 10px", justifyContent: "center", lineHeight: 1.8 }}>
                      {keywords.map((kw, i) => (
                        editingKw?.kw === kw ? (
                          <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: `${T.rose}10`, border: `1px solid ${T.rose}35`, borderRadius: 20, padding: "4px 12px", position: "relative", zIndex: 101 }}>
                            {editingKw.confirmDelete ? (
                              <>
                                <span style={{ fontSize: 11, color: T.text, fontWeight: 500 }}>Remove?</span>
                                <span onClick={() => { removeKeyword(kw); setEditingKw(null); }} style={{ fontSize: 11, fontWeight: 700, color: "#ef4444", cursor: "pointer", userSelect: "none" }}>Yes</span>
                                <span onClick={() => setEditingKw(p => ({ ...p, confirmDelete: false }))} style={{ fontSize: 11, color: T.muted, cursor: "pointer", userSelect: "none" }}>No</span>
                              </>
                            ) : (
                              <>
                                <input autoFocus value={editingKw.text} onChange={e => setEditingKw(p => ({ ...p, text: e.target.value }))}
                                  onKeyDown={e => {
                                    if (e.key === "Enter") { const t = editingKw.text.trim(); if (t) { const upd = { ...userContext }; Object.keys(upd).forEach(k => { if (Array.isArray(upd[k])) { const idx = upd[k].indexOf(kw); if (idx !== -1) { const a = [...upd[k]]; a[idx] = t; upd[k] = a; } } }); setUserContext(upd); localStorage.setItem("soul_user_context", JSON.stringify(upd)); syncUserContext(upd); } setEditingKw(null); }
                                    if (e.key === "Escape") setEditingKw(null);
                                  }}
                                  style={{ border: "none", outline: "none", background: "transparent", fontSize: [11, 13, 12, 14, 11, 13, 12][i % 7], fontWeight: 600, color: T.muted, minWidth: 40, maxWidth: 140 }} />
                                <span onClick={() => { const t = editingKw.text.trim(); if (t) { const upd = { ...userContext }; Object.keys(upd).forEach(k => { if (Array.isArray(upd[k])) { const idx = upd[k].indexOf(kw); if (idx !== -1) { const a = [...upd[k]]; a[idx] = t; upd[k] = a; } } }); setUserContext(upd); localStorage.setItem("soul_user_context", JSON.stringify(upd)); syncUserContext(upd); } setEditingKw(null); }} style={{ fontSize: 14, color: T.rose, cursor: "pointer", fontWeight: 700, userSelect: "none" }}>✓</span>
                                <span onClick={() => setEditingKw(p => ({ ...p, confirmDelete: true }))} title="Remove" style={{ fontSize: 13, color: T.muted, cursor: "pointer", opacity: 0.5, userSelect: "none" }} onMouseEnter={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.color = "#ef4444"; }} onMouseLeave={e => { e.currentTarget.style.opacity = "0.5"; e.currentTarget.style.color = T.muted; }}>🗑</span>
                                <span onClick={() => setEditingKw(null)} style={{ fontSize: 13, color: T.muted, cursor: "pointer", opacity: 0.5, userSelect: "none" }}>✕</span>
                              </>
                            )}
                          </span>
                        ) : (
                          <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                            <span style={{ fontSize: [11, 13, 12, 14, 11, 13, 12][i % 7], fontWeight: [500, 600, 500, 700, 500, 600, 500][i % 7], color: T.muted, opacity: [0.7, 0.85, 0.6, 0.9, 0.65, 0.8, 0.55][i % 7], cursor: "default" }}>{kw}</span>
                            <span onClick={() => setEditingKw({ kw, text: kw, confirmDelete: false })} title="Edit" style={{ fontSize: 10, color: T.muted, opacity: 0.3, cursor: "pointer", lineHeight: 1, userSelect: "none", transition: "opacity 0.15s, color 0.15s" }}
                              onMouseEnter={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.color = T.rose; }}
                              onMouseLeave={e => { e.currentTarget.style.opacity = "0.3"; e.currentTarget.style.color = T.muted; }}>✏</span>
                          </span>
                        )
                      ))}
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                      <div className="add-ctx-btn" onClick={() => { setAddContextStage("keywords"); setContextText(""); setContextVisibility("public"); }}
                        style={{ "--btn-color": T.rose, "--btn-color-bg": `${T.rose}15`, "--btn-color-border": `${T.rose}35` }}>
                        <span className="add-ctx-btn__plus">+</span>
                        <span className="add-ctx-btn__static">Add</span>
                        <span className="add-ctx-btn__label">context</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Add Context Panel (for header columns) ── */}
                <div style={{ position: "relative" }}>
                  <AddContextPanel
                    open={addContextStage === "basics" || addContextStage === "looking" || addContextStage === "dealbreakers" || addContextStage === "keywords"}
                    onClose={() => { setAddContextStage(null); setContextText(""); setIsExact(false); }}
                    accentColor={T.rose}
                    contextVisibility={contextVisibility}
                    setContextVisibility={setContextVisibility}
                    contextText={contextText}
                    setContextText={setContextText}
                    isExact={isExact}
                    setIsExact={setIsExact}
                    label={addContextStage === "basics" ? "Add to Profile" : addContextStage === "looking" ? "Add to Looking For" : addContextStage === "dealbreakers" ? "Add to Deal Breakers" : "Add Keyword"}
                    onSave={() => {
                      const trimmed = contextText.trim();
                      if (contextVisibility === "secret") {
                        const updated = [...secretContext, { text: trimmed, category: addContextStage, createdAt: Date.now(), exact: !!isExact }];
                        setSecretContext(updated);
                        localStorage.setItem("soul_secret_context", JSON.stringify(updated));
                        syncSecretContext(updated);
                      } else {
                        const stageKey = addContextStage === "keywords" ? "kw" : addContextStage;
                        const updated = { ...userContext, [stageKey]: [...(userContext[stageKey] || []), trimmed] };
                        setUserContext(updated);
                        localStorage.setItem("soul_user_context", JSON.stringify(updated));
                        syncUserContext(updated);
                      }
                      if (isExact) {
                        const norm = trimmed.toLowerCase();
                        const next = exactContext.includes(norm) ? exactContext : [...exactContext, norm];
                        setExactContext(next);
                        localStorage.setItem("soul_exact_context", JSON.stringify(next));
                        syncExactContext(next);
                      }
                      setContextText(""); setAddContextStage(null); setIsExact(false);
                    }}
                  />
                </div>
              </div>
            );
          })()}

          {/* ── My Private Context Management ── */}
          <div style={{ marginBottom: bp.isDesktop ? 24 : 18 }}>
            <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: "#7c3aed", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
              🔒 MY PRIVATE CONTEXT
              <span style={{ fontSize: 11, fontWeight: 500, color: T.muted, letterSpacing: "normal" }}>· only visible to you</span>
            </div>
            {secretContext.length === 0 ? (
              <div style={{ padding: "12px 14px", background: "#7c3aed06", border: "1px dashed #7c3aed30", borderRadius: 10, fontSize: 12, color: T.muted, lineHeight: 1.5 }}>
                No private context yet. When you add context and choose <b>Secret</b>, it will appear here for you to manage.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {secretContext.map((item, i) => (
                  editingCtx?.type === "secret" && editingCtx?.idx === i ? (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "10px 14px", background: "#7c3aed12", border: "1px solid #7c3aed50", borderRadius: 10, position: "relative", zIndex: 101 }}>
                      {editingCtx.confirmDelete ? (
                        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 13, color: T.text, fontWeight: 500 }}>Remove this item?</span>
                          <button onClick={() => { const updated = secretContext.filter((_, idx) => idx !== i); setSecretContext(updated); localStorage.setItem("soul_secret_context", JSON.stringify(updated)); syncSecretContext(updated); if (item.exact) { const norm = item.text.trim().toLowerCase(); const updatedExact = exactContext.filter(e => e !== norm); setExactContext(updatedExact); localStorage.setItem("soul_exact_context", JSON.stringify(updatedExact)); syncExactContext(updatedExact); } setEditingCtx(null); }} style={{ fontSize: 12, fontWeight: 700, color: "#ef4444", background: "#ef444415", border: "1px solid #ef444430", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontFamily: "inherit" }}>Yes, remove</button>
                          <button onClick={() => setEditingCtx(p => ({ ...p, confirmDelete: false }))} style={{ fontSize: 12, color: T.muted, background: T.hover, border: `1px solid ${T.border}`, borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                        </div>
                      ) : (
                        <>
                          <textarea autoFocus value={editingCtx.text}
                            onChange={e => setEditingCtx(p => ({ ...p, text: e.target.value }))}
                            onKeyDown={e => {
                              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); const t = editingCtx.text.trim(); if (t) { const a = secretContext.map((x, xi) => xi === i ? { ...x, text: t } : x); setSecretContext(a); localStorage.setItem("soul_secret_context", JSON.stringify(a)); syncSecretContext(a); } setEditingCtx(null); }
                              if (e.key === "Escape") setEditingCtx(null);
                            }}
                            style={{ flex: 1, resize: "none", minHeight: 50, border: "none", outline: "none", background: "transparent", fontSize: 13, fontWeight: 600, color: T.text, fontFamily: "inherit", lineHeight: 1.4 }} />
                          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                            <button onClick={() => { const t = editingCtx.text.trim(); if (t) { const a = secretContext.map((x, xi) => xi === i ? { ...x, text: t } : x); setSecretContext(a); localStorage.setItem("soul_secret_context", JSON.stringify(a)); syncSecretContext(a); } setEditingCtx(null); }} style={{ width: 28, height: 28, borderRadius: "50%", background: "#7c3aed20", border: "1px solid #7c3aed40", color: "#7c3aed", fontSize: 15, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}>✓</button>
                            <button onClick={() => setEditingCtx(p => ({ ...p, confirmDelete: true }))} title="Remove" style={{ width: 28, height: 28, borderRadius: "50%", background: "#ef444410", border: "1px solid #ef444430", color: "#ef4444", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit", opacity: 0.6 }} onMouseEnter={e => e.currentTarget.style.opacity = "1"} onMouseLeave={e => e.currentTarget.style.opacity = "0.6"}>🗑</button>
                            <button onClick={() => setEditingCtx(null)} style={{ width: 28, height: 28, borderRadius: "50%", background: T.hover, border: `1px solid ${T.border}`, color: T.muted, fontSize: 15, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}>✕</button>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                  <div key={i} onClick={() => setEditingCtx({ type: "secret", idx: i, text: item.text, confirmDelete: false })} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: "#7c3aed08", border: "1px solid #7c3aed20", borderRadius: 10, cursor: "pointer" }}>
                    <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{item.text}</span>
                      {item.exact && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: "#ef4444", background: "#ef444415", border: "1px solid #ef444430", borderRadius: 4, padding: "1px 6px", flexShrink: 0 }}>EXACT</span>
                      )}
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#7c3aed", opacity: 0.65, flexShrink: 0 }}>
                        {typeof item.category === "number" ? `Stage ${item.category}` : item.category === "kw" ? "Keyword" : item.category ? item.category.charAt(0).toUpperCase() + item.category.slice(1) : "General"}
                      </span>
                    </div>
                    <span style={{ fontSize: 12, color: "#7c3aed", opacity: 0.4, marginLeft: 12, flexShrink: 0 }}>✏</span>
                  </div>
                  )
                ))}
              </div>
            )}
          </div>

          {(editingKw || editingCtx) && (
            <div onClick={() => { setEditingKw(null); setEditingCtx(null); }} style={{ position: "fixed", inset: 0, zIndex: 100 }} />
          )}

          {/* ── Reactions Detail Modal (MyProfilePage) ── */}
          {showReactionsModal && (
            <div onClick={() => setShowReactionsModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
              <div onClick={e => e.stopPropagation()} style={{ background: T.card, borderRadius: 12, padding: "24px 28px", maxWidth: 340, width: "100%", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 16 }}>Reactions Received</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {REACTION_EMOJIS.filter(e => (vmReactions[e] || 0) > 0).sort((a, b) => (vmReactions[b] || 0) - (vmReactions[a] || 0)).map(emoji => (
                    <div key={emoji} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: `${T.rose}08`, borderRadius: 8 }}>
                      <span style={{ fontSize: 22 }}>{emoji}</span>
                      <span style={{ fontSize: 16, fontWeight: 800, color: T.rose }}>{vmReactions[emoji] || 0}</span>
                    </div>
                  ))}
                </div>
                {receivedVmReactions.length > 0 && (
                  <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, letterSpacing: "0.06em", marginBottom: 8 }}>FROM YOUR MATCHES</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {receivedVmReactions.map((r, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 4, background: `${T.rose}10`, border: `1px solid ${T.rose}25`, borderRadius: 8, padding: "3px 10px" }}>
                          <span style={{ fontSize: 14 }}>{r.emoji}</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{r.reactorName || "Match"}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <button onClick={() => setShowReactionsModal(false)}
                  style={{ marginTop: 16, width: "100%", padding: "10px 0", borderRadius: 8, background: T.rose, color: "#fff", fontSize: 13, fontWeight: 700, border: "none", cursor: "pointer", fontFamily: "inherit" }}>Close</button>
              </div>
            </div>
          )}

          {/* Per-Stage Snapshot Sections (completed only) */}
          {SNAPSHOT_SECTIONS.filter(sec => getStageProgress(sec.stage).done).map((sec, si) => {
            const insight = sec.getInsight();
            const stageCtx = userContext[sec.stage] || [];
            const stageQs = AI_QUESTIONS.filter(q => q.stage === sec.stage);
            const filled = stageQs.filter(q => userAnswers[q.key] && String(userAnswers[q.key]).trim()).length;
            const fillPct = stageQs.length ? Math.round((filled / stageQs.length) * 100) : 100;
            return (
              <div key={si} style={{ marginBottom: bp.isDesktop ? 20 : 16, padding: bp.isDesktop ? "18px 20px" : "14px 16px", background: T.surface, borderRadius: 8, border: `1px solid ${T.border}` }}>
                {/* Section Header + Insight merged */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 6 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: insight?.content ? 5 : 0 }}>
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: sec.color, flexShrink: 0 }} />
                      <div style={{ fontSize: bp.isDesktop ? 11 : 12, color: sec.color, fontWeight: 700, letterSpacing: "0.08em" }}>
                        STAGE {sec.stage} · {sec.title}
                      </div>
                    </div>
                    {insight?.content && (
                      <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, lineHeight: 1.6, paddingLeft: 14 }}>
                        {insight.content}
                      </div>
                    )}
                  </div>
                  <div style={{ flexShrink: 0, width: 60, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3, marginTop: 2 }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: sec.color, lineHeight: 1 }}>{fillPct}%</div>
                    <div style={{ width: 60, height: 3, borderRadius: 99, background: `${sec.color}20`, overflow: "hidden" }}>
                      <div style={{ height: "100%", borderRadius: 99, background: sec.color, width: `${fillPct}%`, transition: "width 0.9s cubic-bezier(0.22,1,0.36,1)" }} />
                    </div>
                  </div>
                </div>
                {/* User-added context tags */}
                {stageCtx.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                    {stageCtx.map((ctx, ci) => (
                      editingCtx?.type === "stage" && editingCtx?.key === sec.stage && editingCtx?.idx === ci ? (
                        <span key={ci} style={{ display: "inline-flex", alignItems: "center", gap: 5, background: `${sec.color}18`, border: `1px solid ${sec.color}50`, borderRadius: 20, padding: "3px 10px", position: "relative", zIndex: 101 }}>
                          {editingCtx.confirmDelete ? (
                            <>
                              <span style={{ fontSize: 11, color: T.text, fontWeight: 500 }}>Remove?</span>
                              <span onClick={() => { const u = { ...userContext, [sec.stage]: stageCtx.filter((_, j) => j !== ci) }; setUserContext(u); localStorage.setItem("soul_user_context", JSON.stringify(u)); syncUserContext(u); setEditingCtx(null); }} style={{ fontSize: 11, fontWeight: 700, color: "#ef4444", cursor: "pointer", userSelect: "none" }}>Yes</span>
                              <span onClick={() => setEditingCtx(p => ({ ...p, confirmDelete: false }))} style={{ fontSize: 11, color: T.muted, cursor: "pointer", userSelect: "none" }}>No</span>
                            </>
                          ) : (
                            <>
                              <input autoFocus value={editingCtx.text}
                                onChange={e => setEditingCtx(p => ({ ...p, text: e.target.value }))}
                                onKeyDown={e => {
                                  if (e.key === "Enter") { const t = editingCtx.text.trim(); if (t) { const a = [...(userContext[sec.stage] || [])]; a[ci] = t; const u = { ...userContext, [sec.stage]: a }; setUserContext(u); localStorage.setItem("soul_user_context", JSON.stringify(u)); syncUserContext(u); } setEditingCtx(null); }
                                  if (e.key === "Escape") setEditingCtx(null);
                                }}
                                style={{ border: "none", outline: "none", background: "transparent", fontSize: 12, fontWeight: 600, color: sec.color, minWidth: 60, maxWidth: 220 }} />
                              <span onClick={() => { const t = editingCtx.text.trim(); if (t) { const a = [...(userContext[sec.stage] || [])]; a[ci] = t; const u = { ...userContext, [sec.stage]: a }; setUserContext(u); localStorage.setItem("soul_user_context", JSON.stringify(u)); syncUserContext(u); } setEditingCtx(null); }} style={{ fontSize: 13, color: sec.color, cursor: "pointer", fontWeight: 700 }}>✓</span>
                              <span onClick={() => setEditingCtx(p => ({ ...p, confirmDelete: true }))} style={{ fontSize: 12, color: T.muted, cursor: "pointer", opacity: 0.5 }} onMouseEnter={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.color = "#ef4444"; }} onMouseLeave={e => { e.currentTarget.style.opacity = "0.5"; e.currentTarget.style.color = T.muted; }}>🗑</span>
                              <span onClick={() => setEditingCtx(null)} style={{ fontSize: 13, color: T.muted, cursor: "pointer", opacity: 0.5 }}>✕</span>
                            </>
                          )}
                        </span>
                      ) : (
                        <div key={ci} onClick={() => setEditingCtx({ type: "stage", key: sec.stage, idx: ci, text: ctx, confirmDelete: false })} style={{ display: "inline-flex", alignItems: "center", gap: 5, background: `${sec.color}10`, border: `1px solid ${sec.color}25`, color: sec.color, fontSize: 12, fontWeight: 600, padding: "4px 10px", borderRadius: 20, cursor: "pointer" }}>
                          <span>{ctx}</span>
                          <span style={{ fontSize: 10, opacity: 0.4, lineHeight: 1 }}>✏</span>
                        </div>
                      )
                    ))}
                  </div>
                )}
                {/* Add Context button + Panel */}
                <div style={{ position: "relative", marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
                  <div
                    className="add-ctx-btn"
                    onClick={() => { setAddContextStage(sec.stage); setContextText(""); setContextVisibility("public"); }}
                    title="Add context"
                    style={{ "--btn-color": T.rose, "--btn-color-bg": `${T.rose}15`, "--btn-color-border": `${T.rose}35` }}>
                    <span className="add-ctx-btn__plus">+</span>
                    <span className="add-ctx-btn__static">Add</span>
                    <span className="add-ctx-btn__label">context</span>
                  </div>
                  <AddContextPanel
                    open={addContextStage === sec.stage}
                    onClose={() => { setAddContextStage(null); setContextText(""); setIsExact(false); }}
                    accentColor={sec.color}
                    contextVisibility={contextVisibility}
                    setContextVisibility={setContextVisibility}
                    contextText={contextText}
                    setContextText={setContextText}
                    isExact={isExact}
                    setIsExact={setIsExact}
                    label={`Stage ${sec.stage} · ${sec.title}`}
                    onSave={() => {
                      const trimmed = contextText.trim();
                      if (contextVisibility === "secret") {
                        const updated = [...secretContext, { text: trimmed, category: String(sec.stage), createdAt: Date.now(), exact: !!isExact }];
                        setSecretContext(updated);
                        localStorage.setItem("soul_secret_context", JSON.stringify(updated));
                        syncSecretContext(updated);
                      } else {
                        const updated = { ...userContext, [sec.stage]: [...(userContext[sec.stage] || []), trimmed] };
                        setUserContext(updated);
                        localStorage.setItem("soul_user_context", JSON.stringify(updated));
                        syncUserContext(updated);
                      }
                      if (isExact) {
                        const norm = trimmed.toLowerCase();
                        const next = exactContext.includes(norm) ? exactContext : [...exactContext, norm];
                        setExactContext(next);
                        localStorage.setItem("soul_exact_context", JSON.stringify(next));
                        syncExactContext(next);
                      }
                      setContextText(""); setAddContextStage(null); setIsExact(false);
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

      {!bp.isDesktop && <div style={{ borderBottom: '1px solid rgba(0,0,0,0.08)', margin: '0 0 24px' }} />}

      {/* ── INCOMPLETE STAGES: Sample Questions (outside panel) ── */}
      {(() => {
        const incompleteSections = SNAPSHOT_SECTIONS.filter(sec => !getStageProgress(sec.stage).done);
        if (incompleteSections.length === 0) return null;
        return (
          <div style={{ marginBottom: bp.isDesktop ? 32 : 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: bp.isDesktop ? 12 : 10 }}>
              <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.rose, fontWeight: 700, letterSpacing: "0.1em" }}>SAMPLE QUESTIONS</div>
              <div style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.muted }}>{incompleteSections.length} stages remaining</div>
            </div>
            {bp.isMobile ? (
              <div onTouchStart={e => e.stopPropagation()} onTouchEnd={e => e.stopPropagation()} style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 8, WebkitOverflowScrolling: "touch", scrollSnapType: "x mandatory", msOverflowStyle: "none", scrollbarWidth: "none" }}>
                {incompleteSections.map((sec, cardIdx) => {
                  const prog = getStageProgress(sec.stage);
                  const isPartial = prog.hasPartial;
                  const badgeColor = isPartial ? T.amber : sec.color;
                  const ringSize = 38;
                  const ringStroke = 2.5;
                  const ringR = (ringSize - ringStroke * 2) / 2;
                  const ringCirc = 2 * Math.PI * ringR;
                  const ringOffset = ringCirc - (prog.pct / 100) * ringCirc;
                  return (
                    <div key={sec.stage} onClick={() => onStartStage && onStartStage(sec.stage)} style={{
                      minWidth: 220, maxWidth: 220,
                      background: T.card, border: `1px solid ${T.border}`, borderRadius: 14,
                      padding: "14px 14px 12px",
                      display: "flex", flexDirection: "column", gap: 0,
                      cursor: "pointer", scrollSnapAlign: "start", flexShrink: 0,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                        <div style={{ position: "relative", width: ringSize, height: ringSize, flexShrink: 0 }}>
                          <svg width={ringSize} height={ringSize} style={{ transform: "rotate(-90deg)", position: "absolute", inset: 0 }}>
                            <circle cx={ringSize/2} cy={ringSize/2} r={ringR} fill="none" stroke={`${sec.color}20`} strokeWidth={ringStroke} />
                            {prog.pct > 0 && (
                              <circle cx={ringSize/2} cy={ringSize/2} r={ringR} fill="none" stroke={sec.color} strokeWidth={ringStroke}
                                strokeDasharray={ringCirc} strokeDashoffset={ringOffset} strokeLinecap="round"
                                style={{ transition: "stroke-dashoffset 0.8s ease", filter: `drop-shadow(0 0 3px ${sec.color}50)` }} />
                            )}
                          </svg>
                          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: `${sec.color}15`, borderRadius: "50%" }}>
                            {(() => { const sd = STAGE_DEFS.find(d => d.n === sec.stage); return sd?.AnimIcon ? <sd.AnimIcon size={18} color={sec.color} /> : <span style={{ fontSize: 14, fontWeight: 800, color: sec.color }}>{sec.stage}</span>; })()}
                          </div>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{STAGES[sec.stage]?.name || sec.title}</div>
                          <div style={{ fontSize: 11, color: T.muted, marginTop: 2, lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{STAGES[sec.stage]?.desc || ""}</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                        <div style={{ flex: 1, height: 4, background: `${sec.color}15`, borderRadius: 2, overflow: "hidden" }}>
                          <div style={{ width: `${prog.pct}%`, height: "100%", background: sec.color, borderRadius: 2, transition: "width 0.6s ease" }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: isPartial ? sec.color : T.muted, flexShrink: 0 }}>{prog.pct}%</span>
                      </div>
                      <div style={{ flex: 1, overflow: "hidden", fontSize: 13, color: T.muted, lineHeight: 1.5, fontStyle: "italic", marginBottom: 6, minHeight: 40 }}>
                        <SampleQTypewriter questions={sec.sampleQs} color={T.muted} fontSize={11} interval={5000} staggerDelay={cardIdx * 600} />
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); onStartStage && onStartStage(sec.stage); }}
                        style={{
                          width: "100%", padding: "7px 0", borderRadius: 6, background: badgeColor, color: "#fff",
                          fontSize: 12, fontWeight: 700, border: "none", cursor: "pointer",
                          fontFamily: "inherit", transition: "all 0.15s", flexShrink: 0
                        }}>
                        {isPartial ? "▶ Resume" : "▶ Start"}
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <ParallaxSlider
                items={incompleteSections.map(sec => ({ ...sec, key: sec.stage }))}
                itemWidth={bp.isDesktop ? 280 : 250}
                itemGap={bp.isDesktop ? 16 : 12}
                sliderHeight={bp.isDesktop ? 200 : 185}
                nudgeCount={bp.isDesktop ? 3 : 2}
                renderItem={(sec, cardIdx, getDragMoved) => {
                  const prog = getStageProgress(sec.stage);
                  const isPartial = prog.hasPartial;
                  const badgeColor = isPartial ? T.amber : sec.color;
                  const iW = bp.isDesktop ? 280 : 250;
                  const ringSize = bp.isDesktop ? 42 : 38;
                  const ringStroke = 2.5;
                  const ringR = (ringSize - ringStroke * 2) / 2;
                  const ringCirc = 2 * Math.PI * ringR;
                  const ringOffset = ringCirc - (prog.pct / 100) * ringCirc;
                  return (
                    <div style={{
                      width: iW, background: T.card, border: `1px solid ${T.border}`, borderRadius: 8,
                      padding: bp.isDesktop ? "14px 16px" : "12px 14px",
                      display: "flex", flexDirection: "column", gap: 0,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)", height: "100%", boxSizing: "border-box", overflow: "hidden",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                        <div style={{ position: "relative", width: ringSize, height: ringSize, flexShrink: 0 }}>
                          <svg width={ringSize} height={ringSize} style={{ transform: "rotate(-90deg)", position: "absolute", inset: 0 }}>
                            <circle cx={ringSize/2} cy={ringSize/2} r={ringR} fill="none" stroke={`${sec.color}20`} strokeWidth={ringStroke} />
                            {prog.pct > 0 && (
                              <circle cx={ringSize/2} cy={ringSize/2} r={ringR} fill="none" stroke={sec.color} strokeWidth={ringStroke}
                                strokeDasharray={ringCirc} strokeDashoffset={ringOffset} strokeLinecap="round"
                                style={{ transition: "stroke-dashoffset 0.8s ease", filter: `drop-shadow(0 0 3px ${sec.color}50)` }} />
                            )}
                          </svg>
                          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: `${sec.color}15`, borderRadius: "50%" }}>
                            {(() => { const sd = STAGE_DEFS.find(d => d.n === sec.stage); return sd?.AnimIcon ? <sd.AnimIcon size={bp.isDesktop ? 20 : 18} color={sec.color} /> : <span style={{ fontSize: 14, fontWeight: 800, color: sec.color }}>{sec.stage}</span>; })()}
                          </div>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: bp.isDesktop ? 13 : 14, fontWeight: 700, color: T.text, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{STAGES[sec.stage]?.name || sec.title}</div>
                          <div style={{ fontSize: bp.isDesktop ? 10 : 11, color: T.muted, marginTop: 2, lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{STAGES[sec.stage]?.desc || ""}</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                        <div style={{ flex: 1, height: 4, background: `${sec.color}15`, borderRadius: 2, overflow: "hidden" }}>
                          <div style={{ width: `${prog.pct}%`, height: "100%", background: sec.color, borderRadius: 2, transition: "width 0.6s ease" }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: isPartial ? sec.color : T.muted, flexShrink: 0 }}>{prog.pct}%</span>
                      </div>
                      <div style={{ flex: 1, overflow: "hidden", fontSize: bp.isDesktop ? 12 : 13, color: T.muted, lineHeight: 1.5, fontStyle: "italic" }}>
                        <SampleQTypewriter questions={sec.sampleQs} color={T.muted} fontSize={bp.isDesktop ? 12 : 11} interval={5000} staggerDelay={cardIdx * 600} />
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); if (!getDragMoved()) onStartStage && onStartStage(sec.stage); }}
                        style={{
                          width: "100%", padding: "6px 0", borderRadius: 5, background: badgeColor, color: "#fff",
                          fontSize: bp.isDesktop ? 11 : 12, fontWeight: 700, border: "none", cursor: "pointer",
                          fontFamily: "inherit", transition: "all 0.15s", marginTop: 6, flexShrink: 0
                        }}
                        onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.02)"; e.currentTarget.style.filter = "brightness(1.1)"; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.filter = "none"; }}>
                        {isPartial ? "▶ Resume" : "▶ Start"}
                      </button>
                    </div>
                  );
                }}
              />
            )}

            {/* Overall Progress Footer */}
            {doneCount < STAGE_DEFS.length && (
              <div style={{ 
                marginTop: 12, 
                padding: bp.isDesktop ? "14px 16px" : "12px 14px", 
                background: `${T.amber}08`,
                border: `1px dashed ${T.amber}40`,
                borderRadius: 6,
                textAlign: "center"
              }}>
                <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.amber, fontWeight: 600 }}>
                  💡 {doneCount} of {STAGE_DEFS.length} stages complete — each stage deepens your personality insights and improves match accuracy
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {!bp.isDesktop && <div style={{ borderBottom: '1px solid rgba(0,0,0,0.08)', margin: '0 0 24px' }} />}

      {/* App Connections Panel */}
      <div style={{ marginBottom: bp.isDesktop ? 32 : 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: bp.isDesktop ? 12 : 14 }}>
          <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.rose, fontWeight: 700, letterSpacing: "0.1em" }}>APP CONNECTIONS</div>
          {bp.isDesktop && (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", background: T.roseSoft, border: `1px solid ${T.borderHover}`, borderRadius: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: T.rose }}>{connectedCount} of {APP_DATA.length} connected</span>
            </div>
          )}
        </div>
        <ParallaxSlider
          items={APP_DATA.map(app => ({ ...app, key: app.id }))}
          itemWidth={bp.isDesktop ? 280 : bp.isTablet ? 250 : 250}
          itemGap={bp.isDesktop ? 16 : 14}
          sliderHeight={bp.isDesktop ? 200 : 200}
          nudgeCount={bp.isDesktop ? 3 : bp.isTablet ? 2 : 1}
          renderItem={(app, cardIdx, getDragMoved) => {
            const isConnected = appConnections[app.id];
            const iW = bp.isDesktop ? 280 : bp.isTablet ? 250 : 250;
            return (
              <div
                onClick={() => { if (!getDragMoved()) setSvcLoginModal(app.id); }}
                style={{
                  width: iW,
                  background: T.card,
                  border: `1px solid ${isConnected ? app.color : T.border}`,
                  borderRadius: 8,
                  padding: bp.isDesktop ? "14px 16px" : "12px 14px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  height: "100%", boxSizing: "border-box",
                  overflow: "hidden",
                  cursor: "pointer",
                  position: "relative",
                  transition: "border-color 0.2s"
                }}>
                {/* Status badge */}
                <div style={{
                  position: "absolute", top: 12, right: 12,
                  width: 28, height: 28,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: isConnected ? app.color : T.dim,
                  borderRadius: "50%", transition: "all 0.3s ease"
                }}>
                  {isConnected ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17L4 12" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 5V19M5 12H19" stroke={T.muted} strokeWidth="3" strokeLinecap="round"/></svg>
                  )}
                </div>

                {/* App header */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, paddingRight: 36 }}>
                  <div style={{ width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", background: `${app.color}15`, borderRadius: 10, flexShrink: 0 }}>
                    <app.Icon size={22} color={app.color} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: bp.isDesktop ? 14 : 15, fontWeight: 700, color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{app.name}</div>
                    <div style={{ fontSize: bp.isDesktop ? 10 : 11, color: isConnected ? app.color : T.muted, fontWeight: 600, letterSpacing: "0.05em" }}>
                      {isConnected ? "Connected" : app.benefit}
                    </div>
                  </div>
                </div>

                {/* Insights or description */}
                <div style={{ flex: 1, overflow: "hidden" }}>
                  {isConnected && app.connectedData ? (
                    <div style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.text, lineHeight: 1.5 }}>
                      {app.connectedData.map((insight, idx) => (
                        <div key={idx} style={{ display: "flex", gap: 6, marginBottom: idx < app.connectedData.length - 1 ? 4 : 0, alignItems: "flex-start" }}>
                          <span style={{ color: app.color, fontSize: 13, lineHeight: 1, marginTop: 2, flexShrink: 0 }}>•</span>
                          <span style={{ flex: 1, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{insight}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.muted, lineHeight: 1.5, margin: 0 }}>{app.description}</p>
                  )}
                </div>
              </div>
            );
          }}
        />
      </div>

      {/* Service Login Modal */}
      {svcLoginModal && (
        <ServiceLoginModal
          serviceId={svcLoginModal}
          isConnected={appConnections[svcLoginModal]}
          onClose={() => setSvcLoginModal(null)}
          onConnect={handleSvcConnect}
          onDisconnect={handleSvcDisconnect}
        />
      )}
      
    </div>
  );
}

// ─── AdminPanel ───────────────────────────────────────────────
function AdminPanel({ setBlindMode }) {
  const bp = useBreakpoint();
  const [adminToken, setAdminToken] = useState(() => {
    try {
      const t = localStorage.getItem("soul_session_token");
      if (!t) return null;
      const payload = JSON.parse(atob(t.split('.')[1]));
      return payload.isAdmin ? t : null;
    } catch { return null; }
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState({});
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentDates, setRecentDates] = useState([]);
  const [tab, setTab] = useState("overview");
  const [saving, setSaving] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [headerMounted, setHeaderMounted] = React.useState(false);

  useEffect(() => { if (adminToken && !stats) loadDashboard(adminToken); }, [adminToken]);
  useEffect(() => { const timer = setTimeout(() => setHeaderMounted(true), 100); return () => clearTimeout(timer); }, []);

  const handleLogin = async () => {
    setLoginError("");
    try {
      const r = await fetch(`${API_BASE}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const d = await r.json();
      if (!r.ok) { setLoginError(d.error || "Login failed"); return; }
      setAdminToken(d.token);
      loadDashboard(d.token);
    } catch { setLoginError("Connection error"); }
  };

  const loadDashboard = async (token) => {
    const defaultSettings = {
      blind_match_mode: { value: "true", label: "Blind Match Mode", description: "Hide photos until mutual interest" },
      show_photos: { value: "true", label: "Show Profile Photos", description: "Display profile photos in match feed and detail view" },
      show_bio: { value: "true", label: "Show User Bio", description: "Display bio text in match feed cards" },
      maintenance_mode: { value: "false", label: "Maintenance Mode", description: "Take the app offline for all users with a maintenance message" },
      ai_matching: { value: "true", label: "AI Matching Engine", description: "Use ML for compatibility scoring" },
      voice_required: { value: "true", label: "Voice Interview Required", description: "Mandate voice onboarding" },
      premium_only_dates: { value: "false", label: "Premium-Only Date Scheduling", description: "Restrict date features to premium" }
    };
    
    // Try to load settings from server first
    let mockSettings = defaultSettings;
    try {
      const r = await fetch(`${API_BASE}/api/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const serverSettings = await r.json();
        // Merge server settings into defaults (server overrides defaults)
        Object.keys(serverSettings).forEach(k => {
          if (defaultSettings[k]) {
            mockSettings[k] = { ...defaultSettings[k], value: serverSettings[k].value || serverSettings[k] };
          } else {
            mockSettings[k] = { value: serverSettings[k].value || serverSettings[k], label: k, description: "" };
          }
        });
      }
    } catch (e) {
      // Fallback to localStorage
      const savedSettings = localStorage.getItem('admin_settings');
      if (savedSettings) {
        try { mockSettings = JSON.parse(savedSettings); } catch {}
      }
    }
    localStorage.setItem('admin_settings', JSON.stringify(mockSettings));
    
    // Mock analytics data - comprehensive platform statistics
    const mockStats = {
      users: {
        total_users: 12847,
        verified_users: 9234,
        premium_users: 3891,
        active_7d: 7456,
        new_today: 127,
        active_30d: 10234,
        retention_rate: 78,
        avg_session_duration: "18m 34s"
      },
      matches: {
        total_matches: 45623,
        matches_today: 156,
        matches_this_week: 892,
        avg_compatibility: 87,
        acceptance_rate: 73,
        message_rate: 64,
        accepted: 33304,
        pending: 8234,
        avg_score: 87
      },
      dates: {
        confirmed: 2847,
        pending: 423,
        completed: 2156,
        success_rate: 82,
        avg_rating: 4.6,
        dates_this_week: 23,
        cancellations: 342,
        reschedules: 567,
        no_shows: 89
      },
      engagement: {
        daily_active: 3421,
        avg_messages_per_user: 12.4,
        voice_interview_completion: 68,
        profile_completion_avg: 84,
        avg_time_to_match: "2.3 days",
        avg_response_time: "4h 12m"
      },
      revenue: {
        mrr: 38910,
        arr: 466920,
        conversion_rate: 30.3,
        churn_rate: 4.2,
        ltv: 287,
        avg_subscription_length: "8.4 months"
      },
      demographics: {
        gender: { male: 6834, female: 5621, nonbinary: 392 },
        age_ranges: {
          "18-24": 1847,
          "25-29": 3921,
          "30-34": 3456,
          "35-39": 2134,
          "40-44": 987,
          "45+": 502
        },
        top_ages: [28, 27, 29, 26, 30, 31, 25, 32],
        avg_age: 29.4,
        height_ranges: {
          "< 5'4\"": 2341,
          "5'4\" - 5'7\"": 4123,
          "5'8\" - 5'11\"": 3892,
          "6'0\" - 6'3\"": 1987,
          "> 6'3\"": 504
        },
        avg_height_male: "5'10\"",
        avg_height_female: "5'5\""
      },
      locations: {
        top_cities: [
          { city: "San Francisco", count: 2847, pct: 22 },
          { city: "New York", count: 2134, pct: 17 },
          { city: "Los Angeles", count: 1923, pct: 15 },
          { city: "Austin", count: 1456, pct: 11 },
          { city: "Seattle", count: 1234, pct: 10 },
          { city: "Chicago", count: 987, pct: 8 },
          { city: "Boston", count: 834, pct: 6 },
          { city: "Denver", count: 723, pct: 6 },
          { city: "Portland", count: 567, pct: 4 },
          { city: "Miami", count: 142, pct: 1 }
        ]
      },
      languages: {
        primary: [
          { lang: "English", count: 11234, pct: 87 },
          { lang: "Spanish", count: 1456, pct: 11 },
          { lang: "Mandarin", count: 734, pct: 6 },
          { lang: "French", count: 423, pct: 3 }
        ],
        multilingual_users: 3421
      },
      family: {
        has_children: 1847,
        no_children: 10234,
        wants_children: 7892,
        doesnt_want_children: 2341,
        unsure_about_children: 2614
      },
      lifestyle: {
        exercise_frequency: {
          "Daily": 3421,
          "3-5x/week": 4892,
          "1-2x/week": 2847,
          "Rarely": 1234,
          "Never": 453
        },
        diet_preferences: {
          "No restrictions": 6234,
          "Vegetarian": 2341,
          "Vegan": 1456,
          "Pescatarian": 987,
          "Gluten-free": 734,
          "Keto/Low-carb": 623,
          "Other": 472
        },
        drinking_habits: {
          "Socially": 6834,
          "Occasionally": 3421,
          "Rarely": 1892,
          "Never": 567,
          "Regularly": 133
        },
        smoking: {
          "Never": 10234,
          "Socially": 1456,
          "Trying to quit": 734,
          "Regularly": 423
        }
      },
      dating_preferences: {
        date_budget: {
          "$20-50": 4234,
          "$50-100": 5621,
          "$100-200": 2341,
          "$200+": 651
        },
        date_frequency: {
          "1-2x/week": 6234,
          "3-4x/week": 3421,
          "Once/week": 2134,
          "Every other week": 892,
          "Flexible": 166
        },
        first_date_preference: {
          "Coffee/Tea": 5621,
          "Dinner": 4234,
          "Activity (hike, museum)": 2134,
          "Drinks": 734,
          "Lunch": 124
        }
      },
      app_connections: {
        spotify: 8234,
        instagram: 7456,
        strava: 3421,
        goodreads: 2134,
        linkedin: 1847
      },
      interview_completion: {
        stage_1: 12847,
        stage_2: 11234,
        stage_3: 10456,
        stage_4: 9821,
        stage_5: 9234,
        stage_6: 8892,
        stage_7: 8456,
        stage_8: 8123,
        stage_9: 7892,
        stage_10: 7621,
        stage_11: 7234,
        stage_12: 6892,
        stage_13: 6456,
        stage_14: 5834
      },
      top_cuisines: [
        { cuisine: "Italian", count: 7234 },
        { cuisine: "Japanese", count: 6421 },
        { cuisine: "Mexican", count: 5892 },
        { cuisine: "Thai", count: 4234 },
        { cuisine: "Indian", count: 3821 },
        { cuisine: "Mediterranean", count: 3456 },
        { cuisine: "Chinese", count: 2987 },
        { cuisine: "French", count: 2134 }
      ],
      personality_types: {
        mbti: {
          "INFJ": 1456,
          "ENFP": 1342,
          "INTJ": 1234,
          "INFP": 1123,
          "ENFJ": 987,
          "ENTP": 892,
          "INTP": 834,
          "ENTJ": 723,
          "ISFJ": 678,
          "ESFP": 567,
          "ISTJ": 456,
          "ESTP": 423,
          "ISFP": 392,
          "ESFJ": 341,
          "ISTP": 287,
          "ESTJ": 234
        },
        love_languages: {
          "Quality Time": 4234,
          "Words of Affirmation": 3421,
          "Physical Touch": 2847,
          "Acts of Service": 1892,
          "Gifts": 453
        }
      }
    };

    const mockRecentUsers = [
      { id: 1, name: "Sarah Chen", email: "sarah.c@email.com", age: 28, location: "San Francisco, CA", joined: "2024-03-20T10:30:00", status: "active", premium: true, matches: 8, dates: 3, stage: 14, profileComplete: 100 },
      { id: 2, name: "Marcus Rodriguez", email: "m.rodriguez@email.com", age: 32, location: "Austin, TX", joined: "2024-03-20T05:15:00", status: "active", premium: false, matches: 3, dates: 1, stage: 7, profileComplete: 68 },
      { id: 3, name: "Emily Watson", email: "emily.w@email.com", age: 26, location: "Seattle, WA", joined: "2024-03-20T02:45:00", status: "interview", premium: true, matches: 0, dates: 0, stage: 4, profileComplete: 42 },
      { id: 4, name: "David Kim", email: "d.kim@email.com", age: 30, location: "Los Angeles, CA", joined: "2024-03-19T22:20:00", status: "active", premium: false, matches: 5, dates: 2, stage: 11, profileComplete: 85 },
      { id: 5, name: "Jessica Martinez", email: "jess.m@email.com", age: 29, location: "Miami, FL", joined: "2024-03-19T14:00:00", status: "active", premium: true, matches: 12, dates: 5, stage: 14, profileComplete: 100 },
      { id: 6, name: "Ryan Thompson", email: "ryan.t@email.com", age: 27, location: "Denver, CO", joined: "2024-03-19T12:30:00", status: "pending", premium: false, matches: 2, dates: 0, stage: 8, profileComplete: 71 },
      { id: 7, name: "Olivia Park", email: "olivia.p@email.com", age: 31, location: "Portland, OR", joined: "2024-03-18T18:45:00", status: "active", premium: true, matches: 15, dates: 7, stage: 14, profileComplete: 100 },
      { id: 8, name: "James Wilson", email: "j.wilson@email.com", age: 33, location: "Boston, MA", joined: "2024-03-18T16:20:00", status: "active", premium: false, matches: 6, dates: 2, stage: 12, profileComplete: 92 },
      { id: 9, name: "Sophia Lee", email: "sophia.l@email.com", age: 25, location: "Chicago, IL", joined: "2024-03-18T09:10:00", status: "active", premium: true, matches: 9, dates: 4, stage: 14, profileComplete: 100 },
      { id: 10, name: "Michael Brown", email: "m.brown@email.com", age: 34, location: "New York, NY", joined: "2024-03-17T21:00:00", status: "active", premium: false, matches: 4, dates: 1, stage: 10, profileComplete: 78 }
    ];

    const mockRecentDates = [
      { id: 1, user1: "Sarah Chen", user2: "Marcus Rodriguez", venue: "Blue Bottle Coffee", date: "2024-03-22", time: "3:00 PM", status: "confirmed", compatibility: 94 },
      { id: 2, user1: "Emily Watson", user2: "David Kim", venue: "The French Laundry", date: "2024-03-23", time: "7:30 PM", status: "confirmed", compatibility: 91 },
      { id: 3, user1: "Jessica Martinez", user2: "Ryan Thompson", venue: "Golden Gate Park", date: "2024-03-24", time: "2:00 PM", status: "pending", compatibility: 88 },
      { id: 4, user1: "Olivia Park", user2: "James Wilson", venue: "Tartine Bakery", date: "2024-03-25", time: "10:00 AM", status: "confirmed", compatibility: 92 },
      { id: 5, user1: "Alex Chen", user2: "Maya Patel", venue: "Nopa Restaurant", date: "2024-03-21", time: "8:00 PM", status: "completed", compatibility: 89 }
    ];

    setStats(mockStats);
    setSettings(mockSettings);
    setRecentUsers(mockRecentUsers);
    setRecentDates(mockRecentDates);
  };

  const toggleSetting = async (key, currentVal) => {
    const newVal = currentVal === "true" ? "false" : "true";
    setSaving(key);
    
    // Update settings state immediately (optimistic)
    setSettings(prev => {
      const updated = { ...prev, [key]: { ...prev[key], value: newVal } };
      localStorage.setItem('admin_settings', JSON.stringify(updated));
      return updated;
    });
    
    // Update blind mode locally if applicable
    if (key === "blind_match_mode") {
      if (setBlindMode) setBlindMode(newVal === "true");
      const appSettings = JSON.parse(localStorage.getItem('app_settings') || '{}');
      appSettings.blind_match_mode = newVal;
      localStorage.setItem('app_settings', JSON.stringify(appSettings));
    }
    
    // Push setting to server so it applies to ALL users
    try {
      await fetch(`${API_BASE}/api/admin/settings`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ key, value: newVal }),
      });
    } catch (err) {
      console.error("Failed to save setting to server:", err);
    }
    
    setSaving(null);
  };

  const containerPad = bp.isDesktop ? "24px 32px 48px" : bp.isTablet ? "28px 32px 60px" : "12px 20px 100px";
  const maxW = bp.isDesktop ? 1400 : bp.isTablet ? 680 : 480;
  const S = { background: T.bg, minHeight: "100vh", padding: containerPad, maxWidth: maxW, margin: "0 auto" };
  
  // NobleUI-style card component with heavy onload animations
  const Card = ({ children, style, gradient, delay = 0 }) => {
    const [mounted, setMounted] = React.useState(false);
    
    React.useEffect(() => {
      const timer = setTimeout(() => setMounted(true), 50 + delay);
      return () => clearTimeout(timer);
    }, [delay]);
    
    return (
      <div style={{ 
        background: gradient || T.card, 
        border: `1px solid ${T.border}`, 
        borderRadius: 4, 
        padding: bp.isDesktop ? 24 : 18, 
        boxShadow: "0 0 1px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.05)",
        opacity: mounted ? 1 : 0,
        transform: mounted ? "translateY(0) scale(1)" : "translateY(30px) scale(0.95)",
        transition: "all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)",
        ...style 
      }}>{children}</div>
    );
  };
  
  // NobleUI-style stat card with heavy animations
  const StatCard = ({ Icon, label, value, change, color = T.rose, trend, delay = 0 }) => {
    const [mounted, setMounted] = React.useState(false);
    
    React.useEffect(() => {
      const timer = setTimeout(() => setMounted(true), 50 + delay);
      return () => clearTimeout(timer);
    }, [delay]);
    
    return (
      <Card style={{ background: T.card, borderColor: T.border }} delay={delay}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div style={{ 
            width: bp.isDesktop ? 48 : 40, 
            height: bp.isDesktop ? 48 : 40, 
            borderRadius: 4, 
            background: `${color}10`, 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center",
            transform: mounted ? "rotate(0deg) scale(1)" : "rotate(-180deg) scale(0)",
            transition: "all 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55)"
          }}>
            <Icon size={bp.isDesktop ? 24 : 20} color={color} />
          </div>
          {trend && (
            <div style={{ 
              fontSize: bp.isDesktop ? 11 : 12, 
              fontWeight: 600, 
              color: trend > 0 ? "#22c55e" : "#ef4444",
              background: trend > 0 ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
              padding: "4px 10px",
              borderRadius: 4,
              display: "flex",
              alignItems: "center",
              gap: 4,
              opacity: mounted ? 1 : 0,
              transform: mounted ? "translateX(0)" : "translateX(20px)",
              transition: "all 0.5s ease 0.3s"
            }}>
              <span>{trend > 0 ? "↑" : "↓"}</span>
              <span>{Math.abs(trend)}%</span>
            </div>
          )}
        </div>
        <div style={{ 
          fontSize: bp.isDesktop ? 12 : 13, 
          color: T.muted, 
          fontWeight: 500, 
          marginBottom: 8, 
          textTransform: "uppercase", 
          letterSpacing: "0.5px",
          opacity: mounted ? 1 : 0,
          transform: mounted ? "translateY(0)" : "translateY(10px)",
          transition: "all 0.4s ease 0.2s"
        }}>{label}</div>
        <div style={{ 
          fontSize: bp.isDesktop ? 28 : 22, 
          fontWeight: 700, 
          color: T.text, 
          lineHeight: 1, 
          marginBottom: 4,
          opacity: mounted ? 1 : 0,
          transform: mounted ? "translateY(0)" : "translateY(15px)",
          transition: "all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s"
        }}>{value ?? "—"}</div>
        {change && <div style={{ 
          fontSize: bp.isDesktop ? 13 : 14, 
          color: T.muted, 
          marginTop: 6,
          opacity: mounted ? 1 : 0,
          transition: "opacity 0.4s ease 0.5s"
        }}>{change}</div>}
      </Card>
    );
  };
  
  const StatNum = ({ n, label, color = T.rose, suffix = "" }) => (
    <div style={{ textAlign: "center" }}>
      {typeof n === "number" ? (
        <CountUpValue value={n} duration={1400} suffix={suffix} style={{ fontSize: bp.isDesktop ? 36 : 28, fontWeight: 900, color, fontFamily: "'Segoe UI', sans-serif" }} />
      ) : (
        <div style={{ fontSize: bp.isDesktop ? 36 : 28, fontWeight: 900, color, fontFamily: "'Segoe UI', sans-serif" }}>{n ?? "—"}</div>
      )}
      <div style={{ fontSize: bp.isDesktop ? 12 : 12, color: T.muted, marginTop: bp.isDesktop ? 6 : 2, letterSpacing: "0.06em", fontWeight: 600 }}>{label}</div>
    </div>
  );

  // Modern sleek vertical bar chart with animations
  const BarChart = ({ data, color = T.rose, maxHeight = 120 }) => {
    const max = Math.max(...data.map(d => d.value));
    const [mounted, setMounted] = React.useState(false);
    const bcRef = React.useRef(null);
    React.useEffect(() => {
      const el = bcRef.current;
      if (!el) return;
      const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setMounted(true); obs.disconnect(); } }, { threshold: 0.2 });
      obs.observe(el);
      return () => obs.disconnect();
    }, []);
    
    return (
      <div ref={bcRef} style={{ display: "flex", alignItems: "flex-end", gap: bp.isDesktop ? 20 : 14, height: maxHeight, padding: "8px 0" }}>
        {data.map((item, i) => {
          const barHeight = Math.max(8, (item.value / max) * maxHeight);
          const barWidth = bp.isDesktop ? 32 : 24;
          return (
            <div key={i} style={{ 
              flex: 1, 
              display: "flex", 
              flexDirection: "column", 
              alignItems: "center", 
              gap: 10,
              opacity: mounted ? 1 : 0,
              transform: mounted ? "translateY(0)" : "translateY(20px)",
              transition: `all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) ${i * 0.1}s`
            }}>
              <div style={{ 
                fontSize: bp.isDesktop ? 13 : 13, 
                fontWeight: 600, 
                color: T.text,
                opacity: mounted ? 1 : 0,
                transition: `opacity 0.4s ease ${i * 0.1 + 0.3}s`
              }}><CountUpValue value={item.value || 0} duration={1000} suffix="" style={{ display: "inline" }} /></div>
              <div style={{ 
                position: "relative",
                width: `${barWidth}px`,
                height: `${barHeight}px`,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start"
              }}>
                {/* Color accent bar on top */}
                <div style={{
                  width: "100%",
                  height: mounted ? "4px" : "0px",
                  background: color,
                  borderRadius: "2px 2px 0 0",
                  boxShadow: `0 0 8px ${color}40`,
                  transition: `all 0.5s ease ${i * 0.1 + 0.2}s`
                }} />
                {/* Main bar body */}
                <div style={{
                  width: "100%",
                  flex: 1,
                  background: `${color}20`,
                  borderRadius: "0 0 2px 2px",
                  position: "relative",
                  overflow: "hidden"
                }}>
                  {/* Animated fill */}
                  <div style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: mounted ? "100%" : "0%",
                    background: `linear-gradient(180deg, ${color}15, ${color}25)`,
                    transition: `height 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) ${i * 0.1 + 0.1}s`
                  }} />
                </div>
              </div>
              <div style={{ 
                fontSize: bp.isDesktop ? 11 : 12, 
                color: T.muted, 
                fontWeight: 500, 
                textAlign: "center", 
                lineHeight: 1.3,
                opacity: mounted ? 1 : 0,
                transition: `opacity 0.4s ease ${i * 0.1 + 0.4}s`
              }}>{item.label}</div>
            </div>
          );
        })}
      </div>
    );
  };

  const HorizontalBarChart = ({ data, color = T.rose }) => {
    const max = Math.max(...data.map(d => d.value));
    const [visible, setVisible] = React.useState(false);
    const hbRef = React.useRef(null);
    React.useEffect(() => {
      const el = hbRef.current;
      if (!el) return;
      const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.2 });
      obs.observe(el);
      return () => obs.disconnect();
    }, []);
    return (
      <div ref={hbRef} style={{ display: "flex", flexDirection: "column", gap: bp.isDesktop ? 14 : 12 }}>
        {data.map((item, i) => {
          const width = (item.value / max) * 100;
          return (
            <div key={i} style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(10px)", transition: `all 0.5s ease ${i * 0.08}s` }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: bp.isDesktop ? 13 : 14, fontWeight: 500, color: T.text }}>{item.label}</span>
                <CountUpValue value={item.value || 0} duration={1000} suffix="" style={{ fontSize: bp.isDesktop ? 13 : 14, fontWeight: 600, color: T.text }} />
              </div>
              <div style={{ width: "100%", height: bp.isDesktop ? 8 : 6, background: `${color}15`, borderRadius: 4, overflow: "hidden" }}>
                <div style={{ 
                  width: visible ? `${width}%` : "0%", 
                  height: "100%", 
                  background: `linear-gradient(90deg, ${color}, ${color}90)`,
                  transition: `width 1.2s cubic-bezier(0.22, 1, 0.36, 1) ${i * 0.08 + 0.15}s`,
                  borderRadius: 4
                }} />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const PieChart = ({ data, size = 160 }) => {
    const total = data.reduce((sum, d) => sum + d.value, 0);
    let currentAngle = -90;
    const colors = [T.rose, T.teal, T.violet, T.amber, "#22c55e", "#3b82f6", "#ec4899", "#f59e0b"];
    const [visible, setVisible] = React.useState(false);
    const pieRef = React.useRef(null);
    React.useEffect(() => {
      const el = pieRef.current;
      if (!el) return;
      const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.3 });
      obs.observe(el);
      return () => obs.disconnect();
    }, []);
    
    return (
      <div ref={pieRef} style={{ display: "flex", gap: bp.isDesktop ? 24 : 16, alignItems: "center", flexWrap: "wrap" }}>
        <svg width={size} height={size} viewBox="0 0 100 100" style={{ 
          transform: visible ? "rotate(0deg) scale(1)" : "rotate(-90deg) scale(0.7)", 
          opacity: visible ? 1 : 0,
          transition: "all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)"
        }}>
          {data.map((item, i) => {
            const percentage = (item.value / total) * 100;
            const angle = (percentage / 100) * 360;
            const startAngle = currentAngle;
            currentAngle += angle;
            
            const x1 = 50 + 40 * Math.cos((startAngle * Math.PI) / 180);
            const y1 = 50 + 40 * Math.sin((startAngle * Math.PI) / 180);
            const x2 = 50 + 40 * Math.cos((currentAngle * Math.PI) / 180);
            const y2 = 50 + 40 * Math.sin((currentAngle * Math.PI) / 180);
            const largeArc = angle > 180 ? 1 : 0;
            
            return (
              <path
                key={i}
                d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`}
                fill={colors[i % colors.length]}
                opacity={visible ? 0.9 : 0}
                style={{ transition: `opacity 0.5s ease ${i * 0.15 + 0.3}s` }}
              />
            );
          })}
          <circle cx="50" cy="50" r="25" fill={T.card} />
        </svg>
        <div style={{ flex: 1, minWidth: 120 }}>
          {data.map((item, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, opacity: visible ? 1 : 0, transform: visible ? "translateX(0)" : "translateX(12px)", transition: `all 0.4s ease ${i * 0.1 + 0.4}s` }}>
              <div style={{ width: 12, height: 12, borderRadius: 3, background: colors[i % colors.length] }} />
              <div style={{ flex: 1, fontSize: bp.isDesktop ? 11 : 12, color: T.text, fontWeight: 600 }}>{item.label}</div>
              <CountUpValue value={item.value || 0} duration={1000} suffix="" style={{ fontSize: bp.isDesktop ? 12 : 13, fontWeight: 700, color: T.text }} />
              <div style={{ fontSize: bp.isDesktop ? 10 : 11, color: T.muted }}>({((item.value / total) * 100).toFixed(1)}%)</div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  const Toggle = ({ settingKey, label, description }) => {
    const val = settings[settingKey]?.value === "true";
    return (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "14px 0", borderBottom: `1px solid ${T.border}` }}>
        <div style={{ flex: 1, marginRight: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{label}</div>
          <div style={{ fontSize: 13, color: T.muted, marginTop: 3, lineHeight: 1.5 }}>{description || settings[settingKey]?.description}</div>
        </div>
        <button onClick={() => toggleSetting(settingKey, settings[settingKey]?.value)}
          style={{ flexShrink: 0, width: 48, height: 26, borderRadius: 13, border: "none", cursor: saving === settingKey ? "not-allowed" : "pointer",
            background: val ? T.rose : T.dim, position: "relative", transition: "background 0.3s" }}>
          <span style={{ position: "absolute", top: 3, left: val ? 25 : 3, width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "left 0.3s" }} />
        </button>
      </div>
    );
  };

  if (!adminToken) {
    return (
      <div style={{ ...S, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
        <div style={{ fontSize: bp.isDesktop ? 64 : 48, marginBottom: 8 }}>🔐</div>
        <h2 style={{ fontSize: bp.isDesktop ? 32 : 24, fontWeight: 900, color: T.text, fontFamily: "'Segoe UI', sans-serif" }}>Admin Login</h2>
        <p style={{ fontSize: bp.isDesktop ? 15 : 15, color: T.muted }}>Superadmin access only</p>
        <div style={{ width: "100%", maxWidth: bp.isDesktop ? 420 : 340, display: "flex", flexDirection: "column", gap: 10 }}>
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Admin email"
            style={{ padding: bp.isDesktop ? "14px 16px" : "12px 14px", borderRadius: bp.isDesktop ? 12 : 10, background: T.card, border: `1px solid ${T.border}`, color: T.text, fontSize: bp.isDesktop ? 15 : 15, fontFamily: "inherit" }} />
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password"
            onKeyDown={e => e.key === "Enter" && handleLogin()}
            style={{ padding: bp.isDesktop ? "14px 16px" : "12px 14px", borderRadius: bp.isDesktop ? 12 : 10, background: T.card, border: `1px solid ${T.border}`, color: T.text, fontSize: bp.isDesktop ? 15 : 15, fontFamily: "inherit" }} />
          {loginError && <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.rose }}>{loginError}</div>}
          <button onClick={handleLogin} style={{ padding: bp.isDesktop ? "15px" : "13px", borderRadius: bp.isDesktop ? 12 : 10, background: `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, color: "#fff", fontSize: bp.isDesktop ? 15 : 15, fontWeight: 700, border: "none", cursor: "pointer", fontFamily: "inherit" }}>
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={S}>
      {/* Header */}
      <div style={{ position: "relative", top: 0, zIndex: 50, background: T.bg, paddingTop: bp.isDesktop ? 4 : 0, paddingBottom: bp.isDesktop ? 6 : 10, overflow: "visible" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <p style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.rose, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 4, marginTop: 0 }}>PLATFORM ANALYTICS</p>
            <div style={{ fontFamily: "'MBF Canno', 'Segoe UI', sans-serif", textTransform: "lowercase", fontSize: bp.isDesktop ? 72 : 48, fontWeight: 900, background: `linear-gradient(90deg, ${T.rose}, ${T.violet}, ${T.rose})`, backgroundSize: "300% 100%", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "aurora 6s ease-in-out infinite", filter: `drop-shadow(0 0 4px ${T.rose}30)`, lineHeight: 1.1, padding: "0 0 6px 0" }}>settup</div>
          </div>
          {stats && (
            <div style={{ 
              padding: bp.isDesktop ? "12px 20px" : "10px 16px", 
              background: T.card, 
              border: `1px solid ${T.border}`, 
              borderRadius: 4,
              boxShadow: "0 0 1px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.05)",
              flexShrink: 0
            }}>
              <div style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.muted, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.5px" }}>Total Users</div>
              <CountUpValue value={stats.users?.total_users || 0} duration={1400} suffix="" style={{ fontSize: bp.isDesktop ? 24 : 20, fontWeight: 700, color: T.text }} />
            </div>
          )}
        </div>
      </div>
      <div style={{
        opacity: headerMounted ? 1 : 0,
        transform: headerMounted ? "translateY(0)" : "translateY(-12px)",
        transition: "all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)",
        marginBottom: bp.isDesktop ? 28 : 16
      }}>
        <p style={{ fontSize: bp.isDesktop ? 14 : 15, color: T.muted, margin: 0 }}>Welcome back, Admin</p>
      </div>

      {/* NobleUI-style Tab Navigation with animations */}
      <div style={{ 
        display: "flex", 
        gap: bp.isDesktop ? 4 : 2, 
        marginBottom: bp.isDesktop ? 28 : 20, 
        background: "transparent",
        padding: 0,
        opacity: headerMounted ? 1 : 0,
        transform: headerMounted ? "translateY(0)" : "translateY(-20px)",
        transition: "all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s",
        borderBottom: `1px solid ${T.border}`
      }}>
        {[
          { key: "overview", Icon: AnimChartIcon, label: "Overview" },
          { key: "demographics", Icon: AnimUsersIcon, label: "Demographics" },
          { key: "behavior", Icon: AnimSparklesIcon, label: "Behavior" },
          { key: "users", Icon: AnimUserIcon, label: "Users" },
          { key: "dates", Icon: AnimCoupleIcon, label: "Dates" },
          { key: "settings", Icon: AnimSettingsIcon, label: "Settings" }
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ 
              flex: 1,
              padding: bp.isDesktop ? "14px 20px" : "12px 8px", 
              background: "transparent", 
              border: "none", 
              borderBottom: tab === t.key ? `2px solid ${T.rose}` : "2px solid transparent",
              borderRadius: 0,
              color: tab === t.key ? T.rose : T.muted, 
              fontSize: bp.isDesktop ? 14 : 14, 
              fontWeight: tab === t.key ? 600 : 500, 
              cursor: "pointer", 
              fontFamily: "inherit", 
              letterSpacing: "0",
              transition: "all 0.2s ease",
              display: "flex",
              flexDirection: bp.isDesktop ? "row" : "column",
              alignItems: "center",
              justifyContent: "center",
              gap: bp.isDesktop ? 8 : 2,
              position: "relative",
              marginBottom: -1
            }}
            onMouseEnter={e => { if (tab !== t.key) e.currentTarget.style.color = T.text; }}
            onMouseLeave={e => { if (tab !== t.key) e.currentTarget.style.color = T.muted; }}>
            <t.Icon size={bp.isDesktop ? 22 : 20} color={tab === t.key ? T.rose : T.muted} />
            {bp.isDesktop && <span>{t.label}</span>}
          </button>
        ))}
      </div>

      {/* Overview tab - Modern Dashboard */}
      {tab === "overview" && stats && (
        <div style={{ display: "flex", flexDirection: "column", gap: bp.isDesktop ? 24 : 16 }}>
          {/* Key Metrics Grid */}
          <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "repeat(4, 1fr)" : "repeat(2, 1fr)", gap: bp.isDesktop ? 20 : 12 }}>
            <StatCard 
              Icon={UsersIcon} 
              label="TOTAL USERS" 
              value={stats.users?.total_users} 
              change="+12% this month"
              trend={12}
              color={T.rose}
              delay={0}
            />
            <StatCard 
              Icon={SparklesIcon} 
              label="PREMIUM MEMBERS" 
              value={stats.users?.premium_users} 
              change="+8% conversion"
              trend={8}
              color={T.rose}
              delay={100}
            />
            <StatCard 
              Icon={CoupleIcon} 
              label="TOTAL MATCHES" 
              value={stats.matches?.total_matches} 
              change="+156 today"
              trend={15}
              color={T.rose}
              delay={200}
            />
            <StatCard 
              Icon={CalendarIcon} 
              label="CONFIRMED DATES" 
              value={stats.dates?.confirmed} 
              change="+23 this week"
              trend={18}
              color={T.rose}
              delay={300}
            />
          </div>

          {/* User Analytics */}
          <Card delay={400}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: bp.isDesktop ? 20 : 16 }}>
              <div>
                <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 600, color: T.text, marginBottom: 4 }}>Platform Growth</div>
                <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted }}>User analytics overview</div>
              </div>
              <button onClick={() => loadDashboard(adminToken)} 
                style={{ 
                  padding: bp.isDesktop ? "8px 16px" : "7px 12px", 
                  borderRadius: 4, 
                  background: T.card, 
                  border: `1px solid ${T.border}`, 
                  color: T.text, 
                  fontSize: bp.isDesktop ? 13 : 14, 
                  fontWeight: 500,
                  cursor: "pointer", 
                  fontFamily: "inherit",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  transition: "all 0.2s ease"
                }}
                onMouseEnter={e => { e.currentTarget.style.background = T.bg; }}
                onMouseLeave={e => { e.currentTarget.style.background = T.card; }}>
                <span>↻</span> Refresh
              </button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "repeat(5, 1fr)" : "repeat(2, 1fr)", gap: bp.isDesktop ? 16 : 12 }}>
              <div style={{ textAlign: "center", padding: bp.isDesktop ? 20 : 16, background: T.bg, borderRadius: 4, border: `1px solid ${T.border}` }}>
                <CountUpValue value={stats.users?.total_users || 0} duration={1400} suffix="" style={{ fontSize: bp.isDesktop ? 24 : 20, fontWeight: 700, color: T.text }} />
                <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, marginTop: 6, fontWeight: 500 }}>Total Users</div>
              </div>
              <div style={{ textAlign: "center", padding: bp.isDesktop ? 20 : 16, background: T.bg, borderRadius: 4, border: `1px solid ${T.border}` }}>
                <CountUpValue value={stats.users?.verified_users || 0} duration={1400} suffix="" style={{ fontSize: bp.isDesktop ? 24 : 20, fontWeight: 700, color: T.text }} />
                <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, marginTop: 6, fontWeight: 500 }}>Verified</div>
              </div>
              <div style={{ textAlign: "center", padding: bp.isDesktop ? 20 : 16, background: T.bg, borderRadius: 4, border: `1px solid ${T.border}` }}>
                <CountUpValue value={stats.users?.premium_users || 0} duration={1400} suffix="" style={{ fontSize: bp.isDesktop ? 24 : 20, fontWeight: 700, color: T.text }} />
                <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, marginTop: 6, fontWeight: 500 }}>Premium</div>
              </div>
              <div style={{ textAlign: "center", padding: bp.isDesktop ? 20 : 16, background: T.bg, borderRadius: 4, border: `1px solid ${T.border}` }}>
                <CountUpValue value={stats.users?.active_7d || 0} duration={1400} suffix="" style={{ fontSize: bp.isDesktop ? 24 : 20, fontWeight: 700, color: T.text }} />
                <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, marginTop: 6, fontWeight: 500 }}>Active 7d</div>
              </div>
              <div style={{ textAlign: "center", padding: bp.isDesktop ? 20 : 16, background: `${T.rose}10`, borderRadius: 4, border: `1px solid ${T.rose}30` }}>
                <CountUpValue value={stats.users?.new_today || 0} duration={1400} suffix="" style={{ fontSize: bp.isDesktop ? 24 : 20, fontWeight: 700, color: T.rose }} />
                <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.rose, marginTop: 6, fontWeight: 600 }}>New Today</div>
              </div>
            </div>
          </Card>

          {/* Match Performance */}
          <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "2fr 1fr" : "1fr", gap: bp.isDesktop ? 20 : 16 }}>
            <Card delay={500}>
              <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 600, color: T.text, marginBottom: 4 }}>Compatibility Engine</div>
              <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, marginBottom: bp.isDesktop ? 20 : 16 }}>Match performance metrics</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: bp.isDesktop ? 20 : 16 }}>
                <div style={{ padding: bp.isDesktop ? 16 : 12, background: T.bg, borderRadius: 4, border: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, marginBottom: 8, fontWeight: 500 }}>Total Matches</div>
                  <CountUpValue value={stats.matches?.total_matches || 0} duration={1400} suffix="" style={{ fontSize: bp.isDesktop ? 28 : 22, fontWeight: 700, color: T.text }} />
                </div>
                <div style={{ padding: bp.isDesktop ? 16 : 12, background: T.bg, borderRadius: 4, border: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, marginBottom: 8, fontWeight: 500 }}>Acceptance Rate</div>
                  <CountUpValue value={stats.matches?.total_matches ? Math.round((stats.matches.accepted / stats.matches.total_matches) * 100) : 0} duration={1400} suffix="%" style={{ fontSize: bp.isDesktop ? 28 : 22, fontWeight: 700, color: "#22c55e" }} />
                </div>
                <div style={{ padding: bp.isDesktop ? 16 : 12, background: T.bg, borderRadius: 4, border: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, marginBottom: 8, fontWeight: 500 }}>Pending Review</div>
                  <CountUpValue value={stats.matches?.pending || 0} duration={1400} suffix="" style={{ fontSize: bp.isDesktop ? 28 : 22, fontWeight: 700, color: T.text }} />
                </div>
                <div style={{ padding: bp.isDesktop ? 16 : 12, background: T.bg, borderRadius: 4, border: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, marginBottom: 8, fontWeight: 500 }}>Avg Compatibility</div>
                  <CountUpValue value={stats.matches?.avg_score ? Math.round(stats.matches.avg_score) : 0} duration={1400} suffix="%" style={{ fontSize: bp.isDesktop ? 28 : 22, fontWeight: 700, color: T.text }} />
                </div>
              </div>
            </Card>

            <Card style={{ background: T.card, borderColor: T.border }} delay={600}>
              <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 600, color: T.text, marginBottom: 4 }}>Date Conversion</div>
              <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, marginBottom: 16 }}>Success rate metrics</div>
              <div style={{ textAlign: "center", padding: bp.isDesktop ? 24 : 18, background: `${T.rose}08`, borderRadius: 4, border: `1px solid ${T.rose}20` }}>
                <CountUpValue value={stats.dates?.total_dates && stats.matches?.accepted ? Math.round((stats.dates.confirmed / stats.matches.accepted) * 100) : 0} duration={1600} suffix="%" style={{ fontSize: bp.isDesktop ? 48 : 36, fontWeight: 700, color: T.rose, lineHeight: 1 }} />
                <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, marginTop: 12, fontWeight: 500 }}>Matches → Dates</div>
              </div>
            </Card>
          </div>

          {/* Date Statistics */}
          <Card delay={700}>
            <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 600, color: T.text, marginBottom: 4 }}>Scheduled Dates Overview</div>
            <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, marginBottom: bp.isDesktop ? 20 : 16 }}>Date statistics and metrics</div>
            <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "repeat(5, 1fr)" : "repeat(2, 1fr)", gap: bp.isDesktop ? 16 : 12 }}>
              <div style={{ padding: bp.isDesktop ? 20 : 16, background: T.bg, borderRadius: 4, border: `1px solid ${T.border}`, textAlign: "center" }}>
                <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, fontWeight: 500, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>Total</div>
                <CountUpValue value={stats.dates?.total_dates || 0} duration={1400} suffix="" style={{ fontSize: bp.isDesktop ? 28 : 22, fontWeight: 700, color: T.text }} />
              </div>
              <div style={{ padding: bp.isDesktop ? 20 : 16, background: "rgba(34,197,94,0.08)", borderRadius: 4, border: "1px solid rgba(34,197,94,0.2)", textAlign: "center" }}>
                <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: "#22c55e", fontWeight: 500, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>Confirmed</div>
                <CountUpValue value={stats.dates?.confirmed || 0} duration={1400} suffix="" style={{ fontSize: bp.isDesktop ? 28 : 22, fontWeight: 700, color: "#22c55e" }} />
              </div>
              <div style={{ padding: bp.isDesktop ? 20 : 16, background: T.bg, borderRadius: 4, border: `1px solid ${T.border}`, textAlign: "center" }}>
                <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, fontWeight: 500, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>Completed</div>
                <CountUpValue value={stats.dates?.completed || 0} duration={1400} suffix="" style={{ fontSize: bp.isDesktop ? 28 : 22, fontWeight: 700, color: T.text }} />
              </div>
              <div style={{ padding: bp.isDesktop ? 20 : 16, background: T.bg, borderRadius: 4, border: `1px solid ${T.border}`, textAlign: "center" }}>
                <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, fontWeight: 500, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>Pending</div>
                <CountUpValue value={stats.dates?.pending || 0} duration={1400} suffix="" style={{ fontSize: bp.isDesktop ? 28 : 22, fontWeight: 700, color: T.text }} />
              </div>
              <div style={{ padding: bp.isDesktop ? 20 : 16, background: T.bg, borderRadius: 4, border: `1px solid ${T.border}`, textAlign: "center" }}>
                <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, fontWeight: 500, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>Cancelled</div>
                <CountUpValue value={stats.dates?.cancelled || 0} duration={1400} suffix="" style={{ fontSize: bp.isDesktop ? 28 : 22, fontWeight: 700, color: T.text }} />
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Demographics tab */}
      {tab === "demographics" && stats && (
        <div style={{ display: "flex", flexDirection: "column", gap: bp.isDesktop ? 24 : 16 }}>
          {/* Gender & Age Overview */}
          <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "1fr 1fr" : "1fr", gap: bp.isDesktop ? 20 : 16 }}>
            <Card delay={0}>
              <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 600, color: T.text, marginBottom: 4 }}>User Demographics</div>
              <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, marginBottom: 20 }}>Gender distribution</div>
              <PieChart data={[
                { label: "Male", value: stats.demographics.gender.male },
                { label: "Female", value: stats.demographics.gender.female },
                { label: "Non-binary", value: stats.demographics.gender.nonbinary }
              ]} size={bp.isDesktop ? 180 : 140} />
            </Card>
            
            <Card delay={100}>
              <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 600, color: T.text, marginBottom: 4 }}>Age Ranges</div>
              <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, marginBottom: 20 }}>Age distribution</div>
              <BarChart 
                data={Object.entries(stats.demographics.age_ranges).map(([label, value]) => ({ label, value }))}
                color={T.rose}
                maxHeight={bp.isDesktop ? 140 : 100}
              />
              <div style={{ marginTop: 16, padding: "12px 16px", background: T.bg, borderRadius: 4, border: `1px solid ${T.border}`, textAlign: "center" }}>
                <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, marginBottom: 4, fontWeight: 500 }}>Average Age</div>
                <CountUpValue value={stats.demographics.avg_age || 0} duration={1200} suffix="" style={{ fontSize: bp.isDesktop ? 24 : 20, fontWeight: 700, color: T.text }} />
              </div>
            </Card>
          </div>

          {/* Height Distribution */}
          <Card delay={200}>
            <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 600, color: T.text, marginBottom: 4 }}>User Heights</div>
            <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, marginBottom: 20 }}>Height distribution</div>
            <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "2fr 1fr" : "1fr", gap: 20 }}>
              <HorizontalBarChart 
                data={Object.entries(stats.demographics.height_ranges).map(([label, value]) => ({ label, value }))}
                color={T.rose}
              />
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ padding: "12px 16px", background: T.bg, borderRadius: 4, border: `1px solid ${T.border}`, textAlign: "center" }}>
                  <div style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.muted, marginBottom: 4, fontWeight: 500 }}>Avg Male Height</div>
                  <div style={{ fontSize: bp.isDesktop ? 20 : 18, fontWeight: 700, color: T.text }}>{stats.demographics.avg_height_male}</div>
                </div>
                <div style={{ padding: "12px 16px", background: T.bg, borderRadius: 4, border: `1px solid ${T.border}`, textAlign: "center" }}>
                  <div style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.muted, marginBottom: 4, fontWeight: 500 }}>Avg Female Height</div>
                  <div style={{ fontSize: bp.isDesktop ? 20 : 18, fontWeight: 700, color: T.text }}>{stats.demographics.avg_height_female}</div>
                </div>
              </div>
            </div>
          </Card>

          {/* Top Locations */}
          <Card delay={300}>
            <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 600, color: T.text, marginBottom: 4 }}>User Cities</div>
            <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, marginBottom: 20 }}>Top locations</div>
            <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "repeat(2, 1fr)" : "1fr", gap: 12 }}>
              {stats.locations.top_cities.map((city, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: T.bg, borderRadius: 4, border: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: bp.isDesktop ? 16 : 15, fontWeight: 700, color: i < 3 ? T.rose : T.muted, minWidth: 28 }}>#{i + 1}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: bp.isDesktop ? 14 : 15, fontWeight: 600, color: T.text }}>{city.city}</div>
                    <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, display: "flex", alignItems: "center", gap: 4 }}><CountUpValue value={city.count || 0} duration={1000} suffix="" style={{ display: "inline" }} /> users · {city.pct}%</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Languages & Family Status */}
          <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "1fr 1fr" : "1fr", gap: bp.isDesktop ? 20 : 16 }}>
            <Card delay={400}>
              <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 600, color: T.text, marginBottom: 4 }}>Primary Languages</div>
              <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, marginBottom: 20 }}>Language distribution</div>
              <HorizontalBarChart 
                data={stats.languages.primary.map(l => ({ label: l.lang, value: l.count }))}
                color={T.rose}
              />
              <div style={{ marginTop: 16, padding: "12px 16px", background: T.bg, borderRadius: 4, border: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, fontWeight: 500 }}>Multilingual Users</span>
                <CountUpValue value={stats.languages.multilingual_users || 0} duration={1200} suffix="" style={{ fontSize: bp.isDesktop ? 16 : 15, fontWeight: 700, color: T.text }} />
              </div>
            </Card>

            <Card delay={500}>
              <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 600, color: T.text, marginBottom: 4 }}>Children Preferences</div>
              <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, marginBottom: 20 }}>Family status</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", background: T.bg, borderRadius: 4, border: `1px solid ${T.border}` }}>
                  <span style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.text, fontWeight: 500 }}>Has Children</span>
                  <CountUpValue value={stats.family.has_children || 0} duration={1200} suffix="" style={{ fontSize: bp.isDesktop ? 14 : 15, fontWeight: 700, color: T.text }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", background: T.bg, borderRadius: 4, border: `1px solid ${T.border}` }}>
                  <span style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.text, fontWeight: 500 }}>No Children</span>
                  <CountUpValue value={stats.family.no_children || 0} duration={1200} suffix="" style={{ fontSize: bp.isDesktop ? 14 : 15, fontWeight: 700, color: T.text }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", background: `${T.rose}10`, borderRadius: 4, border: `1px solid ${T.rose}30` }}>
                  <span style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.text, fontWeight: 500 }}>Wants Children</span>
                  <CountUpValue value={stats.family.wants_children || 0} duration={1200} suffix="" style={{ fontSize: bp.isDesktop ? 14 : 15, fontWeight: 700, color: T.rose }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", background: T.bg, borderRadius: 4, border: `1px solid ${T.border}` }}>
                  <span style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.text, fontWeight: 500 }}>Doesn't Want</span>
                  <CountUpValue value={stats.family.doesnt_want_children || 0} duration={1200} suffix="" style={{ fontSize: bp.isDesktop ? 14 : 15, fontWeight: 700, color: T.text }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", background: T.bg, borderRadius: 4, border: `1px solid ${T.border}` }}>
                  <span style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.text, fontWeight: 500 }}>Unsure</span>
                  <CountUpValue value={stats.family.unsure_about_children || 0} duration={1200} suffix="" style={{ fontSize: bp.isDesktop ? 14 : 15, fontWeight: 700, color: T.text }} />
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Behavior tab */}
      {tab === "behavior" && stats && (
        <div style={{ display: "flex", flexDirection: "column", gap: bp.isDesktop ? 24 : 16 }}>
          {/* Lifestyle Habits */}
          <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "repeat(2, 1fr)" : "1fr", gap: bp.isDesktop ? 20 : 16 }}>
            <Card delay={0}>
              <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 600, color: T.text, marginBottom: 4 }}>Fitness Habits</div>
              <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, marginBottom: 20 }}>Exercise frequency</div>
              <PieChart data={Object.entries(stats.lifestyle.exercise_frequency).map(([label, value]) => ({ label, value }))} size={bp.isDesktop ? 160 : 120} />
            </Card>

            <Card delay={100}>
              <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 600, color: T.text, marginBottom: 4 }}>Dietary Restrictions</div>
              <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, marginBottom: 20 }}>Diet preferences</div>
              <HorizontalBarChart 
                data={Object.entries(stats.lifestyle.diet_preferences).map(([label, value]) => ({ label, value }))}
                color={T.rose}
              />
            </Card>
          </div>

          {/* Drinking & Smoking */}
          <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "1fr 1fr" : "1fr", gap: bp.isDesktop ? 20 : 16 }}>
            <Card delay={200}>
              <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 600, color: T.text, marginBottom: 4 }}>Alcohol Consumption</div>
              <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, marginBottom: 20 }}>Drinking habits</div>
              <BarChart 
                data={Object.entries(stats.lifestyle.drinking_habits).map(([label, value]) => ({ label, value }))}
                color={T.rose}
                maxHeight={bp.isDesktop ? 120 : 90}
              />
            </Card>

            <Card delay={300}>
              <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 600, color: T.text, marginBottom: 4 }}>Tobacco Use</div>
              <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, marginBottom: 20 }}>Smoking status</div>
              <BarChart 
                data={Object.entries(stats.lifestyle.smoking).map(([label, value]) => ({ label, value }))}
                color={T.rose}
                maxHeight={bp.isDesktop ? 120 : 90}
              />
            </Card>
          </div>

          {/* Dating Preferences */}
          <Card delay={400}>
            <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 600, color: T.text, marginBottom: 4 }}>Date Budgets & Frequency</div>
            <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, marginBottom: 20 }}>Dating preferences</div>
            <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "repeat(3, 1fr)" : "1fr", gap: 20 }}>
              <div>
                <div style={{ fontSize: bp.isDesktop ? 12 : 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>Date Budget</div>
                <HorizontalBarChart 
                  data={Object.entries(stats.dating_preferences.date_budget).map(([label, value]) => ({ label, value }))}
                  color={T.rose}
                />
              </div>
              <div>
                <div style={{ fontSize: bp.isDesktop ? 12 : 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>Date Frequency</div>
                <HorizontalBarChart 
                  data={Object.entries(stats.dating_preferences.date_frequency).map(([label, value]) => ({ label, value }))}
                  color={T.rose}
                />
              </div>
              <div>
                <div style={{ fontSize: bp.isDesktop ? 12 : 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>First Date Type</div>
                <HorizontalBarChart 
                  data={Object.entries(stats.dating_preferences.first_date_preference).map(([label, value]) => ({ label, value }))}
                  color={T.rose}
                />
              </div>
            </div>
          </Card>

          {/* Top Cuisines */}
          <Card delay={500}>
            <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 600, color: T.text, marginBottom: 4 }}>Most Popular Food Preferences</div>
            <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, marginBottom: 20 }}>Favorite cuisines</div>
            <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "repeat(4, 1fr)" : "repeat(2, 1fr)", gap: 12 }}>
              {stats.top_cuisines.map((c, i) => (
                <div key={i} style={{ padding: "14px 16px", background: T.bg, borderRadius: 4, border: `1px solid ${T.border}`, textAlign: "center" }}>
                  <div style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.muted, marginBottom: 6, fontWeight: 500 }}>#{i + 1}</div>
                  <div style={{ fontSize: bp.isDesktop ? 14 : 15, fontWeight: 600, color: T.text }}>{c.cuisine}</div>
                  <CountUpValue value={c.count || 0} duration={1200} suffix="" style={{ fontSize: bp.isDesktop ? 16 : 15, fontWeight: 700, color: T.text, marginTop: 6 }} />
                </div>
              ))}
            </div>
          </Card>

          {/* Personality Types */}
          <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "2fr 1fr" : "1fr", gap: bp.isDesktop ? 20 : 16 }}>
            <Card delay={600}>
              <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 600, color: T.text, marginBottom: 4 }}>Personality Types</div>
              <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, marginBottom: 20 }}>MBTI distribution</div>
              <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "repeat(4, 1fr)" : "repeat(2, 1fr)", gap: 8 }}>
                {Object.entries(stats.personality_types.mbti).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([type, count], i) => (
                  <div key={i} style={{ padding: "12px 14px", background: T.bg, borderRadius: 4, border: `1px solid ${T.border}`, textAlign: "center" }}>
                    <div style={{ fontSize: bp.isDesktop ? 16 : 15, fontWeight: 700, color: T.text }}>{type}</div>
                    <CountUpValue value={count || 0} duration={1200} suffix="" style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, marginTop: 4, fontWeight: 500 }} />
                  </div>
                ))}
              </div>
            </Card>

            <Card delay={700}>
              <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 600, color: T.text, marginBottom: 4 }}>Top 5 Love Languages</div>
              <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, marginBottom: 20 }}>Most common preferences</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {Object.entries(stats.personality_types.love_languages).map(([lang, count], i) => (
                  <div key={i} style={{ padding: "12px 14px", background: T.bg, borderRadius: 4, border: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.muted, marginBottom: 4, fontWeight: 500 }}>#{i + 1}</div>
                    <div style={{ fontSize: bp.isDesktop ? 13 : 14, fontWeight: 600, color: T.text }}>{lang}</div>
                    <CountUpValue value={count || 0} duration={1200} suffix="" style={{ fontSize: bp.isDesktop ? 16 : 15, fontWeight: 700, color: T.text, marginTop: 6 }} />
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Interview Completion Funnel */}
          <Card delay={800}>
            <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 600, color: T.text, marginBottom: 4 }}>Stage-by-Stage Drop-off</div>
            <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, marginBottom: 20 }}>Interview completion</div>
            <BarChart 
              data={Object.entries(stats.interview_completion).map(([stage, count]) => ({ 
                label: stage.replace("stage_", "S"), 
                value: count 
              }))}
              color={T.rose}
              maxHeight={bp.isDesktop ? 160 : 120}
            />
            <div style={{ marginTop: 16, padding: "16px 20px", background: `${T.rose}10`, borderRadius: 4, border: `1px solid ${T.rose}30`, textAlign: "center" }}>
              <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, marginBottom: 6, fontWeight: 500 }}>Overall Completion Rate (Stage 14)</div>
              <CountUpValue value={Math.round((stats.interview_completion.stage_14 / stats.interview_completion.stage_1) * 1000) / 10 || 0} duration={1600} suffix="%" style={{ fontSize: bp.isDesktop ? 28 : 22, fontWeight: 700, color: T.rose }} />
            </div>
          </Card>

          {/* App Connections */}
          <Card delay={900}>
            <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 600, color: T.text, marginBottom: 4 }}>Integrated Services</div>
            <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, marginBottom: 20 }}>App connections</div>
            <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "repeat(5, 1fr)" : "repeat(2, 1fr)", gap: 12 }}>
              {Object.entries(stats.app_connections).map(([app, count], i) => (
                <div key={i} style={{ padding: "16px 14px", background: T.bg, borderRadius: 4, textAlign: "center", border: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: bp.isDesktop ? 14 : 15, fontWeight: 600, color: T.text, textTransform: "capitalize", marginBottom: 8 }}>{app}</div>
                  <CountUpValue value={count || 0} duration={1400} suffix="" style={{ fontSize: bp.isDesktop ? 22 : 18, fontWeight: 700, color: T.text }} />
                  <CountUpValue value={Math.round((count / stats.users.total_users) * 1000) / 10 || 0} duration={1400} suffix="%" style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.muted, marginTop: 4, fontWeight: 500 }} />
                </div>
              ))}
            </div>
          </Card>

          {/* Date Behavior */}
          <Card delay={1000}>
            <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 600, color: T.text, marginBottom: 4 }}>Cancellations & Reschedules</div>
            <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, marginBottom: 20 }}>Date behavior</div>
            <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "repeat(3, 1fr)" : "1fr", gap: 16 }}>
              <div style={{ padding: "20px 16px", background: T.bg, borderRadius: 4, border: `1px solid ${T.border}`, textAlign: "center" }}>
                <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, marginBottom: 8, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>Cancellations</div>
                <CountUpValue value={stats.dates.cancellations || 0} duration={1400} suffix="" style={{ fontSize: bp.isDesktop ? 28 : 22, fontWeight: 700, color: T.text }} />
                <CountUpValue value={Math.round((stats.dates.cancellations / stats.dates.completed) * 1000) / 10 || 0} duration={1400} suffix="% rate" style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.muted, marginTop: 6, fontWeight: 500 }} />
              </div>
              <div style={{ padding: "20px 16px", background: T.bg, borderRadius: 4, border: `1px solid ${T.border}`, textAlign: "center" }}>
                <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, marginBottom: 8, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>Reschedules</div>
                <CountUpValue value={stats.dates.reschedules || 0} duration={1400} suffix="" style={{ fontSize: bp.isDesktop ? 28 : 22, fontWeight: 700, color: T.text }} />
                <CountUpValue value={Math.round((stats.dates.reschedules / stats.dates.completed) * 1000) / 10 || 0} duration={1400} suffix="% rate" style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.muted, marginTop: 6, fontWeight: 500 }} />
              </div>
              <div style={{ padding: "20px 16px", background: T.bg, borderRadius: 4, border: `1px solid ${T.border}`, textAlign: "center" }}>
                <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, marginBottom: 8, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>No-Shows</div>
                <CountUpValue value={stats.dates.no_shows || 0} duration={1400} suffix="" style={{ fontSize: bp.isDesktop ? 28 : 22, fontWeight: 700, color: T.text }} />
                <CountUpValue value={Math.round((stats.dates.no_shows / stats.dates.completed) * 1000) / 10 || 0} duration={1400} suffix="% rate" style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.muted, marginTop: 6, fontWeight: 500 }} />
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Users tab - Reorganized with Analytics Columns */}
      {tab === "users" && (() => {
        const reports = (() => { try { return JSON.parse(localStorage.getItem('soul_user_reports') || '[]'); } catch { return []; } })();
        const bans = (() => { try { return JSON.parse(localStorage.getItem('soul_user_bans') || '{}'); } catch { return {}; } })();
        
        const newestUsers = [...recentUsers].sort((a, b) => new Date(b.joined) - new Date(a.joined)).slice(0, 5);
        const activeUsers = [...recentUsers].filter(u => u.status === 'active').slice(0, 5);
        const reportedUserIds = [...new Set(reports.filter(r => r.status === 'pending').map(r => r.reportedUserId))];
        const bannedUserIds = Object.keys(bans);
        
        const UserCard = ({ user, badge, badgeColor, showActions }) => {
          const userBan = bans[user.id];
          const userReports = reports.filter(r => r.reportedUserId === user.id && r.status === 'pending');
          
          return (
            <Card style={{ padding: bp.isDesktop ? "16px 20px" : "12px 14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
                    <div style={{ fontSize: bp.isDesktop ? 15 : 15, fontWeight: 700, color: T.text }}>{user.name}</div>
                    {user.premium && <span style={{ fontSize: 11, color: T.rose, fontWeight: 700, background: `${T.rose}15`, padding: "2px 6px", borderRadius: 4 }}>PREMIUM</span>}
                    {badge && <span style={{ fontSize: 11, color: badgeColor || T.rose, fontWeight: 700, background: `${badgeColor || T.rose}15`, padding: "2px 6px", borderRadius: 4 }}>{badge}</span>}
                    {userBan && <span style={{ fontSize: 11, color: "#ef4444", fontWeight: 700, background: "#fef2f2", padding: "2px 6px", borderRadius: 4 }}>BANNED</span>}
                  </div>
                  <div style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.muted, marginBottom: 6 }}>{user.email}</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: bp.isDesktop ? 12 : 8, fontSize: bp.isDesktop ? 11 : 12 }}>
                    <div><span style={{ color: T.muted }}>Age:</span> <span style={{ color: T.text, fontWeight: 600 }}>{user.age}</span></div>
                    <div><span style={{ color: T.muted }}>📍</span> <span style={{ color: T.text, fontWeight: 600 }}>{user.location}</span></div>
                    <div><span style={{ color: T.muted }}>Matches:</span> <span style={{ color: T.text, fontWeight: 700 }}>{user.matches}</span></div>
                    <div><span style={{ color: T.muted }}>Dates:</span> <span style={{ color: T.text, fontWeight: 700 }}>{user.dates}</span></div>
                    <div><span style={{ color: T.muted }}>Profile:</span> <span style={{ color: user.profileComplete === 100 ? T.text : T.muted, fontWeight: 700 }}>{user.profileComplete}%</span></div>
                  </div>
                  {userReports.length > 0 && (
                    <div style={{ marginTop: 8, padding: "8px 10px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#ef4444", marginBottom: 4 }}>{userReports.length} PENDING REPORT{userReports.length > 1 ? 'S' : ''}</div>
                      <div style={{ fontSize: 13, color: "#991b1b", lineHeight: 1.4 }}>{userReports[0].reason.substring(0, 80)}{userReports[0].reason.length > 80 ? '...' : ''}</div>
                    </div>
                  )}
                  {userBan && (
                    <div style={{ marginTop: 8, padding: "8px 10px", background: "#fef2f2", border: "1px solid #ef4444", borderRadius: 6, fontSize: 13, color: "#991b1b" }}>
                      Banned {userBan.duration === 'permanent' ? 'permanently' : `until ${new Date(userBan.bannedUntil).toLocaleDateString()}`}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: bp.isDesktop ? 10 : 11, color: T.muted, textAlign: "right", whiteSpace: "nowrap" }}>
                  {new Date(user.joined).toLocaleDateString()}
                  <div style={{ marginTop: 2 }}>{new Date(user.joined).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
              </div>
            </Card>
          );
        };
        
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: bp.isDesktop ? 24 : 16 }}>
            {/* Analytics Summary */}
            <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "repeat(4, 1fr)" : "repeat(2, 1fr)", gap: bp.isDesktop ? 16 : 12 }}>
              <Card style={{ background: `${T.rose}10`, borderColor: `${T.rose}30`, textAlign: "center" }}>
                <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, fontWeight: 500, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>Newest Users</div>
                <div style={{ fontSize: bp.isDesktop ? 28 : 22, fontWeight: 700, color: T.rose }}>{newestUsers.length}</div>
                <div style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.muted, marginTop: 6, fontWeight: 500 }}>Last 24 hours</div>
              </Card>
              <Card style={{ background: `${T.teal}10`, borderColor: `${T.teal}30`, textAlign: "center" }}>
                <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, fontWeight: 500, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>Active Users</div>
                <div style={{ fontSize: bp.isDesktop ? 28 : 22, fontWeight: 700, color: T.teal }}>{activeUsers.length}</div>
                <div style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.muted, marginTop: 6, fontWeight: 500 }}>Currently online</div>
              </Card>
              <Card style={{ background: "rgba(245,158,11,0.1)", borderColor: "rgba(245,158,11,0.3)", textAlign: "center" }}>
                <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, fontWeight: 500, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>Reported</div>
                <div style={{ fontSize: bp.isDesktop ? 28 : 22, fontWeight: 700, color: T.amber }}>{reportedUserIds.length}</div>
                <div style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.muted, marginTop: 6, fontWeight: 500 }}>Pending review</div>
              </Card>
              <Card style={{ background: "rgba(239,68,68,0.1)", borderColor: "rgba(239,68,68,0.3)", textAlign: "center" }}>
                <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, fontWeight: 500, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>Banned</div>
                <div style={{ fontSize: bp.isDesktop ? 28 : 22, fontWeight: 700, color: "#ef4444" }}>{bannedUserIds.length}</div>
                <div style={{ fontSize: bp.isDesktop ? 11 : 12, color: T.muted, marginTop: 6, fontWeight: 500 }}>Restricted access</div>
              </Card>
            </div>

            {/* Analytics Sections - 2 Column Grid on Desktop */}
            <div style={{ display: bp.isDesktop ? "grid" : "flex", gridTemplateColumns: bp.isDesktop ? "1fr 1fr" : undefined, flexDirection: bp.isDesktop ? undefined : "column", gap: bp.isDesktop ? 20 : 16 }}>
              {/* Newest Users */}
              <div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: bp.isDesktop ? 16 : 15, color: T.text, fontWeight: 600 }}>Newest Users</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {newestUsers.map(u => <UserCard key={u.id} user={u} badge="NEW" badgeColor="#22c55e" />)}
                </div>
              </div>

              {/* Recently Active Users */}
              <div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: bp.isDesktop ? 16 : 15, color: T.text, fontWeight: 600 }}>Recently Active</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {activeUsers.map(u => <UserCard key={u.id} user={u} badge="ACTIVE" badgeColor={T.teal} />)}
                </div>
              </div>

              {/* Reported Users */}
              {reportedUserIds.length > 0 && (
                <div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: bp.isDesktop ? 16 : 15, color: T.text, fontWeight: 600 }}>Reported Users</div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {reportedUserIds.map(userId => {
                      const user = recentUsers.find(u => u.id === parseInt(userId));
                      if (!user) return null;
                      return <UserCard key={user.id} user={user} badge="REPORTED" badgeColor={T.amber} showActions />;
                    })}
                  </div>
                </div>
              )}

              {/* Banned Users */}
              {bannedUserIds.length > 0 && (
                <div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: bp.isDesktop ? 16 : 15, color: T.text, fontWeight: 600 }}>Banned Users</div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {bannedUserIds.map(userId => {
                      const user = recentUsers.find(u => u.id === parseInt(userId));
                      if (!user) return null;
                      return <UserCard key={user.id} user={user} />;
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* All Users */}
            <div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: bp.isDesktop ? 16 : 15, color: T.text, fontWeight: 600, marginBottom: 4 }}>All Users</div>
                <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted }}>Showing {recentUsers.length} most recent users</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {recentUsers.map(u => <UserCard key={u.id} user={u} />)}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Dates tab */}
      {tab === "dates" && (
        <div style={{ display: bp.isDesktop ? "grid" : "flex", gridTemplateColumns: bp.isDesktop ? "1fr 1fr" : undefined, flexDirection: bp.isDesktop ? undefined : "column", gap: bp.isDesktop ? 16 : 8 }}>
          <div style={{ fontSize: bp.isDesktop ? 13 : 14, color: T.muted, marginBottom: 8, gridColumn: bp.isDesktop ? "1 / -1" : undefined }}>Most recent 50 scheduled dates</div>
          {recentDates.map(d => (
            <Card key={d.id} style={{ padding: bp.isDesktop ? "16px 20px" : "12px 14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontSize: bp.isDesktop ? 14 : 15, fontWeight: 700, color: T.text }}>{d.venue_name || "TBD"}</div>
                  <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, marginTop: 2 }}>{d.user_a_email} + {d.user_b_email}</div>
                  {d.venue_address && <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted }}>📍 {d.venue_address}</div>}
                </div>
                <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
                  <div style={{ fontSize: 12, padding: "2px 8px", borderRadius: 8, display: "inline-block",
                    background: d.status === "confirmed" ? "rgba(34,197,94,0.15)" : d.status === "completed" ? "rgba(20,184,166,0.15)" : "rgba(245,158,11,0.15)",
                    color: d.status === "confirmed" ? "#22c55e" : d.status === "completed" ? T.teal : T.amber, fontWeight: 700 }}>
                    {d.status?.toUpperCase()}
                  </div>
                  {d.date_time && <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>{new Date(d.date_time).toLocaleDateString()}</div>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Settings tab */}
      {tab === "settings" && (
        <div>
          <div style={{ fontSize: 13, color: T.muted, marginBottom: 16, lineHeight: 1.6 }}>
            Changes apply immediately app-wide. Users will see updated settings on their next page load.
          </div>
          <Toggle settingKey="blind_match_mode" label="Blind Match Mode" description="Show voicemail + compatibility stats only — hide photos and bio from match feed and detail" />
          <Toggle settingKey="show_photos" label="Show Profile Photos" description="Display profile photos in match feed and detail view" />
          <Toggle settingKey="show_bio" label="Show User Bio" description="Display bio text in match feed cards" />
          <Toggle settingKey="maintenance_mode" label="Maintenance Mode" description="Take the app offline for all users with a maintenance message" />

          {/* Feature Flags - Premium Features Control */}
          <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.border}` }}>
            <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.rose, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 16 }}>FEATURE FLAGS</div>
            <div style={{ fontSize: 13, color: T.muted, marginBottom: 16, lineHeight: 1.6 }}>
              Enable or disable individual features for all users. Disabled features will be hidden from the UI entirely.
            </div>
            {Object.entries(DEFAULT_FEATURE_FLAGS).map(([key, flag]) => {
              const flags = (() => { try { return JSON.parse(localStorage.getItem(FEATURE_FLAGS_KEY)) || {}; } catch { return {}; } })();
              const isEnabled = flags[key]?.enabled !== undefined ? flags[key].enabled : flag.enabled;
              return (
                <div key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0", borderBottom: `1px solid ${T.border}` }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>{flag.label}</div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: flag.tier === "free" ? "#22c55e" : T.rose, background: flag.tier === "free" ? "rgba(34,197,94,0.1)" : `${T.rose}12`, padding: "2px 8px", borderRadius: 4, letterSpacing: "0.05em" }}>
                        {flag.tier === "free" ? "FREE" : "PREMIUM"}
                      </div>
                    </div>
                    <div style={{ fontSize: 13, color: T.muted }}>{flag.description}</div>
                  </div>
                  <button
                    onClick={() => {
                      const current = (() => { try { return JSON.parse(localStorage.getItem(FEATURE_FLAGS_KEY)) || {}; } catch { return {}; } })();
                      current[key] = { ...flag, ...current[key], enabled: !isEnabled };
                      localStorage.setItem(FEATURE_FLAGS_KEY, JSON.stringify(current));
                      setSaving(key);
                      setTimeout(() => setSaving(null), 600);
                    }}
                    style={{ 
                      width: 48, height: 24, borderRadius: 12, border: "none",
                      background: isEnabled ? T.teal : T.dim,
                      cursor: "pointer", position: "relative", transition: "all 0.3s",
                      flexShrink: 0
                    }}>
                    <div style={{ 
                      width: 20, height: 20, borderRadius: "50%", background: "#fff",
                      position: "absolute", top: 2, left: isEnabled ? 26 : 2,
                      transition: "all 0.3s", boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                    }} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Session helpers ────────────────────────────────────────
function getStoredSession() {
  try {
    const t = localStorage.getItem("soul_session_token");
    if (!t) return null;
    const payload = JSON.parse(atob(t.split('.')[1]));
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      localStorage.removeItem("soul_session_token");
      localStorage.removeItem("soul_user");
      return null;
    }
    return payload;
  } catch { return null; }
}

// ─── SCREEN: Calendar ────────────────────────────────────────
function Calendar({ blindMode = false, onNavigateToUpgrade }) {
  const bp = useBreakpoint();
  const containerPad = bp.isDesktop ? "24px 32px 48px" : bp.isTablet ? "28px 32px 60px" : "24px 20px 100px";
  const maxW = bp.isDesktop ? 1400 : bp.isTablet ? 680 : 480;
  const [selectedDate, setSelectedDate] = useState(null);
  const [playingDateId, setPlayingDateId] = useState(null);
  const [givenReactions, setGivenReactions] = useState(_getGivenReactions);
  const [cancelModal, setCancelModal] = useState(null);
  const [rescheduleModal, setRescheduleModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduleStep, setRescheduleStep] = useState('reason'); // 'reason' | 'venue' | 'datetime'
  const [rescheduleData, setRescheduleData] = useState({ venue: '', date: '', time: '' });
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);

  // Expose selectedDate state for hardware back button handler
  useEffect(() => {
    window.__calendarHasDateDetail = !!selectedDate;
    return () => { window.__calendarHasDateDetail = false; };
  }, [selectedDate]);

  // Listen for hardware back button event to close date detail
  useEffect(() => {
    const handler = () => { if (selectedDate) setSelectedDate(null); };
    window.addEventListener('calendarBack', handler);
    return () => window.removeEventListener('calendarBack', handler);
  }, [selectedDate]);

  const getDateMessage = (dateStr, timeStr) => {
    // Convert 12-hour time to 24-hour format
    let time24 = timeStr || '00:00';
    if (timeStr && (timeStr.includes('PM') || timeStr.includes('AM'))) {
      const [time, period] = timeStr.split(' ');
      const [hours, minutes] = time.split(':');
      let hour = parseInt(hours);
      if (period === 'PM' && hour !== 12) hour += 12;
      if (period === 'AM' && hour === 12) hour = 0;
      time24 = `${hour.toString().padStart(2, '0')}:${minutes}`;
    }
    const target = new Date(`${dateStr}T${time24}`);
    const diff = target.getTime() - now;
    if (diff <= 0) return null;
    const nowDate = new Date(now);
    const todayStart = new Date(nowDate.getFullYear(), nowDate.getMonth(), nowDate.getDate());
    const targetStart = new Date(target.getFullYear(), target.getMonth(), target.getDate());
    const dayDiff = Math.round((targetStart - todayStart) / 86400000);
    const totalHours = diff / 3600000;
    const isEvening = target.getHours() >= 17;
    // Same day
    if (dayDiff === 0) {
      if (totalHours <= 1) return { message: "Right Now!", urgent: true };
      if (isEvening) return { message: "Tonight", urgent: true };
      return { message: "Today", urgent: true };
    }
    // Tomorrow
    if (dayDiff === 1) {
      if (isEvening) return { message: "Tomorrow Night", urgent: false };
      return { message: "Tomorrow", urgent: false };
    }
    // This week (2-6 days away) — show day name
    if (dayDiff >= 2 && dayDiff <= 6) {
      const dayName = target.toLocaleDateString('en-US', { weekday: 'long' });
      return { message: dayName, urgent: false };
    }
    // Next week (7-13 days)
    if (dayDiff >= 7 && dayDiff <= 13) return { message: "Next Week", urgent: false };
    // 2 weeks
    if (dayDiff >= 14 && dayDiff <= 20) return { message: "In 2 Weeks", urgent: false };
    // Weeks
    const weeks = Math.round(dayDiff / 7);
    if (dayDiff <= 30) return { message: `In ${weeks} Weeks`, urgent: false };
    // Month+
    return { message: `In ${Math.round(dayDiff / 30)} Month${Math.round(dayDiff / 30) > 1 ? 's' : ''}`, urgent: false };
  };

  const handleOrbClick = (e, dateId) => {
    e.stopPropagation();
    setPlayingDateId(prev => prev === dateId ? null : dateId);
  };
  
  const handleReaction = (e, dateId, emoji) => {
    e.stopPropagation();
    _addVoicemailReaction(emoji);
    _saveGivenReaction(dateId, emoji);
    setGivenReactions(prev => ({ ...prev, [dateId]: emoji }));
    setPlayingDateId(null);
    window.dispatchEvent(new Event('givenReactionsChanged'));
  };

  useEffect(() => {
    const sync = () => setGivenReactions(_getGivenReactions());
    window.addEventListener('givenReactionsChanged', sync);
    return () => window.removeEventListener('givenReactionsChanged', sync);
  }, []);

  const handleCancelDate = (e, dateItem) => {
    e.stopPropagation();
    setCancelModal(dateItem);
  };

  const confirmCancel = () => {
    if (!cancelReason) return;
    console.log('Date cancelled:', cancelModal.id, 'Reason:', cancelReason);
    // TODO: Send notification to matched user
    setCancelModal(null);
    setCancelReason('');
  };

  const handleRescheduleDate = (e, dateItem) => {
    e.stopPropagation();
    setRescheduleModal(dateItem);
    setRescheduleStep('reason');
  };

  const confirmReschedule = () => {
    console.log('Date rescheduled:', rescheduleModal.id, 'Reason:', rescheduleReason, 'New details:', rescheduleData);
    // Update the date status to 'reschedule_requested'
    setUpcomingDates(prev => prev.map(date => 
      date.id === rescheduleModal.id 
        ? { ...date, status: 'reschedule_requested' }
        : date
    ));
    // TODO: Send notification to matched user to re-confirm
    setRescheduleModal(null);
    setRescheduleReason('');
    setRescheduleStep('reason');
    setRescheduleData({ venue: '', date: '', time: '' });
  };

  // Mock upcoming dates data - using future dates for countdown preview
  const getFutureDate = (daysFromNow) => {
    const date = new Date();
    date.setDate(date.getDate() + daysFromNow);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getFutureTime = (daysFromNow) => {
    const date = new Date();
    date.setDate(date.getDate() + daysFromNow);
    date.setHours(19, 0, 0, 0); // Set to 7:00 PM
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  const [upcomingDates, setUpcomingDates] = useState([
    {
      id: 1,
      match: {
        id: 2,
        name: "Emma",
        photo: "https://i.pravatar.cc/150?img=5",
        age: 28,
        city: "West Hollywood, CA",
        compatibility: 94,
        personalityScore: 92,
        valuesScore: 96,
        traits: ["Adventurous", "Empathetic", "Creative"],
        wantsChildren: true
      },
      date: getFutureDate(90),
      time: getFutureTime(90),
      location: "The Ivy Restaurant, Los Angeles",
      type: "Dinner",
      status: "confirmed",
      notes: "First date - she loves Italian cuisine",
      rating: 4.6,
      reviewCount: "2,847"
    },
    {
      id: 2,
      match: {
        id: 4,
        name: "Sophie",
        photo: "https://i.pravatar.cc/150?img=9",
        age: 26,
        city: "Santa Monica, CA",
        compatibility: 91,
        personalityScore: 88,
        valuesScore: 94,
        traits: ["Intellectual", "Spontaneous", "Caring"],
        wantsChildren: false
      },
      date: getFutureDate(30),
      time: getFutureTime(30),
      location: "Griffith Observatory, Los Angeles",
      type: "Activity",
      status: "confirmed",
      notes: "Afternoon hike and stargazing",
      rating: 4.8,
      reviewCount: "15,392"
    },
    {
      id: 3,
      match: {
        id: 6,
        name: "Olivia",
        photo: "https://i.pravatar.cc/150?img=10",
        age: 27,
        city: "Venice Beach, CA",
        compatibility: 89,
        personalityScore: 91,
        valuesScore: 87,
        traits: ["Artistic", "Thoughtful", "Passionate"],
        wantsChildren: null
      },
      date: getFutureDate(1),
      time: getFutureTime(1),
      location: "Blue Bottle Coffee, Santa Monica",
      type: "Coffee",
      status: "pending",
      notes: "Casual coffee meetup",
      rating: 4.4,
      reviewCount: "1,156"
    }
  ]);

  const getStatusColor = (status) => {
    switch(status) {
      case 'confirmed': return T.teal;
      case 'pending': return T.amber;
      case 'cancelled': return '#ef4444';
      case 'reschedule_requested': return T.violet;
      default: return T.muted;
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'confirmed': return 'CONFIRMED';
      case 'pending': return 'PENDING';
      case 'cancelled': return 'CANCELLED';
      case 'reschedule_requested': return 'RESCHEDULE REQUESTED';
      default: return status.toUpperCase();
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'Dinner': return '🍽️';
      case 'Coffee': return '☕';
      case 'Activity': return '🎯';
      case 'Drinks': return '🍷';
      default: return '📅';
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  };

  const handleDateClick = (dateItem) => {
    setSelectedDate(dateItem);
  };

  // Show DateDetail if a date is selected
  if (selectedDate) {
    return <DateDetail dateItem={selectedDate} onBack={() => setSelectedDate(null)} blindMode={blindMode} onNavigateToUpgrade={onNavigateToUpgrade} />;
  }

  return (
    <>
      <div style={{ maxWidth: maxW, margin: "0 auto", padding: containerPad }}>
        {/* Header */}
        <PageHeader
          label="CALENDAR"
          title="Upcoming Dates"
          description="Your scheduled dates and meetups with matches"
          gradientAnimation
        >
          <div style={{ 
            display: "inline-flex", 
            alignItems: "center", 
            gap: 8, 
            padding: "8px 16px", 
            background: T.roseSoft, 
            border: `1px solid ${T.borderHover}`, 
            borderRadius: 4,
            marginTop: 12
          }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: T.rose }}>
              {upcomingDates.length} upcoming {upcomingDates.length === 1 ? 'date' : 'dates'}
            </span>
          </div>
        </PageHeader>

      {/* Dates List */}
      {upcomingDates.length === 0 ? (
        <div style={{ 
          textAlign: "center", 
          padding: "60px 20px", 
          background: T.card, 
          border: `1px solid ${T.border}`, 
          borderRadius: 8 
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📅</div>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 8 }}>
            No upcoming dates
          </h3>
          <p style={{ fontSize: 15, color: T.muted }}>
            Schedule a date with your matches to see them here
          </p>
        </div>
      ) : (
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: bp.isDesktop ? "repeat(2, 1fr)" : "1fr", 
          gap: 20 
        }}>
          {[...upcomingDates].sort((a, b) => a.date.localeCompare(b.date)).map(dateItem => (
            <div key={dateItem.id} 
            onClick={() => handleDateClick(dateItem)}
            style={{ 
              background: T.card, 
              border: `1px solid ${T.border}`, 
              borderRadius: 8, 
              padding: bp.isDesktop ? 24 : 20,
              transition: "all 0.2s",
              cursor: "pointer",
              position: "relative"
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = T.rose; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.12)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}>
              
              {/* ── TOP ROW: Profile Left | Date Message + Status + Actions Right ── */}
              <div style={{ display: "flex", alignItems: bp.isDesktop ? "flex-start" : "center", justifyContent: "space-between", gap: 16, marginBottom: 20 }}>
                {/* Profile + Name */}
                <div style={{ display: "flex", alignItems: "center", gap: 14, flex: 1, minWidth: 0 }}>
                  {blindMode ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "center" }}>
                      <div onClick={e => handleOrbClick(e, dateItem.id)} style={{ position: "relative", width: bp.isDesktop ? 64 : 60, height: bp.isDesktop ? 64 : 60, flexShrink: 0, cursor: "pointer" }}>
                        <div style={{ position: "absolute", inset: -5, borderRadius: "50%", border: `2px solid ${T.rose}`, animation: "orbRing 1.8s ease-out infinite", opacity: 0.7 }} />
                        <div style={{ width: "100%", height: "100%", borderRadius: "50%", background: `linear-gradient(135deg, ${T.rose}, ${T.violet})`, display: "flex", alignItems: "center", justifyContent: "center", animation: "orbPulse 1.5s ease-in-out infinite alternate", border: `3px solid ${T.rose}` }}>
                          <span style={{ fontSize: 22, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginLeft: playingDateId === dateItem.id ? 0 : 2 }}>{playingDateId === dateItem.id ? "⏸" : <PlayTriangle size={26} />}</span>
                        </div>
                      </div>
                      {playingDateId === dateItem.id && (
                        <div style={{ width: bp.isDesktop ? 64 : 60, height: 3, borderRadius: 3, background: `${T.rose}30`, overflow: "hidden" }}>
                          <div style={{ height: "100%", background: `linear-gradient(90deg, ${T.rose}, ${T.violet})`, borderRadius: 3, animation: "voicemailProgress 60s linear forwards" }} />
                        </div>
                      )}
                      {playingDateId === dateItem.id && (
                        <div onClick={e => e.stopPropagation()} style={{ display: "flex", gap: 4, animation: "reactionSlideUp 0.3s ease both" }}>
                          {REACTION_EMOJIS.map((emoji, ei) => (
                            <button key={emoji} onClick={e => { triggerRipple(e); handleReaction(e, dateItem.id, emoji); }}
                              style={{ width: 28, height: 28, borderRadius: "50%", border: "none", background: T.card, cursor: "pointer", fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s", animation: `emojiPop 0.4s ease ${ei * 0.05}s both` }}
                              onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.2)"; e.currentTarget.style.background = T.bg; }}
                              onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.background = T.card; }}>
                              {emoji}
                            </button>
                          ))}
                        </div>
                      )}
                      {givenReactions[dateItem.id] && (
                        <div style={{ fontSize: 20, animation: "emojiPop 0.5s ease both" }}>{givenReactions[dateItem.id]}</div>
                      )}
                    </div>
                  ) : (
                    <div style={{ position: "relative", width: bp.isDesktop ? 64 : 60, height: bp.isDesktop ? 64 : 60, flexShrink: 0 }}>
                      <div style={{ width: "100%", height: "100%", borderRadius: "50%", overflow: "hidden", border: `3px solid ${T.rose}` }}>
                        <img src={dateItem.match.photo} alt={dateItem.match.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      </div>
                      <button onClick={e => handleOrbClick(e, dateItem.id)} style={{ 
                        position: "absolute", bottom: -4, right: -4, 
                        width: 28, height: 28, borderRadius: "50%", 
                        background: playingDateId === dateItem.id ? T.rose : `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, 
                        border: "2px solid #fff", display: "flex", alignItems: "center", justifyContent: "center", 
                        cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", transition: "all 0.2s", padding: 0
                      }}
                      onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.1)"; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}>
                        <span style={{ fontSize: 11, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginLeft: playingDateId === dateItem.id ? 0 : 1 }}>{playingDateId === dateItem.id ? "⏸" : <PlayTriangle size={14} />}</span>
                      </button>
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 700, color: T.text, marginBottom: 3 }}>
                      {dateItem.match.name}, {dateItem.match.age}
                    </div>
                    {dateItem.match.city && (
                      <div style={{ fontSize: 13, color: T.muted, marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}>
                        <LocationIcon size={12} color={T.muted} /> {dateItem.match.city}
                      </div>
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.rose }}>
                        {dateItem.match.compatibility}% Match
                      </div>
                      <span style={{ color: T.muted }}>•</span>
                      <div style={{ fontSize: 13, color: T.muted }}>
                        {getTypeIcon(dateItem.type)} {dateItem.type}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Desktop: Date Message + Status Badge + Actions */}
                {bp.isDesktop && (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
                    {(() => {
                      const dateMsg = getDateMessage(dateItem.date, dateItem.time);
                      return dateMsg ? (
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: T.muted, textTransform: "uppercase", letterSpacing: "0.06em", lineHeight: 1 }}>YOUR DATE IS</div>
                          <div style={{ fontSize: 26, fontWeight: 800, background: `linear-gradient(90deg, ${T.rose}, ${T.violet}, ${T.rose})`, backgroundSize: '300% 100%', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', animation: 'aurora 6s ease-in-out infinite', lineHeight: 1.2, marginTop: 2 }}>{dateMsg.message}</div>
                        </div>
                      ) : null;
                    })()}
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ background: getStatusColor(dateItem.status), color: "#fff", fontSize: 9, fontWeight: 700, padding: "3px 8px", borderRadius: 5, letterSpacing: "0.05em" }}>
                        {getStatusLabel(dateItem.status)}
                      </div>
                      {(dateItem.status === 'confirmed' || dateItem.status === 'pending') && (
                        <>
                          <button onClick={e => handleRescheduleDate(e, dateItem)}
                            style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 6, padding: 4, color: T.teal, cursor: "pointer", transition: "all 0.2s", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center" }}
                            onMouseEnter={e => { e.currentTarget.style.background = T.bg; e.currentTarget.style.borderColor = T.teal; }}
                            onMouseLeave={e => { e.currentTarget.style.background = T.card; e.currentTarget.style.borderColor = T.border; }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                          </button>
                          <button onClick={e => handleCancelDate(e, dateItem)}
                            style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 6, padding: 4, color: "#ef4444", cursor: "pointer", transition: "all 0.2s", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center" }}
                            onMouseEnter={e => { e.currentTarget.style.background = T.bg; e.currentTarget.style.borderColor = "#ef4444"; }}
                            onMouseLeave={e => { e.currentTarget.style.background = T.card; e.currentTarget.style.borderColor = T.border; }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Mobile: Status Badge + Actions only (date message shown below) */}
                {!bp.isDesktop && (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8, flexShrink: 0 }}>
                    <div style={{ background: getStatusColor(dateItem.status), color: "#fff", fontSize: 10, fontWeight: 700, padding: "4px 10px", borderRadius: 6, letterSpacing: "0.05em" }}>
                      {getStatusLabel(dateItem.status)}
                    </div>
                    {(dateItem.status === 'confirmed' || dateItem.status === 'pending') && (
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={e => handleRescheduleDate(e, dateItem)}
                          style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: 5, color: T.teal, cursor: "pointer", transition: "all 0.2s", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center" }}
                          onMouseEnter={e => { e.currentTarget.style.background = T.bg; e.currentTarget.style.borderColor = T.teal; }}
                          onMouseLeave={e => { e.currentTarget.style.background = T.card; e.currentTarget.style.borderColor = T.border; }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                        </button>
                        <button onClick={e => handleCancelDate(e, dateItem)}
                          style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: 5, color: "#ef4444", cursor: "pointer", transition: "all 0.2s", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center" }}
                          onMouseEnter={e => { e.currentTarget.style.background = T.bg; e.currentTarget.style.borderColor = "#ef4444"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = T.card; e.currentTarget.style.borderColor = T.border; }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ── DETAILS SECTION ── */}
              <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
                {/* Desktop: 2-column grid */}
                {bp.isDesktop ? (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
                    {/* Left column: Date & Time, Notes, Compatibility */}
                    <div>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 8 }}>
                        <div>
                          <div style={{ fontSize: 12, color: T.muted, fontWeight: 600, letterSpacing: "0.05em", marginBottom: 4 }}>DATE & TIME</div>
                          <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>{formatDate(dateItem.date)}</div>
                          <div style={{ fontSize: 14, color: T.muted }}>{dateItem.time}</div>
                        </div>
                      </div>

                      {/* Notes */}
                      {dateItem.notes && (
                        <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
                          <div style={{ fontSize: 12, color: T.muted, fontWeight: 600, letterSpacing: "0.05em", marginBottom: 4 }}>NOTES</div>
                          <div style={{ fontSize: 14, color: T.text, lineHeight: 1.5 }}>{dateItem.notes}</div>
                        </div>
                      )}

                      {/* Compatibility */}
                      <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
                        <div style={{ fontSize: 12, color: T.muted, fontWeight: 600, letterSpacing: "0.05em", marginBottom: 8 }}>COMPATIBILITY</div>
                        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                          <div style={{ flex: 1, textAlign: "center", padding: "8px 4px", background: `${T.rose}10`, borderRadius: 8, border: `1px solid ${T.rose}25` }}>
                            <div style={{ fontSize: 18, fontWeight: 800, color: T.rose, lineHeight: 1 }}>{dateItem.match.personalityScore}%</div>
                            <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>Personality</div>
                          </div>
                          <div style={{ flex: 1, textAlign: "center", padding: "8px 4px", background: `${T.teal}10`, borderRadius: 8, border: `1px solid ${T.teal}25` }}>
                            <div style={{ fontSize: 18, fontWeight: 800, color: T.teal, lineHeight: 1 }}>{dateItem.match.valuesScore}%</div>
                            <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>Values</div>
                          </div>
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {dateItem.match.traits.map(trait => (
                            <span key={trait} style={{ fontSize: 12, padding: "3px 10px", borderRadius: 20, background: `${T.rose}10`, color: T.text, fontWeight: 600, border: `1px solid ${T.border}` }}>{trait}</span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Right column: Location + Map */}
                    <div>
                      <div style={{ fontSize: 12, color: T.muted, fontWeight: 600, letterSpacing: "0.05em", marginBottom: 8 }}>LOCATION</div>
                      <div style={{ width: "100%", height: 160, borderRadius: 8, overflow: "hidden", border: `1px solid ${T.border}`, marginBottom: 8, position: "relative" }}>
                        <iframe
                          src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(dateItem.location)}&zoom=15`}
                          style={{ width: "100%", height: "100%", border: 0, filter: T.bg === "#fafafa" ? "none" : "invert(90%) hue-rotate(180deg)" }}
                          loading="lazy" referrerPolicy="no-referrer-when-downgrade" title={`Map of ${dateItem.location}`}
                        />
                        <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(dateItem.location)}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                          style={{ position: "absolute", top: 8, left: 8, background: "#fff", color: T.rose, fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 4, textDecoration: "none", boxShadow: "0 1px 4px rgba(0,0,0,0.15)", display: "flex", alignItems: "center", gap: 3 }}>
                          Maps <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                        </a>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 4 }}>📍 {dateItem.location}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: T.rose }}>{dateItem.rating || "4.6"}</span>
                        <div style={{ display: "flex", gap: 1 }}>
                          {[1,2,3,4,5].map(star => (
                            <span key={star} style={{ fontSize: 12, color: star <= Math.floor(dateItem.rating || 4.5) ? "#fbbf24" : T.dim }}>★</span>
                          ))}
                        </div>
                        <span style={{ fontSize: 12, color: T.muted }}>({dateItem.reviewCount || "1,234"} reviews)</span>
                      </div>
                      <a href={(() => { const enc = encodeURIComponent(dateItem.location); const ua = navigator.userAgent || ''; if (/iPad|iPhone|iPod/.test(ua)) return `maps://maps.apple.com/?q=${enc}`; if (/android/i.test(ua)) return `geo:0,0?q=${enc}`; return `https://www.google.com/maps/search/?api=1&query=${enc}`; })()}
                        target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                        style={{ fontSize: 13, color: T.rose, fontWeight: 600, textDecoration: "none" }}
                        onMouseEnter={e => { e.currentTarget.style.textDecoration = "underline"; }}
                        onMouseLeave={e => { e.currentTarget.style.textDecoration = "none"; }}>
                        Get Directions →
                      </a>
                    </div>
                  </div>
                ) : (
                  /* Mobile: Single column */
                  <div>
                    {/* Date & Time + Date Message side by side */}
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16 }}>
                      <div>
                        <div style={{ fontSize: 12, color: T.muted, fontWeight: 600, letterSpacing: "0.05em", marginBottom: 4 }}>DATE & TIME</div>
                        <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>{formatDate(dateItem.date)}</div>
                        <div style={{ fontSize: 14, color: T.muted }}>{dateItem.time}</div>
                      </div>
                      {(() => {
                        const dateMsg = getDateMessage(dateItem.date, dateItem.time);
                        return dateMsg ? (
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, textTransform: "uppercase", letterSpacing: "0.06em", lineHeight: 1 }}>YOUR DATE IS</div>
                            <div style={{ fontSize: 22, fontWeight: 800, background: `linear-gradient(90deg, ${T.rose}, ${T.violet}, ${T.rose})`, backgroundSize: '300% 100%', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', animation: 'aurora 6s ease-in-out infinite', lineHeight: 1.2, marginTop: 2 }}>{dateMsg.message}</div>
                          </div>
                        ) : null;
                      })()}
                    </div>

                    {/* Notes */}
                    {dateItem.notes && (
                      <div style={{ marginBottom: 16, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
                        <div style={{ fontSize: 12, color: T.muted, fontWeight: 600, letterSpacing: "0.05em", marginBottom: 4 }}>NOTES</div>
                        <div style={{ fontSize: 14, color: T.text, lineHeight: 1.5 }}>{dateItem.notes}</div>
                      </div>
                    )}

                    {/* Location + Map */}
                    <div style={{ marginBottom: 16, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
                      <div style={{ fontSize: 12, color: T.muted, fontWeight: 600, letterSpacing: "0.05em", marginBottom: 8 }}>LOCATION</div>
                      <div style={{ width: "100%", height: 180, borderRadius: 8, overflow: "hidden", border: `1px solid ${T.border}`, marginBottom: 8, position: "relative" }}>
                        <iframe
                          src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(dateItem.location)}&zoom=15`}
                          style={{ width: "100%", height: "100%", border: 0, filter: T.bg === "#fafafa" ? "none" : "invert(90%) hue-rotate(180deg)" }}
                          loading="lazy" referrerPolicy="no-referrer-when-downgrade" title={`Map of ${dateItem.location}`}
                        />
                        <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(dateItem.location)}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                          style={{ position: "absolute", top: 8, left: 8, background: "#fff", color: T.rose, fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 4, textDecoration: "none", boxShadow: "0 1px 4px rgba(0,0,0,0.15)", display: "flex", alignItems: "center", gap: 3 }}>
                          Maps <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                        </a>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 4 }}>📍 {dateItem.location}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: T.rose }}>{dateItem.rating || "4.6"}</span>
                        <div style={{ display: "flex", gap: 1 }}>
                          {[1,2,3,4,5].map(star => (
                            <span key={star} style={{ fontSize: 12, color: star <= Math.floor(dateItem.rating || 4.5) ? "#fbbf24" : T.dim }}>★</span>
                          ))}
                        </div>
                        <span style={{ fontSize: 12, color: T.muted }}>({dateItem.reviewCount || "1,234"} reviews)</span>
                      </div>
                      <a href={(() => { const enc = encodeURIComponent(dateItem.location); const ua = navigator.userAgent || ''; if (/iPad|iPhone|iPod/.test(ua)) return `maps://maps.apple.com/?q=${enc}`; if (/android/i.test(ua)) return `geo:0,0?q=${enc}`; return `https://www.google.com/maps/search/?api=1&query=${enc}`; })()}
                        target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                        style={{ fontSize: 13, color: T.rose, fontWeight: 600, textDecoration: "none" }}
                        onMouseEnter={e => { e.currentTarget.style.textDecoration = "underline"; }}
                        onMouseLeave={e => { e.currentTarget.style.textDecoration = "none"; }}>
                        Get Directions →
                      </a>
                    </div>

                    {/* Compatibility */}
                    <div style={{ paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
                      <div style={{ fontSize: 12, color: T.muted, fontWeight: 600, letterSpacing: "0.05em", marginBottom: 8 }}>COMPATIBILITY</div>
                      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                        <div style={{ flex: 1, textAlign: "center", padding: "8px 4px", background: `${T.rose}10`, borderRadius: 8, border: `1px solid ${T.rose}25` }}>
                          <div style={{ fontSize: 18, fontWeight: 800, color: T.rose, lineHeight: 1 }}>{dateItem.match.personalityScore}%</div>
                          <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>Personality</div>
                        </div>
                        <div style={{ flex: 1, textAlign: "center", padding: "8px 4px", background: `${T.teal}10`, borderRadius: 8, border: `1px solid ${T.teal}25` }}>
                          <div style={{ fontSize: 18, fontWeight: 800, color: T.teal, lineHeight: 1 }}>{dateItem.match.valuesScore}%</div>
                          <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>Values</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {dateItem.match.traits.map(trait => (
                          <span key={trait} style={{ fontSize: 12, padding: "3px 10px", borderRadius: 20, background: `${T.rose}10`, color: T.text, fontWeight: 600, border: `1px solid ${T.border}` }}>{trait}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

            </div>
          ))}
        </div>
      )}
      </div>

      {/* Cancel Modal */}
      {cancelModal && (
        <div onClick={() => setCancelModal(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: T.card, borderRadius: 8, padding: bp.isDesktop ? 32 : 24, maxWidth: 480, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <h3 style={{ fontSize: bp.isDesktop ? 22 : 20, fontWeight: 700, color: T.text, marginBottom: 8 }}>Cancel Date</h3>
            <p style={{ fontSize: 15, color: T.muted, marginBottom: 20 }}>Cancel your date with {cancelModal.match.name}? They'll be notified.</p>
            
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 12 }}>Reason for cancellation:</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {['Something came up', 'Not feeling well', 'Work conflict', 'Family emergency', 'Changed my mind', 'Other'].map(reason => (
                  <button
                    key={reason}
                    onClick={() => setCancelReason(reason)}
                    style={{
                      padding: "12px 16px",
                      borderRadius: 6,
                      border: cancelReason === reason ? `2px solid ${T.rose}` : `1px solid ${T.border}`,
                      background: cancelReason === reason ? `${T.rose}12` : T.bg,
                      color: T.text,
                      fontSize: 15,
                      fontWeight: cancelReason === reason ? 600 : 400,
                      cursor: "pointer",
                      transition: "all 0.2s",
                      textAlign: "left",
                      fontFamily: "inherit"
                    }}
                    onMouseEnter={e => { if (cancelReason !== reason) e.currentTarget.style.background = T.card; }}
                    onMouseLeave={e => { if (cancelReason !== reason) e.currentTarget.style.background = T.bg; }}>
                    {reason}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={() => { setCancelModal(null); setCancelReason(''); }}
                style={{ flex: 1, padding: "12px 20px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                Nevermind
              </button>
              <button
                onClick={confirmCancel}
                disabled={!cancelReason}
                style={{ flex: 1, padding: "12px 20px", borderRadius: 6, border: "none", background: cancelReason ? "#ef4444" : T.dim, color: "#fff", fontSize: 15, fontWeight: 600, cursor: cancelReason ? "pointer" : "not-allowed", fontFamily: "inherit", opacity: cancelReason ? 1 : 0.5 }}>
                Cancel Date
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {rescheduleModal && (
        <div onClick={() => { setRescheduleModal(null); setRescheduleStep('reason'); setRescheduleReason(''); setRescheduleData({ venue: '', date: '', time: '' }); }} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: T.card, borderRadius: 8, padding: bp.isDesktop ? 32 : 24, maxWidth: 480, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <h3 style={{ fontSize: bp.isDesktop ? 22 : 20, fontWeight: 700, color: T.text, marginBottom: 8 }}>Reschedule Date</h3>
            <p style={{ fontSize: 15, color: T.muted, marginBottom: 20 }}>Reschedule your date with {rescheduleModal.match.name}? They'll need to re-confirm.</p>
            
            {rescheduleStep === 'reason' && (
              <>
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 12 }}>Reason for rescheduling:</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {['Work conflict', 'School commitment', 'Family obligation', 'Emergency', 'Personal reasons', 'Other'].map(reason => (
                      <button
                        key={reason}
                        onClick={() => setRescheduleReason(reason)}
                        style={{
                          padding: "12px 16px",
                          borderRadius: 6,
                          border: rescheduleReason === reason ? `2px solid ${T.teal}` : `1px solid ${T.border}`,
                          background: rescheduleReason === reason ? `${T.teal}12` : T.bg,
                          color: T.text,
                          fontSize: 15,
                          fontWeight: rescheduleReason === reason ? 600 : 400,
                          cursor: "pointer",
                          transition: "all 0.2s",
                          textAlign: "left",
                          fontFamily: "inherit"
                        }}
                        onMouseEnter={e => { if (rescheduleReason !== reason) e.currentTarget.style.background = T.card; }}
                        onMouseLeave={e => { if (rescheduleReason !== reason) e.currentTarget.style.background = T.bg; }}>
                        {reason}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 12 }}>
                  <button
                    onClick={() => { setRescheduleModal(null); setRescheduleReason(''); }}
                    style={{ flex: 1, padding: "12px 20px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                    Cancel
                  </button>
                  <button
                    onClick={() => setRescheduleStep('datetime')}
                    disabled={!rescheduleReason}
                    style={{ flex: 1, padding: "12px 20px", borderRadius: 6, border: "none", background: rescheduleReason ? T.teal : T.dim, color: "#fff", fontSize: 15, fontWeight: 600, cursor: rescheduleReason ? "pointer" : "not-allowed", fontFamily: "inherit", opacity: rescheduleReason ? 1 : 0.5 }}>
                    Continue
                  </button>
                </div>
              </>
            )}

            {rescheduleStep === 'datetime' && (
              <>
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 12 }}>New Date & Time:</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <input
                      type="date"
                      value={rescheduleData.date}
                      onChange={e => setRescheduleData(prev => ({ ...prev, date: e.target.value }))}
                      style={{ padding: "12px 16px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 15, fontFamily: "inherit" }}
                    />
                    <input
                      type="time"
                      value={rescheduleData.time}
                      onChange={e => setRescheduleData(prev => ({ ...prev, time: e.target.value }))}
                      style={{ padding: "12px 16px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 15, fontFamily: "inherit" }}
                    />
                    <input
                      type="text"
                      placeholder="Venue (optional - keep current or suggest new)"
                      value={rescheduleData.venue}
                      onChange={e => setRescheduleData(prev => ({ ...prev, venue: e.target.value }))}
                      style={{ padding: "12px 16px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 15, fontFamily: "inherit" }}
                    />
                  </div>
                </div>

                <div style={{ display: "flex", gap: 12 }}>
                  <button
                    onClick={() => setRescheduleStep('reason')}
                    style={{ flex: 1, padding: "12px 20px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                    Back
                  </button>
                  <button
                    onClick={confirmReschedule}
                    disabled={!rescheduleData.date || !rescheduleData.time}
                    style={{ flex: 1, padding: "12px 20px", borderRadius: 6, border: "none", background: (rescheduleData.date && rescheduleData.time) ? T.teal : T.dim, color: "#fff", fontSize: 15, fontWeight: 600, cursor: (rescheduleData.date && rescheduleData.time) ? "pointer" : "not-allowed", fontFamily: "inherit", opacity: (rescheduleData.date && rescheduleData.time) ? 1 : 0.5 }}>
                    Send Request
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─── Service Login Modal ────────────────────────────────────────
const SERVICE_LOGIN_CONFIG = {
  facebook:    { name: 'Facebook',    color: '#1877F2', loginLabel: 'Email or Phone', passLabel: 'Password', oauthNote: 'Sign in with your Facebook account', Icon: FacebookIcon },
  instagram:   { name: 'Instagram',   color: '#E4405F', loginLabel: 'Username or Email', passLabel: 'Password', oauthNote: 'Sign in with your Instagram account', Icon: CameraIcon },
  spotify:     { name: 'Spotify',     color: '#1DB954', loginLabel: 'Email or Username', passLabel: 'Password', oauthNote: 'Sign in with your Spotify account', Icon: MusicIcon },
  netflix:     { name: 'Netflix',     color: '#E50914', loginLabel: 'Email or Phone', passLabel: 'Password', oauthNote: 'Sign in with your Netflix account', Icon: MovieIcon },
  goodreads:   { name: 'Goodreads',   color: '#553B08', loginLabel: 'Email Address', passLabel: 'Password', oauthNote: 'Sign in with your Goodreads account', Icon: BookIcon },
  linkedin:    { name: 'LinkedIn',    color: '#0A66C2', loginLabel: 'Email or Phone', passLabel: 'Password', oauthNote: 'Sign in with your LinkedIn account', Icon: BriefcaseIcon },
  strava:      { name: 'Strava',      color: '#FC4C02', loginLabel: 'Email Address', passLabel: 'Password', oauthNote: 'Sign in with your Strava account', Icon: RunIcon },
  pinterest:   { name: 'Pinterest',   color: '#E60023', loginLabel: 'Email Address', passLabel: 'Password', oauthNote: 'Sign in with your Pinterest account', Icon: PinIcon },
  twitter:     { name: 'Twitter/X',   color: '#1DA1F2', loginLabel: 'Username or Email', passLabel: 'Password', oauthNote: 'Sign in with your X account', Icon: TwitterIcon },
  tiktok:      { name: 'TikTok',      color: '#000000', loginLabel: 'Username or Email', passLabel: 'Password', oauthNote: 'Sign in with your TikTok account', Icon: TikTokIcon },
  apple_music: { name: 'Apple Music', color: '#FA243C', loginLabel: 'Apple ID', passLabel: 'Password', oauthNote: 'Sign in with your Apple ID', Icon: AppleMusicIcon },
  google:      { name: 'Google',      color: '#4285F4', loginLabel: 'Email', passLabel: 'Password', oauthNote: 'Sign in with your Google account', Icon: SearchIcon },
};

function ServiceLoginModal({ serviceId, isConnected, onClose, onConnect, onDisconnect }) {
  const bp = useBreakpoint();
  const config = SERVICE_LOGIN_CONFIG[serviceId];
  const [loginVal, setLoginVal] = useState('');
  const [passVal, setPassVal] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const backdropRef = useRef(null);
  const panelRef = useRef(null);
  const tlRef = useRef(null);

  useLayoutEffect(() => {
    if (!panelRef.current || !backdropRef.current) return;
    if (tlRef.current) { tlRef.current.kill(); tlRef.current = null; }
    const tl = gsap.timeline({ paused: true, onReverseComplete: onClose });
    tl.fromTo(backdropRef.current, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: "power2.out" }, 0);
    tl.fromTo(panelRef.current,
      { scale: 0.08, opacity: 0, transformOrigin: "50% 50%" },
      { scale: 1, opacity: 1, transformOrigin: "50% 50%", duration: 0.42, ease: "back.out(1.2)" },
    0.05);
    tlRef.current = tl;
    tl.play(0);
    return () => { tl.kill(); tlRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAnimatedClose = () => {
    if (tlRef.current) {
      tlRef.current.eventCallback('onReverseComplete', onClose);
      tlRef.current.reverse();
    } else {
      onClose();
    }
  };

  if (!config) return null;
  const SvcIcon = config.Icon;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!loginVal.trim() || !passVal.trim()) {
      setError('Please fill in all fields');
      return;
    }
    setLoading(true);
    // Simulate OAuth connection delay
    setTimeout(() => {
      setLoading(false);
      setSuccess(true);
      onConnect(serviceId);
      setTimeout(() => handleAnimatedClose(), 1200);
    }, 1500);
  };

  const handleDisconnect = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      onDisconnect(serviceId);
      handleAnimatedClose();
    }, 800);
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 2000 }} onClick={handleAnimatedClose}>
      <div ref={backdropRef} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", opacity: 0 }} />
      <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", height: "100%", padding: 20 }}>
      <div ref={panelRef} style={{
        background: T.card, borderRadius: 12, width: "100%", maxWidth: 420, overflow: "hidden",
        boxShadow: "0 8px 48px rgba(0,0,0,0.35), 0 0px 0px 1px rgba(255,255,255,0.05)",
        opacity: 0
      }} onClick={e => e.stopPropagation()}>

        {/* Service Header */}
        <div style={{
          background: `linear-gradient(135deg, ${config.color}, ${config.color}cc)`,
          padding: bp.isDesktop ? "32px 28px 28px" : "28px 24px 24px",
          textAlign: "center", position: "relative"
        }}>
          <button onClick={handleAnimatedClose} style={{
            position: "absolute", top: 12, right: 12, background: "rgba(255,255,255,0.2)",
            border: "none", color: "#fff", width: 32, height: 32, borderRadius: "50%",
            fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "inherit", transition: "background 0.2s"
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.35)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.2)"; }}>
            ✕
          </button>
          <div style={{
            width: 64, height: 64, borderRadius: 16, background: "rgba(255,255,255,0.2)",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 14px", backdropFilter: "blur(8px)"
          }}>
            <SvcIcon size={36} color="#fff" />
          </div>
          <div style={{ fontSize: bp.isDesktop ? 22 : 20, fontWeight: 800, color: "#fff", marginBottom: 4, fontFamily: "'Segoe UI', sans-serif" }}>
            {isConnected ? `Manage ${config.name}` : `Connect ${config.name}`}
          </div>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.8)" }}>
            {config.oauthNote}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: bp.isDesktop ? "28px 28px 24px" : "24px 24px 20px" }}>

          {success ? (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ width: 64, height: 64, borderRadius: "50%", background: `${config.color}15`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", animation: "widgetScale 0.5s ease" }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17L4 12" stroke={config.color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 4 }}>Connected!</div>
              <div style={{ fontSize: 14, color: T.muted }}>{config.name} has been linked to your profile</div>
            </div>
          ) : isConnected ? (
            /* Already connected — show manage/disconnect */
            <div style={{ textAlign: "center" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: `${config.color}15`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17L4 12" stroke={config.color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, color: T.text, marginBottom: 4 }}>Currently Connected</div>
              <div style={{ fontSize: 14, color: T.muted, marginBottom: 24 }}>Your {config.name} account is linked and syncing data</div>
              <button onClick={handleDisconnect} disabled={loading} style={{
                width: "100%", padding: "14px", borderRadius: 8,
                background: "transparent", border: `1.5px solid #ef4444`,
                color: "#ef4444", fontSize: 15, fontWeight: 700, cursor: loading ? "wait" : "pointer",
                fontFamily: "inherit", transition: "all 0.2s", opacity: loading ? 0.6 : 1
              }}
              onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = "rgba(239,68,68,0.08)"; } }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                {loading ? "Disconnecting..." : "Disconnect Account"}
              </button>
            </div>
          ) : (
            /* Not connected — show login form */
            <form onSubmit={handleSubmit}>
              {/* OAuth badge */}
              <div style={{
                display: "flex", alignItems: "center", gap: 8, marginBottom: 20,
                padding: "10px 14px", background: `${config.color}08`, border: `1px solid ${config.color}20`,
                borderRadius: 6, fontSize: 13, color: T.muted
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke={config.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                <span>Secure OAuth 2.0 connection · Data encrypted</span>
              </div>

              {/* Login field */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 6, letterSpacing: "0.03em" }}>
                  {config.loginLabel}
                </label>
                <input
                  type="text"
                  value={loginVal}
                  onChange={e => setLoginVal(e.target.value)}
                  placeholder={`Enter your ${config.loginLabel.toLowerCase()}`}
                  style={{
                    width: "100%", padding: "12px 14px", borderRadius: 8,
                    border: `1.5px solid ${T.border}`, background: T.bg,
                    color: T.text, fontSize: 15, fontFamily: "inherit",
                    outline: "none", transition: "border-color 0.2s",
                    boxSizing: "border-box"
                  }}
                  onFocus={e => { e.currentTarget.style.borderColor = config.color; }}
                  onBlur={e => { e.currentTarget.style.borderColor = T.border; }}
                />
              </div>

              {/* Password field */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 6, letterSpacing: "0.03em" }}>
                  {config.passLabel}
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPass ? "text" : "password"}
                    value={passVal}
                    onChange={e => setPassVal(e.target.value)}
                    placeholder="Enter your password"
                    style={{
                      width: "100%", padding: "12px 44px 12px 14px", borderRadius: 8,
                      border: `1.5px solid ${T.border}`, background: T.bg,
                      color: T.text, fontSize: 15, fontFamily: "inherit",
                      outline: "none", transition: "border-color 0.2s",
                      boxSizing: "border-box"
                    }}
                    onFocus={e => { e.currentTarget.style.borderColor = config.color; }}
                    onBlur={e => { e.currentTarget.style.borderColor = T.border; }}
                  />
                  <button type="button" onClick={() => setShowPass(p => !p)} style={{
                    position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer", padding: 4,
                    color: T.muted, fontSize: 14, fontFamily: "inherit"
                  }}>
                    {showPass ? "Hide" : "Show"}
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div style={{ marginBottom: 16, padding: "10px 12px", borderRadius: 6, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", color: "#ef4444", fontSize: 14, fontWeight: 600 }}>
                  {error}
                </div>
              )}

              {/* Submit */}
              <button type="submit" disabled={loading} style={{
                width: "100%", padding: "14px", borderRadius: 8,
                background: loading ? `${config.color}88` : config.color,
                border: "none", color: "#fff", fontSize: 16, fontWeight: 700,
                cursor: loading ? "wait" : "pointer", fontFamily: "inherit",
                transition: "all 0.2s", display: "flex", alignItems: "center",
                justifyContent: "center", gap: 8
              }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.filter = "brightness(1.1)"; }}
              onMouseLeave={e => { e.currentTarget.style.filter = "brightness(1)"; }}>
                {loading && (
                  <div style={{ width: 18, height: 18, border: "2.5px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                )}
                {loading ? "Connecting..." : `Connect ${config.name}`}
              </button>

              {/* Footer links */}
              <div style={{ marginTop: 16, textAlign: "center", fontSize: 13, color: T.muted }}>
                <span>Forgot password? </span>
                <span style={{ color: config.color, cursor: "pointer", fontWeight: 600 }}>Reset</span>
                <span style={{ margin: "0 8px" }}>·</span>
                <span>Don't have an account? </span>
                <span style={{ color: config.color, cursor: "pointer", fontWeight: 600 }}>Sign up</span>
              </div>
            </form>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

// ─── SCREEN: Settings ────────────────────────────────────────
function Settings({ profile, onBack, onEditPreferences, isDarkMode, toggleTheme, colorScheme, changeColorScheme, initialTab, onClearInitialTab }) {
  const bp = useBreakpoint();
  const containerPad = bp.isDesktop ? "24px 32px 48px" : bp.isTablet ? "28px 32px 60px" : "24px 20px 100px";
  const maxW = bp.isDesktop ? 1400 : bp.isTablet ? 680 : 480;

  // Upgrade panel state
  const [showUpgradePanel, setShowUpgradePanel] = useState(initialTab === "upgrade");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [currentTier, setCurrentTier] = useState(() => getUserTier());
  const currentIsPremium = currentTier === "monthly" || currentTier === "annual";

  useEffect(() => {
    if (initialTab === "upgrade") {
      setShowUpgradePanel(true);
      if (onClearInitialTab) onClearInitialTab();
    }
  }, [initialTab]);

  const handleUpgrade = (planId) => {
    setUserTier(planId);
    setCurrentTier(planId);
    setSelectedPlan(null);
    setShowUpgradePanel(false);
    setShowPaymentForm(false);
  };

  // State for editable preferences
  const [userLocation, setUserLocation] = useState(() => {
    return localStorage.getItem("soul_user_location") || "Los Angeles, CA";
  });
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [ageRange, setAgeRange] = useState(() => {
    try {
      const saved = localStorage.getItem("soul_age_range");
      return saved ? JSON.parse(saved) : { min: 25, max: 35 };
    } catch {
      return { min: 25, max: 35 };
    }
  });
  const [maxDistance, setMaxDistance] = useState(() => {
    try {
      const saved = localStorage.getItem("soul_max_distance");
      return saved ? parseInt(saved) : 50;
    } catch {
      return 50;
    }
  });
  const [notifications, setNotifications] = useState(() => {
    try {
      const saved = localStorage.getItem("soul_notifications");
      return saved ? JSON.parse(saved) : {
        matchAlert: true,
        dateAlert: true,
        feedbackAlert: false
      };
    } catch {
      return {
        matchAlert: true,
        dateAlert: true,
        feedbackAlert: false
      };
    }
  });
  // Account info state
  const [accountName, setAccountName] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem("soul_user") || "{}");
      if (u.name) return u.name;
      if (u.first_name) return u.first_name;
    } catch {}
    try {
      const ans = JSON.parse(localStorage.getItem("soul_user_answers") || "{}");
      if (ans.first_name) return ans.first_name;
    } catch {}
    return "User";
  });
  const [accountEmail, setAccountEmail] = useState(() => {
    try { const u = JSON.parse(localStorage.getItem("soul_user") || "{}"); return u.email || ""; } catch { return ""; }
  });
  const [accountPhone, setAccountPhone] = useState(() => {
    try { const u = JSON.parse(localStorage.getItem("soul_user") || "{}"); return u.phone || ""; } catch { return ""; }
  });
  // Re-read from localStorage on mount AND when pullFromServer completes
  useEffect(() => {
    const refresh = () => {
      try {
        const u = JSON.parse(localStorage.getItem("soul_user") || "{}");
        if (u.phone) setAccountPhone(u.phone);
        if (u.email) setAccountEmail(u.email);
      } catch {}
      try {
        const u = JSON.parse(localStorage.getItem("soul_user") || "{}");
        const ans = JSON.parse(localStorage.getItem("soul_user_answers") || "{}");
        const name = u.name || u.first_name || ans.first_name;
        if (name) setAccountName(name);
      } catch {}
      // Refresh dating preferences from server sync
      try {
        const ar = JSON.parse(localStorage.getItem("soul_age_range") || "null");
        if (ar && ar.min != null && ar.max != null) setAgeRange(ar);
      } catch {}
      try {
        const md = localStorage.getItem("soul_max_distance");
        if (md) setMaxDistance(parseInt(md));
      } catch {}
      const loc = localStorage.getItem("soul_user_location");
      if (loc) setUserLocation(loc);
    };
    refresh();
    window.addEventListener("soul_sync_complete", refresh);
    return () => window.removeEventListener("soul_sync_complete", refresh);
  }, []);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [newPhone, setNewPhone] = useState("");

  const maskPhone = (phone) => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 4) return phone;
    const last4 = digits.slice(-4);
    return `xxx-xxx-${last4}`;
  };

  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    cardNumber: '',
    cardName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZip: ''
  });

  // Autosave age range to localStorage + server (debounced)
  useEffect(() => {
    localStorage.setItem("soul_age_range", JSON.stringify(ageRange));
    const t = setTimeout(() => {
      saveDatingPreferences({ age_range_min: ageRange.min, age_range_max: ageRange.max });
    }, 600);
    return () => clearTimeout(t);
  }, [ageRange]);

  // Autosave max distance to localStorage + server (debounced)
  useEffect(() => {
    localStorage.setItem("soul_max_distance", maxDistance.toString());
    const t = setTimeout(() => {
      saveDatingPreferences({ max_distance: maxDistance });
    }, 600);
    return () => clearTimeout(t);
  }, [maxDistance]);

  // Autosave notifications to localStorage
  useEffect(() => {
    localStorage.setItem("soul_notifications", JSON.stringify(notifications));
  }, [notifications]);

  const handleEditLocation = async () => {
    if (isEditingLocation) {
      // Save and exit edit mode
      localStorage.setItem("soul_user_location", userLocation);
      saveDatingPreferences({ user_location: userLocation });
      setIsEditingLocation(false);
    } else {
      // Request GPS location
      setLoadingLocation(true);
      
      if ("geolocation" in navigator) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true, // Use GPS + WiFi + Cellular for best accuracy
              timeout: 10000,
              maximumAge: 0
            });
          });

          const { latitude, longitude } = position.coords;
          
          // Reverse geocode to get city name
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
            );
            const data = await response.json();
            
            // Extract location components with multiple fallbacks
            const city = data.address.city || 
                        data.address.town || 
                        data.address.village || 
                        data.address.municipality || 
                        data.address.county || "";
            const state = data.address.state || "";
            const stateAbbr = state ? getStateAbbreviation(state) : "";
            
            // Always prefer city, state format with abbreviation
            let locationString;
            if (city && stateAbbr) {
              locationString = `${city}, ${stateAbbr}`;
            } else if (city) {
              locationString = city;
            } else if (stateAbbr) {
              locationString = stateAbbr;
            } else {
              // If all else fails, use a generic message
              locationString = "Location detected";
              alert("We detected your location but couldn't determine the city name. Please enter it manually.");
            }
            
            setUserLocation(locationString);
            localStorage.setItem("soul_user_location", locationString);
            saveDatingPreferences({ user_location: locationString });
          } catch (geocodeError) {
            alert("Unable to determine city name from your location. Please enter it manually.");
            console.error("Geocoding error:", geocodeError);
          }
          
          setIsEditingLocation(true);
        } catch (error) {
          console.error("Geolocation error:", error);
          // On failure, just open text edit so user can type location manually
          setIsEditingLocation(true);
        }
      } else {
        // Geolocation not supported — just allow manual text entry
        setIsEditingLocation(true);
      }
      
      setLoadingLocation(false);
    }
  };

  return (
    <div style={{ maxWidth: maxW, margin: "0 auto", padding: containerPad }}>
      {/* Header */}
      <PageHeader
        label="SETTINGS"
        title="Preferences"
        description="Manage your dating preferences and account settings"
        gradientAnimation
      />

      {/* Dating Preferences Panel */}
      <div style={{ marginBottom: bp.isDesktop ? 32 : 24 }}>
        <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 16 }}>DATING PREFERENCES</div>
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: bp.isDesktop ? 24 : 20 }}>
          
          {/* User Location */}
          <div style={{ marginBottom: bp.isDesktop ? 32 : 28 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 8 }}>User Location</div>
            <div style={{ display: "flex", gap: 12 }}>
              <input 
                type="text" 
                value={userLocation}
                onChange={e => setUserLocation(e.target.value)}
                readOnly={!isEditingLocation}
                style={{ 
                  flex: 1, 
                  padding: "12px 16px", 
                  borderRadius: 6, 
                  background: isEditingLocation ? T.bg : T.dim, 
                  border: `1px solid ${T.border}`, 
                  color: T.text, 
                  fontSize: 15, 
                  fontFamily: "inherit",
                  cursor: isEditingLocation ? "text" : "not-allowed",
                  opacity: isEditingLocation ? 1 : 0.7
                }} />
              <button 
                onClick={handleEditLocation}
                disabled={loadingLocation}
                style={{ 
                  padding: "12px 20px", 
                  borderRadius: 6, 
                  background: loadingLocation ? T.muted : isEditingLocation ? T.teal : T.rose, 
                  color: "#fff", 
                  fontSize: 15, 
                  fontWeight: 600, 
                  border: "none", 
                  cursor: loadingLocation ? "wait" : "pointer", 
                  fontFamily: "inherit",
                  minWidth: 80,
                  opacity: loadingLocation ? 0.6 : 1
                }}>
                {loadingLocation ? "..." : isEditingLocation ? "Save" : "Edit"}
              </button>
            </div>
            {loadingLocation && (
              <div style={{ fontSize: 13, color: T.muted, marginTop: 8 }}>
                📍 Requesting location access...
              </div>
            )}
          </div>

          {/* Age Dating Bracket */}
          <div style={{ marginBottom: bp.isDesktop ? 32 : 28 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 8 }}>Age Dating Bracket</div>
            <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontSize: 13, color: T.muted }}>18</span>
                  <span style={{ fontSize: 15, fontWeight: 600, color: T.rose }}>
                    {ageRange.min} - {ageRange.max} years
                  </span>
                  <span style={{ fontSize: 13, color: T.muted }}>99</span>
                </div>
                
                {/* Dual-handle slider */}
                <div style={{ position: "relative", height: "40px", margin: "0 8px" }}>
                  {/* Track */}
                  <div style={{ 
                    position: "absolute", 
                    top: "50%", 
                    left: 0, 
                    right: 0, 
                    height: "6px", 
                    background: T.dim, 
                    borderRadius: "3px", 
                    transform: "translateY(-50%)" 
                  }} />
                  
                  {/* Active range */}
                  <div style={{ 
                    position: "absolute", 
                    top: "50%", 
                    left: `${((ageRange.min - 18) / (99 - 18)) * 100}%`, 
                    width: `${((ageRange.max - ageRange.min) / (99 - 18)) * 100}%`, 
                    height: "6px", 
                    background: T.rose, 
                    borderRadius: "3px", 
                    transform: "translateY(-50%)" 
                  }} />
                  
                  {/* Min handle */}
                  <div
                    style={{ 
                      position: "absolute", 
                      top: "50%", 
                      left: `${((ageRange.min - 18) / (99 - 18)) * 100}%`, 
                      width: "24px", 
                      height: "24px", 
                      background: "#fff", 
                      border: `3px solid ${T.rose}`, 
                      borderRadius: "50%", 
                      transform: "translate(-50%, -50%)", 
                      cursor: "grab", 
                      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                      transition: "box-shadow 0.2s"
                    }}
                    onMouseDown={(e) => {
                      const startX = e.clientX;
                      const startMin = ageRange.min;
                      const sliderWidth = e.currentTarget.parentElement.offsetWidth;
                      
                      const handleMouseMove = (moveEvent) => {
                        const deltaX = moveEvent.clientX - startX;
                        const deltaPercent = (deltaX / sliderWidth) * (99 - 18);
                        const newMin = Math.max(18, Math.min(ageRange.max - 1, Math.round(startMin + deltaPercent)));
                        setAgeRange(prev => ({ ...prev, min: newMin }));
                      };
                      
                      const handleMouseUp = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                      };
                      
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      const startX = e.touches[0].clientX;
                      const startMin = ageRange.min;
                      const sliderWidth = e.currentTarget.parentElement.offsetWidth;
                      
                      const handleTouchMove = (moveEvent) => {
                        const deltaX = moveEvent.touches[0].clientX - startX;
                        const deltaPercent = (deltaX / sliderWidth) * (99 - 18);
                        const newMin = Math.max(18, Math.min(ageRange.max - 1, Math.round(startMin + deltaPercent)));
                        setAgeRange(prev => ({ ...prev, min: newMin }));
                      };
                      
                      const handleTouchEnd = () => {
                        document.removeEventListener('touchmove', handleTouchMove);
                        document.removeEventListener('touchend', handleTouchEnd);
                      };
                      
                      document.addEventListener('touchmove', handleTouchMove, { passive: false });
                      document.addEventListener('touchend', handleTouchEnd);
                    }}
                  />
                  
                  {/* Max handle */}
                  <div
                    style={{ 
                      position: "absolute", 
                      top: "50%", 
                      left: `${((ageRange.max - 18) / (99 - 18)) * 100}%`, 
                      width: "24px", 
                      height: "24px", 
                      background: "#fff", 
                      border: `3px solid ${T.rose}`, 
                      borderRadius: "50%", 
                      transform: "translate(-50%, -50%)", 
                      cursor: "grab", 
                      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                      transition: "box-shadow 0.2s"
                    }}
                    onMouseDown={(e) => {
                      const startX = e.clientX;
                      const startMax = ageRange.max;
                      const sliderWidth = e.currentTarget.parentElement.offsetWidth;
                      
                      const handleMouseMove = (moveEvent) => {
                        const deltaX = moveEvent.clientX - startX;
                        const deltaPercent = (deltaX / sliderWidth) * (99 - 18);
                        const newMax = Math.min(99, Math.max(ageRange.min + 1, Math.round(startMax + deltaPercent)));
                        setAgeRange(prev => ({ ...prev, max: newMax }));
                      };
                      
                      const handleMouseUp = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                      };
                      
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      const startX = e.touches[0].clientX;
                      const startMax = ageRange.max;
                      const sliderWidth = e.currentTarget.parentElement.offsetWidth;
                      
                      const handleTouchMove = (moveEvent) => {
                        const deltaX = moveEvent.touches[0].clientX - startX;
                        const deltaPercent = (deltaX / sliderWidth) * (99 - 18);
                        const newMax = Math.min(99, Math.max(ageRange.min + 1, Math.round(startMax + deltaPercent)));
                        setAgeRange(prev => ({ ...prev, max: newMax }));
                      };
                      
                      const handleTouchEnd = () => {
                        document.removeEventListener('touchmove', handleTouchMove);
                        document.removeEventListener('touchend', handleTouchEnd);
                      };
                      
                      document.addEventListener('touchmove', handleTouchMove, { passive: false });
                      document.addEventListener('touchend', handleTouchEnd);
                    }}
                  />
                </div>
            </div>
          </div>

          {/* Distance Willing to Travel */}
          <div style={{ marginBottom: bp.isDesktop ? 32 : 28 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 8 }}>Distance Willing to Travel</div>
            <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontSize: 13, color: T.muted }}>1 mile</span>
                  <span style={{ fontSize: 15, fontWeight: 600, color: T.rose }}>
                    {maxDistance} miles
                  </span>
                  <span style={{ fontSize: 13, color: T.muted }}>200 miles</span>
                </div>
                
                {/* Range slider */}
                <div style={{ position: "relative", height: "40px", margin: "0 8px" }}>
                  {/* Track */}
                  <div style={{ 
                    position: "absolute", 
                    top: "50%", 
                    left: 0, 
                    right: 0, 
                    height: "6px", 
                    background: T.dim, 
                    borderRadius: "3px", 
                    transform: "translateY(-50%)" 
                  }} />
                  
                  {/* Active range */}
                  <div style={{ 
                    position: "absolute", 
                    top: "50%", 
                    left: 0, 
                    width: `${((maxDistance - 1) / (200 - 1)) * 100}%`, 
                    height: "6px", 
                    background: T.rose, 
                    borderRadius: "3px", 
                    transform: "translateY(-50%)" 
                  }} />
                  
                  {/* Handle */}
                  <div
                    style={{ 
                      position: "absolute", 
                      top: "50%", 
                      left: `${((maxDistance - 1) / (200 - 1)) * 100}%`, 
                      width: "24px", 
                      height: "24px", 
                      background: "#fff", 
                      border: `3px solid ${T.rose}`, 
                      borderRadius: "50%", 
                      transform: "translate(-50%, -50%)", 
                      cursor: "grab", 
                      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                      transition: "box-shadow 0.2s"
                    }}
                    onMouseDown={(e) => {
                      const startX = e.clientX;
                      const startDistance = maxDistance;
                      const sliderWidth = e.currentTarget.parentElement.offsetWidth;
                      
                      const handleMouseMove = (moveEvent) => {
                        const deltaX = moveEvent.clientX - startX;
                        const deltaPercent = (deltaX / sliderWidth) * (200 - 1);
                        const newDistance = Math.max(1, Math.min(200, Math.round(startDistance + deltaPercent)));
                        setMaxDistance(newDistance);
                      };
                      
                      const handleMouseUp = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                      };
                      
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      const startX = e.touches[0].clientX;
                      const startDistance = maxDistance;
                      const sliderWidth = e.currentTarget.parentElement.offsetWidth;
                      
                      const handleTouchMove = (moveEvent) => {
                        const deltaX = moveEvent.touches[0].clientX - startX;
                        const deltaPercent = (deltaX / sliderWidth) * (200 - 1);
                        const newDistance = Math.max(1, Math.min(200, Math.round(startDistance + deltaPercent)));
                        setMaxDistance(newDistance);
                      };
                      
                      const handleTouchEnd = () => {
                        document.removeEventListener('touchmove', handleTouchMove);
                        document.removeEventListener('touchend', handleTouchEnd);
                      };
                      
                      document.addEventListener('touchmove', handleTouchMove, { passive: false });
                      document.addEventListener('touchend', handleTouchEnd);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Personality Snapshot */}
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 8 }}>Personality Snapshot</div>
            <div style={{ padding: "16px", borderRadius: 6, background: T.bg, border: `1px solid ${T.border}` }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {["Creative", "Adventurous", "Empathetic", "Intellectual", "Ambitious", "Playful"].map(trait => (
                  <span key={trait} style={{ 
                    fontSize: 13, 
                    padding: "4px 12px", 
                    borderRadius: 8, 
                    background: `${T.rose}15`, 
                    color: T.rose, 
                    fontWeight: 600 
                  }}>
                    {trait}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Appearance Panel */}
      <div style={{ marginBottom: bp.isDesktop ? 32 : 24 }}>
        <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 16 }}>APPEARANCE</div>
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: bp.isDesktop ? 24 : 20 }}>
          
          {/* Dark/Light Mode Toggle */}
          <div style={{ marginBottom: bp.isDesktop ? 28 : 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 2 }}>Theme Mode</div>
                <div style={{ fontSize: 13, color: T.muted }}>{isDarkMode ? "Dark" : "Light"} mode</div>
              </div>
              <button onClick={toggleTheme}
                style={{ width: 56, height: 30, borderRadius: 15, background: isDarkMode ? T.violet : T.dim, border: "none", cursor: "pointer", position: "relative", transition: "all 0.3s" }}>
                <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: isDarkMode ? 29 : 3, transition: "all 0.3s", boxShadow: "0 2px 4px rgba(0,0,0,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>
                  {isDarkMode ? "🌙" : "☀️"}
                </div>
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* Notifications Panel */}
      <div style={{ marginBottom: bp.isDesktop ? 32 : 24 }}>
        <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 16 }}>NOTIFICATIONS</div>
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: bp.isDesktop ? 24 : 20 }}>
          
          {/* Match Alert */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 2 }}>Match Alert</div>
                <div style={{ fontSize: 13, color: T.muted }}>Get notified when you have a new match</div>
              </div>
              <button 
                onClick={() => setNotifications(prev => ({ ...prev, matchAlert: !prev.matchAlert }))}
                style={{ 
                  width: 48, 
                  height: 24, 
                  borderRadius: 6, 
                  background: notifications.matchAlert ? T.rose : T.dim, 
                  border: "none", 
                  cursor: "pointer", 
                  position: "relative", 
                  transition: "all 0.3s" 
                }}>
                <div style={{ 
                  width: 20, 
                  height: 20, 
                  borderRadius: "50%", 
                  background: "#fff", 
                  position: "absolute", 
                  top: 2, 
                  left: notifications.matchAlert ? 26 : 2, 
                  transition: "all 0.3s", 
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)" 
                }} />
              </button>
            </div>
          </div>

          {/* Date Alert */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 2 }}>Date Alert</div>
                <div style={{ fontSize: 13, color: T.muted }}>Get notified about upcoming dates</div>
              </div>
              <button 
                onClick={() => setNotifications(prev => ({ ...prev, dateAlert: !prev.dateAlert }))}
                style={{ 
                  width: 48, 
                  height: 24, 
                  borderRadius: 6, 
                  background: notifications.dateAlert ? T.rose : T.dim, 
                  border: "none", 
                  cursor: "pointer", 
                  position: "relative", 
                  transition: "all 0.3s" 
                }}>
                <div style={{ 
                  width: 20, 
                  height: 20, 
                  borderRadius: "50%", 
                  background: "#fff", 
                  position: "absolute", 
                  top: 2, 
                  left: notifications.dateAlert ? 26 : 2, 
                  transition: "all 0.3s", 
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)" 
                }} />
              </button>
            </div>
          </div>

          {/* Feedback Alert */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 2 }}>Feedback Alert</div>
                <div style={{ fontSize: 13, color: T.muted }}>Get notified when you receive feedback</div>
              </div>
              <button 
                onClick={() => setNotifications(prev => ({ ...prev, feedbackAlert: !prev.feedbackAlert }))}
                style={{ 
                  width: 48, 
                  height: 24, 
                  borderRadius: 6, 
                  background: notifications.feedbackAlert ? T.rose : T.dim, 
                  border: "none", 
                  cursor: "pointer", 
                  position: "relative", 
                  transition: "all 0.3s" 
                }}>
                <div style={{ 
                  width: 20, 
                  height: 20, 
                  borderRadius: "50%", 
                  background: "#fff", 
                  position: "absolute", 
                  top: 2, 
                  left: notifications.feedbackAlert ? 26 : 2, 
                  transition: "all 0.3s", 
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)" 
                }} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Account Panel */}
      <div>
        <div style={{ fontSize: bp.isDesktop ? 12 : 13, color: T.muted, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 16 }}>ACCOUNT</div>
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: bp.isDesktop ? 24 : 20 }}>
          
          {/* Name */}
          <div style={{ marginBottom: 20, paddingBottom: 20, borderBottom: `1px solid ${T.border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 2 }}>Name</div>
                <div style={{ fontSize: 14, color: T.muted }}>{accountName}</div>
              </div>
            </div>
          </div>

          {/* Email */}
          <div style={{ marginBottom: 20, paddingBottom: 20, borderBottom: `1px solid ${T.border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 2 }}>Email</div>
                <div style={{ fontSize: 14, color: T.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{accountEmail || "Not set"}</div>
              </div>
              <button 
                onClick={() => { setIsEditingEmail(!isEditingEmail); setNewEmail(""); }}
                style={{ 
                  padding: "8px 16px", 
                  borderRadius: 6, 
                  background: isEditingEmail ? T.teal : "transparent", 
                  border: isEditingEmail ? "none" : `1px solid ${T.border}`, 
                  color: isEditingEmail ? "#fff" : T.text, 
                  fontSize: 13, 
                  fontWeight: 600, 
                  cursor: "pointer", 
                  fontFamily: "inherit",
                  flexShrink: 0,
                  marginLeft: 12
                }}>
                {isEditingEmail ? "Cancel" : "Change"}
              </button>
            </div>
            {isEditingEmail && (
              <div style={{ marginTop: 12, display: "flex", gap: 10 }}>
                <input 
                  type="email"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  placeholder="Enter new email address"
                  style={{ 
                    flex: 1, 
                    padding: "10px 14px", 
                    borderRadius: 6, 
                    background: T.bg, 
                    border: `1px solid ${T.border}`, 
                    color: T.text, 
                    fontSize: 14, 
                    fontFamily: "inherit" 
                  }} 
                />
                <button 
                  onClick={() => {
                    if (newEmail.trim() && newEmail.includes("@")) {
                      setAccountEmail(newEmail.trim());
                      try {
                        const u = JSON.parse(localStorage.getItem("soul_user") || "{}");
                        u.email = newEmail.trim();
                        localStorage.setItem("soul_user", JSON.stringify(u));
                      } catch {}
                      setIsEditingEmail(false);
                      setNewEmail("");
                    }
                  }}
                  disabled={!newEmail.trim() || !newEmail.includes("@")}
                  style={{ 
                    padding: "10px 18px", 
                    borderRadius: 6, 
                    background: newEmail.trim() && newEmail.includes("@") ? T.rose : T.dim, 
                    color: "#fff", 
                    fontSize: 13, 
                    fontWeight: 600, 
                    border: "none", 
                    cursor: newEmail.trim() && newEmail.includes("@") ? "pointer" : "not-allowed", 
                    fontFamily: "inherit",
                    flexShrink: 0
                  }}>
                  Save
                </button>
              </div>
            )}
          </div>

          {/* Phone Number */}
          <div style={{ marginBottom: 20, paddingBottom: 20, borderBottom: `1px solid ${T.border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 2 }}>Phone Number</div>
                <div style={{ fontSize: 14, color: T.muted, fontFamily: "'Segoe UI', monospace" }}>{accountPhone ? maskPhone(accountPhone) : "Not set"}</div>
              </div>
              <button 
                onClick={() => { setIsEditingPhone(!isEditingPhone); setNewPhone(""); }}
                style={{ 
                  padding: "8px 16px", 
                  borderRadius: 6, 
                  background: isEditingPhone ? T.teal : "transparent", 
                  border: isEditingPhone ? "none" : `1px solid ${T.border}`, 
                  color: isEditingPhone ? "#fff" : T.text, 
                  fontSize: 13, 
                  fontWeight: 600, 
                  cursor: "pointer", 
                  fontFamily: "inherit",
                  flexShrink: 0,
                  marginLeft: 12
                }}>
                {isEditingPhone ? "Cancel" : "Change"}
              </button>
            </div>
            {isEditingPhone && (
              <div style={{ marginTop: 12, display: "flex", gap: 10 }}>
                <input 
                  type="tel"
                  value={newPhone}
                  onChange={e => {
                    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                    let formatted = digits;
                    if (digits.length > 6) formatted = `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`;
                    else if (digits.length > 3) formatted = `${digits.slice(0,3)}-${digits.slice(3)}`;
                    setNewPhone(formatted);
                  }}
                  placeholder="555-555-5555"
                  style={{ 
                    flex: 1, 
                    padding: "10px 14px", 
                    borderRadius: 6, 
                    background: T.bg, 
                    border: `1px solid ${T.border}`, 
                    color: T.text, 
                    fontSize: 14, 
                    fontFamily: "inherit" 
                  }} 
                />
                <button 
                  onClick={() => {
                    const digits = newPhone.replace(/\D/g, "");
                    if (digits.length >= 10) {
                      setAccountPhone(digits);
                      try {
                        const u = JSON.parse(localStorage.getItem("soul_user") || "{}");
                        u.phone = digits;
                        localStorage.setItem("soul_user", JSON.stringify(u));
                      } catch {}
                      // Sync phone to server
                      try {
                        const tk = localStorage.getItem("soul_session_token");
                        if (tk) {
                          fetch(`${API_BASE}/api/user/phone`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` }, body: JSON.stringify({ phone: digits }) })
                            .then(r => { console.log("📱 Phone save response:", r.status); return r.json(); })
                            .then(d => console.log("📱 Phone save result:", d))
                            .catch(e => console.error("📱 Phone save error:", e));
                        } else { console.warn("📱 No token, cannot sync phone"); }
                      } catch (e) { console.error("📱 Phone sync outer error:", e); }
                      setIsEditingPhone(false);
                      setNewPhone("");
                    }
                  }}
                  disabled={newPhone.replace(/\D/g, "").length < 10}
                  style={{ 
                    padding: "10px 18px", 
                    borderRadius: 6, 
                    background: newPhone.replace(/\D/g, "").length >= 10 ? T.rose : T.dim, 
                    color: "#fff", 
                    fontSize: 13, 
                    fontWeight: 600, 
                    border: "none", 
                    cursor: newPhone.replace(/\D/g, "").length >= 10 ? "pointer" : "not-allowed", 
                    fontFamily: "inherit",
                    flexShrink: 0
                  }}>
                  Save
                </button>
              </div>
            )}
          </div>

          {/* Payments */}
          <div style={{ marginBottom: 20, paddingBottom: 20, borderBottom: `1px solid ${T.border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 2 }}>Payments</div>
                <div style={{ fontSize: 13, color: T.muted }}>Manage payment methods and subscriptions</div>
              </div>
              <button 
                onClick={() => setShowPaymentForm(!showPaymentForm)}
                style={{ 
                  padding: "8px 16px", 
                  borderRadius: 6, 
                  background: showPaymentForm ? T.teal : T.rose, 
                  color: "#fff", 
                  fontSize: 13, 
                  fontWeight: 600, 
                  border: "none", 
                  cursor: "pointer", 
                  fontFamily: "inherit" 
                }}>
                {showPaymentForm ? "Close" : "Manage"}
              </button>
            </div>
            
            {/* Payment Details Form */}
            {showPaymentForm && (
              <div style={{ marginTop: 20, padding: 20, borderRadius: 6, background: T.bg, border: `1px solid ${T.border}`, animation: "fadeIn 0.3s ease" }}>
                <h3 style={{ fontSize: 17, fontWeight: 700, color: T.text, marginBottom: 16, fontFamily: "'Segoe UI', sans-serif" }}>Payment Details</h3>
                
                {/* Credit Card Information */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <div style={{ fontSize: 13, color: T.muted, fontWeight: 700, letterSpacing: "0.05em" }}>CREDIT CARD</div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      {/* Visa */}
                      <div style={{ width: 40, height: 24, borderRadius: 4, background: "#1434CB", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#fff", fontFamily: "Arial, sans-serif", letterSpacing: "0.5px" }}>VISA</div>
                      {/* Mastercard */}
                      <div style={{ width: 40, height: 24, borderRadius: 4, background: "#EB001B", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" }}>
                        <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#EB001B", position: "absolute", left: 8 }} />
                        <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#FF5F00", position: "absolute", left: 14 }} />
                        <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#F79E1B", position: "absolute", left: 20 }} />
                      </div>
                      {/* Amex */}
                      <div style={{ width: 40, height: 24, borderRadius: 4, background: "#006FCF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#fff", fontFamily: "Arial, sans-serif" }}>AMEX</div>
                      {/* Discover */}
                      <div style={{ width: 40, height: 24, borderRadius: 4, background: "#FF6000", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 7, fontWeight: 700, color: "#fff", fontFamily: "Arial, sans-serif", letterSpacing: "-0.5px" }}>DISCOVER</div>
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 13, color: T.muted, fontWeight: 600, marginBottom: 4, display: "block" }}>Card Number</label>
                    <input 
                      type="text"
                      value={paymentDetails.cardNumber}
                      onChange={e => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 16);
                        const formatted = value.match(/.{1,4}/g)?.join(' ') || value;
                        setPaymentDetails(prev => ({ ...prev, cardNumber: formatted }));
                      }}
                      placeholder="1234 5678 9012 3456"
                      maxLength="19"
                      style={{ 
                        width: "100%", 
                        padding: "12px 14px", 
                        borderRadius: 6, 
                        background: T.card, 
                        border: `1px solid ${T.border}`, 
                        color: T.text, 
                        fontSize: 15, 
                        fontFamily: "inherit" 
                      }}
                    />
                  </div>
                  
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 13, color: T.muted, fontWeight: 600, marginBottom: 4, display: "block" }}>Cardholder Name</label>
                    <input 
                      type="text"
                      value={paymentDetails.cardName}
                      onChange={e => setPaymentDetails(prev => ({ ...prev, cardName: e.target.value }))}
                      placeholder="John Doe"
                      style={{ 
                        width: "100%", 
                        padding: "12px 14px", 
                        borderRadius: 6, 
                        background: T.card, 
                        border: `1px solid ${T.border}`, 
                        color: T.text, 
                        fontSize: 15, 
                        fontFamily: "inherit" 
                      }}
                    />
                  </div>
                  
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <div>
                      <label style={{ fontSize: 13, color: T.muted, fontWeight: 600, marginBottom: 4, display: "block" }}>Exp. Month</label>
                      <input 
                        type="text"
                        value={paymentDetails.expiryMonth}
                        onChange={e => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                          if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 12)) {
                            setPaymentDetails(prev => ({ ...prev, expiryMonth: value }));
                          }
                        }}
                        placeholder="MM"
                        maxLength="2"
                        style={{ 
                          width: "100%", 
                          padding: "12px 14px", 
                          borderRadius: 6, 
                          background: T.card, 
                          border: `1px solid ${T.border}`, 
                          color: T.text, 
                          fontSize: 15, 
                          fontFamily: "inherit",
                          textAlign: "center"
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 13, color: T.muted, fontWeight: 600, marginBottom: 4, display: "block" }}>Exp. Year</label>
                      <input 
                        type="text"
                        value={paymentDetails.expiryYear}
                        onChange={e => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                          setPaymentDetails(prev => ({ ...prev, expiryYear: value }));
                        }}
                        placeholder="YY"
                        maxLength="2"
                        style={{ 
                          width: "100%", 
                          padding: "12px 14px", 
                          borderRadius: 6, 
                          background: T.card, 
                          border: `1px solid ${T.border}`, 
                          color: T.text, 
                          fontSize: 15, 
                          fontFamily: "inherit",
                          textAlign: "center"
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 13, color: T.muted, fontWeight: 600, marginBottom: 4, display: "block" }}>CVV</label>
                      <input 
                        type="text"
                        value={paymentDetails.cvv}
                        onChange={e => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                          setPaymentDetails(prev => ({ ...prev, cvv: value }));
                        }}
                        placeholder="123"
                        maxLength="4"
                        style={{ 
                          width: "100%", 
                          padding: "12px 14px", 
                          borderRadius: 6, 
                          background: T.card, 
                          border: `1px solid ${T.border}`, 
                          color: T.text, 
                          fontSize: 15, 
                          fontFamily: "inherit",
                          textAlign: "center"
                        }}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Billing Information */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 13, color: T.muted, fontWeight: 700, letterSpacing: "0.05em", marginBottom: 12 }}>BILLING ADDRESS</div>
                  
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 13, color: T.muted, fontWeight: 600, marginBottom: 4, display: "block" }}>Street Address</label>
                    <input 
                      type="text"
                      value={paymentDetails.billingAddress}
                      onChange={e => setPaymentDetails(prev => ({ ...prev, billingAddress: e.target.value }))}
                      placeholder="123 Main Street"
                      style={{ 
                        width: "100%", 
                        padding: "12px 14px", 
                        borderRadius: 6, 
                        background: T.card, 
                        border: `1px solid ${T.border}`, 
                        color: T.text, 
                        fontSize: 15, 
                        fontFamily: "inherit" 
                      }}
                    />
                  </div>
                  
                  <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12 }}>
                    <div>
                      <label style={{ fontSize: 13, color: T.muted, fontWeight: 600, marginBottom: 4, display: "block" }}>City</label>
                      <input 
                        type="text"
                        value={paymentDetails.billingCity}
                        onChange={e => setPaymentDetails(prev => ({ ...prev, billingCity: e.target.value }))}
                        placeholder="Los Angeles"
                        style={{ 
                          width: "100%", 
                          padding: "12px 14px", 
                          borderRadius: 6, 
                          background: T.card, 
                          border: `1px solid ${T.border}`, 
                          color: T.text, 
                          fontSize: 15, 
                          fontFamily: "inherit" 
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 13, color: T.muted, fontWeight: 600, marginBottom: 4, display: "block" }}>State</label>
                      <input 
                        type="text"
                        value={paymentDetails.billingState}
                        onChange={e => {
                          const value = e.target.value.toUpperCase().slice(0, 2);
                          setPaymentDetails(prev => ({ ...prev, billingState: value }));
                        }}
                        placeholder="CA"
                        maxLength="2"
                        style={{ 
                          width: "100%", 
                          padding: "12px 14px", 
                          borderRadius: 6, 
                          background: T.card, 
                          border: `1px solid ${T.border}`, 
                          color: T.text, 
                          fontSize: 15, 
                          fontFamily: "inherit",
                          textAlign: "center"
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 13, color: T.muted, fontWeight: 600, marginBottom: 4, display: "block" }}>ZIP</label>
                      <input 
                        type="text"
                        value={paymentDetails.billingZip}
                        onChange={e => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                          setPaymentDetails(prev => ({ ...prev, billingZip: value }));
                        }}
                        placeholder="90001"
                        maxLength="5"
                        style={{ 
                          width: "100%", 
                          padding: "12px 14px", 
                          borderRadius: 6, 
                          background: T.card, 
                          border: `1px solid ${T.border}`, 
                          color: T.text, 
                          fontSize: 15, 
                          fontFamily: "inherit",
                          textAlign: "center"
                        }}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Save Button */}
                <div style={{ display: "flex", gap: 12 }}>
                  <button 
                    onClick={() => {
                      // In production: call API to save payment details
                      alert("Payment details saved successfully!");
                      setShowPaymentForm(false);
                    }}
                    disabled={!paymentDetails.cardNumber || !paymentDetails.cardName || !paymentDetails.expiryMonth || !paymentDetails.expiryYear || !paymentDetails.cvv}
                    style={{ 
                      flex: 1,
                      padding: "14px", 
                      borderRadius: 6, 
                      background: paymentDetails.cardNumber && paymentDetails.cardName && paymentDetails.expiryMonth && paymentDetails.expiryYear && paymentDetails.cvv ? `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})` : T.dim, 
                      color: "#fff", 
                      fontSize: 15, 
                      fontWeight: 700, 
                      border: "none", 
                      cursor: paymentDetails.cardNumber && paymentDetails.cardName && paymentDetails.expiryMonth && paymentDetails.expiryYear && paymentDetails.cvv ? "pointer" : "not-allowed", 
                      fontFamily: "inherit" 
                    }}>
                    💳 Save Payment Details
                  </button>
                </div>
                
                <div style={{ marginTop: 12, fontSize: 13, color: T.muted, textAlign: "center" }}>
                  🔒 Your payment information is encrypted and secure
                </div>
              </div>
            )}
          </div>

          {/* Subscription / Upgrade Panel */}
          <div style={{ marginBottom: 20, paddingBottom: 20, borderBottom: `1px solid ${T.border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 2 }}>Subscription</div>
                <div style={{ fontSize: 13, color: T.muted }}>
                  Current plan: <strong style={{ color: currentIsPremium ? T.rose : T.text }}>{currentTier === "annual" ? "Premium Annual" : currentTier === "monthly" ? "Premium Monthly" : "Free"}</strong>
                </div>
              </div>
              <button 
                onClick={() => setShowUpgradePanel(!showUpgradePanel)}
                style={{ 
                  padding: "8px 16px", 
                  borderRadius: 6, 
                  background: showUpgradePanel ? T.teal : currentIsPremium ? "transparent" : `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, 
                  color: showUpgradePanel ? "#fff" : currentIsPremium ? T.text : "#fff", 
                  fontSize: 13, 
                  fontWeight: 600, 
                  border: currentIsPremium && !showUpgradePanel ? `1px solid ${T.border}` : "none", 
                  cursor: "pointer", 
                  fontFamily: "inherit",
                  animation: !currentIsPremium && !showUpgradePanel ? "upgradePulse 2s ease-in-out infinite" : "none"
                }}>
                {showUpgradePanel ? "Close" : currentIsPremium ? "Manage Plan" : "Upgrade"}
              </button>
            </div>
            
            {showUpgradePanel && (
              <div style={{ marginTop: 20, animation: "fadeIn 0.3s ease" }}>
                {/* Pricing Cards */}
                <div style={{ display: "grid", gridTemplateColumns: bp.isDesktop ? "repeat(3, 1fr)" : "1fr", gap: 16, marginBottom: selectedPlan ? 20 : 0 }}>
                  {/* Free Plan */}
                  <div onClick={() => { if (currentTier !== "free") { setSelectedPlan("free"); } }}
                    style={{ 
                      padding: bp.isDesktop ? 24 : 18, 
                      borderRadius: 8, 
                      border: `2px solid ${currentTier === "free" ? T.teal : selectedPlan === "free" ? T.rose : T.border}`, 
                      background: currentTier === "free" ? `${T.teal}08` : T.bg,
                      cursor: currentTier === "free" ? "default" : "pointer",
                      transition: "all 0.2s",
                      position: "relative"
                    }}>
                    {currentTier === "free" && (
                      <div style={{ position: "absolute", top: -10, right: 12, background: T.teal, color: "#fff", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 4, letterSpacing: "0.05em" }}>CURRENT</div>
                    )}
                    <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 800, color: T.text, marginBottom: 4 }}>Free</div>
                    <div style={{ fontSize: bp.isDesktop ? 28 : 24, fontWeight: 900, color: T.text, fontFamily: "'Segoe UI', sans-serif", marginBottom: 12 }}>$0</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <div style={{ fontSize: 13, color: T.text, display: "flex", alignItems: "center", gap: 6 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                        Send emoji reaction gifts
                      </div>
                      <div style={{ fontSize: 13, color: T.text, display: "flex", alignItems: "center", gap: 6 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                        Create and send polls
                      </div>
                      <div style={{ fontSize: 13, color: T.text, display: "flex", alignItems: "center", gap: 6 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                        AI-powered matching
                      </div>
                      <div style={{ fontSize: 13, color: T.muted, display: "flex", alignItems: "center", gap: 6 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        Share phone number
                      </div>
                      <div style={{ fontSize: 13, color: T.muted, display: "flex", alignItems: "center", gap: 6 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        Chat and send images
                      </div>
                      <div style={{ fontSize: 13, color: T.muted, display: "flex", alignItems: "center", gap: 6 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        In-app calling
                      </div>
                    </div>
                  </div>

                  {/* Monthly Premium */}
                  <div onClick={() => { if (currentTier !== "monthly") { setSelectedPlan("monthly"); } }}
                    style={{ 
                      padding: bp.isDesktop ? 24 : 18, 
                      borderRadius: 8, 
                      border: `2px solid ${currentTier === "monthly" ? T.teal : selectedPlan === "monthly" ? T.rose : T.rose + "40"}`, 
                      background: currentTier === "monthly" ? `${T.teal}08` : `linear-gradient(135deg, ${T.rose}06, ${T.violet}06)`,
                      cursor: currentTier === "monthly" ? "default" : "pointer",
                      transition: "all 0.2s",
                      position: "relative"
                    }}>
                    {currentTier === "monthly" ? (
                      <div style={{ position: "absolute", top: -10, right: 12, background: T.teal, color: "#fff", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 4, letterSpacing: "0.05em" }}>CURRENT</div>
                    ) : (
                      <div style={{ position: "absolute", top: -10, right: 12, background: T.rose, color: "#fff", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 4, letterSpacing: "0.05em" }}>POPULAR</div>
                    )}
                    <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 800, color: T.text, marginBottom: 4 }}>Premium Monthly</div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 2, marginBottom: 12 }}>
                      <span style={{ fontSize: bp.isDesktop ? 28 : 24, fontWeight: 900, color: T.rose, fontFamily: "'Segoe UI', sans-serif" }}>$19.99</span>
                      <span style={{ fontSize: 13, color: T.muted }}>/month</span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {["Send emoji reaction gifts", "Create and send polls", "AI-powered matching", "Share phone number", "Chat and send images", "In-app calling"].map(f => (
                        <div key={f} style={{ fontSize: 13, color: T.text, display: "flex", alignItems: "center", gap: 6 }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                          {f}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Annual Premium */}
                  <div onClick={() => { if (currentTier !== "annual") { setSelectedPlan("annual"); } }}
                    style={{ 
                      padding: bp.isDesktop ? 24 : 18, 
                      borderRadius: 8, 
                      border: `2px solid ${currentTier === "annual" ? T.teal : selectedPlan === "annual" ? T.rose : T.border}`, 
                      background: currentTier === "annual" ? `${T.teal}08` : T.bg,
                      cursor: currentTier === "annual" ? "default" : "pointer",
                      transition: "all 0.2s",
                      position: "relative"
                    }}>
                    {currentTier === "annual" ? (
                      <div style={{ position: "absolute", top: -10, right: 12, background: T.teal, color: "#fff", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 4, letterSpacing: "0.05em" }}>CURRENT</div>
                    ) : (
                      <div style={{ position: "absolute", top: -10, right: 12, background: "#22c55e", color: "#fff", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 4, letterSpacing: "0.05em" }}>BEST VALUE</div>
                    )}
                    <div style={{ fontSize: bp.isDesktop ? 18 : 17, fontWeight: 800, color: T.text, marginBottom: 4 }}>Premium Annual</div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 2, marginBottom: 4 }}>
                      <span style={{ fontSize: bp.isDesktop ? 28 : 24, fontWeight: 900, color: T.rose, fontFamily: "'Segoe UI', sans-serif" }}>$100</span>
                      <span style={{ fontSize: 13, color: T.muted }}>/year</span>
                    </div>
                    <div style={{ fontSize: 13, color: "#22c55e", fontWeight: 700, marginBottom: 12 }}>Save 58% vs monthly</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {["Send emoji reaction gifts", "Create and send polls", "AI-powered matching", "Share phone number", "Chat and send images", "In-app calling"].map(f => (
                        <div key={f} style={{ fontSize: 13, color: T.text, display: "flex", alignItems: "center", gap: 6 }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                          {f}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Selected Plan Confirmation */}
                {selectedPlan && (
                  <div style={{ padding: bp.isDesktop ? 20 : 16, borderRadius: 8, background: `${T.rose}08`, border: `1px solid ${T.rose}25`, animation: "fadeIn 0.3s ease" }}>
                    <div style={{ fontSize: bp.isDesktop ? 15 : 15, fontWeight: 700, color: T.text, marginBottom: 8 }}>
                      {selectedPlan === "free" ? "Downgrade to Free" : `Upgrade to ${TIERS[selectedPlan].label}`}
                    </div>
                    <div style={{ fontSize: 14, color: T.muted, marginBottom: 16 }}>
                      {selectedPlan === "free" 
                        ? "You will lose access to premium features. Your matches and data will be preserved." 
                        : `You'll be charged ${selectedPlan === "monthly" ? "$19.99/month" : "$100/year"} starting today. Cancel anytime.`}
                    </div>
                    {selectedPlan !== "free" && !showPaymentForm && (
                      <div style={{ fontSize: 13, color: T.muted, marginBottom: 12 }}>
                        Enter your payment details above to complete the upgrade, or:
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 10 }}>
                      <button onClick={() => setSelectedPlan(null)}
                        style={{ flex: 1, padding: "12px", borderRadius: 6, background: "transparent", border: `1px solid ${T.border}`, color: T.muted, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                        Cancel
                      </button>
                      <button onClick={() => handleUpgrade(selectedPlan)}
                        style={{ flex: 2, padding: "12px", borderRadius: 6, background: `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})`, border: "none", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                        {selectedPlan === "free" ? "Confirm Downgrade" : "Confirm & Subscribe"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Privacy Policy */}
          <div style={{ marginBottom: 20, paddingBottom: 20, borderBottom: `1px solid ${T.border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 2 }}>Privacy Policy</div>
                <div style={{ fontSize: 13, color: T.muted }}>How we protect your data and privacy</div>
              </div>
              <button 
                style={{ 
                  padding: "8px 16px", 
                  borderRadius: 6, 
                  background: "transparent", 
                  border: `1px solid ${T.border}`, 
                  color: T.text, 
                  fontSize: 13, 
                  fontWeight: 600, 
                  cursor: "pointer", 
                  fontFamily: "inherit" 
                }}>
                View
              </button>
            </div>
          </div>

          {/* Terms of Service */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 2 }}>Terms of Service</div>
                <div style={{ fontSize: 13, color: T.muted }}>Rules and guidelines for using settup</div>
              </div>
              <button 
                style={{ 
                  padding: "8px 16px", 
                  borderRadius: 6, 
                  background: "transparent", 
                  border: `1px solid ${T.border}`, 
                  color: T.text, 
                  fontSize: 13, 
                  fontWeight: 600, 
                  cursor: "pointer", 
                  fontFamily: "inherit" 
                }}>
                View
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Log Out Button */}
      <div style={{ marginTop: bp.isDesktop ? 32 : 24 }}>
        <button 
          onClick={() => {
            localStorage.removeItem("soul_session_token");
            localStorage.removeItem("soul_user");
            localStorage.removeItem("soul_completed_stages");
            window.location.href = "/";
          }}
          style={{ 
            width: "100%", 
            padding: bp.isDesktop ? "16px" : "14px", 
            borderRadius: 6, 
            background: "transparent", 
            color: "#ef4444", 
            fontSize: bp.isDesktop ? 15 : 15, 
            fontWeight: 700, 
            border: "2px solid rgba(239,68,68,0.3)", 
            cursor: "pointer", 
            fontFamily: "inherit", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            gap: 8, 
            transition: "all 0.2s" 
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(239,68,68,0.08)"; e.currentTarget.style.borderColor = "rgba(239,68,68,0.6)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "rgba(239,68,68,0.3)"; }}>
          ↩ Log Out
        </button>
      </div>
    </div>
  );
}

// ─── App Root ─────────────────────────────────────────────
export default function App() {
  const [screen, setScreenRaw] = useState(() => {
    const p = getStoredSession();
    if (!p) return "landing";
    if (p.isAdmin) return "admin";
    return "profile";
  });
  const setScreen = (s) => { window.scrollTo(0, 0); setScreenRaw(s); };
  const [match, setMatch] = useState(null);
  const [apiMatches, setApiMatches] = useState(null);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [stage1Answers, setStage1Answers] = useState(null);
  const [personalityProfile, setPersonalityProfile] = useState(null);
  const [blindMode, setBlindMode] = useState(() => {
    try {
      const appSettings = JSON.parse(localStorage.getItem('app_settings') || '{}');
      // Default ON unless explicitly set to "false"
      if (appSettings.blind_match_mode === "false") return false;
      return true;
    } catch {
      return true;
    }
  });
  const [upcomingDates, setUpcomingDates] = useState([]);
  const [appSettings, setAppSettings] = useState({});
  const [completedStage, setCompletedStage] = useState(() => {
    try {
      const u = localStorage.getItem("soul_user");
      if (u) { const parsed = JSON.parse(u); return parsed.profile_stage || 0; }
    } catch {}
    return 0;
  });
  const [completedStages, setCompletedStages] = useState(() => {
    try {
      const saved = localStorage.getItem("soul_completed_stages");
      if (saved) return new Set(JSON.parse(saved));
      // Backward compat: derive from profile_stage number
      const u = localStorage.getItem("soul_user");
      if (u) {
        const parsed = JSON.parse(u);
        const stage = parsed.profile_stage || 0;
        const s = new Set();
        for (let i = 1; i <= stage; i++) s.add(i);
        return s;
      }
    } catch {}
    return new Set();
  });
  const [startingStage, setStartingStage] = useState(1);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem("soul_theme");
      return saved === "dark";
    } catch {
      return false;
    }
  });
  const [colorScheme, setColorScheme] = useState(() => {
    try {
      const saved = localStorage.getItem("soul_color_scheme");
      if (saved && COLOR_SCHEMES[saved]) return saved;
      return "rose";
    } catch {
      return "rose";
    }
  });
  const [themeVersion, setThemeVersion] = useState(0);
  const [settingsInitialTab, setSettingsInitialTab] = useState(null);

  // Initialize auto-sync on mount
  useEffect(() => {
    initAutoSync();
  }, []);

  // Fetch app-wide settings (blind mode, etc.) from server on mount
  useEffect(() => {
    fetch(`${API_BASE}/api/app/settings`).then(r => r.json()).then(s => {
      const stored = JSON.parse(localStorage.getItem('app_settings') || '{}');
      const merged = { ...stored, ...s };
      localStorage.setItem('app_settings', JSON.stringify(merged));
      setAppSettings(merged);
      // blind_match_mode: default true unless server says "false"
      if (s.blind_match_mode === "false") setBlindMode(false);
      else setBlindMode(true);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Full cross-device sync: pull ALL user data from server on every app load
  useEffect(() => {
    pullFromServer().then(data => {
      if (!data || !data.user) return;
      const serverStage = Number(data.user.profile_stage) || 0;
      console.log("🔄 Server profile_stage:", serverStage);
      if (serverStage > 0) {
        const s = new Set();
        for (let i = 1; i <= serverStage; i++) s.add(i);
        setCompletedStages(prev => {
          const merged = new Set(prev);
          s.forEach(n => merged.add(n));
          return merged;
        });
        setCompletedStage(prev => Math.max(prev, serverStage));
      }
      // Apply synced theme/color if server has them
      if (data.preferences) {
        if (data.preferences.theme === "dark" && !isDarkMode) setIsDarkMode(true);
        if (data.preferences.theme === "light" && isDarkMode) setIsDarkMode(false);
        if (data.preferences.color_scheme && data.preferences.color_scheme !== colorScheme) {
          setColorScheme(data.preferences.color_scheme);
        }
      }
      // Notify child components (e.g. Settings) that sync data is available
      window.dispatchEvent(new Event("soul_sync_complete"));
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update theme when isDarkMode or colorScheme changes
  useEffect(() => {
    const scheme = COLOR_SCHEMES[colorScheme] || COLOR_SCHEMES.rose;
    T = isDarkMode ? scheme.dark : scheme.light;
    // Auto-sync theme and color scheme to Supabase
    saveTheme(isDarkMode ? "dark" : "light");
    saveColorScheme(colorScheme);
    // Force re-render by updating body background and incrementing version
    document.body.style.background = T.bg;
    setThemeVersion(v => v + 1);
  }, [isDarkMode, colorScheme]);

  const toggleTheme = () => setIsDarkMode(prev => !prev);
  const changeColorScheme = (scheme) => setColorScheme(scheme);

  // Load app-wide settings from backend (blind mode toggle etc.)
  useEffect(() => {
    fetch(`${API_BASE}/api/app/settings`)
      .then(r => r.ok ? r.json() : null)
      .then(s => { if (s) { setBlindMode(s.blind_match_mode === "true"); setAppSettings(s); } })
      .catch(() => {});
  }, []);

  // Initialize mock received communications for testing
  useEffect(() => {
    // Clear and reinitialize to ensure fresh data
    localStorage.removeItem('mock_comms_initialized');
    
    // Add mock communications for user ID 1 (Sarah) - for MatchDetail
    localStorage.setItem('received_gift_1', '💝');
    localStorage.setItem('received_poll_1', JSON.stringify({
      question: "Coffee or dinner for our first date?",
      options: ["Coffee ☕", "Dinner 🍽️", "Surprise me! ✨"]
    }));
    
    // Add mock communications for user ID 2 (Emma) - for MatchDetail and DateDetail
    localStorage.setItem('received_phone_2', '(555) 234-5678');
    localStorage.setItem('received_message_2', JSON.stringify({
      text: "Hey! Really looking forward to our date! I've been thinking about that art gallery you mentioned. 😊",
      image: null
    }));
    
    // Add mock communications for user ID 3 (Olivia) - for MatchDetail
    localStorage.setItem('received_gift_3', '🌹');
    localStorage.setItem('received_message_3', JSON.stringify({
      text: "Thanks for the great conversation! Can't wait to meet in person.",
      image: null
    }));
    
    // Add mock communications for user ID 4 (Sophia) - for MatchDetail and DateDetail
    localStorage.setItem('received_poll_4', JSON.stringify({
      question: "What kind of cuisine are you in the mood for?",
      options: ["Italian 🍝", "Japanese 🍣", "Mexican 🌮", "Mediterranean 🥙"]
    }));
    localStorage.setItem('received_gift_4', '💫');
    
    // Add mock communications for user ID 5 (Ava) - for MatchDetail
    localStorage.setItem('received_phone_5', '(555) 345-6789');
    localStorage.setItem('received_gift_5', '✨');
    
    // Add mock communications for user ID 6 (Isabella) - for DateDetail
    localStorage.setItem('received_message_6', JSON.stringify({
      text: "Just confirmed our reservation! Can't wait to see you tomorrow! 🎉",
      image: null
    }));
    
    localStorage.setItem('mock_comms_initialized', 'true');
  }, []);

  const handleLoginSuccess = (user, token) => {
    console.log("🔵 handleLoginSuccess called, user:", JSON.stringify(user));
    // Check if admin
    let isAdmin = false;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      isAdmin = !!payload.isAdmin;
    } catch {}
    // Load stage progress from login response
    const savedUser = user || {};
    console.log("🔵 profile_stage from server:", savedUser.profile_stage, typeof savedUser.profile_stage);
    if (savedUser.profile_stage) {
      const ps = Number(savedUser.profile_stage);
      setCompletedStage(ps);
      const s = new Set();
      for (let i = 1; i <= ps; i++) s.add(i);
      if (Array.isArray(savedUser.stages_completed)) {
        savedUser.stages_completed.forEach(n => s.add(n));
      }
      console.log("🔵 Setting completedStages to:", [...s]);
      setCompletedStages(s);
      try { localStorage.setItem("soul_completed_stages", JSON.stringify([...s])); } catch {}
    }
    // Admin goes to admin panel, regular user goes to profile
    setScreen(isAdmin ? "admin" : "profile");
    // Pull full sync data now that token is available (mount useEffect may have fired before login)
    pullFromServer().then(data => {
      if (!data || !data.user) return;
      const serverStage = Number(data.user.profile_stage) || 0;
      if (serverStage > 0) {
        const s2 = new Set();
        for (let i = 1; i <= serverStage; i++) s2.add(i);
        setCompletedStages(prev => {
          const merged = new Set(prev);
          s2.forEach(n => merged.add(n));
          return merged;
        });
        setCompletedStage(prev => Math.max(prev, serverStage));
      }
      if (data.preferences) {
        if (data.preferences.theme === "dark" && !isDarkMode) setIsDarkMode(true);
        if (data.preferences.theme === "light" && isDarkMode) setIsDarkMode(false);
        if (data.preferences.color_scheme && data.preferences.color_scheme !== colorScheme) {
          setColorScheme(data.preferences.color_scheme);
        }
      }
      // If server has no answers but we have local ones, push them up
      if ((!data.user_answers || Object.keys(data.user_answers).length === 0)) {
        try {
          const localAnswers = JSON.parse(localStorage.getItem("soul_user_answers") || "{}");
          if (Object.keys(localAnswers).length > 0) {
            const tk = localStorage.getItem("soul_session_token");
            if (tk) {
              const localStage = Number(data.user?.profile_stage) || Number(JSON.parse(localStorage.getItem("soul_user") || "{}").profile_stage) || 1;
              fetch(`${API_BASE}/api/interview/answers`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
                body: JSON.stringify({ answers: localAnswers, stage: localStage }),
              }).then(r => console.log("🔄 Re-pushed local answers to server:", r.status))
                .catch(e => console.error("🔄 Re-push answers failed:", e));
            }
          }
        } catch {}
      }
      window.dispatchEvent(new Event("soul_sync_complete"));
    });
  };

  const handleInterviewComplete = async (answers, stageNum = 1) => {
    // Mark stage as individually complete
    const newCompleted = new Set(completedStages);
    newCompleted.add(stageNum);
    setCompletedStages(newCompleted);
    setCompletedStage(Math.max(completedStage, stageNum));
    try { localStorage.setItem("soul_completed_stages", JSON.stringify([...newCompleted])); } catch {}
    // Also update soul_user with latest profile_stage so other devices see it on login
    try {
      const u = JSON.parse(localStorage.getItem("soul_user") || "{}");
      u.profile_stage = Math.max(u.profile_stage || 0, stageNum);
      u.stages_completed = [...newCompleted];
      localStorage.setItem("soul_user", JSON.stringify(u));
    } catch {}

    // Persist answers locally so they can sync later
    try {
      const existing = JSON.parse(localStorage.getItem("soul_user_answers") || "{}");
      const merged = { ...existing, ...answers };
      localStorage.setItem("soul_user_answers", JSON.stringify(merged));
    } catch {}

    // Save to server — use /interview/answers which updates users.profile_stage in DB
    const sessionToken = getLocalSessionToken();
    if (sessionToken) {
      // Primary: authenticated endpoint that updates user record
      try {
        const resp = await fetch(`${API_BASE}/api/interview/answers`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${sessionToken}` },
          body: JSON.stringify({ answers, stage: stageNum }),
        });
        console.log("✅ Saved stage", stageNum, "to /interview/answers, status:", resp.status);
        if (!resp.ok) console.error("❌ /interview/answers response:", await resp.text());
      } catch (e) { console.error("❌ /interview/answers save failed:", e); }
      // Secondary: also save to assessment session for guest-flow compat
      try {
        await fetch(`${API_BASE}/api/assessment/session/save`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_token: sessionToken, answers, stage_completed: stageNum }),
        });
      } catch {}
    }

    if (stageNum === 1) {
      // Stage 1 builds personality profile
      const profile = generatePersonalityProfile(answers);
      const encouragement = getStage1Encouragement(profile);
      setStage1Answers(answers);
      setPersonalityProfile({ ...profile, encouragement });
      saveStage1Locally(answers);
      setScreen("personality-snapshot");
    } else {
      // All other stages just go back to profile
      setScreen("profile");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("soul_session_token");
    localStorage.removeItem("soul_user");
    localStorage.removeItem("soul_completed_stages");
    setScreen("landing");
    setCompletedStage(0);
    setCompletedStages(new Set());
  };

  const handleSignUpAfterSnapshot = async (method) => {
    if (method === "guest") {
      setScreen("matches");
      return;
    }
    // For now, show matches — real auth flow wires in here
    setScreen("matches");
  };

  return (
    <BreakpointProvider>
      <AppContent
        screen={screen} setScreen={setScreen}
        match={match} setMatch={setMatch}
        apiMatches={apiMatches} loadingMatches={loadingMatches}
        personalityProfile={personalityProfile}
        blindMode={blindMode} setBlindMode={setBlindMode} upcomingDates={upcomingDates}
        completedStage={completedStage} completedStages={completedStages}
        startingStage={startingStage} setStartingStage={setStartingStage}
        isDarkMode={isDarkMode} toggleTheme={toggleTheme}
        colorScheme={colorScheme} changeColorScheme={changeColorScheme}
        themeVersion={themeVersion}
        handleLoginSuccess={handleLoginSuccess}
        handleInterviewComplete={handleInterviewComplete}
        handleSignUpAfterSnapshot={handleSignUpAfterSnapshot}
        handleLogout={handleLogout}
        setUpcomingDates={setUpcomingDates}
        settingsInitialTab={settingsInitialTab} setSettingsInitialTab={setSettingsInitialTab}
      />
    </BreakpointProvider>
  );
}

function AppContent({ screen, setScreen, match, setMatch, apiMatches, loadingMatches, personalityProfile, blindMode, setBlindMode, upcomingDates, completedStage, completedStages, startingStage, setStartingStage, isDarkMode, toggleTheme, colorScheme, changeColorScheme, themeVersion, handleLoginSuccess, handleInterviewComplete, handleSignUpAfterSnapshot, handleLogout, setUpcomingDates, settingsInitialTab, setSettingsInitialTab }) {
  const bp = useBreakpoint();
  const isFullscreen = ["landing", "interview", "personality-snapshot"].includes(screen);

  // Responsive layout offsets
  const sidebarWidth = 300;
  const topBarHeight = 56;
  const contentStyle = isFullscreen ? {} : bp.isDesktop
    ? { marginLeft: sidebarWidth, minHeight: "100vh" }
    : bp.isTablet
    ? { marginTop: topBarHeight, minHeight: "100vh" }
    : { paddingBottom: 70, minHeight: "100vh" };

  const handleNav = id => {
    if (id === "matches") setScreen("matches");
    if (id === "calendar") setScreen("calendar");
    if (id === "messages" && match) setScreen("chat");
    if (id === "profile") setScreen("profile");
    if (id === "myprofile") setScreen("myprofile");
    if (id === "settings") setScreen("settings");
    if (id === "admin") setScreen("admin");
  };

  // ── Mobile swipe navigation ──
  const SWIPE_SCREENS = ["profile", "matches", "calendar", "myprofile"];
  const touchRef = React.useRef({ startX: 0, startY: 0 });
  const isMobileSwipeable = !bp.isDesktop && !bp.isTablet && SWIPE_SCREENS.includes(screen);

  const handleTouchStart = React.useCallback(e => {
    if (!isMobileSwipeable) return;
    touchRef.current.startX = e.touches[0].clientX;
    touchRef.current.startY = e.touches[0].clientY;
  }, [isMobileSwipeable]);

  const handleTouchEnd = React.useCallback(e => {
    if (!isMobileSwipeable) return;
    const dx = e.changedTouches[0].clientX - touchRef.current.startX;
    const dy = e.changedTouches[0].clientY - touchRef.current.startY;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    if (absDx < 60 || absDy > absDx * 0.7) return; // too short or too vertical
    const idx = SWIPE_SCREENS.indexOf(screen);
    if (dx < 0 && idx < SWIPE_SCREENS.length - 1) {
      setScreen(SWIPE_SCREENS[idx + 1]); // swipe left → next
    } else if (dx > 0 && idx > 0) {
      setScreen(SWIPE_SCREENS[idx - 1]); // swipe right → prev
    }
  }, [isMobileSwipeable, screen, setScreen]);

  // ── Android hardware back button handler ──
  useEffect(() => {
    if (!IS_NATIVE) return;
    const listener = CapApp.addListener('backButton', () => {
      // Calendar date detail → calendar feed
      if (screen === "calendar" && window.__calendarHasDateDetail) {
        window.dispatchEvent(new Event('calendarBack'));
        return;
      }
      // Match detail → matches feed
      if (screen === "detail") { setScreen("matches"); return; }
      // Chat → match detail
      if (screen === "chat") { setScreen("detail"); return; }
      // Matches feed → dashboard (profile)
      if (screen === "matches") { setScreen("profile"); return; }
      // Calendar feed → dashboard (profile)
      if (screen === "calendar") { setScreen("profile"); return; }
      // My Profile → dashboard (profile)
      if (screen === "myprofile") { setScreen("profile"); return; }
      // Settings → dashboard (profile)
      if (screen === "settings") { setScreen("profile"); return; }
      // Dashboard (profile) → exit app
      if (screen === "profile") { CapApp.exitApp(); return; }
    });
    return () => { listener.then(l => l.remove()); };
  }, [screen, setScreen]);

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{ minHeight: "100vh", background: T.bg, color: T.text, fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,400&display=swap');
        @font-face {
          font-family: 'MBF Canno';
          src: url('/fonts/MBF Canno Regular.otf') format('opentype');
          font-weight: normal;
          font-style: normal;
          font-display: swap;
        }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 0; }
        textarea:focus, input:focus { outline: none; }
        textarea { resize: none; }
        input::placeholder, textarea::placeholder { color: ${T.muted}; }
        @keyframes qBlurIn { 0%{opacity:0} 100%{opacity:1} }
        @keyframes qBlurOut { 0%{opacity:1} 100%{opacity:0} }
        @keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes slideStackIn { 0%{opacity:0;transform:translateX(-30px) scale(0.96)} 60%{opacity:1;transform:translateX(4px) scale(1.01)} 100%{opacity:1;transform:translateX(0) scale(1)} }
        @keyframes drift { 0%,100%{transform:translate(0,0)} 50%{transform:translate(18px,-18px)} }
        @keyframes orbRing { 0%{transform:scale(0.9);opacity:0.6} 100%{transform:scale(1.4);opacity:0} }
        @keyframes dashPulse { 0%{box-shadow:0 0 0 0 rgba(244,63,94,0.35),0 0 0 0 rgba(244,63,94,0.18)} 50%{box-shadow:0 0 0 10px rgba(244,63,94,0.12),0 0 0 20px rgba(244,63,94,0.06)} 100%{box-shadow:0 0 0 16px rgba(244,63,94,0),0 0 0 28px rgba(244,63,94,0)} }
        @keyframes orbPulse { from{transform:scale(0.96)} to{transform:scale(1.04)} }
        @keyframes orbBreath { from{transform:scale(0.97)} to{transform:scale(1.03)} }
        @keyframes orbSpin { from{filter:hue-rotate(0deg)} to{filter:hue-rotate(360deg)} }
        @keyframes wave { from{height:4px} to{height:24px} }
        @keyframes bounce { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-10px)} }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes voicemailProgress { from{width:0%} to{width:100%} }
        @keyframes emojiPop { 0%{opacity:0;transform:translateY(10px) scale(0.7)} 60%{transform:translateY(-3px) scale(1.15)} 100%{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes reactionSlideUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        @keyframes stageProgressPulse { 0%,100%{opacity:1} 50%{opacity:0.88} }
        @keyframes stageBounceIn { 0%{opacity:0;transform:translateY(20px) scale(0.8)} 60%{transform:translateY(-4px) scale(1.05)} 100%{opacity:1;transform:none} }
        @keyframes shimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        @keyframes aurora { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
        @keyframes barFill { from{width:0%} to{width:var(--bar-w,100%)} }
        @keyframes confettiFall { 0%{transform:translateY(-20px) rotate(0deg);opacity:1} 100%{transform:translateY(120px) rotate(720deg);opacity:0} }
        @keyframes ripple { 0%{transform:scale(0);opacity:0.6} 100%{transform:scale(2.5);opacity:0} }
        @keyframes press { 0%,100%{transform:scale(1)} 50%{transform:scale(0.96)} }
        @keyframes countdownPulse { 0%,100%{opacity:1} 50%{opacity:0.6} }
        @keyframes tickerFlip { 0%{transform:translateY(-100%);opacity:0} 20%{transform:translateY(0);opacity:1} 80%{transform:translateY(0);opacity:1} 100%{transform:translateY(0);opacity:1} }
        @keyframes typingDot { 0%,80%,100%{transform:scale(0.6);opacity:0.4} 40%{transform:scale(1);opacity:1} }
        @keyframes streakGlow { 0%,100%{box-shadow:0 0 8px #f59e0b60} 50%{box-shadow:0 0 20px #f59e0baa,0 0 40px #f59e0b40} }
        @keyframes slideInRight { from{opacity:0;transform:translateX(20px)} to{opacity:1;transform:translateX(0)} }
        @keyframes toastIn { 0%{opacity:0;transform:translateY(16px) scale(0.95)} 100%{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes upgradePulse { 0%,100%{transform:scale(1);box-shadow:0 0 0 0 rgba(244,63,94,0.4)} 50%{transform:scale(1.04);box-shadow:0 0 16px 4px rgba(244,63,94,0.2)} }
        @keyframes iconPulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.08)} }
        @keyframes iconFloat { 0%,100%{transform:translateY(0px)} 50%{transform:translateY(-6px)} }
        @keyframes iconOnload { 0%{opacity:0;transform:translateY(20px) scale(0.8)} 100%{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes slideInDown { 0%{opacity:0;transform:translateX(-50%) translateY(-10px)} 100%{opacity:1;transform:translateX(-50%) translateY(0)} }
        @keyframes modalSlideUp { 0%{opacity:0;transform:translateY(30px) scale(0.95)} 100%{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes modalZoom { 0%{opacity:0;transform:translate(-50%, -50%) scale(0.8)} 100%{opacity:1;transform:translate(-50%, -50%) scale(1)} }
        @keyframes acceptButtonBurst { 0%{transform:scale(1)} 10%{transform:scale(1.08)} 20%{transform:scale(0.98)} 30%{transform:scale(1.05)} 40%{transform:scale(1)} 100%{transform:scale(1)} }
        @keyframes heartBeat { 0%,100%{transform:scale(1)} 14%{transform:scale(1.2)} 28%{transform:scale(1)} }
        @keyframes sparkle { 0%,100%{transform:rotate(0deg) scale(1);opacity:1} 25%{transform:rotate(180deg) scale(1.3);opacity:0.8} 50%{transform:rotate(360deg) scale(1);opacity:1} }
        @keyframes glowPulse { 0%,100%{opacity:0.3} 50%{opacity:0.6} }
        @keyframes widgetSlideUp { 0%{opacity:0;transform:translateY(30px)} 100%{opacity:1;transform:translateY(0)} }
        @keyframes widgetScale { 0%{opacity:0;transform:scale(0.85)} 100%{opacity:1;transform:scale(1)} }
        @keyframes progressFill { from{stroke-dashoffset:var(--dash-total,283)} to{stroke-dashoffset:var(--dash-offset,0)} }
        @keyframes numberCount { from{opacity:0} to{opacity:1} }
        @keyframes shimmerLoad { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        @keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
        .add-ctx-btn {
          display: inline-flex; align-items: center; justify-content: center;
          gap: 4px; overflow: hidden;
          height: 28px;
          padding: 0 10px 0 8px;
          border-radius: 6px;
          border: none;
          background: var(--btn-color);
          color: #fff;
          cursor: pointer;
          opacity: 1;
          transition: box-shadow 0.25s ease, filter 0.2s ease;
          white-space: nowrap;
        }
        .add-ctx-btn:hover, .add-ctx-btn--expanded {
          box-shadow: 0 2px 12px var(--btn-color-border);
          filter: brightness(1.1);
        }
        .add-ctx-btn__plus {
          font-size: 17px; font-weight: 300; line-height: 1; flex-shrink: 0;
          transition: transform 0.38s cubic-bezier(0.34,1.56,0.64,1);
        }
        .add-ctx-btn:hover .add-ctx-btn__plus,
        .add-ctx-btn--expanded .add-ctx-btn__plus { transform: rotate(90deg); }
        .add-ctx-btn__static {
          font-size: 11px; font-weight: 600; letter-spacing: 0.05em;
          flex-shrink: 0;
        }
        .add-ctx-btn__label {
          font-size: 11px; font-weight: 600; letter-spacing: 0.05em;
          max-width: 0; opacity: 0; overflow: hidden;
          transition: max-width 0.32s cubic-bezier(0.34,1.56,0.64,1) 0.05s, opacity 0.22s ease 0.1s;
        }
        .add-ctx-btn:hover .add-ctx-btn__label,
        .add-ctx-btn--expanded .add-ctx-btn__label { max-width: 60px; opacity: 1; }
        @keyframes widgetGlow { 0%,100%{box-shadow:0 2px 8px rgba(0,0,0,0.08)} 50%{box-shadow:0 4px 16px rgba(0,0,0,0.12)} }
        button { transition: transform 0.1s ease, box-shadow 0.2s ease; }
        button:active { animation: press 0.15s ease; }
      `}</style>

      <div style={contentStyle}>
        {screen === "landing"              && <Landing onStart={() => setScreen("interview")} onLoginSuccess={handleLoginSuccess} />}
        {screen === "interview"            && <VoiceInterview onComplete={handleInterviewComplete} onPause={() => setScreen("profile")} startAtStage={startingStage} />}
        {screen === "personality-snapshot" && personalityProfile && <PersonalitySnapshot profile={personalityProfile} onContinue={handleSignUpAfterSnapshot} />}
        {screen === "matches"              && <MatchesFeed matches={apiMatches || MATCHES} loading={loadingMatches} blindMode={blindMode} upcomingDates={upcomingDates} onSelectMatch={m => { setMatch(m); setScreen("detail"); }} />}
        {screen === "detail"               && match && <MatchDetail match={match} onBack={() => setScreen("matches")} onMessage={m => { setMatch(m); setScreen("chat"); }} blindMode={blindMode} onDateConfirmed={d => setUpcomingDates(prev => [...prev, d])} onNavigateToUpgrade={() => { setSettingsInitialTab("upgrade"); setScreen("settings"); }} />}
        {screen === "chat"                 && match && <VoiceChat match={match} onBack={() => setScreen("detail")} blindMode={blindMode} />}
        {screen === "profile"              && <UserProfile profile={personalityProfile} completedStage={completedStage} completedStages={completedStages} isPremium={false} onBack={() => setScreen("matches")} onStartAdmin={getStoredSession()?.isAdmin ? () => setScreen("admin") : undefined} onStartStage={n => { setStartingStage(n); setScreen("interview"); }} onEditPreferences={() => setScreen("matches")} onSelectMatch={m => { setMatch(m); setScreen("detail"); }} onLogout={handleLogout} isDarkMode={isDarkMode} toggleTheme={toggleTheme} colorScheme={colorScheme} changeColorScheme={changeColorScheme} blindMode={blindMode} onNavigateToSettings={() => setScreen("settings")} />}
        {screen === "myprofile"            && <MyProfilePage profile={personalityProfile} completedStages={completedStages} onStartStage={n => { setStartingStage(n); setScreen("interview"); }} onBack={() => setScreen("profile")} />}
        {screen === "calendar"             && <Calendar blindMode={blindMode} onNavigateToUpgrade={() => { setSettingsInitialTab("upgrade"); setScreen("settings"); }} />}
        {screen === "settings"             && <Settings profile={personalityProfile} onBack={() => { setSettingsInitialTab(null); setScreen("profile"); }} onEditPreferences={() => setScreen("matches")} isDarkMode={isDarkMode} toggleTheme={toggleTheme} colorScheme={colorScheme} changeColorScheme={changeColorScheme} initialTab={settingsInitialTab} onClearInitialTab={() => setSettingsInitialTab(null)} />}
        {screen === "admin"                && <AdminPanel setBlindMode={setBlindMode} />}
      </div>

      <AppNav screen={screen} onNav={handleNav} onLogout={handleLogout} />
    </div>
  );
}
